
import { Type } from "@google/genai";
import { Language } from "../contexts/types";
import { type AtlasReferenceCase, formatAtlasReferenceCasesForPrompt } from "../data/atlasReferenceCases";
import { resolveOrthoBannerTemplateAsset } from "../utils/doctorProfile";
import { coalesceSurgicalPlanAliases } from "../utils/surgicalPlanAliases";
import {
  normalizeSurgicalPlanV2,
  summarizeSurgicalPlanV2,
  validateSurgicalPlanV2,
  type SurgicalPlanV2,
} from "../utils/surgicalPlanV2";
import { createPrivateGenAI } from "./aiFunctions";

type PrivateAiRoute =
  | "generateAtlasAnalysis"
  | "generateAtlasImage"
  | "analyzeMedicalReport"
  | "generateSurgicalPlan"
  | "generateBannerAI"
  | "searchOrthopedicGuidelines"
  | "generateContent";

const createGeminiClient = (route: PrivateAiRoute = "generateContent") => createPrivateGenAI(route);

// Increased timeout to 90s to accommodate 4K image generation
const fetchWithRetry = async (fn: () => Promise<any>, retries = 1, delay = 2000, timeoutMs = 90000): Promise<any> => {
  const timeoutPromise = new Promise((_, reject) => 
    setTimeout(() => reject(new Error(`Request timed out after ${timeoutMs/1000}s`)), timeoutMs)
  );

  try {
    return await Promise.race([fn(), timeoutPromise]);
  } catch (error: any) {
    let errorMsg = "Unknown Error";
    let status = 0;

    try {
      const parsed = typeof error === 'string' ? JSON.parse(error) : error;
      // Ensure errorMsg is a string, even if message is missing
      errorMsg = parsed?.message || parsed?.error?.message || (typeof error === 'object' ? JSON.stringify(error) : String(error));
      if (!errorMsg) errorMsg = "Unknown Error"; // Fallback if stringify fails or returns empty
      
      status = parsed?.status || parsed?.error?.code || error?.status || 0;
    } catch {
      errorMsg = String(error);
    }
    
    // Non-retryable errors
    const isQuotaError = status === 429 || errorMsg.includes('429') || errorMsg.includes('RESOURCE_EXHAUSTED');
    const isPermissionError = status === 403 || errorMsg.includes('403') || errorMsg.includes('PERMISSION_DENIED');
    const isSafetyError = errorMsg.includes('SAFETY') || errorMsg.includes('blocked');

    if (isQuotaError || isPermissionError || isSafetyError) {
      const cleanError = new Error(errorMsg);
      (cleanError as any).status = status || (isQuotaError ? 429 : isPermissionError ? 403 : 500);
      throw cleanError;
    }

    // Retryable errors (Server errors, Timeouts)
    const isRetryable = errorMsg.includes('503') || status === 503 || errorMsg.includes('UNAVAILABLE') || errorMsg.includes('timed out');
    
    if (isRetryable && retries > 0) {
      console.warn(`Retrying... attempts left: ${retries}. Error: ${errorMsg}`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return fetchWithRetry(fn, retries - 1, delay * 1.5, timeoutMs + 10000); // Increase timeout on retry
    }
    throw error;
  }
};

const getSystemInstruction = (lang: Language) => {
  if (lang === 'en') {
    return "You are a Senior Musculoskeletal Radiologist. Provide precise technical medical data in English. Generate the response in the user's selected language.";
  }
  if (lang === 'es') {
    return "Eres un RadiÃƒÂ³logo MusculoesquelÃƒÂ©tico Senior. Proporciona datos mÃƒÂ©dicos tÃƒÂ©cnicos precisos en EspaÃƒÂ±ol. Genera la respuesta en el idioma seleccionado por el usuario.";
  }
  return "VocÃƒÂª ÃƒÂ© um Radiologista MusculoesquelÃƒÂ©tico SÃƒÂªnior. ForneÃƒÂ§a dados tÃƒÂ©cnicos precisos e detalhados em PortuguÃƒÂªs. Gere a resposta no idioma selecionado pelo usuÃƒÂ¡rio.";
};

const getSelectedLanguageInstruction = (lang: Language) => {
  if (lang === 'en') return "Generate the response in the user's selected language: English.";
  if (lang === 'es') return "Genera la respuesta en el idioma seleccionado por el usuario: EspaÃƒÂ±ol.";
  return "Gere a resposta no idioma selecionado pelo usuÃƒÂ¡rio: PortuguÃƒÂªs.";
};

const getClinicalResponseLanguageName = (lang: Language) => {
  if (lang === 'en') return 'English';
  if (lang === 'es') return 'Spanish';
  return 'Portuguese';
};

const getClinicalResponseInstruction = (lang: Language) =>
  `IMPORTANT: The response MUST be written in ${getClinicalResponseLanguageName(lang)}. Do not mix languages. All clinical guidance, surgical technique, tips and pitfalls, contingency plan, materials, and other textual medical content must be in ${getClinicalResponseLanguageName(lang)}.`;

const cleanAiJsonResponse = (raw: string): string => {
  return String(raw || "")
    .replace(/```json/gi, "")
    .replace(/```/g, "")
    .replace(/Here is the JSON requested:/gi, "")
    .trim();
};

const extractJsonObject = (raw: string): Record<string, any> | null => {
  const cleaned = cleanAiJsonResponse(raw);

  try {
    return JSON.parse(cleaned);
  } catch {
    // continue
  }

  const firstBrace = cleaned.indexOf("{");
  const lastBrace = cleaned.lastIndexOf("}");
  if (firstBrace >= 0 && lastBrace > firstBrace) {
    const jsonSlice = cleaned.slice(firstBrace, lastBrace + 1);
    try {
      return JSON.parse(jsonSlice);
    } catch {
      return null;
    }
  }

  return null;
};

// Helper to clean JSON string from Markdown code blocks and chatty prefixes
const cleanJson = (text: string): string => {
  const cleaned = cleanAiJsonResponse(text);
  const firstBrace = cleaned.indexOf('{');
  const lastBrace = cleaned.lastIndexOf('}');
  if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
    return cleaned.substring(firstBrace, lastBrace + 1);
  }
  return cleaned;
};

const parseJsonObjectFromAiText = (raw: string): Record<string, any> | null => extractJsonObject(raw);

const normalizeDocumentText = (value: string) =>
  value
    .replace(/\r/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeMedicalReportDocumentContent = (content: string) => {
  const text = String(content || '').trim();
  if (!text) return '';

  if (!/<\/?[a-z][\s\S]*>/i.test(text)) {
    return text;
  }

  if (typeof DOMParser === 'undefined') {
    return text
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|section|article|h[1-6]|li)>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'text/html');

  const inlineToMarkdown = (node: ChildNode): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return normalizeDocumentText(node.textContent || '');
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();
    const children = Array.from(el.childNodes).map(inlineToMarkdown).join(' ');

    if (tag === 'br') return '\n';
    if (tag === 'strong' || tag === 'b') return `**${normalizeDocumentText(children)}**`;
    if (tag === 'em' || tag === 'i') return normalizeDocumentText(children);
    if (tag === 'span' || tag === 'a' || tag === 'u') return normalizeDocumentText(children);
    return normalizeDocumentText(children);
  };

  const blockToMarkdown = (node: ChildNode): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return normalizeDocumentText(node.textContent || '');
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return '';
    }

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    if (tag === 'br') return '\n';
    if (tag === 'h1' || tag === 'h2' || tag === 'h3') {
      const level = Number(tag.slice(1));
      const textContent = Array.from(el.childNodes).map(inlineToMarkdown).join(' ');
      return `${'#'.repeat(level)} ${normalizeDocumentText(textContent)}\n\n`;
    }
    if (tag === 'p') {
      const textContent = Array.from(el.childNodes).map(inlineToMarkdown).join(' ');
      return `${normalizeDocumentText(textContent)}\n\n`;
    }
    if (tag === 'ul' || tag === 'ol') {
      const ordered = tag === 'ol';
      const items = Array.from(el.children)
        .filter((child) => child.tagName.toLowerCase() === 'li')
        .map((li, index) => {
          const liContent = Array.from(li.childNodes).map(inlineToMarkdown).join(' ');
          return `${ordered ? `${index + 1}.` : '- '} ${normalizeDocumentText(liContent)}`;
        })
        .join('\n');
      return `${items}\n\n`;
    }
    if (tag === 'li') {
      const textContent = Array.from(el.childNodes).map(inlineToMarkdown).join(' ');
      return `- ${normalizeDocumentText(textContent)}\n`;
    }
    if (tag === 'div' || tag === 'section' || tag === 'article' || tag === 'header' || tag === 'footer') {
      return Array.from(el.childNodes).map(blockToMarkdown).join('\n');
    }

    return Array.from(el.childNodes).map(blockToMarkdown).join(' ');
  };

  return Array.from(doc.body.childNodes)
    .map(blockToMarkdown)
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+\n/g, '\n')
    .trim();
};

const downscaleImageDataUrl = async (
  dataUrl: string,
  maxDimension = 1280,
  quality = 0.82
): Promise<string> => {
  if (typeof window === "undefined") return dataUrl;
  if (!dataUrl.startsWith("data:image/")) return dataUrl;

  const mimeMatch = dataUrl.match(/^data:(image\/[a-zA-Z0-9+.-]+);base64,/);
  const mimeType = mimeMatch?.[1] || "image/jpeg";

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const longestSide = Math.max(img.width, img.height);
      if (!longestSide || longestSide <= maxDimension) {
        resolve(dataUrl);
        return;
      }

      const scale = maxDimension / longestSide;
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        resolve(dataUrl);
        return;
      }

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL(mimeType, quality));
    };

    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
};

type AtlasPreparedImage = {
  base64: string;
  mimeType: string;
  width: number;
  height: number;
  originalSizeBytes: number;
  finalSizeBytes: number;
  usedCompression: boolean;
};

const SUPPORTED_ATLAS_IMAGE_MIME_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const ATLAS_IMAGE_MAX_DIMENSION = 1280;
const ATLAS_IMAGE_MAX_FINAL_BYTES = 2_500_000;

const stripDataUrlPrefix = (value: string) => value.replace(/^data:[^;]+;base64,/, "");

const estimateBase64Bytes = (base64: string) => Math.max(0, Math.floor((base64.length * 3) / 4));

type AtlasImageInputPayload = {
  imageBase64: string;
  mimeType: string;
  base64: string;
  dataUrl: string;
};

const normalizeAtlasImageInput = (imageBase64: string): AtlasImageInputPayload => {
  const raw = String(imageBase64 || "").trim();
  const mimeMatch = raw.match(/^data:([^;]+);base64,/i);
  const mimeType = String(mimeMatch?.[1] || "image/jpeg").toLowerCase();
  const base64 = stripDataUrlPrefix(raw).replace(/\s+/g, "");
  if (!SUPPORTED_ATLAS_IMAGE_MIME_TYPES.has(mimeType)) {
    throw new Error("Formato de imagem não suportado.");
  }
  return {
    imageBase64: base64,
    mimeType,
    base64,
    dataUrl: `data:${mimeType};base64,${base64}`,
  };
};

const readFileAsDataUrl = (file: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Falha ao ler arquivo de imagem."));
    reader.readAsDataURL(file);
  });

const loadImageFromDataUrl = (dataUrl: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Falha ao carregar imagem para compressão."));
    image.src = dataUrl;
  });

const canvasToJpegBase64 = async (canvas: HTMLCanvasElement, quality: number) => {
  if (canvas.toBlob) {
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
    if (blob) {
      const buffer = await blob.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      let binary = "";
      for (let i = 0; i < bytes.length; i += 1) {
        binary += String.fromCharCode(bytes[i]);
      }
      return btoa(binary);
    }
  }

  return stripDataUrlPrefix(canvas.toDataURL("image/jpeg", quality));
};

export const prepareAtlasImageForFunction = async (
  fileOrDataUrl: File | Blob | string,
  options?: {
    maxDimension?: number;
    quality?: number;
    maxFinalBytes?: number;
  }
): Promise<AtlasPreparedImage> => {
  const maxDimension = options?.maxDimension ?? ATLAS_IMAGE_MAX_DIMENSION;
  const quality = options?.quality ?? 0.82;
  const maxFinalBytes = options?.maxFinalBytes ?? ATLAS_IMAGE_MAX_FINAL_BYTES;

  const sourceDataUrl =
    typeof fileOrDataUrl === "string"
      ? fileOrDataUrl.trim()
      : await readFileAsDataUrl(fileOrDataUrl);

  if (!sourceDataUrl) {
    throw new Error("Imagem muito grande. Reduza a resolução ou envie outra imagem.");
  }

  if (!sourceDataUrl.startsWith("data:image/")) {
    throw new Error("Imagem muito grande. Reduza a resolução ou envie outra imagem.");
  }

  const mimeMatch = sourceDataUrl.match(/^data:([^;]+);base64,/i);
  const inputMimeType = String(mimeMatch?.[1] || "").toLowerCase();

  if (!SUPPORTED_ATLAS_IMAGE_MIME_TYPES.has(inputMimeType)) {
    throw new Error("Imagem muito grande. Reduza a resolução ou envie outra imagem.");
  }

  const inputBase64 = stripDataUrlPrefix(sourceDataUrl);
  if (!inputBase64.trim()) {
    throw new Error("Imagem muito grande. Reduza a resolução ou envie outra imagem.");
  }

  const originalSizeBytes = estimateBase64Bytes(inputBase64);
  const image = await loadImageFromDataUrl(sourceDataUrl);

  if (originalSizeBytes < 1_000_000) {
    return {
      base64: inputBase64,
      mimeType: inputMimeType,
      width: image.width || 1,
      height: image.height || 1,
      originalSizeBytes,
      finalSizeBytes: originalSizeBytes,
      usedCompression: false,
    };
  }

  const longestSide = Math.max(image.width || 0, image.height || 0);
  const scale = longestSide > maxDimension ? maxDimension / longestSide : 1;
  const width = Math.max(1, Math.round((image.width || 1) * scale));
  const height = Math.max(1, Math.round((image.height || 1) * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("Imagem muito grande. Reduza a resolução ou envie outra imagem.");
  }

  context.drawImage(image, 0, 0, width, height);

  const finalBase64 = await canvasToJpegBase64(canvas, quality);
  const finalSizeBytes = estimateBase64Bytes(finalBase64);

  if (!finalBase64.trim() || finalSizeBytes > maxFinalBytes) {
    throw new Error("Imagem muito grande. Reduza a resolução ou envie outra imagem.");
  }

  return {
    base64: finalBase64,
    mimeType: "image/jpeg",
    width,
    height,
    originalSizeBytes,
    finalSizeBytes,
    usedCompression: true,
  };
};

const normalizeClinicalText = (value: unknown) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const estimateFast2DLesionLocalization = (analysis: AtlasImageAnalysis, anatomyContext?: string) => {
  const hint = normalizeClinicalText([
    anatomyContext,
    analysis.examRegion,
    analysis.anatomicalRegion,
    analysis.fractureLocation,
    analysis.suspectedSubregion,
    analysis.anatomicalAnchors,
    analysis.atlasFindings?.anatomyRegion,
    analysis.atlasFindings?.alignmentNotes,
  ].filter(Boolean).join(" "));

  const makeBox = (xmin: number, ymin: number, xmax: number, ymax: number, imagingEvidenceRegion: string, confidence = 0.58) => {
    const adjustedConfidence = Math.max(
      0.35,
      Math.min(
        0.82,
        confidence +
          (analysis.fractureConfidence === "alta" ? 0.12 : analysis.fractureConfidence === "moderada" ? 0.04 : -0.06)
      )
    );

    return {
      xmin,
      ymin,
      xmax,
      ymax,
      confidence: adjustedConfidence,
      imagingEvidenceRegion,
    };
  };

  if (!hint) {
    return makeBox(320, 260, 680, 620, "central technical focus");
  }

  if (/(patella|knee|joelho)/.test(hint)) {
    return makeBox(260, 250, 720, 600, "knee / patella");
  }

  if (/(ankle|tornozelo|foot|pe)/.test(hint)) {
    return makeBox(280, 330, 700, 650, "ankle / foot");
  }

  if (/(wrist|punho|carpo|hand|mao|mcp|metacar)/.test(hint)) {
    return makeBox(270, 240, 690, 520, "wrist / hand");
  }

  if (/(femur|hip|quadril|pelvis|pelve)/.test(hint)) {
    return makeBox(300, 220, 700, 650, "hip / femur");
  }

  if (/(shoulder|ombro|humer|clavicula|clavicle)/.test(hint)) {
    return makeBox(280, 230, 720, 560, "shoulder / humerus");
  }

  if (/(spine|coluna|vertebra|vertebral)/.test(hint)) {
    return makeBox(400, 160, 620, 700, "spine");
  }

  return makeBox(320, 260, 680, 620, "technical focus");
};

export type StructuredFractureCase = {
  examType: "radiografia";
  examRegion: string;
  laterality: string;
  suspectedBone: string;
  suspectedSubregion: string;
  primaryBone?: string;
  boneSegment?: string;
  fractureLineDescription?: string;
  imagingEvidence?: string[];
  associatedFindings?: string[];
  fractureLocation: string;
  modalityExpertView: string;
  fracturePattern: string;
  displacementLevel: "ausente" | "leve" | "moderado" | "importante";
  articularExtension: boolean;
  alignmentPreserved: boolean;
  fractureVisibility: "sutil" | "clara" | "indeterminada";
  fractureConfidence: "baixa" | "moderada" | "alta";
  confidenceReason: string;
  differentialNotes: string;
  recommendFurtherImaging: boolean;
  furtherImagingSuggestion: string;
  recommendedConduct: "conservador" | "possivelmente_cirurgico" | "cirurgico";
  diagnosticSummary: string;
  conductRationale: string;
  uncertaintyNote: string;
  primaryStructureType?: "ossea" | "ligamentar" | "tendinea" | "meniscal" | "mista" | "indeterminada";
  primaryStructureName?: string;
  associatedStructures?: string[];
  lesionConfidence?: "baixa" | "moderada" | "alta";
};

const hasExplicitUncertainty = (value: unknown) => {
  const normalized = normalizeClinicalText(value);
  if (!normalized) return false;
  return [
    "indeterminado",
    "inconclusivo",
    "incerto",
    "duvidoso",
    "nao conclusivo",
    "nao definido",
    "sem evidencia suficiente",
    "evidencia insuficiente",
    "imagem insuficiente",
    "cannot determine",
    "uncertain",
    "equivocal",
  ].some((term) => normalized.includes(term));
};

const buildFractureNarrative = (
  source: {
    examRegion?: string;
    laterality?: string;
    suspectedBone?: string;
    suspectedSubregion?: string;
              primaryBone?: string;
              boneSegment?: string;
              fractureLineDescription?: string;
              imagingEvidence?: string[];
              associatedFindings?: string[];
    hasVisibleFracture?: boolean;
    fractureConfidence?: string;
    fractureVisibility?: string;
    fracturePattern?: string;
    corticalDisruption?: boolean;
    trabecularDisruption?: boolean;
    displacementLevel?: string;
    articularExtension?: boolean;
    alignmentPreserved?: boolean;
    associatedInstability?: string;
    diagnosticCertaintyText?: string;
    diagnosticUncertainty?: string;
    recommendFurtherImaging?: boolean;
    furtherImagingSuggestion?: string;
    conductRationale?: string;
    recommendedConduct?: string;
    anatomicalRegion?: string;
    boneInvolved?: string;
    hasFracture?: boolean;
    fractureType?: string;
    fractureLocation?: string;
    displacement?: string;
    articularInvolvement?: boolean;
    comminution?: boolean;
    alignmentStatus?: string;
    instabilitySigns?: string;
    severityLevel?: string;
    treatmentRecommendation?: string;
    treatmentRationale?: string;
    uncertaintyNote?: string;
  },
  lang: Language = "pt"
) => {
  const region = String(source.examRegion || source.anatomicalRegion || "").trim();
  const bone = String(source.primaryBone || source.suspectedBone || source.boneInvolved || region).trim();
  const subregion = String(source.boneSegment || source.suspectedSubregion || "").trim();
  const fractureType = String(source.fracturePattern || source.fractureType || "").trim();
  const fractureLocation = String(source.fractureLocation || source.boneSegment || source.suspectedSubregion || "").trim();
  const imagingEvidence = Array.isArray(source.imagingEvidence)
    ? source.imagingEvidence.map((item) => String(item || "").trim()).filter(Boolean)
    : [];
  const fractureLineDescription = String(source.fractureLineDescription || imagingEvidence[0] || "").trim();
  const displacement = String(source.displacementLevel || source.displacement || "").trim();
  const alignmentStatus = String(source.alignmentStatus || "").trim();
  const instabilitySigns = String(source.associatedInstability || source.instabilitySigns || "").trim();
  const severityLevel = String(source.severityLevel || "").trim();
  const uncertaintyNote = String(source.diagnosticUncertainty || source.uncertaintyNote || "").trim();
  const furtherImagingSuggestion = String(source.furtherImagingSuggestion || "").trim();
  const recommendFurtherImaging = Boolean(source.recommendFurtherImaging);
  const hasFracture =
    typeof source.hasVisibleFracture === "boolean"
      ? source.hasVisibleFracture
      : typeof source.hasFracture === "boolean"
        ? source.hasFracture
        : undefined;
  const fractureConfidence = normalizeClinicalText(source.fractureConfidence);
  const fractureVisibility = normalizeClinicalText(source.fractureVisibility);
  const cautious =
    fractureVisibility === "indeterminada" ||
    fractureConfidence === "baixa" ||
    fractureConfidence === "moderada" ||
    hasExplicitUncertainty(uncertaintyNote) ||
    hasFracture === false;

  if (cautious) {
    const scope = bone || region || (lang === "en" ? "the analyzed region" : lang === "es" ? "la regiÃ³n analizada" : "a regiÃ£o analisada");
    const preciseLocation = fractureLocation || subregion || "";
    const subregionText =
      fractureConfidence === "alta"
        ? preciseLocation
        : preciseLocation
          ? preciseLocation
          : (lang === "en" ? "indeterminate subregion" : lang === "es" ? "subregiÃ³n indeterminada" : "subregiÃ£o indeterminada");
    const note = uncertaintyNote || (lang === "en"
      ? "Imaging evidence is insufficient to confirm a definite fracture."
      : lang === "es"
        ? "La evidencia de imagen es insuficiente para confirmar una fractura definitiva."
        : "A evidência de imagem é insuficiente para confirmar uma fratura definitiva.");
    const imagingNote = recommendFurtherImaging
      ? (furtherImagingSuggestion ||
          (lang === "en" ? "Recommend CT or MRI correlation." : lang === "es" ? "Se recomienda correlaciï¿½n con TC o RM." : "Recomenda-se correlaï¿½ï¿½o com TC ou RM."))
      : "";
    const suspected =
      lang === "en"
      ? hasFracture === false
          ? `Indeterminate traumatic abnormality in ${scope}`.trim()
          : `Suspected fracture in ${scope}${subregionText ? `, ${subregionText}` : ""}`.trim()
        : lang === "es"
          ? hasFracture === false
            ? `Hallazgo traumático indeterminado en ${scope}`.trim()
            : `Sospecha de fractura en ${scope}${subregionText ? `, ${subregionText}` : ""}`.trim()
          : hasFracture === false
            ? `Achado traumático indeterminado em ${scope}`.trim()
            : `Suspeita de fratura em ${scope}${subregionText ? `, ${subregionText}` : ""}`.trim();
    return lang === "en"
      ? `${suspected}. ${note} ${imagingNote || "Clinical correlation and CT are recommended."}`.trim()
      : lang === "es"
        ? `${suspected}. ${note} ${imagingNote || "Se recomienda correlaciÃ³n clÃ­nica y TC."}`.trim()
        : `${suspected}. ${note} ${imagingNote || "Recomenda-se correlação clínica e TC."}`.trim();
  }

  const parts: string[] = [];
  const canSpecifySubregion = fractureConfidence === "alta" && (subregion || fractureLocation);
  const title =
    fractureType ||
    (lang === "en" ? "fracture" : lang === "es" ? "fractura" : "fratura");

  if (lang === "en") {
    parts.push(`${title} of ${bone || region || "the analyzed bone"}`);
    if (canSpecifySubregion) parts.push(`at ${fractureLocation || subregion}`);
    else if (fractureLocation) parts.push(`at ${fractureLocation}`);
    if (fractureLineDescription) parts.push(fractureLineDescription);
    if (displacement && displacement !== "ausente") parts.push(`${displacement} displacement`);
    if (source.articularInvolvement) parts.push("with articular involvement");
    if (source.comminution) parts.push("comminuted");
    if (alignmentStatus) parts.push(alignmentStatus);
    if (instabilitySigns) parts.push(instabilitySigns);
    if (severityLevel) parts.push(`severity ${severityLevel}`);
  } else if (lang === "es") {
    parts.push(`${title} de ${bone || region || "el hueso analizado"}`);
    if (canSpecifySubregion) parts.push(`en ${fractureLocation || subregion}`);
    else if (fractureLocation) parts.push(`en ${fractureLocation}`);
    if (fractureLineDescription) parts.push(fractureLineDescription);
    if (displacement && displacement !== "ausente") parts.push(`desplazamiento ${displacement}`);
    if (source.articularInvolvement) parts.push("con compromiso articular");
    if (source.comminution) parts.push("conminuta");
    if (alignmentStatus) parts.push(alignmentStatus);
    if (instabilitySigns) parts.push(instabilitySigns);
    if (severityLevel) parts.push(`gravedad ${severityLevel}`);
  } else {
    parts.push(`${title} de ${bone || region || "o osso analisado"}`);
    if (canSpecifySubregion) parts.push(`na ${fractureLocation || subregion}`);
    else if (fractureLocation) parts.push(`na ${fractureLocation}`);
    if (fractureLineDescription) parts.push(fractureLineDescription);
    if (displacement && displacement !== "ausente") parts.push(`desvio ${displacement}`);
    if (source.articularInvolvement) parts.push("com acometimento articular");
    if (source.comminution) parts.push("cominuta");
    if (alignmentStatus) parts.push(alignmentStatus);
    if (instabilitySigns) parts.push(instabilitySigns);
    if (severityLevel) parts.push(`gravidade ${severityLevel}`);
  }

  return `${parts.filter(Boolean).join(", ").replace(/\s+/g, " ").replace(/,\s*,/g, ",").trim()}.`;
};

export interface AtlasFindings {
  anatomyRegion: string;
  bone: string;
  side: string;
  lesionCategory?: "bone_fracture" | "bone_contusion" | "soft_tissue_cyst" | "meniscal_lesion" | "ligament_lesion" | "cartilage_lesion" | "tendon_lesion" | "spine_disc" | "unknown";
  visibleFindings: string[];
  degenerativeSigns: string[];
  fractureSigns: string[];
  boneLossSigns: string[];
  alignmentNotes: string;
  primaryBone?: string;
  boneSegment?: string;
  fractureLineDescription?: string;
  imagingEvidence?: string[];
  associatedFindings?: string[];
  primaryStructureType?: "ossea" | "ligamentar" | "tendinea" | "meniscal" | "mista" | "indeterminada";
  primaryStructureName?: string;
  associatedStructures?: string[];
  lesionConfidence?: "baixa" | "moderada" | "alta";
}

export interface AtlasImageAnalysis {
  examType: "radiografia";
  examRegion: string;
  laterality: string;
  lesionCategory?: "bone_fracture" | "bone_contusion" | "soft_tissue_cyst" | "meniscal_lesion" | "ligament_lesion" | "cartilage_lesion" | "tendon_lesion" | "spine_disc" | "unknown";
  suspectedBone: string;
  suspectedSubregion: string;
  primaryBone?: string;
  boneSegment?: string;
  fractureLineDescription?: string;
  imagingEvidence?: string[];
  associatedFindings?: string[];
  modalityExpertView: string;
  hasVisibleFracture: boolean;
  fractureConfidence: "baixa" | "moderada" | "alta";
  confidenceReason: string;
  fractureVisibility: "sutil" | "clara" | "indeterminada";
  fracturePattern: string;
  corticalDisruption: boolean;
  trabecularDisruption: boolean;
  displacementLevel: "ausente" | "leve" | "moderado" | "importante";
  articularExtension: boolean;
  alignmentPreserved: boolean;
  associatedInstability: string;
  differentialNotes: string;
  diagnosticCertaintyText: string;
  diagnosticUncertainty: string;
  recommendFurtherImaging: boolean;
  furtherImagingSuggestion: string;
  conductRationale: string;
  recommendedConduct: "conservador" | "possivelmente_cirurgico" | "cirurgico";
  diagnosticSummary: string;
  anatomicalRegion: string;
  boneInvolved: string;
  hasFracture: boolean;
  fractureType: string;
  fractureLocation: string;
  displacement: "ausente" | "leve" | "moderado" | "importante";
  articularInvolvement: boolean;
  comminution: boolean;
  alignmentStatus: string;
  instabilitySigns: string;
  severityLevel: "baixo" | "moderado" | "alto";
  treatmentRecommendation: "conservador" | "possivelmente_cirurgico" | "cirurgico";
  treatmentRationale: string;
  uncertaintyNote: string;
  primaryStructureType?: "ossea" | "ligamentar" | "tendinea" | "meniscal" | "mista" | "indeterminada";
  primaryStructureName?: string;
  associatedStructures?: string[];
  lesionConfidence?: "baixa" | "moderada" | "alta";
  surgicalPlan: string;
  visualPrompt: string;
  anatomicalAnchors: string;
  preservationInstructions: string;
  diagnosis?: string;
  materials?: string;
  technique?: string;
  tips?: string;
  planB?: string;
  differentialDiagnosis?: string[];
  clinicalOutput?: AtlasClinicalOutput;
  structuredFractureCase?: StructuredFractureCase;
  radiographicFractureCheck?: AtlasRadiographicFractureCheck;
  surgicalDetails?: {
    materials?: string;
    technique?: string;
    tips?: string;
    planB?: string;
  };
  atlasFindings: AtlasFindings;
  // Structured analysis chain layers â€” populated by the 4-step reasoning chain
  detectionLayer?: {
    primaryVisualFinding: string;
    suspectedFractureZone: string;
    imagingEvidenceShort: string;
    corticalBreakConfidence: string;
    fractureDetectedBeforeBoneNaming: boolean;
  };
  boneIdentificationLayer?: {
    identifiedBone: string;
    identifiedSegment: string;
    anatomicalJustification: string;
    neighborStructureCheck: string;
    identificationConfidence: string;
    dominantFractureFocus: string;    // which bone shows the most fracture energy across ALL views
    dominantFocusRationale: string;   // why this bone is dominant, not an adjacent one
  };
  validationLayer?: {
    isValidated: boolean;
    correctedPrimaryBone: string;
    correctedSegment: string;
    validationReason: string;
    conflictDetected: boolean;
    conflictNotes: string;
    finalConfidenceAdjustment: string;
    crossViewValidation: string;      // explicit comparison AP vs lateral (or other views)
    lateralViewBiasChecked: boolean;  // confirms lateral-only anchoring was not used
  };
}

export type AtlasClinicalOutput = {
  diagnosis?: string;
  differentialDiagnosis?: string[];
  materials?: string;
  technique?: string;
  tips?: string;
  planB?: string;
};

type AtlasRadiographicFractureCheck = {
  affectedBone: string;
  location: string;
  notAffectedPrimaryBone: string[];
  confidence: number;
  conflictWithPreviousDiagnosis: boolean;
  correctedDiagnosis: string;
};

const freezeStructuredFractureCase = (analysis: AtlasImageAnalysis): StructuredFractureCase =>
  Object.freeze({
    examType: "radiografia",
    examRegion: analysis.examRegion || analysis.anatomicalRegion || "",
    laterality: analysis.laterality || analysis.atlasFindings?.side || "",
  suspectedBone: analysis.suspectedBone || analysis.boneInvolved || analysis.atlasFindings?.bone || "",
  suspectedSubregion: analysis.suspectedSubregion || analysis.fractureLocation || "",
  primaryBone: analysis.primaryBone || analysis.suspectedBone || analysis.boneInvolved || analysis.atlasFindings?.primaryBone || "",
  boneSegment: analysis.boneSegment || analysis.suspectedSubregion || analysis.fractureLocation || "",
  fractureLineDescription: analysis.fractureLineDescription || "",
  imagingEvidence: analysis.imagingEvidence || analysis.atlasFindings?.imagingEvidence || [],
  associatedFindings: analysis.associatedFindings || analysis.atlasFindings?.associatedFindings || [],
  fractureLocation: analysis.fractureLocation || analysis.suspectedSubregion || "",
    modalityExpertView: analysis.modalityExpertView || analysis.diagnosticSummary || analysis.diagnosis || "",
    fracturePattern: analysis.fracturePattern || analysis.fractureType || "",
    displacementLevel: analysis.displacementLevel || analysis.displacement || "ausente",
    articularExtension: Boolean(analysis.articularExtension || analysis.articularInvolvement),
    alignmentPreserved: Boolean(analysis.alignmentPreserved),
    fractureVisibility: analysis.fractureVisibility || "indeterminada",
    fractureConfidence: analysis.fractureConfidence || "baixa",
    confidenceReason: analysis.confidenceReason || analysis.diagnosticCertaintyText || analysis.diagnosticUncertainty || "",
    differentialNotes: analysis.differentialNotes || "",
    recommendFurtherImaging: Boolean(analysis.recommendFurtherImaging),
    furtherImagingSuggestion: analysis.furtherImagingSuggestion || "",
    recommendedConduct: analysis.recommendedConduct || analysis.treatmentRecommendation || "conservador",
    diagnosticSummary: analysis.diagnosticSummary || analysis.diagnosis || "",
    conductRationale: analysis.conductRationale || analysis.treatmentRationale || "",
    uncertaintyNote: analysis.uncertaintyNote || analysis.diagnosticUncertainty || "",
    primaryStructureType: analysis.primaryStructureType || analysis.atlasFindings?.primaryStructureType || "ossea",
    primaryStructureName: analysis.primaryStructureName || analysis.atlasFindings?.primaryStructureName || "",
    associatedStructures: analysis.associatedStructures || analysis.atlasFindings?.associatedStructures || [],
    lesionConfidence: analysis.lesionConfidence || analysis.atlasFindings?.lesionConfidence || analysis.fractureConfidence || "moderada",
  });

const validateAtlasFractureFromRadiograph = async (
  preparedImages: Array<{ base64: string; mimeType: string } | null>,
  analysis: AtlasImageAnalysis,
  anatomyContext?: string,
  mode: AtlasGenerationMode = 'detailed'
): Promise<AtlasRadiographicFractureCheck> => {
  const ai = createGeminiClient("generateContent");
  const parts: any[] = preparedImages
    .filter(Boolean)
    .map((img) => ({
      inlineData: {
        data: img!.base64,
        mimeType: img!.mimeType,
      },
    }));

  const previousDiagnosis = [
    analysis.diagnosis,
    analysis.diagnosticSummary,
    analysis.fractureLocation,
    analysis.boneInvolved,
    analysis.suspectedBone,
    anatomyContext,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(" | ");

  const validationPrompt = `
You are a senior pediatric/adult musculoskeletal radiologist and orthopedic trauma surgeon.

Your first task is to identify the exact fracture location directly from the radiograph.
Do not blindly trust the previous text diagnosis.
If the previous diagnosis conflicts with the image, correct it.

For elbow radiographs, explicitly check:
- distal humerus / supracondylar region
- lateral condyle
- medial epicondyle
- radial head/neck
- olecranon / proximal ulna

Determine which structure is actually fractured before creating the 3D image.

Previous text diagnosis:
${previousDiagnosis || "none provided"}

Current analysis hints:
- diagnosis: ${analysis.diagnosis || analysis.diagnosticSummary || ""}
- region: ${analysis.examRegion || analysis.anatomicalRegion || anatomyContext || ""}
- suspectedBone: ${analysis.suspectedBone || analysis.boneInvolved || ""}
- fractureLocation: ${analysis.fractureLocation || ""}
- fracturePattern: ${analysis.fracturePattern || analysis.fractureType || ""}
- displacement: ${analysis.displacementLevel || analysis.displacement || ""}

Return strict JSON only with:
{
  "radiographicFractureCheck": {
    "affectedBone": "distal humerus | proximal ulna | olecranon | radial head | radial neck | lateral condyle | medial epicondyle | other exact bone",
    "location": "exact subregion seen on the radiograph",
    "notAffectedPrimaryBone": ["list of bones that are not the main fracture site"],
    "confidence": 0.0,
    "conflictWithPreviousDiagnosis": true,
    "correctedDiagnosis": "short corrected diagnosis string"
  }
}

Rules:
- If the textual diagnosis conflicts with the radiographic image, follow the radiographic image.
- Do not generate an olecranon fracture if the fracture is supracondylar.
- Do not place the fracture on the ulna if the affected bone is the distal humerus.
- Do not create generic elbow trauma.
- If the image shows a supracondylar distal humerus fracture, the main fracture must be on the distal humerus above the condyles.
`;

  const response = await fetchWithRetry(async () => {
    const result = await ai.models.generateContent({
      model: "gemini-3-pro-preview",
      contents: { parts: [...parts, { text: validationPrompt }] },
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            radiographicFractureCheck: {
              type: Type.OBJECT,
              properties: {
                affectedBone: { type: Type.STRING },
                location: { type: Type.STRING },
                notAffectedPrimaryBone: { type: Type.ARRAY, items: { type: Type.STRING } },
                confidence: { type: Type.NUMBER },
                conflictWithPreviousDiagnosis: { type: Type.BOOLEAN },
                correctedDiagnosis: { type: Type.STRING },
              },
              required: [
                "affectedBone",
                "location",
                "notAffectedPrimaryBone",
                "confidence",
                "conflictWithPreviousDiagnosis",
                "correctedDiagnosis",
              ],
            },
          },
          required: ["radiographicFractureCheck"],
        },
      },
    });
    return JSON.parse(cleanJson(result.text || "{}"));
  }, 0, 2000, 45000);

  const check = response?.radiographicFractureCheck || {};
  const affectedBone = String(check.affectedBone || "").trim();
  const location = String(check.location || "").trim();
  const notAffectedPrimaryBone = Array.isArray(check.notAffectedPrimaryBone)
    ? check.notAffectedPrimaryBone.map((item: unknown) => String(item || "").trim()).filter(Boolean)
    : [];
  const confidence = Math.max(0, Math.min(1, Number(check.confidence ?? 0) || 0));
  const correctedDiagnosis = String(check.correctedDiagnosis || analysis.diagnosis || analysis.diagnosticSummary || "").trim();
  const priorText = previousDiagnosis.toLowerCase();
  const conflictWithPreviousDiagnosis =
    Boolean(check.conflictWithPreviousDiagnosis) ||
    (affectedBone ? priorText.includes("olecr") && affectedBone.toLowerCase().includes("humer") : false) ||
    (affectedBone ? priorText.includes("humer") && affectedBone.toLowerCase().includes("ulna") : false) ||
    (affectedBone ? priorText.includes("radial") && affectedBone.toLowerCase().includes("humer") : false);

  return {
    affectedBone,
    location,
    notAffectedPrimaryBone,
    confidence,
    conflictWithPreviousDiagnosis,
    correctedDiagnosis,
  };
};

export interface AtlasValidation {
  sideConfirmed: boolean;
  boneAxis: { x: number; y: number; z: number };
  implantAlignmentValid: boolean;
  anatomyConsistencyScore: number;
  correctionPrompt: string;
}

export interface AtlasROI {
  x: number;
  y: number;
  width: number;
  height: number;
  regionType: string;
}

export interface AtlasGenerationStages {
  baseBoneStage: string;
  anatomicalAdjustmentStage: string;
  clinicalFindingsStage: string;
  implantStage: string;
  finalRenderStage: string;
}

export type AtlasGenerationMode = 'fast' | 'detailed';

const getAtlasVisualModePrompt = (
  styleOrMode: '2D' | '3D' | AtlasGenerationMode,
  mode?: AtlasGenerationMode
) => {
  const style: '2D' | '3D' = mode ? (styleOrMode as '2D' | '3D') : '3D';
  const resolvedMode: AtlasGenerationMode = mode ?? (styleOrMode as AtlasGenerationMode);
  const isFast = resolvedMode === 'fast';

  if (style === '2D' && isFast) {
    return {
      generationLabel: "FAST ORTHOPEDIC 2D ATLAS VIEW",
      visualInstructions: `FAST 2D VISUAL STYLE:
      - Flat technical orthopedic illustration.
      - Focus on bone contours, fracture site, alignment, and lesion localization.
      - Use the original image as a geometric and anatomical blueprint.
      - Preserve anatomical proportions and spatial relationships exactly.
      - Do not invent fractures, lesions, or anatomical structures.
      - Do not duplicate fractures or create additional fragments.
      - Keep surrounding anatomy minimal and clean.
      - No volumetric depth, cinematic lighting, dramatic shadows, rich anatomy, or detailed soft tissues.
      - Prefer a simple medical background and fast readable output.`,
    };
  }

  if (style === '2D') {
    return {
      generationLabel: "DETAILED ORTHOPEDIC 2D ATLAS VIEW",
      visualInstructions: `DETAILED 2D VISUAL STYLE:
      - Flat orthopedic illustration with clear bone edges and lesion boundaries.
      - Use the original image as a geometric and anatomical blueprint.
      - Keep the image clinical, readable, and bone-focused.
      - Do not invent additional anatomy, fractures, or lesions.
      - Avoid 3D anatomy, volumetric depth, cinematic rendering, rich anatomy, and detailed soft tissues.
      - Use a clean medical presentation with restrained surrounding structures.`,
    };
  }

  if (isFast) {
    return {
      generationLabel: "SIMPLIFIED ORTHOPEDIC 3D BONE RENDER",
      visualInstructions: `FAST 3D VISUAL STYLE:
      - Keep the reconstruction three-dimensional, but simplify surrounding anatomy.
      - Emphasize the osseous structure and dominant pathology with lean tissue layering.
      - Avoid heavy soft-tissue richness or cinematic complexity.
      - Preserve laterality and fracture/pathology position with a clinically readable 3D appearance.`,
    };
  }

  return {
    generationLabel: "HIGH-FIDELITY 3D MEDICAL ANATOMICAL RENDER",
    visualInstructions: `DETAILED MODE VISUAL STYLE:
    - Generate an advanced realistic anatomical reconstruction with rich surgical detail and a dramatic clinical presentation.
    - Show bone, muscles, tendons, ligaments, fascial planes, and surrounding soft tissues in anatomically plausible layered depth.
    - Keep the lesion or fracture as the dominant visual focus, with strong emphasis on the injured zone and surrounding anatomical context.
    - Use a cinematic medical-reconstruction look: volumetric depth, defined musculature, visible tendon paths, crisp ligament contours, and a high-end 3D surgical atlas appearance.
    - Preserve the real fracture, wear, bone loss, cortical irregularity, and other pathological findings exactly as detected.
    - Maintain the correct laterality, bone axis, and anatomical relationships.
    - Do not place tendons, ligaments, or muscles in impossible trajectories or through cortical bone.
    - Do not beautify, normalize, or smooth pathological anatomy.
    - Keep a refined medical rendering style with depth, layered anatomy, clinically faithful detail, and visually rich soft tissues similar to a premium anatomical atlas.`,
  };
};

const getAtlasFinalRenderPrompt = (
  subject: string,
  style: '2D' | '3D',
  mode: AtlasGenerationMode,
  details?: { anchors?: string; visualPrompt?: string }
) => {
  const isFast = mode === 'fast';

  if (style === '2D' && isFast) {
    return `Generate a fast orthopedic 2D medical illustration based on the provided radiographic anatomy. Keep the composition clinically clear, simple, and highly faithful to the original image. Use the original image as a geometric and anatomical blueprint. The output must be a direct structured interpretation, not a creative reconstruction.

CRITICAL MEDICAL CONSTRAINTS:
- The generated image MUST strictly follow the original radiographic anatomy.
- Do NOT invent fractures, lesions, or anatomical structures.
- Do NOT duplicate fractures.
- Do NOT move or reinterpret the fracture location.
- Do NOT create additional bone fragments.
- The fracture must match the exact position and shape observed in the original image.
- Preserve anatomical proportions and spatial relationships.
- If uncertain, prefer simplification instead of invention.
- This is a medical reconstruction, not an artistic interpretation.

Focus on bone contours, fracture site, alignment, and lesion localization. Avoid volumetric effects, dramatic shadows, highly detailed soft tissues, complex anatomical realism, or premium 3D language. Prefer a clean technical medical illustration style with fast rendering, simplified surrounding structures, and emphasis on fracture visibility and surgical relevance.
Subject: "${subject}".
Core lesion map: ${details?.anchors || "Preserve the dominant fracture or lesion exactly where it appears in the exam."}
Visual preservation: ${details?.visualPrompt || "Keep the result flat, clean, strict, and fracture-focused."}
Use a clean neutral background and avoid any 3D sculpted anatomy.
Reference the provided image strictly.`;
  }

  if (style === '2D') {
    return `Generate a detailed 2D orthopedic medical atlas illustration. Keep it flat, clean, and clinically readable. Use the original image as a geometric and anatomical blueprint. Preserve laterality, bone orientation, fracture pattern, and lesion boundaries with clear cortical definition. Do not invent additional anatomy, fractures, or lesions. Avoid volumetric depth, sculpted soft tissue, cinematic 3D cues, and detailed soft tissues. Subject: "${subject}". Core lesion map: ${details?.anchors || "Preserve the dominant fracture or lesion exactly where it appears in the exam."} Visual preservation: ${details?.visualPrompt || "Keep the result flat, clean, and bone-focused."} Reference the provided image strictly.`;
  }

  if (isFast) {
    return `Generate a simplified 3D orthopedic anatomical reconstruction based on the input X-ray, preserving correct laterality and fracture location. Keep the reconstruction three-dimensional, but lean, fast, and less anatomically dense.
Subject: "${subject}".
Core lesion map: ${details?.anchors || "Preserve the dominant fracture or lesion exactly where it appears in the exam."}
Visual preservation: ${details?.visualPrompt || "Keep the main bone contour and fracture pattern coherent with the source exam."}
Reduce soft tissue richness and volumetric complexity while keeping the result clearly 3D and clinically coherent.`;
  }

  return `Generate a detailed orthopedic anatomical reconstruction based on the input X-ray, preserving correct laterality, bone orientation, and fracture pattern. Include realistic depth, detailed anatomy, and refined structural coherence suitable for detailed 3D clinical visualization.
Subject: "${subject}".
Core lesion map: ${details?.anchors || "Preserve the dominant fracture or lesion exactly where it appears in the exam."}
Visual preservation: ${details?.visualPrompt || "Keep the main fracture pattern, surrounding structural relationships, and depth cues coherent with the source exam."}
Include layered anatomical depth, refined structural detail, and high-fidelity spatial coherence, while keeping the pathology as the visual focus.`;
};

const buildStrictAtlasVisualPrompt = (
  analysis: AtlasImageAnalysis,
  lang: Language = 'pt',
  mode: AtlasGenerationMode = "detailed"
) => {
  const lesionCategory = classifyAtlasLesionCategory(analysis);
  if (lesionCategory !== "bone_fracture") {
    return buildAtlasSoftTissueVisualPrompt(analysis, lang, mode, buildAtlasLesionPromptContext(analysis));
  }

  const caseData = analysis.structuredFractureCase || freezeStructuredFractureCase(analysis);
  const region = caseData.examRegion || analysis.anatomicalRegion || "";
  const bone = caseData.suspectedBone || "";
  const subregion = caseData.suspectedSubregion || "";
  const fractureType = caseData.fracturePattern || "";
  const displacement = caseData.displacementLevel || "";
  const alignmentStatus = analysis.alignmentStatus || (caseData.alignmentPreserved ? "alignment preserved" : "alignment not preserved");
  const instabilitySigns = analysis.associatedInstability || analysis.instabilitySigns || "";
  const confidenceReason = caseData.confidenceReason || analysis.diagnosticCertaintyText || analysis.diagnosticUncertainty || "";
  const treatmentRecommendation = caseData.recommendedConduct || "";
  const uncertaintyNote = caseData.uncertaintyNote || analysis.diagnosticUncertainty || analysis.uncertaintyNote || "";
  const side = caseData.laterality || analysis.atlasFindings?.side || "";
  const visibility = caseData.fractureVisibility || "";
  const fractureLocation = String(caseData.fractureLocation || analysis.fractureLocation || subregion || "").trim();
  const recommendFurtherImaging = caseData.recommendFurtherImaging || analysis.recommendFurtherImaging;
  const furtherImagingSuggestion = caseData.furtherImagingSuggestion || analysis.furtherImagingSuggestion || "";
  const fractureSeverityLevel = getAtlasFractureSeverityLevel(analysis);
  const elbowHint = normalizeClinicalText([region, bone, subregion, fractureLocation, analysis.anatomicalRegion, analysis.examRegion].filter(Boolean).join(" "));
  const isElbowCase = /(?:cotovelo|elbow|umer|humer|olecr|ulna|radio|radial head|proximal ulna|distal humerus|epicondyl|condyl)/.test(elbowHint);
  const elbowSpecificLines = isElbowCase
    ? [
        "",
        "ELBOW-SPECIFIC LOCK:",
        "- For elbow radiographs, confirm distal humerus / supracondylar region, condyles / epicondyles, olecranon / proximal ulna, radial head / neck, and radiocapitellar plus humeroulnar alignment before naming the fracture.",
        "- If the main line or angulation is above the condyles in the distal humerus, classify it as supracondylar distal humerus fracture.",
        "- Do not label a supracondylar distal humerus fracture as an olecranon fracture.",
        "- Do not move the fracture to proximal ulna if the radiographic abnormality is in the distal humerus.",
        "- Preserve the correct bone: distal humerus.",
      ]
    : [];

  const anatomyLockLines = [
    "ANATOMY LOCK:",
    `SOURCE OF TRUTH: use the original RX and the structured JSON below as the only anatomical reference.`,
    `If confidence is baixa or the uncertainty is explicit, keep the lesion subtle, anatomically neutral, and do not dramatize displacement or fragment separation.`,
    `anatomicalRegion: ${region}`,
    `examRegion: ${caseData.examRegion || region}`,
    `laterality: ${side}`,
    `suspectedBone: ${bone}`,
    `suspectedSubregion: ${subregion}`,
    `modalityExpertView: ${caseData.modalityExpertView || ""}`,
    `hasVisibleFracture: ${String(analysis.hasVisibleFracture)}`,
    `fractureConfidence: ${caseData.fractureConfidence}`,
    `confidenceReason: ${confidenceReason}`,
    `fractureVisibility: ${visibility}`,
    `fracturePattern: ${fractureType}`,
    `boneInvolved: ${bone}`,
    `fractureLocation: ${fractureLocation || subregion}`,
    `fractureSeverityLevel: ${fractureSeverityLevel}`,
    `fractureType: ${fractureType}`,
    `corticalDisruption: ${String(analysis.corticalDisruption)}`,
    `trabecularDisruption: ${String(analysis.trabecularDisruption)}`,
    `displacement: ${displacement}`,
    `displacementLevel: ${displacement}`,
    `articularInvolvement: ${String(analysis.articularInvolvement)}`,
    `articularExtension: ${String(caseData.articularExtension)}`,
    `comminution: ${String(analysis.comminution)}`,
    `alignmentStatus: ${alignmentStatus}`,
    `alignmentPreserved: ${String(caseData.alignmentPreserved)}`,
    `instabilitySigns: ${instabilitySigns}`,
    `associatedInstability: ${instabilitySigns}`,
    `treatmentRecommendation: ${treatmentRecommendation}`,
    `recommendedConduct: ${treatmentRecommendation}`,
    `recommendFurtherImaging: ${String(recommendFurtherImaging)}`,
    `furtherImagingSuggestion: ${furtherImagingSuggestion}`,
    uncertaintyNote ? `diagnosticUncertainty: ${uncertaintyNote}` : "",
    uncertaintyNote ? `uncertaintyNote: ${uncertaintyNote}` : "",
    "",
    "VISUAL RULES:",
    "- Do not change the affected bone.",
    "- Do not move the fracture to another bone or another region.",
    "- Do not invent a new fracture.",
    "- Do not shift the lesion to another carpal bone, scaphoid, radius, ulna, or any unrelated segment.",
    "- Keep laterality and anatomical region exact.",
    "- Keep the fracture line locked to the original location from the RX.",
    "- Do not change suspectedSubregion or fractureLocation unless fractureConfidence is high and the JSON explicitly supports it.",
    "- Do not increase displacement or fragment separation beyond what is structured in the JSON.",
    "- Do not increase the number of fragments beyond what is visible.",
    "- Do not create comminution unless clearly present.",
    "- Do not exaggerate displacement.",
    "- Preserve cortical continuity when partially intact.",
    "- Keep fracture lines subtle if they are subtle in the exam.",
    "- If confidence is baixa or moderada, keep suspectedSubregion generic only when fractureLocation is absent; otherwise preserve the exact visible fractureLocation.",
    "- If confidence is baixa, render the lesion neutrally and discretely.",
    "- If uncertaintyNote is present, preserve uncertainty instead of inventing detail.",
    "- Preserve only the anatomy supported by the JSON fields.",
  ];

  const visualQualityEnhancementLines = mode === "detailed"
    ? [
        "",
        "VISUAL QUALITY ENHANCEMENT:",
        "Enhance visual realism WITHOUT changing anatomy.",
        "Preserve exact anatomical geometry and bone alignment. Do not modify positions, proportions, or fracture location.",
        `For this case, the fracture severity is: ${fractureSeverityLevel}. Render the fracture with proportional visual intensity.`,
        "Use a clean high-resolution medical render focused on anatomical fidelity.",
        "Render highly detailed cortical bone texture, realistic trabecular microstructure, subtle irregularities and imperfections in the bone surface, and natural anatomical asymmetry with no perfect shapes.",
        "Soft tissues must remain clinically discreet and secondary to osseous anatomy; avoid prominent muscles/tendons/ligaments.",
        "Add subtle, anatomically coherent nerve pathways only when naturally visible; keep nerves discreet and non-dominant.",
        "Do not exaggerate tendon paths, muscle volume, or ligament contours; avoid artificial soft-tissue dominance.",
        "Use neutral medical lighting and realistic shading without cinematic effects.",
        "Aim for high-quality medical visualization at 1024px resolution with sharp edges, no blur, no smoothing filters, and no simplified atlas look.",
        "Keep precise carpal spacing, crisp cortical margins, and medically accurate depth and proportion while preserving the locked lesion exactly.",
        "Prefer a clean fracture line over explosive comminution when the exam shows a limited injury pattern.",
        "Keep the fracture morphology faithful to the RX and avoid high-energy visual exaggeration.",
        "If the region is wrist/carpus: enforce true carpal biomechanics (scaphoid aligned with radius, lunate centered, capitate aligned with 3rd metacarpal axis, and coherent proximal/distal carpal rows).",
        "If radius/ulna are visible: preserve natural radius-ulna proportion and alignment with carpus, without artificial rotation or deformation.",
        "Do not fuse, overlap, or displace carpal bones unless explicitly supported by the source exam.",
        "Only improve definition, realism, and clarity.",
      ]
    : [];

  const localizedTail = lang === 'pt'
    ? "A imagem gerada deve repetir exatamente o local da fratura do RX original, sem reinterpretar o osso acometido."
    : lang === 'es'
      ? "La imagen generada debe repetir exactamente el lugar de la fractura del RX original, sin reinterpretar el hueso afectado."
      : "The generated image must exactly preserve the fracture location from the original X-ray, without reinterpreting the involved bone.";

  return `${anatomyLockLines.filter(Boolean).join("\n")}${elbowSpecificLines.length > 0 ? `\n${elbowSpecificLines.filter(Boolean).join("\n")}` : ""}${visualQualityEnhancementLines.length > 0 ? `\n${visualQualityEnhancementLines.filter(Boolean).join("\n")}` : ""}\n${localizedTail}`;
};

export const analyzeAtlasImageFindings = async (
  imagesBase64: string | string[],
  lang: Language = 'pt',
  anatomyContext?: string,
  mode: AtlasGenerationMode = 'detailed'
): Promise<AtlasImageAnalysis> => {
  const ai = createGeminiClient("generateAtlasAnalysis");
  const images = Array.isArray(imagesBase64) ? imagesBase64 : [imagesBase64];

  const userContextPrompt = anatomyContext
    ? lang === 'pt'
      ? `CONTEXTO OBRIGATORIO: a regiao anatomica e "${anatomyContext}". Preserve esse contexto mesmo com ambiguidade visual.`
      : `MANDATORY CONTEXT: the anatomical region is "${anatomyContext}". Preserve this context even with visual ambiguity.`
    : "";

  const anatomyLocalizationAddendum = lang === "en"
    ? `ANATOMY PRECISION ADDENDUM:
- Act as a musculoskeletal radiologist and orthopedic trauma surgeon with MRI, CT, and plain-film expertise.
- Prioritize the exact visible fracture site over any broad diagnostic label.
- fractureLocation must be the exact subsite seen on the image, while anatomicalRegion may remain broader.
- Do not turn a visible scaphoid waist lesion into a generic wrist diagnosis, and do not move the lesion to adjacent carpal bones.
- If the exact site is not visible, return an indeterminate localization rather than guessing.`
    : lang === "es"
      ? `ADENDA DE PRECISIÃ“N ANATÃ“MICA:
- ActÃºa como radiÃ³logo musculoesquelÃ©tico y cirujano de trauma ortopÃ©dico con experiencia en RM, TC y radiografÃ­a simple.
- Prioriza el sitio exacto de la fractura visible por encima de cualquier etiqueta diagnÃ³stica amplia.
- fractureLocation debe ser el subsitio exacto observado en la imagen, mientras anatomicalRegion puede ser mÃ¡s amplio.
- No conviertas una lesiÃ³n visible de la cintura del escafoides en un diagnÃ³stico genÃ©rico de muÃ±eca y no muevas la lesiÃ³n a carpos vecinos.
- Si el sitio exacto no es visible, devuelve una localizaciÃ³n indeterminada en lugar de adivinar.`
      : `ADENDO DE PRECISÃƒO ANATÃ”MICA:
- Atue como radiologista musculoesquelÃ©tico e ortopedista de trauma com domÃ­nio de RM, TC e radiografia simples.
- Priorize o ponto exato da fratura visÃ­vel acima de qualquer rÃ³tulo diagnÃ³stico amplo.
- fractureLocation deve ser o sublocal exato visto na imagem, enquanto anatomicalRegion pode permanecer mais amplo.
- NÃ£o transforme uma lesÃ£o visÃ­vel da cintura do escafoide em diagnÃ³stico genÃ©rico de punho e nÃ£o mova a lesÃ£o para ossos do carpo vizinhos.
- Se o local exato nÃ£o estiver visÃ­vel, devolva localizaÃ§Ã£o indeterminada em vez de adivinhar.`;

  const carpalLocalizationAddendum = lang === "en"
    ? `CARPAL LOCALIZATION ADDENDUM:
- First determine the exact bone involved, then the exact bone segment, and only then describe the fracture line.
- Use primaryBone for the exact carpal bone, boneSegment for the exact subsite, and fractureLineDescription for the visual sign that supports the conclusion.
- imagingEvidence should list 1 to 3 short visual findings that directly support the localization.
- In scaphoid cases, separate proximal pole, waist, distal pole, and tubercle only when the radiograph clearly supports it.
- Do not infer scapholunate dissociation, intercarpal instability, or ligament injury unless there is strong radiographic evidence.
- Do not spread the diagnosis across multiple neighboring carpal bones when only one bone is convincingly affected.
- associatedFindings must contain only directly visible accompanying signs, not speculative associations.`
    : lang === "es"
      ? `ADENDA DE LOCALIZACION DEL CARPO:
- Primero determine el hueso exacto afectado, luego el segmento exacto del hueso y solo despues describa la linea de fractura.
- Use primaryBone para el hueso carpiano exacto, boneSegment para el subtipo exacto y fractureLineDescription para el signo visual que sustenta la conclusion.
- imagingEvidence debe listar de 1 a 3 hallazgos visuales cortos que apoyen directamente la localizacion.
- En casos de escafoides, diferencie polo proximal, cintura, polo distal y tuberculo solo cuando la radiografia lo sustente con claridad.
- No infiera disociacion escafolunar, inestabilidad intercarpal ni lesion ligamentaria sin evidencia radiografica fuerte.
- No distribuya el diagnostico entre varios huesos carpianos vecinos si solo uno esta convincentemente afectado.
- associatedFindings debe contener solo hallazgos acompanantes directamente visibles, no asociaciones especulativas.`
      : `ADENDO DE LOCALIZACAO DO CARPO:
- Primeiro determine o osso exato acometido, depois o segmento exato do osso e so entao descreva a linha de fratura.
- Use primaryBone para o osso carpal exato, boneSegment para o sublocal exato e fractureLineDescription para o sinal visual que sustenta a conclusao.
- imagingEvidence deve listar de 1 a 3 achados visuais curtos que apoiem diretamente a localizacao.
- Em casos de escaf oide, separe polo proximal, cintura, polo distal e tuberculo somente quando a radiografia sustentar com clareza.
- Nao infira dissociacao escafolunar, instabilidade intercarpal ou lesao ligamentar sem evidencia radiografica forte.
- Nao espalhe o diagnostico por varios ossos do carpo vizinhos quando apenas um osso estiver convincentemente acometido.
- associatedFindings deve conter apenas sinais acompanhantes diretamente visiveis, nao associacoes especulativas.`;

  const fastModePrecisionAddendum = mode === 'fast'
    ? (lang === 'en'
        ? `FAST 2D PRECISION LOCK (MANDATORY):
Act as a musculoskeletal radiology and orthopedic trauma specialist with strong orthopedic X-ray and musculoskeletal MRI correlation expertise.
Analyze the original X-ray strictly.
Use the original X-ray as the strict and primary source of truth.
Identify the exact bone and exact segment affected.
Do not approximate or generalize.
Do not hallucinate structures.
Be conservative when imaging evidence is limited.

Before finalizing the JSON, internally verify:
1) which is the primary injured bone?
2) which exact bone segment is affected?
3) which visible fracture line is present (if any)?
4) which specific imaging evidence supports the diagnosis?
5) is there a truly visible fracture or only indeterminate trauma signs?

CRITICAL RULES:
- do not infer fractures without visible evidence
- do not shift fracture location to adjacent bones
- do not infer tendon/ligament injuries unless clearly visible
- do not generalize diagnosis
- prioritize visible fracture line over broad interpretation
- prioritize exact anatomical localization`
        : lang === 'es'
          ? `BLOQUEO DE PRECISIÃ“N 2D RÃPIDA (OBLIGATORIO):
Act as a musculoskeletal radiology and orthopedic trauma specialist with strong orthopedic X-ray and musculoskeletal MRI correlation expertise.
Analyze the original X-ray strictly.
Use the original X-ray as the strict and primary source of truth.
Identify the exact bone and exact segment affected.
Do not approximate or generalize.
Do not hallucinate structures.
Be conservative when imaging evidence is limited.

Antes de finalizar el JSON, verifique internamente:
1) Â¿cuÃ¡l es el hueso primario lesionado?
2) Â¿quÃ© segmento Ã³seo exacto estÃ¡ afectado?
3) Â¿quÃ© lÃ­nea de fractura visible estÃ¡ presente (si existe)?
4) Â¿quÃ© evidencia de imagen especÃ­fica sostiene el diagnÃ³stico?
5) Â¿existe realmente una fractura visible o solo signos traumÃ¡ticos indeterminados?

REGLAS CRÃTICAS:
- do not infer fractures without visible evidence
- do not shift fracture location to adjacent bones
- do not infer tendon/ligament injuries unless clearly visible
- do not generalize diagnosis
- prioritize visible fracture line over broad interpretation
- prioritize exact anatomical localization`
          : `BLOQUEIO DE PRECISÃƒO 2D RÃPIDA (OBRIGATÃ“RIO):
Act as a musculoskeletal radiology and orthopedic trauma specialist with strong orthopedic X-ray and musculoskeletal MRI correlation expertise.
Analyze the original X-ray strictly.
Use the original X-ray as the strict and primary source of truth.
Identify the exact bone and exact segment affected.
Do not approximate or generalize.
Do not hallucinate structures.
Be conservative when imaging evidence is limited.

Antes de finalizar o JSON, verifique internamente:
1) qual Ã© o osso principal lesionado?
2) qual Ã© o segmento Ã³sseo exato acometido?
3) qual Ã© o traÃ§o de fratura visÃ­vel (se houver)?
4) qual achado de imagem especÃ­fico sustenta o diagnÃ³stico?
5) existe realmente fratura visÃ­vel ou apenas sinais traumÃ¡ticos indeterminados?

REGRAS CRÃTICAS:
- do not infer fractures without visible evidence
- do not shift fracture location to adjacent bones
- do not infer tendon/ligament injuries unless clearly visible
- do not generalize diagnosis
- prioritize visible fracture line over broad interpretation
- prioritize exact anatomical localization`)
    : "";

  // 4-step structured chain â€” enforces sequential reasoning for ALL modes and bone types
  const structuredAnalysisChainAddendum = lang === 'en'
    ? `STRUCTURED FRACTURE ANALYSIS CHAIN (MANDATORY â€” FILL IN ORDER):

STEP 1 â€” LESION DETECTION (fill detectionLayer BEFORE naming any bone):
Scan the image for visual fracture evidence only:
  - cortical break or discontinuity
  - linear lucency through trabecular tissue
  - displaced or angulated bone fragment
  - joint step-off or impaction
  - focal periosteal reaction
Describe WHERE in the image the primary finding is (e.g. "upper-medial quadrant", "distal third cortex", "posterior margin of proximal segment") WITHOUT naming a specific bone yet.
Fill: detectionLayer.primaryVisualFinding, detectionLayer.suspectedFractureZone, detectionLayer.imagingEvidenceShort, detectionLayer.corticalBreakConfidence, detectionLayer.fractureDetectedBeforeBoneNaming.

STEP 2 â€” BONE IDENTIFICATION FROM ZONE (fill boneIdentificationLayer AFTER locating the zone):
Using ONLY the spatial position detected in STEP 1, determine:
  - which specific bone anatomically occupies that zone in this patient
  - which exact bone segment (proximal, mid, distal, medial condyle, olecranon, etc.) is at that location
  - explicitly confirm this is NOT an adjacent neighboring structure (e.g. "confirmed olecranon, not radial head" or "confirmed scaphoid waist, not lunate")
Do not assign primaryBone from a general impression. Ground it in the zone from STEP 1.
Fill: boneIdentificationLayer.identifiedBone, identifiedSegment, anatomicalJustification, neighborStructureCheck, identificationConfidence.

STEP 3 â€” CROSS-VALIDATION (fill validationLayer):
Verify:
  - Does the identified bone match the clinical context and laterality?
  - If multiple views exist, do they all confirm the same bone?
  - Is a neighboring structure being confused with the primary site?
  - Does the fracture pattern (transverse, oblique, comminuted) make sense at this bone and segment?
If a conflict exists between the initial bone impression and the zone-based identification:
  - set conflictDetected: true
  - set correctedPrimaryBone to the correct bone name
  - set correctedSegment to the correct segment
If consistent, set isValidated: true and leave correctedPrimaryBone empty.
Fill: validationLayer.isValidated, correctedPrimaryBone, correctedSegment, validationReason, conflictDetected, conflictNotes, finalConfidenceAdjustment.

STEP 4 â€” FINAL DIAGNOSIS (all primary fields):
Only after completing STEPS 1-3, fill all primary diagnostic fields (primaryBone, boneSegment, fractureLocation, etc.).
primaryBone MUST match boneIdentificationLayer.identifiedBone OR validationLayer.correctedPrimaryBone if a correction was made.
Use conservative reporting when imaging evidence is limited.`
    : lang === 'es'
      ? `CADENA DE ANÃLISIS ESTRUCTURADO DE FRACTURA (OBLIGATORIO â€” RELLENAR EN ORDEN):

PASO 1 â€” DETECCIÃ“N DE LESIÃ“N (rellenar detectionLayer ANTES de nombrar ningÃºn hueso):
Escanee la imagen en busca de evidencia visual de fractura:
  - rotura cortical o discontinuidad
  - lucencia lineal trabecular
  - fragmento Ã³seo desplazado
  - escalÃ³n articular o impactaciÃ³n
  - reacciÃ³n periÃ³stica focal
Describa DÃ“NDE en la imagen se encuentra el hallazgo principal (e.g. "cuadrante superomedial", "tercio distal cortical") SIN nombrar ningÃºn hueso todavÃ­a.
Rellene: detectionLayer.primaryVisualFinding, suspectedFractureZone, imagingEvidenceShort, corticalBreakConfidence, fractureDetectedBeforeBoneNaming.

PASO 2 â€” IDENTIFICACIÃ“N DEL HUESO DESDE LA ZONA (rellenar boneIdentificationLayer DESPUÃ‰S):
Usando SOLO la posiciÃ³n espacial detectada en el PASO 1, determine:
  - quÃ© hueso ocupa anatÃ³micamente esa zona
  - quÃ© segmento exacto del hueso se encuentra en esa posiciÃ³n
  - confirme explÃ­citamente que NO es una estructura vecina adyacente
No asigne primaryBone por impresiÃ³n general. Ancle en la zona del PASO 1.
Rellene: boneIdentificationLayer.identifiedBone, identifiedSegment, anatomicalJustification, neighborStructureCheck, identificationConfidence.

PASO 3 â€” VALIDACIÃ“N CRUZADA (rellenar validationLayer):
Verifique coherencia clÃ­nica, lateralidad, mÃºltiples proyecciones si disponibles.
Si hay conflicto: conflictDetected true, correctedPrimaryBone, correctedSegment.
Si es coherente: isValidated true, correctedPrimaryBone vacÃ­o.

PASO 4 â€” DIAGNÃ“STICO FINAL:
primaryBone DEBE coincidir con el hueso confirmado o corregido en el PASO 3.`
      : `CADEIA DE ANÃLISE ESTRUTURADA DE FRATURA (OBRIGATÃ“RIO â€” PREENCHER EM ORDEM):

ETAPA 1 â€” DETECÃ‡ÃƒO DA LESÃƒO (preencher detectionLayer ANTES de nomear qualquer osso):
Examine a imagem em busca de evidÃªncia visual de fratura:
  - interrupÃ§Ã£o cortical ou descontinuidade
  - lucÃªncia linear trabecular
  - fragmento Ã³sseo deslocado
  - degrau articular ou impactaÃ§Ã£o
  - reaÃ§Ã£o periostal focal
Descreva ONDE na imagem estÃ¡ o achado principal (ex: "quadrante sÃºpero-medial", "terÃ§o distal cortical", "margem posterior") SEM nomear osso especÃ­fico ainda.
Preencha: detectionLayer.primaryVisualFinding, suspectedFractureZone, imagingEvidenceShort, corticalBreakConfidence, fractureDetectedBeforeBoneNaming.

ETAPA 2 â€” IDENTIFICAÃ‡ÃƒO DO OSSO A PARTIR DA ZONA (preencher boneIdentificationLayer DEPOIS):
Usando SOMENTE a posiÃ§Ã£o espacial detectada na ETAPA 1, determine:
  - qual osso especÃ­fico ocupa anatomicamente essa zona
  - qual segmento exato (proximal, mÃ©dio, distal, cÃ´ndilo medial, olÃ©crano, etc.) estÃ¡ nessa posiÃ§Ã£o
  - confirme explicitamente que NÃƒO Ã© uma estrutura vizinha adjacente (ex: "confirmado olÃ©crano, nÃ£o cabeÃ§a do rÃ¡dio")
NÃ£o atribua primaryBone por impressÃ£o geral. Ancora na zona detectada na ETAPA 1.
Preencha: boneIdentificationLayer.identifiedBone, identifiedSegment, anatomicalJustification, neighborStructureCheck, identificationConfidence.

ETAPA 3 â€” VALIDAÃ‡ÃƒO CRUZADA (preencher validationLayer):
Verifique:
  - O osso identificado Ã© coerente com contexto clÃ­nico e lateralidade?
  - Se mÃºltiplas incidÃªncias disponÃ­veis, todas confirmam o mesmo osso?
  - Alguma estrutura vizinha estÃ¡ sendo confundida com o sÃ­tio principal?
  - O padrÃ£o de fratura faz sentido para esse osso e segmento?
Se houver conflito entre impressÃ£o inicial e identificaÃ§Ã£o baseada na zona:
  - conflictDetected: true
  - correctedPrimaryBone: nome do osso correto
  - correctedSegment: segmento correto
Se consistente: isValidated: true, correctedPrimaryBone vazio.
Preencha: validationLayer.isValidated, correctedPrimaryBone, correctedSegment, validationReason, conflictDetected, conflictNotes, finalConfidenceAdjustment.

ETAPA 4 â€” DIAGNÃ“STICO FINAL:
Somente apÃ³s concluir as ETAPAS 1-3, preencha os campos diagnÃ³sticos principais.
primaryBone DEVE corresponder ao osso confirmado em boneIdentificationLayer OU ao correctedPrimaryBone se houver correÃ§Ã£o.
Use relato conservador quando evidÃªncia de imagem for limitada.`;

  const differentialDiagnosisAddendum = lang === "en"
    ? `DIFFERENTIAL DIAGNOSIS DISCIPLINE (MANDATORY):
Before deciding the final diagnosis, internally perform this sequence:
1) list at least 3 principal hypotheses
2) compare radiographic signs that support each hypothesis
3) justify why the alternatives are excluded
4) only then state the most probable final diagnosis

If there is a well-defined soft-tissue structure, rounded/oval lesion, cystic-appearing focus, joint effusion, or periarticular fluid collection, you must actively consider:
- meniscal cyst or parameniscal cyst
- meniscal lesion/tear
- ligamentous injury
- joint effusion
- degenerative or soft-tissue abnormality

Do not assume fracture unless there is a clear cortical fracture line, cortical discontinuity, displaced fragment, malalignment, or convincing fragmentation.
Do not let the diagnosis default to fracture when the lesion is extraosseous or predominantly soft tissue.

INTERNAL FORMAT TO FOLLOW:
HYPOTHESES:
- hypothesis 1
- hypothesis 2
- hypothesis 3

ANALYSIS:
- supporting signs
- opposing signs

FINAL DECISION:
- most probable diagnosis`
    : lang === "es"
      ? `DISCIPLINA DE DIAGNOSTICO DIFERENCIAL (OBLIGATORIO):
Antes de definir el diagnostico final, realice internamente esta secuencia:
1) listar al menos 3 hipotesis principales
2) comparar los signos radiograficos que apoyan cada hipotesis
3) justificar por que se excluyen las alternativas
4) solo entonces definir el diagnostico final mas probable

Si hay una estructura de partes blandas bien delimitada, una lesion redondeada/oval, aspecto quistico, derrame articular o coleccion de liquido periarticular, considere obligatoriamente:
- quiste meniscal o parameniscal
- lesion/rotura meniscal
- lesion ligamentaria
- derrame articular
- alteracion degenerativa o de partes blandas

No asuma fractura sin una linea cortical clara, discontinuidad cortical, fragmento desplazado, malalineacion o fragmentacion convincente.
No permita que el diagnostico se convierta por defecto en fractura cuando la lesion sea extraosea o predominantemente de partes blandas.

FORMATO INTERNO A SEGUIR:
HIPOTESIS:
- hipotesis 1
- hipotesis 2
- hipotesis 3

ANALISIS:
- signos a favor
- signos en contra

DECISION FINAL:
- diagnostico mas probable`
      : `DISCIPLINA DE DIAGNOSTICO DIFERENCIAL (OBRIGATORIO):
Antes de definir o diagnostico final, execute internamente esta sequencia:
1) listar pelo menos 3 hipoteses principais
2) comparar os sinais radiograficos que sustentam cada hipotese
3) justificar por que as alternativas sao excluidas
4) somente entao definir o diagnostico final mais provavel

Se houver estrutura de partes moles bem delimitada, lesao arredondada/oval, aspecto cistico, derrame articular ou colecao liquida periarticular, considere obrigatoriamente:
- cisto meniscal ou parameniscal
- lesao/rotura meniscal
- lesao ligamentar
- derrame articular
- alteracao degenerativa ou de partes moles

Nao assuma fratura sem linha cortical clara, descontinuidade cortical, fragmento deslocado, desalinhamento ou fragmentacao convincente.
Nao deixe o diagnostico cair por padrao em fratura quando a lesao for extraossea ou predominantemente de partes moles.

FORMATO INTERNO A SEGUIR:
HIPOTESES:
- hipotese 1
- hipotese 2
- hipotese 3

ANALISE:
- sinais a favor
- sinais contra

DECISAO FINAL:
- diagnostico mais provavel`;

  const analysisPrompt = (lang === 'en'
    ? `ROLE: SENIOR MUSCULOSKELETAL RADIOLOGIST AND FULL-SPECTRUM ORTHOPEDIC TRAUMA IMAGING SPECIALIST WITH DEEP EXPERTISE IN WRIST, HAND, CARPAL FRACTURES, OCCULT SCAPHOID FRACTURES, ELBOW FRACTURES, DISTAL HUMERUS FRACTURES, PERIARTICULAR TRAUMA, KNEE, ANKLE, FOOT, HIP, AND SHOULDER FRACTURES, MRI CORRELATION, CT CORRELATION, AND MULTI-VIEW TRAUMA IMAGING.
       TASK: Interpret the uploaded radiograph(s) conservatively and anatomically, prioritizing exact bone identification, exact subregion localization when confidence is sufficient, fracture confidence grading, strict multi-view cross-validation, and strict avoidance of hallucinated findings.
       CRITICAL DOMINANT-BONE ATTRIBUTION RULE: When multiple views are provided, determine which bone shows the DOMINANT fracture energy across ALL views before naming primaryBone. Do not anchor to any single bone from a single view alone.
       LATERAL VIEW WARNING: The lateral view of the elbow makes the olecranon/proximal ulna visually prominent. Do NOT automatically assign primaryBone = olecranon/ulna from the lateral view alone. Check the AP view to determine whether the dominant fracture focus is the DISTAL HUMERUS, proximal ulna, or radial head.
       ELBOW-SPECIFIC RULE: before final diagnosis, explicitly verify 1) distal humerus / supracondylar region, 2) condyles / epicondyles, 3) olecranon / proximal ulna, 4) radial head / neck, and 5) radiocapitellar and humeroulnar alignment. Do not diagnose olecranon / proximal ulna if the main line or angulation is above the condyles in the distal humerus. If the abnormality is in the distal humeral metaphysis above the elbow joint, classify it as a supracondylar distal humerus fracture.
       FIRST ANALYZE GLOBAL ANATOMY ACROSS ALL VIEWS, THEN IDENTIFY THE DOMINANT FRACTURE BONE, AND ONLY THEN DESCRIBE THE FRACTURE.
       Do not overcall a fracture. In subtle radiographs, prefer expert uncertainty over false certainty.
       When radiographic confidence is limited, explicitly recommend MRI or CT correlation instead of producing an anatomically overconfident conclusion.
       ${userContextPrompt}

       MODE: ${mode.toUpperCase()}
       Return strict JSON with these mandatory fields:
       - examRegion
       - examType: "radiografia"
       - laterality
       - suspectedBone
       - suspectedSubregion
       - primaryBone
       - boneSegment
       - fractureLineDescription
       - imagingEvidence
       - associatedFindings
       - modalityExpertView
       - hasVisibleFracture
       - fractureConfidence: one of "baixa" | "moderada" | "alta"
       - confidenceReason
       - fractureVisibility: one of "sutil" | "clara" | "indeterminada"
       - fractureLocation
       - fracturePattern
       - corticalDisruption
       - trabecularDisruption
       - displacementLevel: one of "ausente" | "leve" | "moderado" | "importante"
       - articularExtension
       - alignmentPreserved
       - associatedInstability
       - differentialNotes
       - diagnosticCertaintyText
       - diagnosticUncertainty
       - recommendFurtherImaging
       - furtherImagingSuggestion
       - recommendedConduct: one of "conservador" | "possivelmente_cirurgico" | "cirurgico"
       - conductRationale
       - anatomicalRegion
       - boneInvolved
       - hasFracture
       - fractureType
       - fractureLocation
       - displacement: one of "ausente" | "leve" | "moderado" | "importante"
       - articularInvolvement
       - comminution
       - alignmentStatus
       - instabilitySigns
       - severityLevel: one of "baixo" | "moderado" | "alto"
       - treatmentRecommendation: one of "conservador" | "possivelmente_cirurgico" | "cirurgico"
       - treatmentRationale
       - conductRationale: explanation for recommendedConduct, separate from diagnosis
       - uncertaintyNote
       - diagnosticSummary: a single objective anatomical sentence with anatomy, laterality, fracture/pattern, displacement/comminution/diastasis if visible, and no treatment recommendation
       - diagnosis: compatibility alias that may mirror diagnosticSummary
       - diagnosticCertaintyText: short confidence or uncertainty statement, separate from diagnosis
       - Avoid generic labels like "fratura da patela", "lesao no joelho", or "fratura com desvio"
       - materials
       - technique
       - tips
       - planB
       - differentialDiagnosis: array with 3 to 5 clinically plausible alternatives from the same anatomical region
       - surgicalPlan with [DIAGNOSIS], [DIFFERENTIAL_DIAGNOSIS], [MATERIALS], [TECHNIQUE], [TIPS], [PLAN_B] as compatibility fallback
       - visualPrompt
       - anatomicalAnchors
       - preservationInstructions
       - atlasFindings:
         anatomyRegion, bone, side, visibleFindings[], degenerativeSigns[], fractureSigns[], boneLossSigns[], alignmentNotes

       PRIORITIZE ONLY IF CLEARLY VISIBLE:
       fissures, joint wear, bone loss, arthrosis, cortical irregularity, joint space narrowing, osteophytes, focal bone defects, visible malalignment, real asymmetries.

       IMPORTANT:
       - Do not invent findings.
       - Omit uncertain findings.
       - If the radiograph is subtle, low-resolution, oblique, or insufficient to confirm a fracture with confidence, do not state a definite fracture as certain.
       - In low-confidence cases, explicitly label it as suspected fracture or indeterminate traumatic abnormality and recommend clinical correlation or CT.
       - If evidence is insufficient, set diagnosticUncertainty and uncertaintyNote explicitly and avoid naming a specific fracture type or location as certain.
       - If fractureConfidence is not "alta", do not state a specific sublocalization with certainty. Keep suspectedSubregion generic or explicitly uncertain.
       - In scaphoid cases, actively distinguish waist, proximal pole, distal pole, and tubercle only when the radiograph truly supports that level of certainty.
       - Never swap one carpal bone for another and never move the fracture line away from the exact location shown on the original RX.
       - Think carefully about occult scaphoid fracture, subtle waist fracture, proximal pole vs waist vs tubercle, scapholunate dissociation, associated ligament injury, and real fracture line versus anatomic overlap.
       - If there is no convincing fracture, do not infer a fracture pattern from anatomy alone.
       - Let recommendedConduct follow the visible findings: stable/minimally displaced cases stay conservative; borderline cases stay possibly surgical; clear instability/displacement/articular involvement go surgical.
       - Differential diagnoses must stay in the same anatomical region and remain clinically plausible.
       - The visualPrompt must preserve the real pathology and avoid beautifying the anatomy.`
    : `FUNCAO: RADIOLOGISTA MUSCULOESQUELETICO SENIOR E ESPECIALISTA EM TRAUMA ORTOPEDICO DE ESPECTRO COMPLETO, COM FORTE DOMINIO DE PUNHO, MAO, FRATURAS DO CARPO, FRATURAS OCULTAS DO ESCAFOIDE, FRATURAS DO COTOVELO, FRATURAS DO UMERO DISTAL, TRAUMA PERIARTICULAR, JOELHO, TORNOZELO, PE, QUADRIL, OMBRO, CORRELACAO COM RM, CORRELACAO COM TC E IMAGEM COM MULTIPLAS INCIDENCIAS.
       TAREFA: interpretar a(s) radiografia(s) enviada(s) com prudencia clinica e precisao anatomica, priorizando a identificacao exata do osso, a sub-regiao apenas quando houver confianca suficiente, a graduacao da confianca diagnostica, a VALIDACAO CRUZADA ENTRE INCIDENCIAS e a recusa em inventar achados.
       REGRA CRITICA DE ATRIBUICAO DO OSSO DOMINANTE: Quando multiplas incidencias estiverem disponiveis, determine qual osso exibe a FRATURA DOMINANTE considerando TODAS as incidencias juntas antes de nomear o primaryBone. Nao ancore em nenhum osso com base em apenas uma incidencia.
       AVISO INCIDENCIA LATERAL: A incidencia lateral do cotovelo torna o olecrano/ulna proximal visualmente proeminente. NAO atribua automaticamente primaryBone = olecrano/ulna pela incidencia lateral isolada. Verifique a incidencia AP para determinar se o foco dominante e no UMERO DISTAL, ulna proximal ou cabeca do radio.
       REGRA ESPECIFICA DO COTOVELO: antes de fechar o diagnostico, confirme obrigatoriamente 1) umero distal/regiao supracondilar, 2) condilos/epicondilos, 3) olecrano/ulna proximal, 4) cabeca/colo do radio e 5) alinhamento radiocapitelar e umero-ulnar. Nao diagnostique olecrano/ulna proximal se a linha ou angulacao principal estiver acima dos condilos no umero distal. Se a alteracao estiver na metafise distal do umero, acima da articulacao do cotovelo, classifique como fratura supracondilar do umero distal.
       PRIMEIRO ANALISE A ANATOMIA GLOBAL EM TODAS AS INCIDENCIAS, DEPOIS IDENTIFIQUE O OSSO DA FRATURA DOMINANTE, E SO ENTAO DESCREVA A FRATURA.
       Nao supervalorize fraturas sutis. Prefira incerteza especializada a falsa certeza.
       Quando a confianca radiografica for limitada, recomende explicitamente correlacao com RM ou TC em vez de produzir uma conclusao anatomica excessivamente confiante.
       ${userContextPrompt}

       MODO: ${mode.toUpperCase()}
       Retorne JSON estrito com estes campos obrigatï¿½rios:
       - examRegion
       - examType: "radiografia"
       - laterality
       - suspectedBone
       - suspectedSubregion
       - primaryBone
       - boneSegment
       - fractureLineDescription
       - imagingEvidence
       - associatedFindings
       - modalityExpertView
       - hasVisibleFracture
       - fractureConfidence: "baixa" | "moderada" | "alta"
       - confidenceReason
       - fractureVisibility: "sutil" | "clara" | "indeterminada"
       - fractureLocation
       - fracturePattern
       - corticalDisruption
       - trabecularDisruption
       - displacementLevel: "ausente" | "leve" | "moderado" | "importante"
       - articularExtension
       - alignmentPreserved
       - associatedInstability
       - differentialNotes
       - diagnosticCertaintyText
       - diagnosticUncertainty
       - recommendFurtherImaging
       - furtherImagingSuggestion
       - recommendedConduct: "conservador" | "possivelmente_cirurgico" | "cirurgico"
       - conductRationale
       - anatomicalRegion
       - boneInvolved
       - hasFracture
       - fractureType
       - fractureLocation
       - displacement: "ausente" | "leve" | "moderado" | "importante"
       - articularInvolvement
       - comminution
       - alignmentStatus
       - instabilitySigns
       - severityLevel: "baixo" | "moderado" | "alto"
       - treatmentRecommendation: "conservador" | "possivelmente_cirurgico" | "cirurgico"
       - treatmentRationale
       - conductRationale: explicacao para recommendedConduct, separada do diagnostico
       - uncertaintyNote
       - diagnosticSummary: uma frase anatomica objetiva, sem recomendacao de tratamento
       - diagnosis: alias de compatibilidade que pode espelhar diagnosticSummary
       - diagnosticCertaintyText: frase curta de confianca ou incerteza, separada do diagnostico
       - materials
       - technique
       - tips
       - planB
       - differentialDiagnosis: array com 3 a 5 diagnosticos diferenciais plausiveis da mesma regiao anatomica
       - surgicalPlan com [DIAGNOSTICO], [DIAGNOSTICOS_DIFERENCIAIS], [MATERIAIS], [TECNICA], [DICAS], [PLANO_B] como fallback de compatibilidade
       - visualPrompt
       - anatomicalAnchors
       - preservationInstructions
       - atlasFindings:
         anatomyRegion, bone, side, visibleFindings[], degenerativeSigns[], fractureSigns[], boneLossSigns[], alignmentNotes

       PRIORIZAR SOMENTE SE ESTIVER VISIVEL:
       fissuras, desgaste articular, perda ossea, artrose, irregularidade cortical, reducao do espaco articular, osteofitos, defeitos osseos focais, desalinhamentos visiveis, assimetrias reais.

       IMPORTANTE:
       - nao invente achados.
       - omita o que nao estiver claro.
       - Se a radiografia for sutil, de baixa resolucao, obliqua ou insuficiente para confirmar fratura com confianca, nao declare uma fratura definitiva como certa.
       - Em casos de baixa confianca, rotule explicitamente como suspeita de fratura ou alteracao traumatica indeterminada e recomende correlacao clinica ou TC.
       - Se a evidÃªncia for insuficiente, preencha diagnosticUncertainty e uncertaintyNote explicitamente e nÃ£o nomeie um tipo ou local de fratura especÃ­fico como certo.
       - Se fractureConfidence nÃ£o for "alta", nÃ£o afirme uma sublocalizaÃ§Ã£o especÃ­fica com certeza. Mantenha suspectedSubregion genÃ©rica ou explicitamente incerta.
       - Em casos de escafoide, diferencie cintura, polo proximal, polo distal e tubï¿½rculo somente quando a radiografia realmente sustentar esse nï¿½vel de certeza.
       - Nunca troque um osso do carpo por outro e nunca mova a linha de fratura para fora da posiï¿½ï¿½o exata mostrada no RX original.
       - Pense com cuidado em fratura oculta do escafoide, fratura sutil da cintura, polo proximal versus cintura versus tubï¿½rculo, dissociaï¿½ï¿½o escafolunar, lesï¿½es ligamentares associadas e linha de fratura real versus superposiï¿½ï¿½o anatï¿½mica.
       - Se nÃ£o houver fratura convincente, nÃ£o infira um padrÃ£o fraturÃ¡rio apenas pela anatomia.
       - FaÃ§a recommendedConduct seguir os achados visiveis: casos estÃ¡veis e minimamente desviados permanecem conservadores; casos limÃ­trofes ficam como possivelmente_cirurgico; desvio importante, instabilidade evidente e acometimento articular relevante vÃ£o para cirurgico.
       - Se a confianï¿½a do caso congelado for baixa ou moderada, mantenha a tï¿½cnica cirï¿½rgica genï¿½rica ou adiada e evite um plano de acesso/fixaï¿½ï¿½o especï¿½fico.
       - os diagnosticos diferenciais devem permanecer na mesma regiao anatomica e ser clinicamente plausiveis.
       - o visualPrompt deve preservar a patologia real e impedir embelezamento da anatomia.`
    ) + `\n\n${anatomyLocalizationAddendum}\n\n${carpalLocalizationAddendum}${fastModePrecisionAddendum ? `\n\n${fastModePrecisionAddendum}` : ""}\n\n${structuredAnalysisChainAddendum}\n\n${differentialDiagnosisAddendum}\n\n${getClinicalResponseInstruction(lang)}\n${getSelectedLanguageInstruction(lang)}`;

    

  return fetchWithRetry(async () => {
    // Always send up to 2 views for analysis â€” prevents single-view anchoring bias.
    // Mode 'fast' previously used only the first image, causing false olecranon/ulna anchoring
    // when the lateral view was image[0] without the AP available for cross-validation.
    const imagesForAnalysis = mode === 'fast' ? images.slice(0, 2) : images;
    const parts: any[] = imagesForAnalysis.map((img) => {
      const mimeMatch = img.match(/^data:(image\/\w+);base64,/);
      const mimeType = mimeMatch ? mimeMatch[1] : 'image/jpeg';
      return {
        inlineData: {
          data: img.replace(/^data:image\/\w+;base64,/, ""),
          mimeType,
        },
      };
    });

    parts.push({ text: analysisPrompt });

    const res = await ai.models.generateContent({
      model: 'gemini-3-pro-preview',
      contents: { parts },
      config: {
        responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              examType: { type: Type.STRING },
              examRegion: { type: Type.STRING },
              laterality: { type: Type.STRING },
              suspectedBone: { type: Type.STRING },
              suspectedSubregion: { type: Type.STRING },
              primaryBone: { type: Type.STRING },
              boneSegment: { type: Type.STRING },
              fractureLineDescription: { type: Type.STRING },
              imagingEvidence: { type: Type.ARRAY, items: { type: Type.STRING } },
              associatedFindings: { type: Type.ARRAY, items: { type: Type.STRING } },
              modalityExpertView: { type: Type.STRING },
              hasVisibleFracture: { type: Type.BOOLEAN },
              fractureConfidence: { type: Type.STRING },
              confidenceReason: { type: Type.STRING },
              fractureVisibility: { type: Type.STRING },
              fractureLocation: { type: Type.STRING },
              fracturePattern: { type: Type.STRING },
              corticalDisruption: { type: Type.BOOLEAN },
              trabecularDisruption: { type: Type.BOOLEAN },
              displacementLevel: { type: Type.STRING },
              articularExtension: { type: Type.BOOLEAN },
              alignmentPreserved: { type: Type.BOOLEAN },
              associatedInstability: { type: Type.STRING },
              differentialNotes: { type: Type.STRING },
              diagnosticCertaintyText: { type: Type.STRING },
              diagnosticUncertainty: { type: Type.STRING },
              recommendFurtherImaging: { type: Type.BOOLEAN },
              furtherImagingSuggestion: { type: Type.STRING },
              recommendedConduct: { type: Type.STRING },
              conductRationale: { type: Type.STRING },
              anatomicalRegion: { type: Type.STRING },
              boneInvolved: { type: Type.STRING },
              hasFracture: { type: Type.BOOLEAN },
              fractureType: { type: Type.STRING },
              displacement: { type: Type.STRING },
              articularInvolvement: { type: Type.BOOLEAN },
              comminution: { type: Type.BOOLEAN },
              alignmentStatus: { type: Type.STRING },
              instabilitySigns: { type: Type.STRING },
              severityLevel: { type: Type.STRING },
              treatmentRecommendation: { type: Type.STRING },
              treatmentRationale: { type: Type.STRING },
              uncertaintyNote: { type: Type.STRING },
              diagnosticSummary: { type: Type.STRING },
              diagnosis: { type: Type.STRING },
              materials: { type: Type.STRING },
              technique: { type: Type.STRING },
              tips: { type: Type.STRING },
              planB: { type: Type.STRING },
              surgicalPlan: { type: Type.STRING },
              differentialDiagnosis: { type: Type.ARRAY, items: { type: Type.STRING } },
              visualPrompt: { type: Type.STRING },
              anatomicalAnchors: { type: Type.STRING },
              preservationInstructions: { type: Type.STRING },
            atlasFindings: {
              type: Type.OBJECT,
              properties: {
                anatomyRegion: { type: Type.STRING },
                bone: { type: Type.STRING },
                side: { type: Type.STRING },
                visibleFindings: { type: Type.ARRAY, items: { type: Type.STRING } },
                degenerativeSigns: { type: Type.ARRAY, items: { type: Type.STRING } },
                fractureSigns: { type: Type.ARRAY, items: { type: Type.STRING } },
                boneLossSigns: { type: Type.ARRAY, items: { type: Type.STRING } },
                alignmentNotes: { type: Type.STRING },
              },
            },
            detectionLayer: {
              type: Type.OBJECT,
              properties: {
                primaryVisualFinding: { type: Type.STRING },
                suspectedFractureZone: { type: Type.STRING },
                imagingEvidenceShort: { type: Type.STRING },
                corticalBreakConfidence: { type: Type.STRING },
                fractureDetectedBeforeBoneNaming: { type: Type.BOOLEAN },
              },
            },
            boneIdentificationLayer: {
              type: Type.OBJECT,
              properties: {
                identifiedBone: { type: Type.STRING },
                identifiedSegment: { type: Type.STRING },
                anatomicalJustification: { type: Type.STRING },
                neighborStructureCheck: { type: Type.STRING },
                identificationConfidence: { type: Type.STRING },
              },
            },
            validationLayer: {
              type: Type.OBJECT,
              properties: {
                isValidated: { type: Type.BOOLEAN },
                correctedPrimaryBone: { type: Type.STRING },
                correctedSegment: { type: Type.STRING },
                validationReason: { type: Type.STRING },
                conflictDetected: { type: Type.BOOLEAN },
                conflictNotes: { type: Type.STRING },
                finalConfidenceAdjustment: { type: Type.STRING },
              },
            },
          },
          required: [
            "examType",
            "examRegion",
            "laterality",
            "suspectedBone",
            "suspectedSubregion",
            "primaryBone",
            "boneSegment",
            "fractureLineDescription",
            "imagingEvidence",
            "associatedFindings",
            "modalityExpertView",
            "hasVisibleFracture",
            "fractureConfidence",
            "confidenceReason",
            "fractureVisibility",
            "fractureLocation",
            "fracturePattern",
            "corticalDisruption",
            "trabecularDisruption",
            "displacementLevel",
            "articularExtension",
            "alignmentPreserved",
            "associatedInstability",
            "differentialNotes",
            "diagnosticCertaintyText",
            "diagnosticUncertainty",
            "recommendFurtherImaging",
            "furtherImagingSuggestion",
            "recommendedConduct",
            "conductRationale",
            "anatomicalRegion",
            "boneInvolved",
            "hasFracture",
            "fractureType",
            "displacement",
            "articularInvolvement",
            "comminution",
            "alignmentStatus",
            "instabilitySigns",
            "severityLevel",
            "treatmentRecommendation",
            "treatmentRationale",
            "uncertaintyNote",
            "diagnosticSummary",
            "diagnosis",
            "materials",
            "technique",
            "tips",
            "planB",
            "surgicalPlan",
            "visualPrompt",
            "anatomicalAnchors",
            "atlasFindings",
          ],
        },
      },
    });

    const extractFullPlan = (plan: string) => {
  return {
    diagnosis:
      extractTaggedPlanValue(plan, "DIAGNOSTICO") ||
      extractTaggedPlanValue(plan, "DIAGNOSIS") ||
      "",

    materials:
      extractTaggedPlanValue(plan, "MATERIAIS") ||
      extractTaggedPlanValue(plan, "MATERIALS") ||
      "",

    technique:
      extractTaggedPlanValue(plan, "TECNICA") ||
      extractTaggedPlanValue(plan, "TECHNIQUE") ||
      "",

    tips:
      extractTaggedPlanValue(plan, "DICAS") ||
      extractTaggedPlanValue(plan, "TIPS") ||
      "",

    planB:
      extractTaggedPlanValue(plan, "PLANO_B") ||
      extractTaggedPlanValue(plan, "PLAN_B") ||
      "",
  };
};

  const parsed = JSON.parse(cleanJson(res.text || "{}"));

  // â”€â”€ STRUCTURAL VALIDATION AUTO-CORRECTION â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // If the 4-step chain detected a conflict between initial bone impression and
  // spatial zone-based identification, override primaryBone with the corrected value.
  // This applies universally to all bone types (elbow, wrist, knee, ankle, etc.)
  {
    const vl = parsed.validationLayer;
    const correctedBone = String(vl?.correctedPrimaryBone || "").trim();
    const correctedSeg  = String(vl?.correctedSegment    || "").trim();
    if (correctedBone && vl?.conflictDetected === true) {
      console.log(`[ATLAS][CHAIN] Conflict detected â€” overriding primaryBone: "${parsed.primaryBone}" â†’ "${correctedBone}" | reason: ${vl.validationReason || ""}`);
      parsed.primaryBone   = correctedBone;
      parsed.suspectedBone = correctedBone;
      parsed.boneInvolved  = correctedBone;
      if (correctedSeg) parsed.boneSegment = correctedSeg;
      if (parsed.atlasFindings) parsed.atlasFindings.bone = correctedBone;
    }
    // Also propagate the detection zone as fractureLocation if it is more specific
    const detectedZone = String(parsed.detectionLayer?.suspectedFractureZone || "").trim();
    if (detectedZone && !parsed.fractureLocation) {
      parsed.fractureLocation = detectedZone;
    }
  }
  // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const planData = extractFullPlan(parsed.surgicalPlan || "");
  const explicitSections = extractAtlasSurgicalPlanSections({
    ...parsed,
    surgicalPlan: parsed.surgicalPlan || "",
  });
  const diagnosticSummary =
    String(parsed.diagnosticSummary || parsed.modalityExpertView || parsed.diagnosis || "").trim();

const diagnosis = buildDetailedDiagnosisFallback(
  {
    diagnosis:
      (mode === 'fast' ? '' : diagnosticSummary) ||
      explicitSections.diagnosis ||
      (mode === 'fast' ? '' : planData.diagnosis) ||
      "",
    examRegion: parsed.examRegion,
    laterality: parsed.laterality,
    suspectedBone: parsed.suspectedBone,
    suspectedSubregion: parsed.suspectedSubregion,
    primaryBone: parsed.primaryBone,
    boneSegment: parsed.boneSegment,
    fractureLineDescription: parsed.fractureLineDescription,
    imagingEvidence: parsed.imagingEvidence,
    associatedFindings: parsed.associatedFindings,
    hasVisibleFracture: parsed.hasVisibleFracture,
    fractureConfidence: parsed.fractureConfidence,
    fractureVisibility: parsed.fractureVisibility,
    fracturePattern: parsed.fracturePattern,
    corticalDisruption: parsed.corticalDisruption,
    trabecularDisruption: parsed.trabecularDisruption,
    displacementLevel: parsed.displacementLevel,
    articularExtension: parsed.articularExtension,
    alignmentPreserved: parsed.alignmentPreserved,
    associatedInstability: parsed.associatedInstability,
    diagnosticCertaintyText: parsed.diagnosticCertaintyText,
    diagnosticUncertainty: parsed.diagnosticUncertainty,
    recommendedConduct: parsed.recommendedConduct,
    conductRationale: parsed.conductRationale,
    anatomicalRegion: parsed.anatomicalRegion,
    boneInvolved: parsed.boneInvolved,
    hasFracture: parsed.hasFracture,
    fractureType: parsed.fractureType,
    fractureLocation: parsed.fractureLocation,
    displacement: parsed.displacement,
    articularInvolvement: parsed.articularInvolvement,
    comminution: parsed.comminution,
    alignmentStatus: parsed.alignmentStatus,
    instabilitySigns: parsed.instabilitySigns,
    severityLevel: parsed.severityLevel,
    treatmentRecommendation: parsed.treatmentRecommendation,
    treatmentRationale: parsed.treatmentRationale,
    uncertaintyNote: parsed.uncertaintyNote,
    atlasFindings: parsed.atlasFindings,
    surgicalPlan: parsed.surgicalPlan || "",
  },
  lang
);

const surgicalSections = extractAtlasSurgicalPlanSections({
  ...parsed,
  diagnosis,
  surgicalPlan: parsed.surgicalPlan || "",
});
const builtSurgicalPlan = buildAtlasSurgicalPlanText(surgicalSections);

console.log("[ATLAS][TEXT] parsed =", parsed);
console.log("[ATLAS][TEXT] parsed.diagnosis =", parsed.diagnosis);
console.log("[ATLAS][TEXT] surgicalPlan =", parsed.surgicalPlan);
console.log("[ATLAS][TEXT] planData =", planData);
console.log("[ATLAS][TEXT] final diagnosis =", diagnosis);
console.log("[ATLAS][TEXT] final surgicalDetails =", {
  materials: surgicalSections.materials || planData.materials,
  technique: surgicalSections.technique || planData.technique,
  tips: surgicalSections.tips || planData.tips,
  planB: surgicalSections.planB || planData.planB,
});

return {
  ...parsed,
  examType: parsed.examType || "radiografia",
  diagnosticSummary,
  diagnosis,
  differentialDiagnosis: surgicalSections.differentialDiagnosis,
  surgicalPlan: builtSurgicalPlan || parsed.surgicalPlan || "",
  clinicalOutput: {
    diagnosis,
    differentialDiagnosis: surgicalSections.differentialDiagnosis,
    materials: surgicalSections.materials,
    technique: surgicalSections.technique,
    tips: surgicalSections.tips,
    planB: surgicalSections.planB,
  },
  surgicalDetails: {
    materials: surgicalSections.materials || planData.materials,
    technique: surgicalSections.technique || planData.technique,
    tips: surgicalSections.tips || planData.tips,
    planB: surgicalSections.planB || planData.planB,
  },
  structuredFractureCase: freezeStructuredFractureCase({
    ...parsed,
    examType: parsed.examType || "radiografia",
    diagnosticSummary,
    diagnosis: diagnosticSummary,
    examRegion: parsed.examRegion,
    laterality: parsed.laterality,
    suspectedBone: parsed.suspectedBone,
    suspectedSubregion: parsed.suspectedSubregion,
    modalityExpertView: parsed.modalityExpertView,
    fracturePattern: parsed.fracturePattern,
    displacementLevel: parsed.displacementLevel,
    articularExtension: parsed.articularExtension,
    alignmentPreserved: parsed.alignmentPreserved,
    confidenceReason: parsed.confidenceReason,
    fractureVisibility: parsed.fractureVisibility,
    fractureConfidence: parsed.fractureConfidence,
    differentialNotes: parsed.differentialNotes,
    recommendFurtherImaging: parsed.recommendFurtherImaging,
    furtherImagingSuggestion: parsed.furtherImagingSuggestion,
    recommendedConduct: parsed.recommendedConduct,
    conductRationale: parsed.conductRationale,
    uncertaintyNote: parsed.uncertaintyNote,
    anatomicalRegion: parsed.anatomicalRegion,
    boneInvolved: parsed.boneInvolved,
    hasVisibleFracture: parsed.hasVisibleFracture,
    hasFracture: parsed.hasFracture,
    fractureType: parsed.fractureType,
    fractureLocation: parsed.fractureLocation,
    displacement: parsed.displacement,
    articularInvolvement: parsed.articularInvolvement,
    comminution: parsed.comminution,
    alignmentStatus: parsed.alignmentStatus,
    instabilitySigns: parsed.instabilitySigns,
    severityLevel: parsed.severityLevel,
    treatmentRecommendation: parsed.treatmentRecommendation,
    treatmentRationale: parsed.treatmentRationale,
  } as AtlasImageAnalysis),
  materials: surgicalSections.materials || planData.materials,
  technique: surgicalSections.technique || planData.technique,
  tips: surgicalSections.tips || planData.tips,
  planB: surgicalSections.planB || planData.planB,
  description: builtSurgicalPlan,
};
  });
};

const inferBoneAxis = (bone: string, region: string) => {
  const normalized = `${bone} ${region}`.toLowerCase();

  if (
    normalized.includes("fem") ||
    normalized.includes("tib") ||
    normalized.includes("fib") ||
    normalized.includes("humer") ||
    normalized.includes("radio") ||
    normalized.includes("ulna") ||
    normalized.includes("metacar") ||
    normalized.includes("metatar")
  ) {
    return { x: 0, y: 1, z: 0 };
  }

  if (
    normalized.includes("pelv") ||
    normalized.includes("acet") ||
    normalized.includes("clav") ||
    normalized.includes("scap") ||
    normalized.includes("mandib")
  ) {
    return { x: 1, y: 0, z: 0 };
  }

  return { x: 0, y: 0, z: 1 };
};

export const validateAtlasAnatomy = (
  analysis: AtlasImageAnalysis,
  anatomyContext?: string,
  mode: AtlasGenerationMode = 'detailed'
): AtlasValidation => {
  const bone = analysis.atlasFindings?.bone || "";
  const region = analysis.atlasFindings?.anatomyRegion || analysis.anatomicalRegion || anatomyContext || "";
  const side = (analysis.atlasFindings?.side || "").toLowerCase();
  const context = (anatomyContext || "").toLowerCase();
  const anchors = (analysis.anatomicalAnchors || "").toLowerCase();
  const visualPrompt = (analysis.visualPrompt || "").toLowerCase();
  const surgicalPlan = (analysis.surgicalPlan || "").toLowerCase();

  const contextMentionsLeft = /\bleft\b|esq/.test(context);
  const contextMentionsRight = /\bright\b|dir/.test(context);
  const sideConfirmed =
    !side ||
    (!contextMentionsLeft && !contextMentionsRight) ||
    (contextMentionsLeft && side.includes("left")) ||
    (contextMentionsRight && side.includes("right"));

  const mentionsImplant =
    /plac|plate|paraf|screw|nail|haste|fixador|fixator|cage|anchor|ancora/.test(
      `${visualPrompt} ${surgicalPlan}`
    );
  const mentionsSurfaceOrCanal =
    /canal|cortical|cortex|contour|contorno|perioste|medullar|medular|surface|superficie/.test(
      `${visualPrompt} ${anchors}`
    );
  const implantAlignmentValid = !mentionsImplant || mentionsSurfaceOrCanal;

  let anatomyConsistencyScore = mode === 'fast' ? 45 : 55;
  if (bone) anatomyConsistencyScore += 10;
  if (region) anatomyConsistencyScore += 10;
  if (analysis.atlasFindings?.side) anatomyConsistencyScore += 10;
  if (analysis.anatomicalAnchors) anatomyConsistencyScore += 10;
  if (analysis.atlasFindings?.alignmentNotes) anatomyConsistencyScore += 5;
  if (sideConfirmed) anatomyConsistencyScore += 5;
  if (implantAlignmentValid) anatomyConsistencyScore += 5;
  anatomyConsistencyScore = Math.max(0, Math.min(100, anatomyConsistencyScore));

  const boneAxis = inferBoneAxis(bone, region);
  const correctionPrompt = [
    sideConfirmed
      ? `Respect detected laterality: ${analysis.atlasFindings?.side || "preserve original side"}.`
      : `Correct laterality drift and preserve the original detected side: ${analysis.atlasFindings?.side || "do not invert side"}.`,
    `Use plausible implant orientation following the main bone axis vector (${boneAxis.x}, ${boneAxis.y}, ${boneAxis.z}).`,
    implantAlignmentValid
      ? "Keep implants within bone boundaries and plates along the cortical contour."
      : "Reinforce implant plausibility: screws must remain intra-osseous/bicortical, plates must follow bone contour, and hardware must not cross impossible regions.",
    mode === 'fast'
      ? "Prevent the most obvious impossible anatomy while preserving the main findings."
      : "Prevent ligaments crossing bone, tissues inside cortical bone, or artificially deformed joints.",
    "Preserve real pathological findings without removing or normalizing them.",
  ].join(" ");

  return {
    sideConfirmed,
    boneAxis,
    implantAlignmentValid,
    anatomyConsistencyScore,
    correctionPrompt,
  };
};

const clampRoi = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

const extractTaggedPlanValue = (plan: string, tag: string) => {
  const regex = new RegExp(`\\[${tag}\\](.*?)(?=\\[|$)`, "is");
  const match = (plan || "").match(regex);
  return match ? match[1].trim() : "";
};

const extractFullPlan = (plan: string) => {
  return {
    diagnosis:
      extractTaggedPlanValue(plan, "DIAGNOSTICO") ||
      extractTaggedPlanValue(plan, "DIAGNOSIS") ||
      "",

    conduta:
      extractTaggedPlanValue(plan, "CONDUTA") ||
      extractTaggedPlanValue(plan, "SURGICAL_INDICATION") ||
      extractTaggedPlanValue(plan, "SURGICAL INDICATION") ||
      extractTaggedPlanValue(plan, "CLINICAL_DISPOSITION") ||
      extractTaggedPlanValue(plan, "CLINICAL DISPOSITION") ||
      "",

    materials:
      extractTaggedPlanValue(plan, "MATERIAIS") ||
      extractTaggedPlanValue(plan, "MATERIALS") ||
      "",

    technique:
      extractTaggedPlanValue(plan, "TECNICA") ||
      extractTaggedPlanValue(plan, "TECHNIQUE") ||
      "",

    tips:
      extractTaggedPlanValue(plan, "DICAS") ||
      extractTaggedPlanValue(plan, "TIPS") ||
      "",

    planB:
      extractTaggedPlanValue(plan, "PLANO_B") ||
      extractTaggedPlanValue(plan, "PLAN_B") ||
      "",
  };
};

const normalizeDifferentialDiagnosis = (raw?: string | string[]) => {
  const values = Array.isArray(raw)
    ? raw
    : (raw || "")
        .replace(/\r\n/g, "\n")
        .split(/\n|;|\|/)
        .map((item) => item.trim());

  return Array.from(
    new Set(
      values
        .map((item) => item.replace(/^[-*â€¢\d.)\s]+/, "").trim())
        .filter(Boolean)
    )
  ).slice(0, 5);
};

const isTooShortDiagnosis = (value?: string) => {
  const normalized = (value || "")
    .replace(/\s+/g, " ")
    .trim();

  return normalized.split(" ").filter(Boolean).length < 6;
};

const normalizeSideLabel = (side?: string) => {
  const normalized = (side || "").trim().toLowerCase();
  if (!normalized) return "";
  if (/\b(left|esq|esquerda)\b/.test(normalized)) return "esquerda";
  if (/\b(right|dir|direita)\b/.test(normalized)) return "direita";
  return (side || "").trim();
};

const buildDetailedDiagnosisFallback = (
  source: {
    diagnosis?: string;
    diagnosticSummary?: string;
    examRegion?: string;
    modalityExpertView?: string;
    laterality?: string;
    suspectedBone?: string;
    suspectedSubregion?: string;
    primaryBone?: string;
    boneSegment?: string;
    fractureLineDescription?: string;
    imagingEvidence?: string[];
    associatedFindings?: string[];
    hasVisibleFracture?: boolean;
    fractureConfidence?: string;
    fractureVisibility?: string;
    fracturePattern?: string;
    corticalDisruption?: boolean;
    trabecularDisruption?: boolean;
    displacementLevel?: string;
    articularExtension?: boolean;
    alignmentPreserved?: boolean;
    associatedInstability?: string;
    diagnosticCertaintyText?: string;
    diagnosticUncertainty?: string;
    confidenceReason?: string;
    differentialNotes?: string;
    recommendFurtherImaging?: boolean;
    furtherImagingSuggestion?: string;
    recommendedConduct?: string;
    conductRationale?: string;
    anatomicalRegion?: string;
    atlasFindings?: Partial<AtlasFindings>;
    boneInvolved?: string;
    hasFracture?: boolean;
    fractureType?: string;
    fractureLocation?: string;
    displacement?: string;
    articularInvolvement?: boolean;
    comminution?: boolean;
    alignmentStatus?: string;
    instabilitySigns?: string;
    severityLevel?: string;
    treatmentRecommendation?: string;
    treatmentRationale?: string;
    uncertaintyNote?: string;
    surgicalPlan?: string;
  },
  lang: Language = "pt"
) => {
  const currentDiagnosis = (source.diagnosticSummary || source.diagnosis || "").trim();
  const hasStructuredFractureContext =
    Boolean(source.examRegion) ||
    Boolean(source.suspectedBone) ||
    Boolean(source.suspectedSubregion) ||
    Boolean(source.primaryBone) ||
    Boolean(source.boneSegment) ||
    Boolean(source.fractureLineDescription) ||
    (Array.isArray(source.associatedFindings) && source.associatedFindings.length > 0) ||
    typeof source.hasVisibleFracture === "boolean" ||
    typeof source.hasFracture === "boolean" ||
    Boolean(source.fractureConfidence) ||
    Boolean(source.fractureVisibility) ||
    Boolean(source.diagnosticCertaintyText) ||
    Boolean(source.modalityExpertView) ||
    Boolean(source.confidenceReason) ||
    Boolean(source.differentialNotes) ||
    Boolean(source.recommendFurtherImaging) ||
    Boolean(source.furtherImagingSuggestion) ||
    Boolean(source.diagnosticUncertainty) ||
    Boolean(source.diagnosticSummary) ||
    Boolean(source.recommendedConduct) ||
    Boolean(source.conductRationale);

  if (!hasStructuredFractureContext && !isTooShortDiagnosis(currentDiagnosis)) return currentDiagnosis;

  const region = (source.atlasFindings?.anatomyRegion || source.anatomicalRegion || "").trim();
  const bone = (source.atlasFindings?.bone || region).trim();
  const structuredDiagnosis = buildFractureNarrative(
    {
      examRegion: source.examRegion || region,
      laterality: source.laterality,
      suspectedBone: source.suspectedBone || bone,
      suspectedSubregion: source.suspectedSubregion,
      primaryBone: source.primaryBone || source.suspectedBone || bone,
      boneSegment: source.boneSegment || source.suspectedSubregion,
      fractureLineDescription: source.fractureLineDescription || "",
      imagingEvidence: source.imagingEvidence || [],
      associatedFindings: source.associatedFindings || [],
      hasVisibleFracture: source.hasVisibleFracture,
      fractureConfidence: source.fractureConfidence,
      fractureVisibility: source.fractureVisibility,
      fracturePattern: source.fracturePattern,
      corticalDisruption: source.corticalDisruption,
      trabecularDisruption: source.trabecularDisruption,
      displacementLevel: source.displacementLevel,
      articularExtension: source.articularExtension,
      alignmentPreserved: source.alignmentPreserved,
      associatedInstability: source.associatedInstability,
      diagnosticCertaintyText: source.diagnosticCertaintyText,
      diagnosticUncertainty: source.diagnosticUncertainty,
      recommendedConduct: source.recommendedConduct,
      conductRationale: source.conductRationale,
      anatomicalRegion: source.anatomicalRegion || region,
      boneInvolved: source.boneInvolved || bone,
      hasFracture: source.hasFracture,
      fractureType: source.fractureType,
      fractureLocation: source.fractureLocation,
      displacement: source.displacement,
      articularInvolvement: source.articularInvolvement,
      comminution: source.comminution,
      alignmentStatus: source.alignmentStatus,
      instabilitySigns: source.instabilitySigns,
      severityLevel: source.severityLevel,
      treatmentRecommendation: source.treatmentRecommendation,
      treatmentRationale: source.treatmentRationale,
      uncertaintyNote: source.uncertaintyNote,
    },
    lang
  );

  if (structuredDiagnosis) return structuredDiagnosis;

  const uncertaintyText =
    source.diagnosticCertaintyText ||
    source.diagnosticUncertainty ||
    source.uncertaintyNote ||
    "";

  return buildFractureNarrative(
    {
      examRegion: source.examRegion || region,
      laterality: source.laterality,
      suspectedBone: source.suspectedBone || bone,
      primaryBone: source.primaryBone || source.suspectedBone || bone,
      boneSegment: source.boneSegment || source.suspectedSubregion,
      fractureLineDescription: source.fractureLineDescription || "",
      imagingEvidence: source.imagingEvidence || [],
      associatedFindings: source.associatedFindings || [],
      hasVisibleFracture: false,
      fractureConfidence: "baixa",
      fracturePattern: "",
      corticalDisruption: false,
      trabecularDisruption: false,
      displacementLevel: "ausente",
      articularExtension: false,
      alignmentPreserved: true,
      associatedInstability: "",
      diagnosticCertaintyText: "",
      diagnosticUncertainty: uncertaintyText || "Evidencia insuficiente para confirmar fratura definitiva.",
      recommendedConduct: "conservador",
      conductRationale: "",
      anatomicalRegion: source.anatomicalRegion || region,
      boneInvolved: source.boneInvolved || bone,
      hasFracture: false,
      fractureType: "",
      fractureLocation: source.fractureLocation || source.suspectedSubregion || region || bone || "",
      displacement: "ausente",
      articularInvolvement: false,
      comminution: false,
      alignmentStatus: "alinhamento preservado",
      instabilitySigns: "",
      severityLevel: "baixo",
      treatmentRecommendation: "conservador",
      treatmentRationale: source.surgicalPlan || "",
      uncertaintyNote: uncertaintyText || "Evidencia insuficiente para confirmar fratura definitiva.",
    },
    lang
  );
};

type AtlasSurgicalPlanSections = {
  diagnosis: string;
  differentialDiagnosis: string[];
  conduta: string;
  materials: string;
  technique: string;
  tips: string;
  planB: string;
};

const extractAtlasSurgicalPlanSections = (
  source: {
    surgicalPlan?: string;
    diagnosis?: string;
    differentialDiagnosis?: string[] | string;
    conduta?: string;
    recommendedConduct?: string;
    materials?: string;
    technique?: string;
    tips?: string;
    planB?: string;
  }
): AtlasSurgicalPlanSections => {
  const surgicalPlan = source.surgicalPlan || "";
  const diagnosis =
    (source.diagnosis || "").trim() ||
    extractTaggedPlanValue(surgicalPlan, "DIAGNOSTICO") ||
    extractTaggedPlanValue(surgicalPlan, "DIAGNOSIS") ||
    "";

  const conduta =
    (source.conduta || source.recommendedConduct || "").trim() ||
    extractTaggedPlanValue(surgicalPlan, "CONDUTA") ||
    extractTaggedPlanValue(surgicalPlan, "SURGICAL_INDICATION") ||
    extractTaggedPlanValue(surgicalPlan, "SURGICAL INDICATION") ||
    extractTaggedPlanValue(surgicalPlan, "CLINICAL_DISPOSITION") ||
    extractTaggedPlanValue(surgicalPlan, "CLINICAL DISPOSITION") ||
    "";

  const materials =
    (source.materials || "").trim() ||
    extractTaggedPlanValue(surgicalPlan, "MATERIAIS") ||
    extractTaggedPlanValue(surgicalPlan, "MATERIALS") ||
    "";

  const technique =
    (source.technique || "").trim() ||
    extractTaggedPlanValue(surgicalPlan, "TECNICA") ||
    extractTaggedPlanValue(surgicalPlan, "TECHNIQUE") ||
    "";

  const tips =
    (source.tips || "").trim() ||
    extractTaggedPlanValue(surgicalPlan, "DICAS") ||
    extractTaggedPlanValue(surgicalPlan, "TIPS") ||
    "";

  const planB =
    (source.planB || "").trim() ||
    extractTaggedPlanValue(surgicalPlan, "PLANO_B") ||
    extractTaggedPlanValue(surgicalPlan, "PLAN_B") ||
    "";

  const normalizedDifferentialDiagnosis = normalizeDifferentialDiagnosis(
    source.differentialDiagnosis ||
      extractTaggedPlanValue(surgicalPlan, "DIAGNOSTICOS_DIFERENCIAIS") ||
      extractTaggedPlanValue(surgicalPlan, "DIAGNOSTICOS_DIFERENCIALES") ||
      extractTaggedPlanValue(surgicalPlan, "DIFFERENTIAL_DIAGNOSIS")
  );

  return {
    diagnosis,
    differentialDiagnosis: normalizedDifferentialDiagnosis,
    conduta,
    materials,
    technique,
    tips,
    planB,
  };
};

const buildAtlasSurgicalPlanText = (sections: AtlasSurgicalPlanSections) => {
  const parts: string[] = [];

  if (sections.diagnosis) {
    parts.push(`--- DIAGNÃ“STICO ---\n[DIAGNOSTICO]\n${sections.diagnosis}`);
  }

  if (sections.differentialDiagnosis.length > 0) {
    parts.push(
      `--- DIAGNÃ“STICOS DIFERENCIAIS ---\n[DIAGNOSTICOS_DIFERENCIAIS]\n${sections.differentialDiagnosis
        .map((item) => `- ${item}`)
        .join("\n")}`
    );
  }

  if (sections.conduta) {
    parts.push(`--- CONDUTA ---\n[CONDUTA]\n${sections.conduta}`);
  }

  if (sections.materials) {
    parts.push(`--- MATERIAIS & OPME ---\n[MATERIAIS]\n${sections.materials}`);
  }

  if (sections.technique) {
    parts.push(`--- TÃ‰CNICA CIRÃšRGICA ---\n[TECNICA]\n${sections.technique}`);
  }

  if (sections.tips) {
    parts.push(`--- DICAS & ARMADILHAS ---\n[DICAS]\n${sections.tips}`);
  }

  if (sections.planB) {
    parts.push(`--- PLANO B ---\n[PLANO_B]\n${sections.planB}`);
  }

  return parts.join("\n\n");
};

export const detectAtlasROI = (
  analysis: AtlasImageAnalysis,
  anatomyContext?: string,
  mode: AtlasGenerationMode = 'detailed'
): AtlasROI => {
  const regionLabel = (
    analysis.atlasFindings?.anatomyRegion ||
    analysis.anatomicalRegion ||
    anatomyContext ||
    "bone_region"
  )
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_");

  const anchorText = `${analysis.anatomicalAnchors || ""} ${analysis.atlasFindings?.alignmentNotes || ""}`.toLowerCase();

  let centerX = 500;
  let centerY = 500;
  let width = mode === 'fast' ? 520 : 420;
  let height = mode === 'fast' ? 520 : 420;

  if (/(left|esq|lateral left|superior left|inferior left)/.test(anchorText)) {
    centerX = 340;
  } else if (/(right|dir|lateral right|superior right|inferior right)/.test(anchorText)) {
    centerX = 660;
  }

  if (/(top|upper|superior)/.test(anchorText)) {
    centerY = 340;
  } else if (/(bottom|lower|inferior|distal)/.test(anchorText)) {
    centerY = 660;
  }

  if (/(center|central|middle|metade)/.test(anchorText)) {
    centerX = 500;
    centerY = 500;
  }

  if (/(shaft|diafis|diaphys)/.test(regionLabel)) {
    width = 300;
    height = 560;
  } else if (/(joint|condyle|plateau|acetab|knee|hip|ankle|shoulder|elbow|wrist)/.test(regionLabel)) {
    width = 460;
    height = 460;
  }

  if (
    (analysis.atlasFindings?.fractureSigns || []).length > 0 ||
    (analysis.atlasFindings?.boneLossSigns || []).length > 0
  ) {
    width -= mode === 'fast' ? 20 : 40;
    height -= mode === 'fast' ? 20 : 40;
  }

  return {
    x: clampRoi(Math.round(centerX - width / 2), 0, 1000),
    y: clampRoi(Math.round(centerY - height / 2), 0, 1000),
    width: clampRoi(width, 180, 700),
    height: clampRoi(height, 180, 700),
    regionType: regionLabel || "bone_region",
  };
};

export const buildProgressiveAtlasGenerationStages = (
  analysis: AtlasImageAnalysis,
  validation: AtlasValidation,
  roi: AtlasROI,
  anatomyContext?: string,
  mode: AtlasGenerationMode = 'detailed'
): AtlasGenerationStages => {
  const atlasFindings = analysis.atlasFindings;
  const regionContext =
    atlasFindings?.anatomyRegion || analysis.anatomicalRegion || anatomyContext || "Bone";
  const boneContext = atlasFindings?.bone || regionContext;
  const sideContext = atlasFindings?.side || "preserve original side";

  const diagnosisText =
    extractTaggedPlanValue(analysis.surgicalPlan, "DIAGNOSTICO") ||
    extractTaggedPlanValue(analysis.surgicalPlan, "DIAGNOSIS") ||
    "Preserve only exam-supported diagnosis details.";
  const materialsText =
    extractTaggedPlanValue(analysis.surgicalPlan, "MATERIAIS") ||
    extractTaggedPlanValue(analysis.surgicalPlan, "MATERIALS");
  const techniqueText =
    extractTaggedPlanValue(analysis.surgicalPlan, "TECNICA") ||
    extractTaggedPlanValue(analysis.surgicalPlan, "TECHNIQUE");

  const visibleFindings = [
    ...(atlasFindings?.visibleFindings || []),
    ...(atlasFindings?.fractureSigns || []),
    ...(atlasFindings?.degenerativeSigns || []),
    ...(atlasFindings?.boneLossSigns || []),
  ].filter(Boolean);

  const baseBoneStage = `Stage 1 - Base bone structure:
  Reconstruct only the primary osseous anatomy of ${boneContext} in the ${regionContext} region.
  Respect original laterality (${sideContext}) and keep the main ROI centered at x=${roi.x}, y=${roi.y}, width=${roi.width}, height=${roi.height}.
  Preserve overall exam context without cropping away surrounding anatomy.${
    mode === 'fast' ? " Keep the representation lean and prioritize the dominant bone contour." : ""
  }`;

  const anatomicalAdjustmentStage = `Stage 2 - Anatomical adjustment:
  Align the bone using the validated main axis vector (${validation.boneAxis.x}, ${validation.boneAxis.y}, ${validation.boneAxis.z}).
  Maintain plausible cortical contour, joint orientation, and alignment.
  ${validation.correctionPrompt}`;

  const clinicalFindingsStage = `Stage 3 - Clinical findings:
  Apply only the clinically visible abnormalities detected in the exam: ${
    (mode === 'fast' ? visibleFindings.slice(0, 4) : visibleFindings).join("; ") ||
    "no additional visible findings beyond the analyzed region"
  }.
  Preserve diagnosis-oriented details: ${diagnosisText}
  Never remove fissures, cortical irregularity, arthrosis, wear, bone loss, joint space reduction, osteophytes, focal defects, or asymmetry when present.`;

  const implantStage = `Stage 4 - Implant application:
  Apply planned implants only after anatomy and pathology are already coherent.
  Planned materials: ${materialsText || "No implant should be introduced unless clearly supported by planning data."}
  Planned technique: ${techniqueText || "Preserve anatomy without altering user planning."}
  Keep plates on cortical contour, screws intra-osseous/bicortical when appropriate, and hardware aligned with the validated bone axis.${
    mode === 'fast' ? " Limit implant refinement to the primary hardware only." : ""
  }`;

  const finalRenderStage = `Final render:
  Merge the previous stages without losing laterality, ROI focus, pathology, or implant plausibility.
  Produce a ${mode === 'fast' ? "clean fast" : "high-fidelity"} medical render with preserved pathology and no artificial normalization.`;

  return {
    baseBoneStage,
    anatomicalAdjustmentStage,
    clinicalFindingsStage,
    implantStage,
    finalRenderStage,
  };
};

export const generateAtlasImageFromAnalysis = async (
  primaryImageBase64: string,
  analysis: AtlasImageAnalysis,
  anatomyContext?: string,
  validation?: AtlasValidation,
  roi?: AtlasROI,
  mode: AtlasGenerationMode = 'detailed'
) => {
  const ai = createGeminiClient("generateAtlasImage");
  const startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
  const normalizedImage = normalizeAtlasImageInput(primaryImageBase64);
  const preparedImage = await prepareAtlasImageForFunction(normalizedImage.dataUrl, {
    maxDimension: 1024,
    quality: 0.8,
    maxFinalBytes: 1_200_000,
  }).catch((error) => {
    console.warn("[ATLAS][measureAnatomyFromImage][compression-failed]", error);
    return null;
  });
  const mimeType = preparedImage?.mimeType || normalizedImage.mimeType;
  const cleanBase64 = preparedImage?.base64 || normalizedImage.imageBase64;

  const atlasFindings: AtlasFindings = {
    anatomyRegion:
      analysis.atlasFindings?.anatomyRegion || analysis.anatomicalRegion || anatomyContext || "",
    bone: analysis.atlasFindings?.bone || "",
    side: analysis.atlasFindings?.side || "",
    visibleFindings: analysis.atlasFindings?.visibleFindings || [],
    degenerativeSigns: analysis.atlasFindings?.degenerativeSigns || [],
    fractureSigns: analysis.atlasFindings?.fractureSigns || [],
    boneLossSigns: analysis.atlasFindings?.boneLossSigns || [],
    alignmentNotes: analysis.atlasFindings?.alignmentNotes || "",
    primaryStructureType: analysis.atlasFindings?.primaryStructureType || analysis.primaryStructureType,
    primaryStructureName: analysis.atlasFindings?.primaryStructureName || analysis.primaryStructureName,
    associatedStructures: analysis.atlasFindings?.associatedStructures || analysis.associatedStructures || [],
    lesionConfidence: analysis.atlasFindings?.lesionConfidence || analysis.lesionConfidence,
  };

  const regionContext = anatomyContext || analysis.anatomicalRegion || "Bone";
  const findingsSummary = [
    ...atlasFindings.visibleFindings,
    ...atlasFindings.degenerativeSigns,
    ...atlasFindings.fractureSigns,
    ...atlasFindings.boneLossSigns,
    atlasFindings.alignmentNotes,
  ]
    .filter(Boolean)
    .join("; ");

  const preservationInstructions =
    analysis.preservationInstructions ||
    "Preserve the real pathology from the exam. Do not normalize, smooth, heal, or beautify the anatomy.";
  const atlasValidation = validation || validateAtlasAnatomy(analysis, anatomyContext, mode);
  const atlasROI = roi || detectAtlasROI(analysis, anatomyContext, mode);
  const visualMode = getAtlasVisualModePrompt(mode);
  const progressiveStages = buildProgressiveAtlasGenerationStages(
    analysis,
    atlasValidation,
    atlasROI,
    anatomyContext,
    mode
  );
  const radiographicFractureCheck = await validateAtlasFractureFromRadiograph(
    [normalizedImage],
    analysis,
    anatomyContext,
    mode
  );

  if (radiographicFractureCheck.conflictWithPreviousDiagnosis) {
    console.warn("[Atlas][radiographicFractureCheck][conflict]", {
      affectedBone: radiographicFractureCheck.affectedBone,
      location: radiographicFractureCheck.location,
      correctedDiagnosis: radiographicFractureCheck.correctedDiagnosis,
      previousDiagnosis: analysis.diagnosis || analysis.diagnosticSummary || "",
    });
  }

  const correctedDiagnosis =
    radiographicFractureCheck.correctedDiagnosis ||
    analysis.diagnosis ||
    analysis.diagnosticSummary ||
    "";
  const correctedAffectedBone =
    radiographicFractureCheck.affectedBone || atlasFindings.bone || regionContext;
  const correctedFractureLocation =
    radiographicFractureCheck.location || analysis.fractureLocation || "";
  const notAffectedPrimaryBone = radiographicFractureCheck.notAffectedPrimaryBone || [];
  const confidence = radiographicFractureCheck.confidence ?? 0;

  const atlasPrompt = `You are a senior pediatric/adult musculoskeletal radiologist and orthopedic trauma surgeon.

Your first task is to identify the exact fracture location directly from the radiograph.
Do not blindly trust the previous text diagnosis.
If the previous diagnosis conflicts with the image, correct it.

For elbow radiographs, explicitly check:
- distal humerus / supracondylar region
- lateral condyle
- medial epicondyle
- radial head/neck
- olecranon / proximal ulna

Determine which structure is actually fractured before creating the 3D image.

Radiographic fracture check:
- affected bone: ${correctedAffectedBone || "unknown"}
- location: ${correctedFractureLocation || "unknown"}
- confidence: ${confidence}
- conflict with previous diagnosis: ${radiographicFractureCheck.conflictWithPreviousDiagnosis ? "true" : "false"}
- corrected diagnosis: ${correctedDiagnosis || "unknown"}
- not affected primary bone: ${notAffectedPrimaryBone.join(", ") || "none"}

Previous diagnosis:
${analysis.diagnosis || analysis.diagnosticSummary || ""}

Input diagnosis:
${correctedDiagnosis}

Anatomical region:
${analysis.examRegion || analysis.anatomicalRegion || anatomyContext || ""}

Fracture location:
${correctedFractureLocation}

Fracture pattern:
${analysis.fracturePattern || analysis.fractureType || ""}

Displacement:
${analysis.displacementLevel || analysis.displacement || ""}

Create a high-quality 3D orthopedic atlas visualization.

CRITICAL RULES:
- Do NOT create a generic fracture.
- Do NOT move the fracture to another bone region.
- Do NOT simplify the fracture pattern.
- Preserve the exact anatomical location.
- The fracture must match the radiographic finding.
- If the diagnosis says olecranon / proximal ulna, the fracture must be on the olecranon / proximal ulna only.
- If the radiograph shows supracondylar distal humerus fracture, the main fracture must be on the distal humerus above the condyles.
- Do not place the main fracture in the distal humerus, radial head, or a generic elbow joint unless the radiograph supports it.
- Highlight the fracture line clearly on the affected bone.

The provided image is the geometric and pathological blueprint.
Use the original X-ray as the source of truth for bone positioning and alignment.
Preserve exact anatomical geometry and bone alignment.
If the text diagnosis conflicts with the image, the image wins.

STRUCTURED ANATOMY:
- Region: ${analysis.atlasFindings?.anatomyRegion || regionContext}
- Bone: ${correctedAffectedBone || atlasFindings.bone || regionContext}
- Side: ${atlasFindings.side || "preserve original side"}

PATHOLOGY TO PRESERVE:
- Anchors: ${analysis.anatomicalAnchors}
- Visible findings: ${findingsSummary || "Preserve only clearly visible real findings from the exam."}
- Alignment: ${atlasFindings.alignmentNotes || "Maintain the original visible alignment."}
- Preservation rule: ${preservationInstructions}
- Corrected diagnosis: ${correctedDiagnosis}
- Corrected fracture location: ${correctedFractureLocation}

Rendering requirements:
- Resolution: 1024px
- Clean dark or neutral medical background
- Focus on affected bone and fracture
- Show the elbow anatomy only as needed for orientation
- The affected bone must be central and visually dominant when it is the diagnosed site
- Fracture line must be clearly visible and anatomically plausible
- Avoid excessive soft tissue
- Avoid cinematic effects
- Avoid decorative elements
- Prioritize clinical accuracy over aesthetics
- Preserve original anatomy, axis, laterality, and bone geometry
- The primary fracture must be on the corrected affected bone and corrected fracture location

Negative constraints:
- Do not generate an olecranon fracture if the fracture is supracondylar.
- Do not place the fracture on the ulna if the affected bone is the distal humerus.
- Do not follow a wrong previous diagnosis.
- Do not create generic elbow trauma.
- Do not generate a normal elbow.
- Do not generate a generic elbow fracture.
- Do not hide the fracture.
- Do not add labels unless explicitly requested.

Output:
One single medical 3D visualization focused on the exact fracture.`;

  return fetchWithRetry(async () => {
    try {
      const response = await ai.models.generateContent({
        model: "gemini-3-pro-image-preview",
        contents: {
          parts: [
            { inlineData: { data: cleanBase64, mimeType } },
            { text: atlasPrompt },
          ],
        },
        config: {
          imageConfig: {
            aspectRatio: "1:1",
            imageSize: "1K",
          },
        },
      });

      for (const part of response.candidates?.[0]?.content?.parts || []) {
        if (part.inlineData) return part.inlineData.data;
      }
      return null;
    } catch (e) {
      console.warn("Image generation failed", e);
      return null;
    }
  }, 0, 2000, 300000);
};

// NEW: Face Detection Service
export const detectFaceData = async (imageBase64: string) => {
  const ai = createGeminiClient("generateContent");
  const mimeMatch = imageBase64.match(/^data:(image\/\w+);base64,/);
  const mimeType = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const cleanBase64 = imageBase64.replace(/^data:image\/\w+;base64,/, "");

  const prompt = `
    Analyze this image and detect the MAIN HUMAN FACE.
    Return the bounding box coordinates normalized to 1000 (0-1000 scale).
    
    Return JSON:
    {
      "ymin": number,
      "xmin": number,
      "ymax": number,
      "xmax": number
    }
    If no face is clearly visible, return { "ymin": 0, "xmin": 0, "ymax": 1000, "xmax": 1000 }.
  `;

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: {
        parts: [
          { inlineData: { data: cleanBase64, mimeType: mimeType } },
          { text: prompt }
        ]
      },
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            ymin: { type: Type.NUMBER },
            xmin: { type: Type.NUMBER },
            ymax: { type: Type.NUMBER },
            xmax: { type: Type.NUMBER },
          }
        }
      }
    });

    const box = JSON.parse(cleanJson(response.text || "{}"));
    
    // Calculate Center Percentages and Scale
    // Coordinates are 0-1000.
    const centerY = (box.ymin + box.ymax) / 2;
    const centerX = (box.xmin + box.xmax) / 2;
    
    // Calculate face width relative to image
    const faceWidth = box.xmax - box.xmin; // 0-1000
    
    // Calculate Translation (Shift) needed to bring Center to 50% (500)
    // Formula: (Target - Actual) / 10
    // If face is at 800 (right), we need to shift image LEFT, so result should be negative.
    // CSS Translate logic: positive moves right.
    // To center 800 at 500, we move image by (500 - 800) = -300 units (-30%).
    const xPercent = (500 - centerX) / 10;
    const yPercent = (500 - centerY) / 10;

    // Calculate Ideal Scale
    // We want the face to occupy roughly 50-60% of the circle width
    // Current face fraction = faceWidth / 1000.
    // Target fraction = 0.55
    // Scale = Target / Current
    let idealScale = 1;
    if (faceWidth > 0) {
        idealScale = 550 / faceWidth;
    }
    
    // Clamp scale to reasonable limits
    idealScale = Math.min(Math.max(idealScale, 1), 3.5);

    return {
        x: xPercent,
        y: yPercent,
        scale: idealScale
    };

  } catch (error) {
    console.error("Face Detect Error", error);
    return { x: 0, y: 0, scale: 1 };
  }
};

export const analyzeDisease = async (diseaseName: string, lang: Language = 'pt') => {
  const ai = createGeminiClient("generateContent");
  const pathologyResponseInstruction = (lang === 'en'
    ? `Also return these extra sections, without removing any existing fields:
       - definicao: concise definition of the pathology.
       - etiologia: main causes, risk factors, and relevant pathophysiology.
       - tiposConduta: the possible management types, organized and clinically relevant.
       - tratamentoDetalhado: detailed treatment plan, including conservative and surgical options when applicable.
       - prognostico: prognosis, expected evolution, and main prognostic factors.
       - reabilitacao: rehabilitation and functional recovery guidance.
       Keep each section short, readable, and ready for markdown rendering inside the JSON string.`
    : lang === 'es'
      ? `También devuelve estas secciones extra, sin eliminar ningún campo existente:
       - definicao: definición concisa de la patología.
       - etiologia: causas principales, factores de riesgo y fisiopatología relevante.
       - tiposConduta: los posibles tipos de conducta, organizados y clínicamente relevantes.
       - tratamentoDetalhado: plan de tratamiento detallado, incluyendo opciones conservadoras y quirúrgicas cuando corresponda.
       - prognostico: pronóstico, evolución esperada y factores pronósticos principales.
       - reabilitacao: rehabilitación y guía de recuperación funcional.
       Mantén cada sección breve, legible y lista para renderizar en markdown dentro de la cadena JSON.`
      : `Inclua também, sem remover nenhum campo existente, estas seções extras:
       - definicao: definição concisa da patologia.
       - etiologia: causas principais, fatores de risco e fisiopatologia relevante.
       - tiposConduta: os possíveis tipos de conduta, organizados e clinicamente relevantes.
       - tratamentoDetalhado: plano de tratamento detalhado, incluindo opções conservadoras e cirúrgicas quando aplicável.
       - prognostico: prognóstico, evolução esperada e principais fatores prognósticos.
       - reabilitacao: reabilitação e orientação de recuperação funcional.
       Mantenha cada seção curta, legível e pronta para renderização em markdown dentro da string JSON.`);
  const prompt = (lang === 'en'
    ? `Analyze detail: "${diseaseName}". Return technical info for an orthopedic surgeon.`
    : (lang === 'es'
      ? `Analiza detalle: "${diseaseName}". Retorna info técnica para cirujano ortopédico.`
      : `Analise detalhadamente a seguinte patologia ou fratura: "${diseaseName}". Retorne as informações técnicas para um cirurgião ortopedista.`))
    + `\n\n${getSystemInstruction(lang)}\n${getClinicalResponseInstruction(lang)}\n${pathologyResponseInstruction}\n${getSelectedLanguageInstruction(lang)}`;

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-2.5-flash',
      contents: prompt,
      config: {
        temperature: 0.1,
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            nome: { type: Type.STRING },
            classificacao: { type: Type.ARRAY, items: { type: Type.STRING } },
            exameFisico: { type: Type.ARRAY, items: { type: Type.STRING } },
            imagem: { type: Type.ARRAY, items: { type: Type.STRING } },
            conduta: { type: Type.STRING },
            opme: { type: Type.ARRAY, items: { type: Type.STRING } },
            definicao: { type: Type.STRING },
            etiologia: { type: Type.STRING },
            tiposConduta: { type: Type.STRING },
            tratamentoDetalhado: { type: Type.STRING },
            prognostico: { type: Type.STRING },
            reabilitacao: { type: Type.STRING }
          },
          required: ["nome", "classificacao", "exameFisico", "imagem", "conduta", "opme", "definicao", "etiologia", "tiposConduta", "tratamentoDetalhado", "prognostico", "reabilitacao"]
        },
      },
    });
    return normalizeMedicalReportPrescription(JSON.parse(cleanJson(response.text || "{}")));
  } catch (error) {
    console.error("Gemini Consult Error:", error);
    throw error;
  }
};

export type PatientContext = {
  age?: string;
  chiefComplaint?: string;
  comorbidities?: string[];
  priorTreatments?: string;
  observations?: string;
  anatomy?: string;
  allergies?: string[];
};

const buildPatientContextBlock = (ctx: PatientContext | undefined, lang: Language): string => {
  if (!ctx) return '';
  const lines: string[] = [];
  if (ctx.age) lines.push(lang === 'pt' ? `Idade: ${ctx.age}` : `Age: ${ctx.age}`);
  if (ctx.anatomy) lines.push(lang === 'pt' ? `Região anatômica: ${ctx.anatomy}` : `Anatomical region: ${ctx.anatomy}`);
  if (ctx.chiefComplaint) lines.push(lang === 'pt' ? `Queixa principal: ${ctx.chiefComplaint}` : `Chief complaint: ${ctx.chiefComplaint}`);
  if (ctx.observations) lines.push(lang === 'pt' ? `Histórico clínico: ${ctx.observations}` : `Clinical history: ${ctx.observations}`);
  if (ctx.comorbidities?.length) lines.push((lang === 'pt' ? 'Comorbidades: ' : 'Comorbidities: ') + ctx.comorbidities.join(', '));
  if (ctx.allergies?.length) lines.push((lang === 'pt' ? 'Alergias: ' : 'Allergies: ') + ctx.allergies.join(', '));
  if (ctx.priorTreatments) lines.push(lang === 'pt' ? `Tratamentos prévios: ${ctx.priorTreatments}` : `Prior treatments: ${ctx.priorTreatments}`);
  if (!lines.length) return '';
  const header = lang === 'pt'
    ? '\n\nCONTEXTO CLÍNICO DO PACIENTE (use estes dados para personalizar a análise):\n'
    : '\n\nPATIENT CLINICAL CONTEXT (use this data to personalize the analysis):\n';
  return header + lines.join('\n');
};

export const analyzeMedicalReport = async (imageBase64: string, lang: Language = 'pt', patientContext?: PatientContext) => {
  const ai = createGeminiClient("analyzeMedicalReport");

  const mimeMatch = imageBase64.match(/^data:([^;]+);base64,/);
  const mimeType = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const cleanBase64 = imageBase64.replace(/^data:[^;]+;base64,/, "");

  const patientBlock = buildPatientContextBlock(patientContext, lang);

  const prompt = (patientBlock ? patientBlock + "\n\n" : "") + (lang === "pt"
    ? `Analise esta imagem de um LAUDO MÃƒâ€°DICO (RessonÃƒÂ¢ncia, Tomografia, Raio-X ou Ultrassom).

       TAREFA:
       1. Extraia o DIAGNÃƒâ€œSTICO PRINCIPAL e os achados relevantes do texto.
       2. Com base na literatura ortopÃƒÂ©dica atual, classifique o caso como:
          - "Conservador" (Tratamento clÃƒÂ­nico/imobilizaÃƒÂ§ÃƒÂ£o)
          - "CirÃƒÂºrgico" (Necessidade de intervenÃƒÂ§ÃƒÂ£o)
          - "LimÃƒÂ­trofe/Avaliar" (Depende de critÃƒÂ©rios clÃƒÂ­nicos)
       3. Detalhe o plano de tratamento para a conduta selecionada.
       4. Gere uma SUGESTÃƒÆ’O DE PRESCRIÃƒâ€¡ÃƒÆ’O MÃƒâ€°DICA completa para este caso (Medicamentos sintomÃƒÂ¡ticos/especÃƒÂ­ficos e Cuidados gerais).
       5. A prescrição deve conter SOMENTE a saída final limpa, sem raciocínio interno, sem rascunho, sem autocorreção, sem comandos internos e sem meta-comentários.
       6. Liste cada medicamento apenas uma vez. Não repita o mesmo medicamento.
       7. Se houver dose, via, frequência, duração ou observações, inclua-as de forma objetiva e estruturada.
       8. Não inclua linhas como "Nome da Medicação", "Instrução", "Vamos simplificar" ou texto editorial similar.

       Retorne APENAS um JSON vÃƒÂ¡lido com esta estrutura:
       {
         "diagnosis": "Nome da Patologia Principal",
         "findings": ["Lista de achados relevantes", "Ex: Rotura completa supraespinhal", "RetraÃƒÂ§ÃƒÂ£o tendÃƒÂ­nea"],
         "category": "CirÃƒÂºrgico" | "Conservador" | "Avaliar",
         "reasoning": "ExplicaÃƒÂ§ÃƒÂ£o tÃƒÂ©cnica curta do motivo da classificaÃƒÂ§ÃƒÂ£o baseada no laudo (ex: rotura transfixante com retraÃƒÂ§ÃƒÂ£o).",
         "treatmentPlan": "DescriÃƒÂ§ÃƒÂ£o detalhada do tratamento (ex: Artroscopia para reparo de manguito ou Fisioterapia motora).",
         "rehabProtocol": "Breve resumo do pÃƒÂ³s-tratamento/reabilitaÃƒÂ§ÃƒÂ£o.",
         "prescription": {
            "medications": [
               { "name": "Nome do remÃƒÂ©dio e dosagem (Ex: Dipirona 1g)", "instructions": "Posologia (Ex: 1 cp a cada 6h se dor)" }
            ],
            "careInstructions": ["Lista de cuidados gerais (Ex: Gelo local, Repouso relativo)"]
         }
       }`
    : `Analyze this MEDICAL REPORT image (MRI, CT, X-Ray, or Ultrasound).
       
       TASK:
       1. Extract the MAIN DIAGNOSIS and relevant findings.
       2. Classify the case based on current orthopedic literature:
          - "Conservative"
          - "Surgical"
          - "Borderline/Assess"
       3. Detail the treatment plan.
       4. Generate a MEDICAL PRESCRIPTION SUGGESTION for this case.
       5. The prescription must contain ONLY the final clean output, with no internal reasoning, no draft, no autocorrection, no internal commands, and no meta-comments.
       6. List each medication only once. Do not repeat the same medication.
       7. If dose, route, frequency, duration, or observations are available, include them in an objective and structured way.
       8. Do not include lines such as "Medication Name", "Instruction", "Let's simplify", or similar editorial text.

       Return ONLY valid JSON:
       {
         "diagnosis": "Main Pathology Name",
         "findings": ["List of findings", "e.g. Full thickness tear"],
         "category": "Surgical" | "Conservative" | "Assess",
         "reasoning": "Technical explanation for classification.",
         "treatmentPlan": "Detailed treatment description.",
         "rehabProtocol": "Brief rehab summary.",
         "prescription": {
            "medications": [
               { "name": "Medication Name & Dosage", "instructions": "Frequency and duration" }
            ],
            "careInstructions": ["General care advice"]
         }
       }`)
    + `\n\n${getClinicalResponseInstruction(lang)}\n${getSelectedLanguageInstruction(lang)}`;

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-3-pro-preview', // Pro model for better reasoning on text/OCR
      contents: {
        parts: [
          { inlineData: { data: cleanBase64, mimeType: mimeType } },
          { text: prompt }
        ]
      },
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            diagnosis: { type: Type.STRING },
            findings: { type: Type.ARRAY, items: { type: Type.STRING } },
            category: { type: Type.STRING },
            reasoning: { type: Type.STRING },
            treatmentPlan: { type: Type.STRING },
            rehabProtocol: { type: Type.STRING },
            prescription: {
              type: Type.OBJECT,
              properties: {
                medications: {
                  type: Type.ARRAY,
                  items: {
                    type: Type.OBJECT,
                    properties: {
                      name: { type: Type.STRING },
                      instructions: { type: Type.STRING },
                      dose: { type: Type.STRING },
                      route: { type: Type.STRING },
                      frequency: { type: Type.STRING },
                      duration: { type: Type.STRING },
                      observations: { type: Type.STRING }
                    }
                  }
                },
                careInstructions: {
                  type: Type.ARRAY,
                  items: { type: Type.STRING }
                }
              }
            }
          },
          required: ["diagnosis", "findings", "category", "reasoning", "treatmentPlan", "prescription"]
        }
      }
    });
    const parsed = JSON.parse(cleanJson(response.text || "{}"));
    return normalizeMedicalReportPrescription(parsed);
  } catch (error) {
    console.error("Report Analysis Error:", error);
    throw error;
  }
};

export type MedicalReportDocumentType =
  | 'technical_report'
  | 'functional_opinion'
  | 'clinical_report'
  | 'patient_summary';

export type MedicalReportAnalysisResult = {
  diagnosis?: string;
  findings?: string[];
  category?: string;
  reasoning?: string;
  treatmentPlan?: string;
  rehabProtocol?: string;
  prescription?: {
    medications?: Array<{
      name?: string;
      instructions?: string;
    }>;
    careInstructions?: string[];
  };
};

export type GeneratedMedicalReportDocument = {
  title: string;
  content: string;
};

export type MedicalReportPatientContext = {
  id?: string;
  name?: string;
  age?: string;
  sex?: string;
  anatomy?: string;
  cpf?: string;
  phone?: string;
  email?: string;
  doctorOrthoId?: string;
  responsibleDoctorName?: string;
  profession?: string;
  physicalExam?: string;
};

const normalizePrescriptionText = (value: unknown) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .replace(/^(nome da medica[cç][aã]o|nome do medicamento|medicamento|instru[cç][aã]o(?:es)?|instructions?)\s*[:\-]?\s*/i, "")
    .trim();

const shouldDropPrescriptionLine = (value: string) => {
  const normalized = normalizeClinicalText(value);
  return [
    "correcao de formatacao",
    "vamos simplificar",
    "fim da autocorrecao",
    "nome da medicacao",
    "nome do medicamento",
    "instrucao",
    "instrucoes",
    "medication name",
    "instructions",
    "draft",
    "rascunho",
    "autocorrecao",
    "system prompt",
    "comandos internos",
    "meta comentario",
  ].some((term) => normalized.includes(term));
};

const normalizeMedicalReportPrescription = (report: any) => {
  const rawPrescription = report?.prescription || {};
  const medications = Array.isArray(rawPrescription.medications) ? rawPrescription.medications : [];
  const careInstructions = Array.isArray(rawPrescription.careInstructions) ? rawPrescription.careInstructions : [];
  const seenNames = new Set<string>();

  const cleanedMedications = medications
    .map((item: any) => {
      const name = normalizePrescriptionText(item?.name);
      const dose = normalizePrescriptionText(item?.dose);
      const route = normalizePrescriptionText(item?.route || item?.via);
      const frequency = normalizePrescriptionText(item?.frequency);
      const duration = normalizePrescriptionText(item?.duration);
      const observations = normalizePrescriptionText(item?.observations || item?.notes);
      const instructions = normalizePrescriptionText(item?.instructions);

      if (!name || shouldDropPrescriptionLine(name) || shouldDropPrescriptionLine(instructions)) {
        return null;
      }

      const normalizedName = normalizeClinicalText(name);
      if (seenNames.has(normalizedName)) {
        return null;
      }
      seenNames.add(normalizedName);

      const instructionParts = [
        dose ? `Dose: ${dose}` : "",
        route ? `Via: ${route}` : "",
        frequency ? `Frequência: ${frequency}` : "",
        duration ? `Duração: ${duration}` : "",
        observations ? `Observações: ${observations}` : "",
      ].filter(Boolean);

      const finalInstructions = instructionParts.length > 0
        ? instructionParts.join(" | ")
        : instructions;

      if (!finalInstructions || shouldDropPrescriptionLine(finalInstructions)) {
        return null;
      }

      return { name, instructions: finalInstructions };
    })
    .filter(Boolean);

  const cleanedCareInstructions = Array.from(
    new Set(
      careInstructions
        .map((item: any) => normalizePrescriptionText(item))
        .filter((item: string) => item && !shouldDropPrescriptionLine(item))
    )
  );

  return {
    ...report,
    prescription: {
      medications: cleanedMedications,
      careInstructions: cleanedCareInstructions,
    },
  };
};

const getMedicalReportDocumentConfig = (
  documentType: MedicalReportDocumentType,
  lang: Language
) => {
  const configs = {
    technical_report: {
      pt: {
        label: 'Laudo Ortopédico Técnico',
        tone: 'formal, técnico e objetivo',
        structure:
          'Inclua seções como Identificação, Síntese do laudo, Achados relevantes, Impressão diagnóstica, Conduta sugerida e Observações finais.',
      },
      en: {
        label: 'Technical Orthopedic Report',
        tone: 'formal, technical, and objective',
        structure:
          'Include sections such as Identification, Report summary, Relevant findings, Diagnostic impression, Suggested management, and Final notes.',
      },
      es: {
        label: 'Informe Ortopédico Técnico',
        tone: 'formal, técnico y objetivo',
        structure:
          'Incluya secciones como Identificación, Resumen del informe, Hallazgos relevantes, Impresión diagnóstica, Conducta sugerida y Observaciones finales.',
      },
    },
    functional_opinion: {
      pt: {
        label: 'Parecer Funcional / Pericial (INSS)',
        tone: 'técnico, funcional e pericial',
        structure:
          'Inclua obrigatoriamente as seguintes seções: (1) Identificação do paciente; (2) Achados do laudo e diagnóstico principal; (3) CID-10 principal e secundário; (4) Exame físico (Lasègue, amplitude de movimento, força muscular, reflexos patelares/aquileus e sensibilidade — com nota de "dados não registrados" para itens ausentes); (5) Repercussão funcional e limitações; (6) Impacto laboral considerando a profissão do paciente; (7) Estimativa de afastamento segmentada por tipo de atividade profissional (sedentária, moderada, pesada); (8) Capacidade laborativa atual; (9) Conclusão pericial médica.',
      },
      en: {
        label: 'Functional / Forensic Opinion (INSS)',
        tone: 'technical, functional, and forensic',
        structure:
          'Mandatory sections: (1) Patient identification; (2) Report findings and main diagnosis; (3) Primary and secondary ICD-10 codes; (4) Physical examination (Lasègue sign, range of motion, muscle strength, patellar/Achilles reflexes, sensitivity — note "not recorded" for missing items); (5) Functional impact and limitations; (6) Occupational impact considering the patient\'s profession; (7) Leave duration estimate segmented by occupation type (sedentary, moderate, heavy); (8) Current work capacity; (9) Medical forensic conclusion.',
      },
      es: {
        label: 'Dictamen Funcional / Pericial (INSS)',
        tone: 'técnico, funcional y pericial',
        structure:
          'Incluya obligatoriamente: (1) Identificación del paciente; (2) Hallazgos del informe y diagnóstico principal; (3) CIE-10 principal y secundario; (4) Exploración física (Lasègue, amplitud de movimiento, fuerza muscular, reflejos rotuliano/aquíleo y sensibilidad — indicar "no registrado" para ítems ausentes); (5) Repercusión funcional y limitaciones; (6) Impacto laboral considerando la profesión del paciente; (7) Estimación de baja laboral segmentada por tipo de actividad (sedentaria, moderada, pesada); (8) Capacidad laboral actual; (9) Conclusión médico-pericial.',
      },
    },
    clinical_report: {
      pt: {
        label: 'Relatório Clínico',
        tone: 'claro, clínico e estruturado',
        structure:
          'Inclua seções como Contexto clínico, Achados principais, Interpretação, Conduta e Seguimento recomendado.',
      },
      en: {
        label: 'Clinical Report',
        tone: 'clear, clinical, and structured',
        structure:
          'Include sections such as Clinical context, Main findings, Interpretation, Management, and Recommended follow-up.',
      },
      es: {
        label: 'Informe Clínico',
        tone: 'claro, clínico y estructurado',
        structure:
          'Incluya secciones como Contexto clínico, Hallazgos principales, Interpretación, Conducta y Seguimiento recomendado.',
      },
    },
    patient_summary: {
      pt: {
        label: 'Resumo para Paciente',
        tone: 'simples, acolhedor e de fácil compreensão',
        structure:
          'Explique o que o laudo mostra em linguagem leiga, o que isso significa, quais os próximos passos e sinais de alerta para procurar atendimento.',
      },
      en: {
        label: 'Patient Summary',
        tone: 'simple, supportive, and easy to understand',
        structure:
          'Explain what the report shows in plain language, what it means, the next steps, and warning signs that should prompt medical care.',
      },
      es: {
        label: 'Resumen para Paciente',
        tone: 'simple, cercano y fácil de entender',
        structure:
          'Explique qué muestra el informe en lenguaje sencillo, qué significa, los próximos pasos y las señales de alarma que requieren atención médica.',
      },
    },
  } as const;

  const localeConfig = configs[documentType][lang] || configs[documentType].pt;

  return {
    promptLabel: localeConfig.label,
    tone: localeConfig.tone,
    structure: localeConfig.structure,
  };
};

export const generateMedicalReportDocument = async (
  reportAnalysis: MedicalReportAnalysisResult,
  documentType: MedicalReportDocumentType,
  physicianOpinion: string = '',
  lang: Language = 'pt',
  patientContext?: MedicalReportPatientContext
): Promise<GeneratedMedicalReportDocument> => {
  const ai = createGeminiClient("generateContent");

  const analysisPayload = {
    diagnosis: reportAnalysis.diagnosis || '',
    findings: Array.isArray(reportAnalysis.findings) ? reportAnalysis.findings : [],
    category: reportAnalysis.category || '',
    reasoning: reportAnalysis.reasoning || '',
    treatmentPlan: reportAnalysis.treatmentPlan || '',
    rehabProtocol: reportAnalysis.rehabProtocol || '',
    prescription: reportAnalysis.prescription || null,
  };

  const patientPayload = patientContext
    ? {
        id: patientContext.id || '',
        name: patientContext.name || '',
        age: patientContext.age || '',
        sex: patientContext.sex || '',
        anatomy: patientContext.anatomy || '',
        cpf: patientContext.cpf || '',
        phone: patientContext.phone || '',
        email: patientContext.email || '',
        doctorOrthoId: patientContext.doctorOrthoId || '',
        responsibleDoctorName: patientContext.responsibleDoctorName || '',
        profession: patientContext.profession || '',
        physicalExam: patientContext.physicalExam || '',
      }
    : null;

  const docConfig = getMedicalReportDocumentConfig(documentType, lang);
  const physicianOpinionBlock = physicianOpinion.trim()
    ? `\n${lang === 'pt' ? 'PARECER CLÍNICO DO MÉDICO (OPCIONAL)' : lang === 'es' ? 'DICTAMEN CLÍNICO DEL MÉDICO (OPCIONAL)' : 'PHYSICIAN CLINICAL OPINION (OPTIONAL)'}:\n${physicianOpinion.trim()}`
    : '';

  const isINSS = documentType === 'functional_opinion';
  const profession = patientContext?.profession?.trim() || '';
  const physicalExam = patientContext?.physicalExam?.trim() || '';

  const inssExtrasBlock = isINSS
    ? lang === 'pt'
      ? `\nINSTRUÇÕES ESPECÍFICAS PARA PARECER PERICIAL (INSS):

ATENÇÃO: Este documento deve ser gerado COMPLETO, sem truncar nenhuma seção. Escreva todas as seções até o final antes de retornar o JSON. Não abrevie, não omita conteúdo, não use "[...]" ou "continuação omitida".

As seções obrigatórias são:
(1) Identificação do paciente — nome, idade, profissão/ocupação;
(2) Diagnóstico principal com CID-10 principal e secundário;
(3) Achados do exame físico;
(4) Repercussão funcional e limitações;
(5) Impacto laboral considerando a profissão do paciente;
(6) Estimativa de afastamento por tipo de atividade;
(7) Prognóstico e plano terapêutico;
(8) Capacidade laborativa atual;
(9) Conclusão pericial médica.

Escreva cada seção de forma completa e detalhada antes de passar para a próxima.

1. PROFISSÃO / IMPACTO LABORAL:
${profession
  ? `A profissão/ocupação do paciente é: "${profession}". Considere esta profissão ao avaliar o impacto funcional e ao indicar o tempo de afastamento. Descreva de que forma as limitações encontradas afetam especificamente as demandas físicas e cognitivas dessa atividade.`
  : 'Profissão não informada — mencione no parecer que este dado é necessário para estimativa precisa do afastamento.'}

2. EXAME FÍSICO (OBRIGATÓRIO NO PARECER):
${physicalExam
  ? `Dados de exame físico registrados no prontuário:\n${physicalExam}\nIncorpore esses dados na seção de exame físico do parecer (Lasègue, amplitude de movimento, força muscular, reflexos patelares e aquileus, sensibilidade). Se algum item não estiver descrito, indique "não registrado" para esse item específico.`
  : 'Dados de exame físico não registrados no prontuário — inclua obrigatoriamente no parecer a seguinte observação: "Dados de exame físico não registrados — necessário complementar antes de emitir parecer pericial."'}

3. CID-10 E TEMPO DE AFASTAMENTO:
- Extraia o CID-10 principal e, se aplicável, o secundário, com base no diagnóstico da análise do laudo. Se não for possível determinar com precisão, indique o CID-10 mais provável com justificativa.
- Inclua a seção "Estimativa de Afastamento" COMPLETA com tempo em dias segmentado por tipo de atividade:
  • Trabalho sedentário / administrativo: [X]–[Y] dias
  • Trabalho em pé / moderado: [X]–[Y] dias
  • Trabalho físico pesado / manual: [X]–[Y] dias
  Ajuste os intervalos de acordo com a gravidade dos achados e a profissão informada.`
      : lang === 'es'
      ? `\nINSTRUCCIONES ESPECÍFICAS PARA DICTAMEN PERICIAL (INSS):

ATENCIÓN: Este documento debe generarse COMPLETO, sin truncar ninguna sección. Escriba todas las secciones hasta el final antes de devolver el JSON. No abrevie, no omita contenido, no use "[...]" ni "continuación omitida".

Las secciones obligatorias son:
(1) Identificación del paciente — nombre, edad, profesión/ocupación;
(2) Diagnóstico principal con CIE-10 principal y secundario;
(3) Hallazgos de la exploración física;
(4) Repercusión funcional y limitaciones;
(5) Impacto laboral considerando la profesión del paciente;
(6) Estimación de baja laboral por tipo de actividad;
(7) Pronóstico y plan terapéutico;
(8) Capacidad laboral actual;
(9) Conclusión médico-pericial.

Escriba cada sección de forma completa y detallada antes de pasar a la siguiente.

1. PROFESIÓN / IMPACTO LABORAL:
${profession
  ? `La profesión/ocupación del paciente es: "${profession}". Considere esta profesión al evaluar el impacto funcional y al indicar el tiempo de baja laboral. Describa de qué manera las limitaciones encontradas afectan específicamente las demandas físicas y cognitivas de esa actividad.`
  : 'Profesión no informada — mencione en el dictamen que este dato es necesario para una estimación precisa de la baja.'}

2. EXPLORACIÓN FÍSICA (OBLIGATORIA EN EL DICTAMEN):
${physicalExam
  ? `Datos de exploración física registrados en la historia clínica:\n${physicalExam}\nIncorpore estos datos en la sección de exploración física del dictamen (Lasègue, amplitud de movimiento, fuerza muscular, reflejos rotuliano/aquíleo y sensibilidad). Si algún elemento no está descrito, indique "no registrado" para ese ítem.`
  : 'Datos de exploración física no registrados — incluya obligatoriamente en el dictamen: "Datos de exploración física no registrados — es necesario completarlos antes de emitir el dictamen pericial."'}

3. CIE-10 Y TIEMPO DE BAJA:
- Extraiga el CIE-10 principal y, si aplica, el secundario, con base en el diagnóstico del análisis del informe.
- Incluya la sección "Estimación de Baja Laboral" COMPLETA con el tiempo en días segmentado por tipo de actividad:
  • Trabajo sedentario / administrativo: [X]–[Y] días
  • Trabajo de pie / moderado: [X]–[Y] días
  • Trabajo físico pesado / manual: [X]–[Y] días`
      : `\nSPECIFIC INSTRUCTIONS FOR FORENSIC MEDICAL OPINION (INSS):

IMPORTANT: This document must be generated COMPLETE, without truncating any section. Write all sections through to the end before returning the JSON. Do not abbreviate, do not omit content, do not use "[...]" or "content omitted".

Mandatory sections:
(1) Patient identification — name, age, profession/occupation;
(2) Main diagnosis with primary and secondary ICD-10 codes;
(3) Physical examination findings;
(4) Functional impact and limitations;
(5) Occupational impact considering the patient's specific profession;
(6) Leave duration estimate by activity type;
(7) Prognosis and therapeutic plan;
(8) Current work capacity;
(9) Medical forensic conclusion.

Write each section completely and in detail before moving to the next.

1. PROFESSION / OCCUPATIONAL IMPACT:
${profession
  ? `The patient's profession/occupation is: "${profession}". Consider this profession when assessing functional impact and estimating leave duration. Describe how the identified limitations specifically affect the physical and cognitive demands of this activity.`
  : 'Profession not provided — note in the opinion that this information is required for an accurate leave estimate.'}

2. PHYSICAL EXAMINATION (MANDATORY IN THE OPINION):
${physicalExam
  ? `Physical examination data recorded in the chart:\n${physicalExam}\nIncorporate these findings into the physical examination section of the opinion (Lasègue sign, range of motion, muscle strength, patellar/Achilles reflexes, sensitivity). If any item is not described, state "not recorded" for that specific item.`
  : 'Physical examination data not recorded in the chart — include the following mandatory note: "Physical examination data not recorded — must be completed before issuing a forensic medical opinion."'}

3. ICD-10 AND LEAVE DURATION:
- Extract the primary ICD-10 code and, if applicable, a secondary code, based on the diagnosis from the report analysis.
- Include the COMPLETE "Leave Duration Estimate" section with time in days segmented by occupation type:
  • Sedentary / office work: [X]–[Y] days
  • Standing / moderate work: [X]–[Y] days
  • Heavy physical / manual work: [X]–[Y] days`
    : '';

  const prompt = (lang === "pt"
    ? `Com base EXCLUSIVAMENTE na análise do laudo abaixo, redija um documento do tipo "${docConfig.promptLabel}".

REGRAS:
- Não invente dados, diagnósticos, exames ou condutas que não estejam apoiados na análise fornecida.
- Use o parecer clínico do médico somente como complemento, sem contrariar a análise do laudo.
- Use os dados do paciente selecionado apenas para identificar e contextualizar o documento.
- Mantenha linguagem ${docConfig.tone}.
- Estruture o texto com títulos e listas legíveis.
- Inclua os elementos essenciais para este tipo de documento:
${docConfig.structure}
- Se houver incerteza na análise, preserve essa nuance no texto.
- Use apenas Markdown. Não use tags HTML como <h1>, <h2>, <ul>, <li>, <br>, <b> ou <strong>.
${inssExtrasBlock}
ANÁLISE BASE:
${JSON.stringify(analysisPayload, null, 2)}

PACIENTE VINCULADO:
${JSON.stringify(patientPayload, null, 2)}${physicianOpinionBlock}

Retorne APENAS um JSON válido com esta estrutura:
{
  "title": "Título do documento",
  "content": "Conteúdo completo em Markdown"
}`
    : lang === 'es'
      ? `Con base EXCLUSIVAMENTE en el análisis del informe que aparece abajo, redacte un documento del tipo "${docConfig.promptLabel}".

REGLAS:
- No invente datos, diagnósticos, estudios ni conductas que no estén respaldados por el análisis proporcionado.
- Use el dictamen clínico del médico solo como complemento, sin contradecir el análisis del informe.
- Use los datos del paciente seleccionado solo para identificar y contextualizar el documento.
- Mantenga un lenguaje ${docConfig.tone}.
- Estructure el texto con títulos y listas legibles.
- Incluya los elementos esenciales para este tipo de documento:
${docConfig.structure}
- Si hay incertidumbre en el análisis, preserve ese matiz en el texto.
- Use solo Markdown. No incluya etiquetas HTML como <h1>, <h2>, <ul>, <li>, <br>, <b> o <strong>.
${inssExtrasBlock}
ANÁLISIS BASE:
${JSON.stringify(analysisPayload, null, 2)}

PACIENTE VINCULADO:
${JSON.stringify(patientPayload, null, 2)}${physicianOpinionBlock}

Devuelva SOLO un JSON válido con esta estructura:
{
  "title": "Título del documento",
  "content": "Contenido completo en Markdown"
}`
      : `Using ONLY the report analysis below, draft a document of type "${docConfig.promptLabel}".

RULES:
- Do not invent data, diagnoses, exams, or management steps that are not supported by the provided analysis.
- Use the physician's clinical opinion only as a complement, without contradicting the report analysis.
- Use the selected patient data only to identify and contextualize the document.
- Keep the language ${docConfig.tone}.
- Structure the text with readable headings and lists.
- Include the essential elements for this document type:
${docConfig.structure}
- If the analysis contains uncertainty, preserve that nuance in the text.
- Use Markdown only. Do not emit HTML tags such as <h1>, <h2>, <ul>, <li>, <br>, <b>, or <strong>.
${inssExtrasBlock}
BASE ANALYSIS:
${JSON.stringify(analysisPayload, null, 2)}

LINKED PATIENT:
${JSON.stringify(patientPayload, null, 2)}${physicianOpinionBlock}

Return ONLY valid JSON with this structure:
{
  "title": "Document title",
  "content": "Full content in Markdown"
}`) + `\n\n${getClinicalResponseInstruction(lang)}\n${getSelectedLanguageInstruction(lang)}`;

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-3-pro-preview',
      contents: prompt,
      config: {
        temperature: 0.15,
        maxOutputTokens: isINSS ? 4096 : 2048,
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            title: { type: Type.STRING },
            content: { type: Type.STRING }
          },
          required: ["title", "content"]
        },
      },
    });

    const parsed = JSON.parse(cleanJson(response.text || "{}"));
    return {
      title: normalizeDocumentText(parsed.title || ''),
      content: normalizeMedicalReportDocumentContent(parsed.content || ''),
    };
  } catch (error) {
    console.error("Medical report document generation error:", error);
    throw error;
  }
};

export const highlightLesionInImage = async (
  imagesBase64: string | string[],
  lang: Language = 'pt',
  anatomyContext?: string,
  style: '2D' | '3D' = '3D',
  mode: AtlasGenerationMode = 'detailed',
  analysisOverride?: AtlasImageAnalysis
) => {
  const ai = createGeminiClient("generateAtlasImage");
  const startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
  
  // Normalize to array
  const images = Array.isArray(imagesBase64) ? imagesBase64 : [imagesBase64];
  const visualMode = getAtlasVisualModePrompt(style, mode);
  const preparedImages: Array<AtlasPreparedImage | null> = await Promise.all(
    images.map((img) =>
      prepareAtlasImageForFunction(img, {
        maxDimension: 1400,
        quality: 0.88,
        maxFinalBytes: mode === 'fast' ? 1_800_000 : ATLAS_IMAGE_MAX_FINAL_BYTES,
      })
    )
  );
  const primaryPreparedImage = preparedImages[0];
  if (!primaryPreparedImage) {
    throw new Error("Imagem muito grande. Reduza a resolução ou envie outra imagem.");
  }

  // If user provided anatomy context, enforce it strongly
  const userContextPrompt = anatomyContext 
    ? (lang === 'pt' ? `CONTEXTO OBRIGATÃƒâ€œRIO: A regiÃƒÂ£o anatÃƒÂ´mica ÃƒÂ© "${anatomyContext}". Ignore qualquer parecer visual contrÃƒÂ¡rio.` : `MANDATORY CONTEXT: The anatomical region is "${anatomyContext}". Ignore conflicting visual cues.`)
    : "";

  {
    const analysisResponse = analysisOverride || await analyzeAtlasImageFindings(preparedImages.filter(Boolean).map((item) => {
      if (!item) return "";
      return `data:${item.mimeType};base64,${item.base64}`;
    }), lang, anatomyContext, mode);
    const planData = extractFullPlan(analysisResponse.surgicalPlan || "");
    const frozenCase = analysisResponse.structuredFractureCase || freezeStructuredFractureCase(analysisResponse);
    const diagnosis = buildDetailedDiagnosisFallback(
      {
        diagnosis:
          (analysisResponse.diagnosticSummary || analysisResponse.diagnosis || "").trim() ||
          planData.diagnosis ||
          "",
        examRegion: analysisResponse.examRegion,
        laterality: analysisResponse.laterality,
        suspectedBone: analysisResponse.suspectedBone,
        suspectedSubregion: analysisResponse.suspectedSubregion,
        primaryBone: analysisResponse.primaryBone,
        boneSegment: analysisResponse.boneSegment,
        fractureLineDescription: analysisResponse.fractureLineDescription,
        imagingEvidence: analysisResponse.imagingEvidence,
        associatedFindings: analysisResponse.associatedFindings,
        hasVisibleFracture: analysisResponse.hasVisibleFracture,
        fractureConfidence: analysisResponse.fractureConfidence,
        fractureVisibility: analysisResponse.fractureVisibility,
        fracturePattern: analysisResponse.fracturePattern,
        corticalDisruption: analysisResponse.corticalDisruption,
        trabecularDisruption: analysisResponse.trabecularDisruption,
        displacementLevel: analysisResponse.displacementLevel,
        articularExtension: analysisResponse.articularExtension,
        alignmentPreserved: analysisResponse.alignmentPreserved,
        associatedInstability: analysisResponse.associatedInstability,
        diagnosticCertaintyText: analysisResponse.diagnosticCertaintyText,
        diagnosticUncertainty: analysisResponse.diagnosticUncertainty,
        recommendedConduct: analysisResponse.recommendedConduct,
        conductRationale: analysisResponse.conductRationale,
        anatomicalRegion: analysisResponse.anatomicalRegion,
        boneInvolved: analysisResponse.boneInvolved,
        hasFracture: analysisResponse.hasFracture,
        fractureType: analysisResponse.fractureType,
        fractureLocation: analysisResponse.fractureLocation,
        displacement: analysisResponse.displacement,
        articularInvolvement: analysisResponse.articularInvolvement,
        comminution: analysisResponse.comminution,
        alignmentStatus: analysisResponse.alignmentStatus,
        instabilitySigns: analysisResponse.instabilitySigns,
        severityLevel: analysisResponse.severityLevel,
        treatmentRecommendation: analysisResponse.treatmentRecommendation,
        treatmentRationale: analysisResponse.treatmentRationale,
        uncertaintyNote: analysisResponse.uncertaintyNote,
        atlasFindings: analysisResponse.atlasFindings,
        surgicalPlan: analysisResponse.surgicalPlan || "",
      },
      lang
    );
    const surgicalSections = extractAtlasSurgicalPlanSections({
      ...analysisResponse,
      diagnosis,
      surgicalPlan: analysisResponse.surgicalPlan || "",
    });
    const description = buildAtlasSurgicalPlanText(surgicalSections) || (analysisResponse.surgicalPlan || "");

    const effectiveStyle: '2D' | '3D' = style;
    const effectiveMode: AtlasGenerationMode = mode;
    const outputImageSize = effectiveMode === 'fast' ? "1K" : "2K";
    const anatomicalRegion =
      analysisResponse.examRegion ||
      analysisResponse.anatomicalRegion ||
      analysisResponse.atlasFindings?.anatomyRegion ||
      anatomyContext ||
      "Bone";
    const fractureLocation =
      analysisResponse.fractureLocation ||
      analysisResponse.boneSegment ||
      analysisResponse.suspectedSubregion ||
      analysisResponse.atlasFindings?.alignmentNotes ||
      analysisResponse.anatomicalAnchors ||
      "";
    const anatomicalAnchors =
      analysisResponse.anatomicalAnchors ||
      analysisResponse.atlasFindings?.alignmentNotes ||
      "";
  const finalVisualPrompt = buildStrictAtlasVisualPrompt(analysisResponse, lang, effectiveMode);
  const fractureSeverityLevel = getAtlasFractureSeverityLevel(analysisResponse);
  const renderPrompt = effectiveStyle === '2D'
      ? `
${getAtlasFinalRenderPrompt(fractureLocation || anatomicalRegion, effectiveStyle, effectiveMode, {
  anchors: anatomicalAnchors,
  visualPrompt: finalVisualPrompt,
})}

${visualMode.visualInstructions}

ATLAS 2D PRIORITIES:
- Preserve the original anatomy, axis, laterality, and bone proportions exactly.
- Use the structured fracture facts as the source of truth:
  - anatomicalRegion: ${analysisResponse.examRegion || analysisResponse.anatomicalRegion || ""}
  - boneInvolved: ${analysisResponse.suspectedBone || analysisResponse.boneInvolved || ""}
  - hasVisibleFracture: ${String(analysisResponse.hasVisibleFracture)}
  - fractureConfidence: ${analysisResponse.fractureConfidence}
  - fracturePattern: ${analysisResponse.fracturePattern || analysisResponse.fractureType}
  - fractureLocation: ${analysisResponse.fractureLocation}
  - displacementLevel: ${analysisResponse.displacementLevel || analysisResponse.displacement}
  - corticalDisruption: ${String(analysisResponse.corticalDisruption)}
  - trabecularDisruption: ${String(analysisResponse.trabecularDisruption)}
  - articularExtension: ${String(analysisResponse.articularExtension)}
  - alignmentPreserved: ${String(analysisResponse.alignmentPreserved)}
  - associatedInstability: ${analysisResponse.associatedInstability}
  - recommendedConduct: ${analysisResponse.recommendedConduct}
  - alignmentStatus: ${analysisResponse.alignmentStatus}
- If confidence is baixa, keep the reconstruction neutral and discreet; do not exaggerate displacement or fragment separation.
- Base the fracture morphology strictly on the imaging findings. Do not exaggerate severity.
- For this case, the fracture severity is: ${fractureSeverityLevel}. Render the fracture with proportional visual intensity.
- Do not increase the number of fragments beyond what is visible.
- Do not create comminution unless clearly present.
- Do not exaggerate displacement.
- Preserve cortical continuity when partially intact.
- Keep fracture lines subtle if they are subtle in the exam.
- Prefer a clean fracture line, minimal fragmentation, preserved bone geometry, and avoid an explosive/comminuted appearance when the exam does not support it.
- If hasVisibleFracture is false or diagnosticUncertainty is present, do not invent a fracture line; render intact bone or explicit uncertainty instead.
- Keep the output flat, clean, and didactic, with a true medical atlas illustration look.
- Emphasize cortical outline, fracture line, fragment alignment, and hardware if present.
- Avoid volumetric depth, cadaveric tissue rendering, radiographic contrast, or cinematic 3D cues.
- Keep surrounding soft tissue minimal and clinically neutral.
- Do not invent new lesions, and do not make the result look like a radiograph.
`
      : `
${getAtlasFinalRenderPrompt(fractureLocation || anatomicalRegion, effectiveStyle, effectiveMode, {
  anchors: anatomicalAnchors,
  visualPrompt: finalVisualPrompt,
})}

${visualMode.visualInstructions}

ATLAS 3D PRIORITIES:
- Preserve the original anatomy, axis, laterality, and bone proportions exactly.
- Use the structured fracture facts as the source of truth:
  - anatomicalRegion: ${analysisResponse.examRegion || analysisResponse.anatomicalRegion || ""}
  - boneInvolved: ${analysisResponse.suspectedBone || analysisResponse.boneInvolved || ""}
  - hasVisibleFracture: ${String(analysisResponse.hasVisibleFracture)}
  - fractureConfidence: ${analysisResponse.fractureConfidence}
  - fracturePattern: ${analysisResponse.fracturePattern || analysisResponse.fractureType}
  - fractureLocation: ${analysisResponse.fractureLocation}
  - displacementLevel: ${analysisResponse.displacementLevel || analysisResponse.displacement}
  - corticalDisruption: ${String(analysisResponse.corticalDisruption)}
  - trabecularDisruption: ${String(analysisResponse.trabecularDisruption)}
  - articularExtension: ${String(analysisResponse.articularExtension)}
  - alignmentPreserved: ${String(analysisResponse.alignmentPreserved)}
  - associatedInstability: ${analysisResponse.associatedInstability}
  - recommendedConduct: ${analysisResponse.recommendedConduct}
  - alignmentStatus: ${analysisResponse.alignmentStatus}
- Preserve laterality and fracture geometry, and keep the reconstruction anatomically coherent and faithful to the structured findings.
- If confidence is baixa, keep the reconstruction conservative, neutral, and minimally dramatized.
- Base the fracture morphology strictly on the imaging findings. Do not exaggerate severity.
- For this case, the fracture severity is: ${fractureSeverityLevel}. Render the fracture with proportional visual intensity.
- Do not increase the number of fragments beyond what is visible.
- Do not create comminution unless clearly present.
- Do not exaggerate displacement.
- Preserve cortical continuity when partially intact.
- Keep fracture lines subtle if they are subtle in the exam.
- Prefer a clean fracture line, minimal fragmentation, preserved bone geometry, and avoid an explosive/comminuted appearance when the exam does not support it.
`;

    const lesionLocalization = estimateFast2DLesionLocalization(analysisResponse, anatomyContext);

    const lesionBoundingBox = (() => {
      if (!lesionLocalization) return null;
      const xmin = Math.max(0, Math.min(1000, Number(lesionLocalization.xmin ?? 0)));
      const ymin = Math.max(0, Math.min(1000, Number(lesionLocalization.ymin ?? 0)));
      const xmax = Math.max(0, Math.min(1000, Number(lesionLocalization.xmax ?? 0)));
      const ymax = Math.max(0, Math.min(1000, Number(lesionLocalization.ymax ?? 0)));
      const confidence = Math.max(0, Math.min(1, Number(lesionLocalization.confidence ?? 0)));
      if (xmax <= xmin || ymax <= ymin || confidence < 0.35) return null;
      return {
        x: (xmin / 1000) * 100,
        y: (ymin / 1000) * 100,
        width: ((xmax - xmin) / 1000) * 100,
        height: ((ymax - ymin) / 1000) * 100,
        confidence,
      };
    })();

    console.log("[ATLAS][generateAtlasImage][client-start]", {
      style: effectiveStyle,
      mode: effectiveMode,
      analysisMode: effectiveMode,
      model: "gemini-3-pro-image-preview",
      imageSize: outputImageSize,
      outputResolution: outputImageSize,
      payloadBase64Length: primaryPreparedImage.base64.length,
      originalSizeBytes: primaryPreparedImage.originalSizeBytes,
      finalSizeBytes: primaryPreparedImage.finalSizeBytes,
      usedCompression: primaryPreparedImage.usedCompression,
      promptLength: renderPrompt.length,
      imageCount: preparedImages.filter(Boolean).length,
    });

    let imageData: string | null = null;
    try {
      imageData = await fetchWithRetry(async () => {
      try {
        const prompt = `${renderPrompt}

INPUT IMAGE AUTHORITY:
- The provided image is the geometric blueprint.
- The fracture location in the generated image must match the input exam exactly.
- Do not shift, beautify, normalize, or heal the lesion.
- Preserve laterality, bone axis, and fracture orientation exactly as seen in the exam.`;

        const analysisSnapshot = analysisResponse as Record<string, any>;

        const payload = {
          route: "generateAtlasImage",
          model: "gemini-3-pro-image-preview",
          style: effectiveStyle,
          analysisMode: effectiveMode,
          prompt,
          analysisText: description || diagnosis || analysisResponse.surgicalPlan || "",
          resultText: description || diagnosis || analysisResponse.surgicalPlan || "",
          analysis: {
            diagnosis: analysisSnapshot.diagnosis,
            anatomicalRegion,
            lesionType: analysisSnapshot.lesionType || analysisSnapshot.fractureType || analysisSnapshot.diagnosis || "",
            lesionLocation: analysisSnapshot.lesionLocation || analysisSnapshot.fractureLocation || analysisSnapshot.boneSegment || "",
            fractureLocation,
            recommendedConduct: analysisSnapshot.recommendedConduct,
            confidence: analysisSnapshot.confidence,
            hasVisibleFracture: analysisSnapshot.hasVisibleFracture,
          },
          imageBase64: primaryPreparedImage.base64,
          mimeType: primaryPreparedImage.mimeType,
          imageSize: `${primaryPreparedImage.width}x${primaryPreparedImage.height}`,
          outputResolution: outputImageSize,
          aspectRatio: "1:1",
          originalSizeBytes: primaryPreparedImage.originalSizeBytes,
          finalSizeBytes: primaryPreparedImage.finalSizeBytes,
        };

        console.log("[ATLAS][ENV_IMAGE_PAYLOAD]", {
          env: import.meta.env.MODE,
          host: window.location.host,
          route: payload.route,
          model: payload.model,
          mimeType: payload.mimeType,
          base64Length: payload.imageBase64.length,
          estimatedBytes: Math.floor((payload.imageBase64.length || 0) * 0.75),
          imageSize: payload.imageSize,
          wasCompressed: primaryPreparedImage.usedCompression,
        });

        console.log("[ATLAS][3D][request:start]", {
          hasInputImage: Boolean(primaryPreparedImage.base64),
          promptLength: prompt.length,
          model: payload.model,
        });
        console.info("[Atlas] generateAtlasImage payload", {
          route: payload.route,
          model: payload.model,
          mimeType: payload.mimeType,
          base64Length: payload.imageBase64.length,
          estimatedBytes: primaryPreparedImage.finalSizeBytes,
          imageSize: payload.imageSize,
          aspectRatio: payload.aspectRatio,
          outputResolution: outputImageSize,
          analysisMode: effectiveMode,
        });

        const response = await ai.models.generateContent({
          ...payload,
          model: 'gemini-3-pro-image-preview',
          config: { imageConfig: { aspectRatio: "1:1", imageSize: outputImageSize } }
        });

        console.log("[ATLAS][3D][response]", {
          hasImage: Boolean(response?.candidates?.[0]?.content?.parts?.some((part: any) => Boolean(part?.inlineData?.data))),
          keys: Object.keys(response || {}),
        });

        for (const part of response.candidates?.[0]?.content?.parts || []) {
          if (part.inlineData) return part.inlineData.data;
        }
        const emptyResponseError = new Error("A IA nÃ£o retornou imagem para o Atlas.");
        (emptyResponseError as any).code = "ATLAS_IMAGE_EMPTY_RESPONSE";
        throw emptyResponseError;
      } catch (e) {
        console.error("[ATLAS][generateAtlasImage][client-error]", e);
        if (e instanceof Error) {
          throw e;
        }
        const wrappedError = new Error(String(e || "Falha ao gerar imagem do Atlas."));
        (wrappedError as any).code = "ATLAS_AI_GENERIC";
        throw wrappedError;
      }
      }, 0, 2000, 300000);
    } catch (e) {
      console.error("[ATLAS][generateAtlasImage][primary-failure]", {
        mode: effectiveMode,
        style: effectiveStyle,
        error: e,
      });
      if (effectiveStyle !== '2D') {
        throw e;
      }
    }

    if (!imageData && effectiveStyle === '2D') {
      console.warn("[ATLAS][generateAtlasImage][2D-fallback:start]", {
        mode: effectiveMode,
        promptLength: renderPrompt.length,
        imageSize: outputImageSize,
        base64Length: primaryPreparedImage.base64.length,
      });

      const fallbackResult = await generateAtlasImageFromAnalysis(
        `data:${primaryPreparedImage.mimeType};base64,${primaryPreparedImage.base64}`,
        analysisResponse,
        anatomyContext,
        undefined,
        undefined,
        effectiveMode
      );

      imageData = fallbackResult || null;

      console.warn("[ATLAS][generateAtlasImage][2D-fallback:end]", {
        mode: effectiveMode,
        hasImageData: Boolean(imageData),
      });
    }

    console.log("[ATLAS][generateAtlasImage][client-end]", {
      style: effectiveStyle,
      mode: effectiveMode,
      durationMs: Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - startedAt),
      hasImageData: Boolean(imageData),
      hasBoundingBox: Boolean(lesionBoundingBox),
    });

    return {
      imageData,
      description: description || diagnosis || analysisResponse.surgicalPlan,
      anatomicalRegion,
      anatomicalAnchors,
      fractureLocation,
      suspectedLesionRegion:
        lesionLocalization?.imagingEvidenceRegion ||
        analysisResponse.fractureLocation ||
        analysisResponse.boneSegment ||
        analysisResponse.suspectedSubregion ||
        "",
      imagingEvidenceRegion: lesionLocalization?.imagingEvidenceRegion || "",
      lesionBoundingBox,
      visualPrompt: finalVisualPrompt,
      differentialDiagnosis: analysisResponse.differentialDiagnosis,
      diagnosis: diagnosis || analysisResponse.diagnosis,
      materials: analysisResponse.materials,
      technique: analysisResponse.technique,
      tips: analysisResponse.tips,
      planB: analysisResponse.planB,
      clinicalOutput: analysisResponse.clinicalOutput,
      surgicalDetails: analysisResponse.surgicalDetails,
      analysis: analysisResponse,
    };
  }

  const analysisPrompt = `
You are a medical imaging expert specialized in orthopedic trauma.

Analyze the provided image(s) and return a structured JSON with:

- anatomicalRegion: exact anatomical structure involved
- fractureType: precise classification if possible
- diagnosis: explicit diagnosis string
- materials: explicit materials & OPME string
- technique: explicit surgical technique string
- tips: explicit tips & pitfalls string
- planB: explicit contingency plan string
- surgicalPlan: high-level orthopedic approach
- differentialDiagnosis: array with 3 to 5 clinically plausible alternatives from the same anatomical region
- anatomicalAnchors: spatial references describing EXACT lesion position
- visualPrompt: a highly descriptive VISUAL depiction of the injury

Keep surgicalPlan as a compatibility fallback using tags like [DIAGNOSIS], [MATERIALS], [TECHNIQUE], [TIPS], [PLAN_B].

CRITICAL INSTRUCTIONS:

1. The lesion MUST be described visually, not just diagnostically.

2. Describe EXACT DAMAGE MORPHOLOGY:
- jagged fracture edges
- bone displacement
- comminution fragments (if present)
- exposed trabecular structure
- cortical disruption

3. FRACTURE ANCHORING RULE:
- If a fracture or cortical break is visible, the exact bone and exact segment are the source of truth.
- Prioritize the real anatomical subsite over any general diagnosis label.
- Do not move the lesion to a neighboring bone, adjacent joint, or soft-tissue structure.
- Keep fractureLocation and anatomicalAnchors as the most precise visible bony location.
- If the fracture is subtle, be cautious, but do not generalize the bone site away from the true location.

4. DO NOT hallucinate anatomy you are not confident about.

5. Focus on what is VISUALLY PLAUSIBLE from the source exam.

Return ONLY valid JSON.
`;

  try {
    const analysisResponse = await fetchWithRetry(async () => {
      // Build parts array with all images
      const parts: any[] = preparedImages
        .filter(Boolean)
        .map((img) => {
          const dataUrl = `data:${img!.mimeType};base64,${img!.base64}`;
          return {
            inlineData: {
              data: stripDataUrlPrefix(dataUrl),
              mimeType: img!.mimeType,
            }
          };
        });
      
      const localizedAnalysisPrompt = `${analysisPrompt}\n\n${getClinicalResponseInstruction(lang)}\n${getSelectedLanguageInstruction(lang)}`;

      // Add text prompt at the end
      parts.push({ text: localizedAnalysisPrompt });

      const res = await ai.models.generateContent({
        model: 'gemini-3-pro-preview',
        contents: { parts },
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              anatomicalRegion: { type: Type.STRING },
              diagnosis: { type: Type.STRING },
              materials: { type: Type.STRING },
              technique: { type: Type.STRING },
              tips: { type: Type.STRING },
              planB: { type: Type.STRING },
              surgicalPlan: { type: Type.STRING },
              differentialDiagnosis: { type: Type.ARRAY, items: { type: Type.STRING } },
              visualPrompt: { type: Type.STRING },
              anatomicalAnchors: { type: Type.STRING }
            },
            required: [
              "anatomicalRegion",
              "diagnosis",
              "materials",
              "technique",
              "tips",
              "planB",
              "surgicalPlan",
              "visualPrompt",
              "anatomicalAnchors"
            ]
          }
        }
      });
      const parsed = JSON.parse(cleanJson(res.text || "{}"));
      const planData = extractFullPlan(parsed.surgicalPlan || "");
      const diagnosis = buildDetailedDiagnosisFallback(
        {
          diagnosis:
            (parsed.diagnosis || "").trim() ||
            planData.diagnosis ||
          "",
          anatomicalRegion: parsed.anatomicalRegion,
          primaryBone: parsed.primaryBone,
          boneSegment: parsed.boneSegment,
          fractureLineDescription: parsed.fractureLineDescription,
          imagingEvidence: parsed.imagingEvidence,
          associatedFindings: parsed.associatedFindings,
          surgicalPlan: parsed.surgicalPlan || "",
        },
        lang
      );
      const surgicalSections = extractAtlasSurgicalPlanSections({
        ...parsed,
        diagnosis,
        surgicalPlan: parsed.surgicalPlan || "",
      });
      const description = buildAtlasSurgicalPlanText(surgicalSections) || (parsed.surgicalPlan || "");

      console.log("[ATLAS][TEXT] parsed =", parsed);
      console.log("[ATLAS][TEXT] surgicalPlan =", parsed.surgicalPlan);
      console.log("[ATLAS][TEXT] planData =", planData);
      console.log("[ATLAS][TEXT] final diagnosis =", diagnosis);
      console.log("[ATLAS][TEXT] final surgicalDetails =", {
        materials: surgicalSections.materials || planData.materials,
        technique: surgicalSections.technique || planData.technique,
        tips: surgicalSections.tips || planData.tips,
        planB: surgicalSections.planB || planData.planB,
      });
      console.log("[ATLAS][TEXT][render] descriptionReady:", Boolean(description));

      return {
        ...parsed,
        diagnosis,
        differentialDiagnosis: surgicalSections.differentialDiagnosis,
        materials: surgicalSections.materials || planData.materials,
        technique: surgicalSections.technique || planData.technique,
        tips: surgicalSections.tips || planData.tips,
        planB: surgicalSections.planB || planData.planB,
        clinicalOutput: {
          diagnosis,
          differentialDiagnosis: surgicalSections.differentialDiagnosis,
          materials: surgicalSections.materials || planData.materials,
          technique: surgicalSections.technique || planData.technique,
          tips: surgicalSections.tips || planData.tips,
          planB: surgicalSections.planB || planData.planB,
        },
        surgicalDetails: {
          materials: surgicalSections.materials || planData.materials,
          technique: surgicalSections.technique || planData.technique,
          tips: surgicalSections.tips || planData.tips,
          planB: surgicalSections.planB || planData.planB,
        },
        description,
      };
    });

  const { surgicalPlan, visualPrompt, anatomicalAnchors, anatomicalRegion, differentialDiagnosis, description } = analysisResponse;
    
  // For visual generation (Hyper-Realistic), we only use the FIRST image as the geometric reference
  const primaryPreparedImage3D = preparedImages[0] as AtlasPreparedImage;
  if (!primaryPreparedImage3D) {
    throw new Error("Imagem muito grande. Reduza a resolução ou envie outra imagem.");
  }
  const mimeType = primaryPreparedImage3D.mimeType;
  const primaryImageClean = primaryPreparedImage3D.base64;

  const regionContext = anatomyContext || anatomicalRegion || "Bone";
  const effectiveStyle: '2D' | '3D' = style;
  const effectiveMode: AtlasGenerationMode = mode;
  const visualMode = getAtlasVisualModePrompt(effectiveStyle, effectiveMode);
  const lesionCategory = classifyAtlasLesionCategory(analysisResponse);
  const lesionPromptContext = buildAtlasLesionPromptContext(analysisResponse);
  const finalVisualPrompt = lesionCategory === "bone_fracture"
    ? (visualPrompt || "Preserve the original exam anatomy and any confirmed abnormality without inventing a new fracture.")
    : buildAtlasSoftTissueVisualPrompt(analysisResponse, lang, effectiveMode, lesionPromptContext);

const renderPrompt3D = lesionCategory === "bone_fracture" ? `
You are an expert musculoskeletal radiologist and orthopedic trauma surgeon.

Your task is to create a medical 3D visualization that reproduces the EXACT fracture described from the original radiograph.

CRITICAL RULES:
- Do NOT create a generic fracture.
- Do NOT move the fracture to another bone region.
- Do NOT simplify the fracture pattern.
- Preserve the exact anatomical location.
- The fracture must match the radiographic finding.
- If the diagnosis says olecranon / proximal ulna, the fracture must be on the olecranon / proximal ulna only.
- Do not place the main fracture in the distal humerus, radial head, or a generic elbow joint.
- Highlight the fracture line clearly on the affected bone.

Input diagnosis: ${analysisResponse.diagnosis || description || ""}

Anatomical region: ${analysisResponse.examRegion || analysisResponse.anatomicalRegion || anatomicalRegion || ""}

Fracture location: ${analysisResponse.fractureLocation || analysisResponse.boneSegment || anatomicalRegion || ""}

Fracture pattern: ${analysisResponse.fracturePattern || analysisResponse.fractureType || ""}

Displacement: ${analysisResponse.displacementLevel || analysisResponse.displacement || ""}

The provided image is the geometric blueprint.
Use the original X-ray as the source of truth for bone positioning and alignment.
Preserve exact anatomical geometry and bone alignment.
Anatomical accuracy is more important than visual quality.

SPATIAL ANCHOR LOCK (CRITICAL):

- Use anatomicalAnchors as a fixed coordinate system.
- The fracture must be placed in the exact same relative position along the bone length.
- Match fracture angle, displacement direction, and fragment orientation.
- Do not reposition the fracture to the joint if it is not articular in the X-ray.
- Do not centralize or dramatize the location.

If conflict occurs, prioritize anatomicalAnchors over aesthetics.

MANDATORY RULES:

- The fracture location must match the X-ray exactly.
- Do not shift, rotate, or reinterpret anatomy.
- Do not heal, smooth, or beautify the lesion.
- Preserve spatial alignment strictly.
- Do not reposition bones.
- Do not smooth fracture lines.
- Do not reinterpret anatomy.
- Do not enhance beyond the actual structure.

WRIST/CARPAL ANATOMY LOCK (MANDATORY WHEN WRIST/CARPUS IS PRESENT):

- Keep true carpal organization and spacing.
- Scaphoid must align with distal radius.
- Lunate must remain central in the proximal row.
- Capitate must align with the longitudinal axis of the third metacarpal.
- Preserve coherent proximal and distal carpal rows without overlap or fusion.
- Preserve realistic radius/ulna proportion and natural alignment with the carpus.
- Do not introduce artificial rotation, collapse, or non-evidenced displacement.

VISUAL OBJECTIVE:

Render a clean medical-grade visualization of the injury.

INCLUDE:

- raw exposed bone at fracture site
- jagged irregular edges
- visible fracture gaps or compression
- micro-fragments if applicable
- cortical break with depth
- subtle blood infiltration (non-exaggerated)
- stretched or torn periosteum (if applicable)

SOFT TISSUE REALISM (HIGH PRIORITY):

- Keep soft tissues secondary and clinically discreet:

  MUSCLE:
  - subtle, low-prominence volume only
  - no exaggerated fiber display

  TENDONS:
  - subtle and anatomically coherent paths only
  - never dominant in the frame

  LIGAMENTS:
  - minimally visible unless essential
  - do not cross bone incorrectly

  NERVES:
  - discrete, anatomically plausible trajectories only
  - subtle visibility without crowding the osseous field

  JOINT STRUCTURES:
  - cartilage
  - capsule
  - synovial space (if applicable)

- Maintain realistic layering:
  bone â†’ periosteum â†’ muscle â†’ fascia

- Soft tissue must:
  - look biological (NOT plastic)
  - follow anatomy strictly
  - never intersect bone incorrectly

- Add subtle trauma response:
  - localized swelling
  - tissue tension around fracture
  - minimal blood (controlled, not exaggerated)

- Do not let soft tissues dominate the image or obscure osseous alignment.
- Keep soft tissue complexity minimal.

TEXTURE & MATERIAL:

- wet organic bone texture
- realistic subsurface scattering
- fine trabecular detail
- non-plastic surface (NO CGI look)

MATERIAL DIFFERENTIATION:

- Bone: rigid, porous, mineral texture
- Muscle: soft, fibrous, volumetric
- Tendon/Ligament: dense linear fibers with tension
- Cartilage: smooth, slightly translucent

LIGHTING:

- neutral clinical lighting
- soft non-dramatic light
- realistic medical contrast without stylization
- plain clean background

FOCUS:

- ultra sharp on fracture zone
- background fully clean and dark

TONE:

The injury must look:
- clinically faithful
- structurally coherent
- biologically real

STYLE:

- high-fidelity medical reconstruction
- NOT illustration
- NOT stylized
- NOT cinematic art

ANTI-ATLAS RULE:

- Do NOT generate clean anatomical atlas models
- Do NOT simplify anatomy
- Do NOT exaggerate or dramatize soft tissue structures
- Avoid cadaveric/dissection-like muscle exposure
- Anatomical accuracy is more important than visual quality.

ANATOMICAL ANCHORS:
${anatomicalAnchors}

VISUAL GUIDANCE:
${finalVisualPrompt}
` : `${buildAtlasSoftTissueVisualPrompt(analysisResponse, lang, effectiveMode, lesionPromptContext)}

${visualMode.visualInstructions}`;

    const renderPrompt = effectiveStyle === '2D'
      ? `
${getAtlasFinalRenderPrompt(finalVisualPrompt, effectiveStyle, effectiveMode, {
  anchors: anatomicalAnchors,
  visualPrompt: finalVisualPrompt,
})}

${visualMode.visualInstructions}

ATLAS 2D PRIORITIES:
- Preserve the original anatomy, axis, laterality, and bone proportions exactly.
- Keep the output flat, clean, and didactic, with a true medical atlas illustration look.
- Emphasize cortical outline, fracture line, fragment alignment, and hardware if present.
- Avoid volumetric depth, cadaveric tissue rendering, radiographic contrast, or cinematic 3D cues.
- Keep surrounding soft tissue minimal and clinically neutral.
- Do not invent new lesions, and do not make the result look like a radiograph.
${userContextPrompt ? `\n${userContextPrompt}\n` : ""}
`
      : renderPrompt3D;

    // STEP 2: Final reconstruction aligned with the selected Atlas mode
    const imageData = await fetchWithRetry(async () => {
      try {
        const response = await ai.models.generateContent({
          model: 'gemini-3-pro-image-preview',
          contents: {
            parts: [
              { inlineData: { data: primaryImageClean, mimeType: mimeType } },
              { text: `${renderPrompt}

INPUT IMAGE AUTHORITY:
- The provided image is the geometric blueprint.
- The fracture location in the generated image must match the input exam exactly.
- Do not shift, beautify, normalize, or heal the lesion.
- Preserve laterality, bone axis, and fracture orientation exactly as seen in the exam.` }
            ],
          },
          config: { imageConfig: { aspectRatio: "1:1", imageSize: "1K" } }
        });
        
        for (const part of response.candidates?.[0]?.content?.parts || []) {
          if (part.inlineData) return part.inlineData.data;
        }
        return null;
      } catch (e) {
        console.warn("Image generation failed", e);
        return null;
      }
    }, 0, 2000, 300000);

    return {
      imageData,
      description: description || surgicalPlan,
      anatomicalRegion,
      anatomicalAnchors,
      visualPrompt: finalVisualPrompt,
      differentialDiagnosis,
      diagnosis: analysisResponse.diagnosis,
      materials: analysisResponse.materials,
      technique: analysisResponse.technique,
      tips: analysisResponse.tips,
      planB: analysisResponse.planB,
      clinicalOutput: analysisResponse.clinicalOutput,
      surgicalDetails: analysisResponse.surgicalDetails,
    };

  } catch (error: any) {
    console.error("Lesion Analysis Error:", error);
    throw error;
  }
};

export const measureAnatomyFromImage = async (imageBase64: string, lang: Language = 'pt') => {
  const ai = createGeminiClient("generateAtlasAnalysis");
  
  const normalizedImage = normalizeAtlasImageInput(imageBase64);
  const preparedImage = await prepareAtlasImageForFunction(normalizedImage.dataUrl, {
    maxDimension: 768,
    quality: 0.72,
    maxFinalBytes: 650_000,
  }).catch((error) => {
    console.warn("[ATLAS][measureAnatomyFromImage][compression-failed]", error);
    return null;
  });
  const mimeType = preparedImage?.mimeType || normalizedImage.mimeType;
  const cleanBase64 = preparedImage?.base64 || normalizedImage.imageBase64;

  const prompt = (lang === 'en'
    ? `You are a Senior Radiologist. Analyze the medical image. Identify the anatomical region. Estimate RELEVANT biomechanical measurements.
       Return strictly numeric values with units (e.g. "12 mm", "45 deg") for the 'value' field.`
    : `VocÃƒÂª ÃƒÂ© um Radiologista SÃƒÂªnior. Analise a imagem mÃƒÂ©dica. Identifique a regiÃƒÂ£o anatÃƒÂ´mica. Estime mediÃƒÂ§ÃƒÂµes biomecÃƒÂ¢nicas RELEVANTES.
       Retorne valores estritamente numÃƒÂ©ricos com unidades (ex: "12 mm", "45 graus") para o campo 'value'.`)
    + `\n\n${getSelectedLanguageInstruction(lang)}`;

  try {
    console.info("[ATLAS][measureAnatomyFromImage][payload]", {
      route: "generateAtlasAnalysis",
      mimeType,
      originalBase64Length: normalizedImage.imageBase64.length,
      finalBase64Length: cleanBase64.length,
      usedCompression: Boolean(preparedImage),
    });

    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: {
        parts: [
          { inlineData: { data: cleanBase64, mimeType: mimeType } },
          { text: prompt }
        ]
      },
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            region: { type: Type.STRING },
            measurements: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  label: { type: Type.STRING },
                  value: { type: Type.STRING },
                  status: { type: Type.STRING }
                },
                required: ["label", "value", "status"]
              }
            }
          },
          required: ["region", "measurements"]
        }
      }
    });
    return JSON.parse(cleanJson(response.text || '{"region": "N/A", "measurements": []}'));
  } catch (error) {
    console.error("[ATLAS][measureAnatomyFromImage][error]", error);
    return { region: "Erro na AnÃƒÂ¡lise", measurements: [] };
  }
};

export type SpineFindings = {
  region: string;
  level: string;
  discCondition: string;
  herniaType: string;
  nerveCompression: boolean;
  severity: "leve" | "moderada" | "grave";
  confidence: number;
  conflictWithPreviousDiagnosis?: boolean;
  correctedDiagnosis?: string;
};

export type SpineSurgicalPlanOutput = {
  diagnosis: string;
  anatomicalLocation: string;
  classification: string;
  severity: string;
  level: string;
  affectedRoot: string;
  differentialDiagnoses: string[];
  complementaryExams: string[];
  treatmentIndication: "surgical" | "relative" | "non-surgical";
  preoperativePlanning: string[];
  surgicalTechniqueOrConservativeProtocol: string[];
  materials: string[];
  intraoperativeSteps: string[];
  risksAndPitfalls: string[];
  planB: string[];
  postoperativeCare: string[];
  redFlags: string[];
  scientificReferences: string[];
  clinicalInterpretation: string;
  treatmentPlan: string;
  surgicalIndication: "surgical" | "relative" | "non-surgical";
  surgicalIndicationReason: string;
  recommendations: string[];
};

const isSpinePlanningQuery = (query: string) => {
  const normalized = normalizeClinicalText(query);
  return /(coluna|spine|vertebr|disco|disc|hernia|h[eé]rnia|lombar|cervical|tor[aá]c|radicul)/i.test(normalized);
};

const normalizeSpineLevel = (value: string) => {
  const normalized = String(value || "").trim().toUpperCase();
  const match = normalized.match(/\b([CTLS]\d{1,2}(?:-[CTLS]\d{1,2})?)\b/);
  return match?.[1] || normalized;
};

type SurgicalPlanStrategy = "conservative" | "surgical" | "uncertain";

export type NormalizedSurgicalPlanResponse = {
  recommendation: SurgicalPlanStrategy;
  selectedStrategy: "conservative" | "surgical" | null;
  mainDiagnosis: string;
  diagnosis: string;
  anatomicalLocation: string;
  severity: string;
  differentialDiagnoses: string[];
  differentialDiagnosis?: string[];
  associatedInjuries?: string[];
  associatedLesions?: string[];
  complementaryExams: string[];
  preoperativePlanning: string[];
  classificationCode?: string;
  classificationLabel?: string;
  classificationDescription?: string;
  classificationSeverity?: "low" | "medium" | "high";
  conservativePlan: {
    indication: string;
    protocol: string[];
    followUp: string[];
    redFlags: string[];
    conversionCriteria: string[];
  };
  surgicalOption: {
    indicationCriteria: string[];
    preferredTechnique: string;
    patientPositioning: string[];
    surgicalApproach: string;
    reductionStrategy: string[];
    fixationStrategy: string[];
    stepByStepTechnique: string[];
    implantsAndMaterials: string[];
    imagingControl: string[];
    intraoperativeChecks: string[];
    closureAndImmobilization: string[];
    postoperativeProtocol: string[];
    risksAndPitfalls: string[];
    contingencyPlan: {
      title: string;
      summary: string;
      scenarios: Array<{
        problem: string;
        action: string;
      }>;
      intraoperativeDecisionPoints: string[];
      conversionOptions: string[];
    };
  };
  contingencyPlan?: string[];
  alternativePlan?: string[];
  planB?: string[];
  postoperativeCare?: string[];
  scientificEvidence?: string[];
  evidence?: string[];
  surgicalTechniques?: Array<{
    title: string;
    isMostCurrent?: boolean;
    isAiRecommended?: boolean;
    confidence?: number;
    recommendationReason?: string;
    indication?: string;
    whenToChoose?: string;
    patientPositioning?: string[];
    surgicalApproach?: string;
    advantages?: string[];
    limitations?: string[];
    materials?: string[];
    stepByStep?: string[];
    stepByStepTechnique?: string[];
    practicalNotes?: string[];
    fluoroscopyOrImagingControl?: string[];
    intraoperativeChecks?: string[];
    closureAndImmobilization?: string[];
    postoperativeProtocol?: string[];
    planB?: string[];
    complications?: string[];
    recoveryTime?: string;
    consolidationTime?: string;
  }>;
  materials?: string[];
  techniqueComparison?: string;
  selectionCriteria?: string;
  mostCurrentTechniqueTitle?: string;
  aiRecommendedTechniqueTitle?: string;
  aiRecommendationRationale?: string;
  alternativeTechniqueRationale?: string;
  scientificReferences: string[];
  clinicalInterpretation: string;
  treatmentPlan: string;
  surgicalIndicationReason: string;
  recommendations: string[];
  level?: string;
  affectedRoot?: string;
  classification?: string;
};

const FALLBACK_SURGICAL_OPTION: NormalizedSurgicalPlanResponse["surgicalOption"] = {
  indicationCriteria: [
    "Dor persistente ou incapacitante apesar do tratamento conservador",
    "Progressão radiográfica ou confirmação de fratura instável",
    "Instabilidade articular, desvio ou risco funcional",
  ],
  preferredTechnique: "Definir técnica após confirmação por TC/RM e avaliação clínica.",
  patientPositioning: ["Conforme segmento anatômico e acesso cirúrgico planejado."],
  surgicalApproach: "Definir conforme localização exata da lesão.",
  reductionStrategy: ["Redução anatômica se houver fratura/desvio confirmado."],
  fixationStrategy: ["Fixação conforme padrão da fratura e estabilidade."],
  stepByStepTechnique: [
    "Confirmar diagnóstico e localização por imagem",
    "Planejar acesso e proteção neurovascular",
    "Realizar redução anatômica quando indicada",
    "Estabilizar conforme padrão da lesão",
    "Confirmar alinhamento e estabilidade",
  ],
  implantsAndMaterials: [],
  imagingControl: ["Radiografias intraoperatórias ou fluoroscopia quando aplicável"],
  intraoperativeChecks: ["Alinhamento", "Estabilidade", "Integridade neurovascular"],
  closureAndImmobilization: ["Conforme técnica e estabilidade obtida."],
  postoperativeProtocol: ["Controle de dor", "Proteção de carga", "Reabilitação progressiva"],
  risksAndPitfalls: ["Rigidez", "Dor persistente", "Não consolidação se houver fratura"],
  contingencyPlan: {
    title: "Plano B / Contingência",
    summary: "Condutas se a estratégia principal não for possível",
    scenarios: [
      {
        problem: "Redução inadequada",
        action: "Converter para fixação alternativa ou abordagem ampliada"
      },
      {
        problem: "Cominuição maior que previsto",
        action: "Ampliar exposição e reconstruir superfície articular"
      },
      {
        problem: "Osteoporose/baixa qualidade óssea",
        action: "Usar implantes bloqueados adicionais ou enxerto ósseo"
      }
    ],
    intraoperativeDecisionPoints: [],
    conversionOptions: []
  },
};

const FALLBACK_CONSERVATIVE_PLAN = {
  indication: "Sem indicação cirúrgica imediata. Reavaliar com correlação clínica e imagem complementar.",
  protocol: [
    "Analgesia e controle de sintomas",
    "Imobilização / proteção conforme segmento",
    "Correlação clínica seriada",
    "Reavaliação por imagem se houver piora",
  ],
  followUp: [
    "Reavaliação clínica programada",
    "Repetir exame se persistirem sintomas",
  ],
  redFlags: [
    "Piora progressiva da dor",
    "Déficit neurológico novo",
    "Instabilidade clínica ou funcional",
  ],
  conversionCriteria: [
    "Falha do tratamento conservador",
    "Progressão do desvio / instabilidade",
    "Piora funcional importante",
  ],
};

const normalizeSurgicalPlanList = (value: unknown, maxItems = 10) =>
  Array.isArray(value)
    ? value.map((item) => String(item || "").replace(/\s+/g, " ").trim()).filter(Boolean).slice(0, maxItems)
    : typeof value === "string"
      ? value
          .split(/\n|•|;|(?<=\.)\s+/g)
          .map((item) => String(item || "").replace(/\s+/g, " ").trim())
          .filter(Boolean)
          .slice(0, maxItems)
      : [];

const normalizeSurgicalPlanText = (value: unknown) =>
  String(value ?? "").replace(/\r\n/g, "\n").replace(/\s+/g, " ").trim();

const pickFirstSurgicalPlanText = (...values: unknown[]) => {
  for (const value of values) {
    const text = normalizeSurgicalPlanText(value);
    if (text) return text;
  }
  return "";
};

const normalizeTechniqueList = (value: unknown) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item: any) => {
      if (typeof item === "string") {
        const title = normalizeSurgicalPlanText(item);
        return title ? {
          title,
          isMostCurrent: false,
          isAiRecommended: false,
          confidence: undefined,
          recommendationReason: "",
          indication: "",
          whenToChoose: "",
          patientPositioning: [],
          surgicalApproach: "",
          advantages: [],
          limitations: [],
          materials: [],
          stepByStep: [],
          stepByStepTechnique: [],
          practicalNotes: [],
          fluoroscopyOrImagingControl: [],
          intraoperativeChecks: [],
          closureAndImmobilization: [],
          postoperativeProtocol: [],
          planB: [],
          consolidationTime: "",
          complications: [],
          recoveryTime: "",
        } : null;
      }
      if (!item || typeof item !== "object") return null;
      const record = item as Record<string, any>;
      const stepByStepTechnique = normalizeSurgicalPlanList(
        record.stepByStepTechnique ||
        record.stepByStep ||
        record.surgicalSteps ||
        record.operativeSteps ||
        record.operativeSequence ||
        record.steps ||
        record.procedureSteps ||
        [],
        12
      );
      const materials = normalizeSurgicalPlanList(
        record.materials ||
        record.implants ||
        record.materialList ||
        record.requiredMaterials ||
        record.surgicalMaterials ||
        record.implantsAndMaterials ||
        [],
        12
      );
      const complications = normalizeSurgicalPlanList(
        record.complications ||
        record.complicationsPossible ||
        record.risksAndPitfalls ||
        record.risks ||
        [],
        10
      );
      return {
        title: normalizeSurgicalPlanText(record.title || record.name || record.label || record.technique || ""),
        isMostCurrent: Boolean(record.isMostCurrent || record.mostCurrent),
        isAiRecommended: Boolean(record.isAiRecommended || record.aiRecommended),
        confidence: Number.isFinite(Number(record.confidence)) ? Number(record.confidence) : undefined,
        recommendationReason: normalizeSurgicalPlanText(record.recommendationReason || record.reasoning || record.rationale || ""),
        indication: normalizeSurgicalPlanText(record.indication || record.indications || ""),
        whenToChoose: normalizeSurgicalPlanText(record.whenToChoose || record.selectionCriteria || record.criteria || ""),
        patientPositioning: normalizeSurgicalPlanList(record.patientPositioning || record.positioning || [], 8),
        surgicalApproach: normalizeSurgicalPlanText(record.surgicalApproach || record.approach || ""),
        advantages: normalizeSurgicalPlanList(record.advantages || record.benefits || [], 8),
        limitations: normalizeSurgicalPlanList(record.limitations || record.risks || [], 8),
        materials,
        stepByStep: stepByStepTechnique,
        stepByStepTechnique,
        practicalNotes: normalizeSurgicalPlanList(
          record.practicalNotes ||
          record.notes ||
          record.postoperativeCare ||
          record.postoperativeProtocol ||
          [],
          12
        ),
        fluoroscopyOrImagingControl: normalizeSurgicalPlanList(record.fluoroscopyOrImagingControl || record.imagingControl || [], 8),
        intraoperativeChecks: normalizeSurgicalPlanList(record.intraoperativeChecks || record.finalChecks || [], 8),
        closureAndImmobilization: normalizeSurgicalPlanList(record.closureAndImmobilization || record.closure || [], 8),
        postoperativeProtocol: normalizeSurgicalPlanList(record.postoperativeProtocol || record.postop || [], 8),
        planB: normalizeSurgicalPlanList(record.planB || record.contingencyPlan || record.fallbackPlan || record.rescueStrategy || [], 8),
        consolidationTime: normalizeSurgicalPlanText(record.consolidationTime || record.healingTime || record.recoveryTime || ""),
        complications,
        recoveryTime: normalizeSurgicalPlanText(record.recoveryTime || record.consolidationTime || record.healingTime || ""),
      };
    })
    .filter((item): item is Exclude<typeof item, null> => item !== null);
};

type AtlasFractureSeverityLevel = "mild" | "moderate" | "severe";

const getAtlasFractureSeverityLevel = (analysis: Partial<AtlasImageAnalysis>) => {
  const pattern = normalizeClinicalText(analysis.fracturePattern || analysis.fractureType || "");
  const displacement = normalizeClinicalText(analysis.displacementLevel || analysis.displacement || "");
  const hasComminution = Boolean(analysis.comminution) || /\b(comminut|fragmentad|multipl|multiple fragments?|fragments?)\b/i.test(pattern);
  const hasArticularExtension = Boolean(analysis.articularExtension || analysis.articularInvolvement);
  const hasCorticalBreak = Boolean(analysis.corticalDisruption || analysis.trabecularDisruption || analysis.hasVisibleFracture);

  if (hasComminution || /m\u00faltipl|multiple/.test(pattern) || displacement === "importante" || /importante|displaced|sever/.test(displacement)) {
    return "severe" as const;
  }

  if (/fin|linear|sutil|sutil|transversal|obliqu|nondisplaced|sem desvio|ausente/.test(pattern) && hasCorticalBreak) {
    return "mild" as const;
  }

  if (displacement === "leve" || /leve|moderad|step-off|gap/.test(displacement) || hasArticularExtension) {
    return "moderate" as const;
  }

  if (hasCorticalBreak) {
    return "moderate" as const;
  }

  return "mild" as const;
};

type AtlasLesionCategory = "bone_fracture" | "bone_contusion" | "soft_tissue_cyst" | "meniscal_lesion" | "ligament_lesion" | "cartilage_lesion" | "tendon_lesion" | "spine_disc" | "unknown";

type AtlasLesionPromptContext = {
  lesionCategory: AtlasLesionCategory;
  affectedStructure: string;
  exactLocation: string;
  relationshipToBone: string;
  relationshipToMeniscusOrLigament: string;
  region: string;
  bone: string;
  side: string;
};

const normalizeAtlasPromptText = (value: unknown) =>
  normalizeSurgicalPlanText(value).replace(/\s+/g, " ").trim();

const classifyAtlasLesionCategory = (analysis: Partial<AtlasImageAnalysis> & {
  lesionType?: string;
  diagnosis?: string;
  diagnosticSummary?: string;
  surgicalPlan?: string;
}): AtlasLesionCategory => {
  const text = [
    analysis.lesionCategory,
    analysis.atlasFindings?.lesionCategory,
    analysis.atlasFindings?.primaryStructureType,
    analysis.primaryStructureType,
    analysis.primaryStructureName,
    analysis.atlasFindings?.primaryStructureName,
    analysis.lesionType,
    analysis.diagnosis,
    analysis.diagnosticSummary,
    analysis.fracturePattern,
    analysis.fractureType,
    analysis.fractureLineDescription,
    analysis.fractureLocation,
    analysis.differentialNotes,
    analysis.uncertaintyNote,
    analysis.diagnosticUncertainty,
    analysis.surgicalPlan,
    ...(analysis.atlasFindings?.visibleFindings || []),
    ...(analysis.atlasFindings?.associatedStructures || []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  const hasSpine = /(spine|coluna|vertebr|disc|disco|hernia|hérnia|radicul)/.test(text);
  const hasCyst = /(cyst|cisto|parameniscal|para meniscal|ganglion|baker)/.test(text);
  const hasMeniscus = /menisc/.test(text);
  const hasLigament = /(ligament|acl|pcl|mcl|lcl|lca|lcp|lcm|sprain|ruptur|tear|entorse)/.test(text);
  const hasCartilage = /(condral|cartilag|chondr|osteochondral|osteocondral)/.test(text);
  const hasTendon = /(tendin|tendon|enthes|entes|tendÃ£o|tendao)/.test(text);
  const hasContusion = /(edema osseo|edema ósseo|bone bruise|bone contusion|contus|marrow edema|subchondral edema|occult fracture|fratura oculta)/.test(text);
  const hasFracture = Boolean(analysis.hasVisibleFracture) || /(fractur|fratura|fissur|avuls|break|cortical break)/.test(text);

  if (hasSpine) return "spine_disc";
  if (hasCyst && hasMeniscus) return "soft_tissue_cyst";
  if (hasMeniscus) return "meniscal_lesion";
  if (hasLigament) return "ligament_lesion";
  if (hasCartilage) return "cartilage_lesion";
  if (hasTendon) return "tendon_lesion";
  if (hasContusion && !hasFracture) return "bone_contusion";
  if (hasFracture) return "bone_fracture";
  if (hasCyst) return "soft_tissue_cyst";
  return "unknown";
};

const buildAtlasLesionPromptContext = (analysis: Partial<AtlasImageAnalysis> & {
  lesionType?: string;
  diagnosis?: string;
  diagnosticSummary?: string;
  surgicalPlan?: string;
}) : AtlasLesionPromptContext => {
  const lesionCategory = classifyAtlasLesionCategory(analysis);
  const region = normalizeAtlasPromptText(analysis.examRegion || analysis.anatomicalRegion || analysis.atlasFindings?.anatomyRegion || "");
  const bone = normalizeAtlasPromptText(analysis.suspectedBone || analysis.primaryBone || analysis.boneInvolved || analysis.atlasFindings?.bone || region || "");
  const side = normalizeAtlasPromptText(analysis.laterality || analysis.atlasFindings?.side || "");
  const primaryStructure = normalizeAtlasPromptText(
    analysis.primaryStructureName ||
      analysis.atlasFindings?.primaryStructureName ||
      analysis.lesionType ||
      analysis.fractureType ||
      ""
  );
  const locationHint = normalizeAtlasPromptText(
    analysis.fractureLocation ||
      analysis.suspectedSubregion ||
      analysis.boneSegment ||
      analysis.atlasFindings?.alignmentNotes ||
      region
  );
  const diagnosisHint = normalizeAtlasPromptText(analysis.diagnosis || analysis.diagnosticSummary || analysis.surgicalPlan || "");
  const kneeContext = /(knee|joelho|menisc|femur|tibia|fibula|patel|acl|pcl|mcl|lcl|plateau tibial|tibial plateau)/i.test(
    [region, bone, primaryStructure, locationHint, diagnosisHint].join(" ")
  );

  if (lesionCategory === "soft_tissue_cyst" || lesionCategory === "meniscal_lesion") {
    return {
      lesionCategory,
      affectedStructure: primaryStructure || "lateral meniscus",
      exactLocation: locationHint || (kneeContext ? "posterolateral knee adjacent to the lateral meniscus" : region),
      relationshipToBone: kneeContext ? "extraosseous, adjacent to the tibial plateau" : "extraosseous, adjacent to the affected joint",
      relationshipToMeniscusOrLigament: "associated with meniscal tear or meniscal margin abnormality",
      region,
      bone,
      side,
    };
  }

  if (lesionCategory === "ligament_lesion") {
    return {
      lesionCategory,
      affectedStructure: primaryStructure || "ACL / PCL / MCL / LCL",
      exactLocation: locationHint || (kneeContext ? "intercondylar or collateral ligament region" : region),
      relationshipToBone: "extraosseous, near the osseous insertions",
      relationshipToMeniscusOrLigament: "ligament-centered lesion; show the torn or thickened ligament as primary finding",
      region,
      bone,
      side,
    };
  }

  if (lesionCategory === "cartilage_lesion") {
    return {
      lesionCategory,
      affectedStructure: primaryStructure || "articular cartilage",
      exactLocation: locationHint || "focal articular surface defect",
      relationshipToBone: "subchondral interface / articular surface",
      relationshipToMeniscusOrLigament: "cartilage-centered lesion; keep ligaments secondary",
      region,
      bone,
      side,
    };
  }

  if (lesionCategory === "tendon_lesion") {
    return {
      lesionCategory,
      affectedStructure: primaryStructure || "tendon",
      exactLocation: locationHint || region,
      relationshipToBone: "enthesis or tendon adjacent to the bone, depending on the described site",
      relationshipToMeniscusOrLigament: "tendon-centered lesion; keep bone secondary",
      region,
      bone,
      side,
    };
  }

  if (lesionCategory === "bone_contusion") {
    return {
      lesionCategory,
      affectedStructure: primaryStructure || "subchondral bone / marrow",
      exactLocation: locationHint || region,
      relationshipToBone: "intraosseous, subchondral bone marrow edema / contusion pattern",
      relationshipToMeniscusOrLigament: "secondary soft tissues only if explicitly described",
      region,
      bone,
      side,
    };
  }

  if (lesionCategory === "spine_disc") {
    return {
      lesionCategory,
      affectedStructure: primaryStructure || "intervertebral disc",
      exactLocation: locationHint || region,
      relationshipToBone: "disc lesion between vertebral bodies; not a fracture",
      relationshipToMeniscusOrLigament: "not applicable unless explicitly described",
      region,
      bone,
      side,
    };
  }

  return {
    lesionCategory,
    affectedStructure: primaryStructure || bone || region || "bone",
    exactLocation: locationHint || region || bone || "",
    relationshipToBone: "osseous lesion / fracture-focused reconstruction",
    relationshipToMeniscusOrLigament: "not applicable unless explicitly described",
    region,
    bone,
    side,
  };
};

const buildAtlasSoftTissueVisualPrompt = (
  analysis: Partial<AtlasImageAnalysis> & { lesionType?: string; diagnosis?: string; diagnosticSummary?: string; surgicalPlan?: string; },
  lang: Language = "pt",
  mode: AtlasGenerationMode = "detailed",
  context?: AtlasLesionPromptContext
) => {
  const lesionContext = context || buildAtlasLesionPromptContext(analysis);
  const kneeHints = [
    lesionContext.region,
    lesionContext.bone,
    lesionContext.exactLocation,
    lesionContext.affectedStructure,
    analysis.diagnosis,
    analysis.diagnosticSummary,
    analysis.lesionType,
    analysis.surgicalPlan,
  ].join(" ");
  const kneeSpecific = /(knee|joelho|menisc|femur|tibia|fibula|plateau tibial|tibial plateau|acl|pcl|mcl|lcl)/i.test(kneeHints);
  const category = lesionContext.lesionCategory;

  const categoryRules: Record<AtlasLesionCategory, string> = {
    soft_tissue_cyst: `- Focus on a soft tissue cyst adjacent to the meniscus or joint line.
- Represent the cyst as a small translucent fluid-filled rounded/lobulated lesion.
- The lesion must be extraosseous and should not become bone-colored or bony deformity.`,
    meniscal_lesion: `- Focus on the meniscus as the primary structure.
- Highlight the tear, cleavage, degeneration, or truncation at the meniscal margin.
- If a parameniscal cyst is described, show the cyst as a translucent extraosseous lesion adjacent to the meniscus.`,
    ligament_lesion: `- Focus on the injured ligament as the primary structure.
- Show a torn, thickened, lax, or disrupted ligament depending on the report.
- Show ACL, PCL, MCL, or LCL only when anatomically relevant.`,
    cartilage_lesion: `- Focus on the articular cartilage and focal chondral defect.
- Show the cartilage surface as the main lesion.
- Show subchondral edema only if explicitly described.`,
    tendon_lesion: `- Focus on the affected tendon and its enthesis.
- Show tendon thickening, partial tear, retraction, or tendinopathy if described.
- Keep the bone secondary unless the report describes an enthesophyte or insertional change.`,
    bone_contusion: `- Focus on subchondral bone marrow edema / contusion.
- Do not create a large cortical break or displaced fracture.
- Preserve cortical continuity unless the exam explicitly shows a fracture line.`,
    spine_disc: `- Focus on the intervertebral disc and nerve root relationship.
- Show disc bulge, protrusion, extrusion, or herniation according to the report.
- Do not create a bone fracture or bony displacement.`,
    bone_fracture: `- This category should not reach the soft tissue prompt; fall back to the fracture prompt if detected.`,
    unknown: `- Preserve the described lesion conservatively.
- Do not invent a fracture or convert a soft-tissue finding into a bone injury.`,
  };

  const intro = lang === "en"
    ? `You are an expert musculoskeletal radiologist and orthopedic surgeon.\n\nCreate a high-quality 3D medical visualization focused on a soft tissue or non-fracture lesion.\nThis is NOT a bone fracture unless explicitly diagnosed.`
    : `Você é um radiologista musculoesquelético e cirurgião ortopédico experiente.\n\nCrie uma visualização médica 3D de alta qualidade focada em uma lesão de partes moles ou lesão não fraturária.\nIsto NÃO é uma fratura óssea, a menos que isso esteja explicitamente diagnosticado.`;

  const kneeAnatomyBlock = kneeSpecific
    ? (lang === "en"
        ? `\nRendering requirements:\n- Show distal femur, proximal tibia, fibula, menisci, articular cartilage and major ligaments for orientation.\n- Keep bones semi-realistic but secondary.\n- Emphasize the soft tissue target and surrounding joint anatomy.`
        : `\nRequisitos de renderização:\n- Mostre fêmur distal, tíbia proximal, fíbula, meniscos, cartilagem articular e principais ligamentos para orientação.\n- Mantenha os ossos semi-realistas, mas secundários.\n- Destaque o alvo de partes moles e a anatomia articular ao redor.`)
    : (lang === "en"
        ? `\nRendering requirements:\n- Show the relevant joint or segment with surrounding soft tissues for orientation.\n- Keep bone anatomy secondary to the soft tissue lesion.`
        : `\nRequisitos de renderização:\n- Mostre a articulação ou segmento relevante com os tecidos moles ao redor para orientação.\n- Mantenha a anatomia óssea secundária em relação à lesão de partes moles.`);

  const negativeBlock = lang === "en"
    ? `\nNegative prompt global:\n- Do not generate a fracture unless explicitly diagnosed.\n- Do not make the lesion bone-colored.\n- Do not ignore soft tissues.\n- Do not replace a cyst with bone deformity.\n- Do not show generic trauma.\n- Do not create cortical breaks, fracture lines, or bone displacement.\n- Do not place the lesion inside bone unless it is explicitly intraosseous.`
    : `\nNegative prompt global:\n- Não gere fratura a menos que ela esteja explicitamente diagnosticada.\n- Não deixe a lesão com aparência de osso.\n- Não ignore os tecidos moles.\n- Não substitua um cisto por deformidade óssea.\n- Não mostre trauma genérico.\n- Não crie ruptura cortical, linha de fratura ou desvio ósseo.\n- Não coloque a lesão dentro do osso, a menos que seja explicitamente intraóssea.`;

  return `${intro}

Structured lesion context:
- lesionCategory: ${lesionContext.lesionCategory}
- affectedStructure: ${lesionContext.affectedStructure}
- exactLocation: ${lesionContext.exactLocation}
- relationshipToBone: ${lesionContext.relationshipToBone}
- relationshipToMeniscusOrLigament: ${lesionContext.relationshipToMeniscusOrLigament}
- region: ${lesionContext.region}
- bone: ${lesionContext.bone}
- side: ${lesionContext.side || "preserve original side"}

Clinical target:
- The image must match the described soft-tissue lesion and not default to a fracture model.
- Clinical accuracy is more important than beauty.
- Preserve the described target structure and location exactly.
- Do not invent osseous injury if the diagnosis is soft tissue, meniscal, ligamentous, chondral, or disc-related.

${categoryRules[category]}

${kneeAnatomyBlock}

${negativeBlock}

Style:
- high-fidelity medical visualization
- neutral or dark medical background
- clinically legible and anatomically coherent
- no cinematic effects
- no decorative elements
`;
};

const escapeSurgicalPlanRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const extractLegacySurgicalPlanSection = (text: string, aliases: string[]) => {
  const cleaned = String(text || "").replace(/\r\n/g, "\n");
  const allAliases = [
    "DIAGNÓSTICO",
    "DIAGNOSTICO",
    "DIAGNÓSTICOS DIFERENCIAIS",
    "DIAGNOSTICOS DIFERENCIAIS",
    "MATERIAIS",
    "MATERIALS",
    "TÉCNICA",
    "TECNICA",
    "DICAS",
    "PLANO_B",
    "PLANO B",
  ];
  const startPattern = aliases.map((alias) => `\\[${escapeSurgicalPlanRegExp(alias)}\\]|^\\s*${escapeSurgicalPlanRegExp(alias)}\\s*[:\\-]`).join("|");
  const endPattern = allAliases.map((alias) => `\\[${escapeSurgicalPlanRegExp(alias)}\\]|^\\s*${escapeSurgicalPlanRegExp(alias)}\\s*[:\\-]`).join("|");
  const regex = new RegExp(`(?:${startPattern})\\s*([\\s\\S]*?)(?=(?:${endPattern})|$)`, "gim");
  const match = regex.exec(cleaned);
  return normalizeSurgicalPlanText(match?.[1] || "");
};

const normalizeRecommendation = (value: unknown): SurgicalPlanStrategy => {
  const normalized = normalizeSurgicalPlanText(value).toLowerCase();
  if (!normalized) return "uncertain";
  if (normalized.includes("conservative") || normalized.includes("conservador") || normalized.includes("não cirurg") || normalized.includes("nao cirurg")) {
    return "conservative";
  }
  if (normalized.includes("surgical") || normalized.includes("cirurg")) {
    return "surgical";
  }
  return "uncertain";
};

const normalizeSurgicalPlanResponseObject = (payload: Record<string, any>): NormalizedSurgicalPlanResponse => {
  const aliased = coalesceSurgicalPlanAliases(payload);
  const recommendation = normalizeRecommendation(payload.recommendation);
  const selectedStrategy = ((): "conservative" | "surgical" | null => {
    if (recommendation === "uncertain") return null;
    const explicitSelection = normalizeRecommendation(payload.selectedStrategy);
    if (explicitSelection === "conservative" || explicitSelection === "surgical") return explicitSelection;
    return recommendation === "conservative" || recommendation === "surgical" ? recommendation : null;
  })();
  const conservativePlan = aliased.conservativePlan || {};
  const surgicalOption = aliased.surgicalOption || {};
  const topLevelTechniqueProtocol = normalizeSurgicalPlanList(
    payload.surgicalTechniqueOrConservativeProtocol ||
    payload.stepByStepTechnique ||
    payload.stepByStep ||
    payload.surgicalSteps ||
    payload.operativeSteps ||
    payload.operativeSequence ||
    payload.intraoperativeSteps ||
    payload.treatmentPlan
  );
  const topLevelImagingControl = normalizeSurgicalPlanList(
    payload.fluoroscopyOrImagingControl || payload.imagingControl || payload.imagingGuidance || []
  );
  const topLevelPostoperativeCare = normalizeSurgicalPlanList(
    payload.postoperativeCare ||
    payload.postOperativeCare ||
    payload.postoperativeProtocol ||
    payload.rehabProtocol ||
    payload.rehabilitation ||
    payload.recoveryPlan ||
    []
  );
  const topLevelRisks = normalizeSurgicalPlanList(
    payload.risksAndPitfalls || payload.risks || payload.limitations || []
  );
  const topLevelPlanB = normalizeSurgicalPlanList(
    payload.planB ||
    payload.contingencyPlan ||
    payload.intraoperativeContingencies ||
    payload.fallbackPlan ||
    payload.rescueStrategy ||
    payload.alternativePlan ||
    []
  );
  const topLevelMaterials = normalizeSurgicalPlanList(
    payload.materials ||
    payload.materialList ||
    payload.implants ||
    payload.materialsAndImplants ||
    payload.requiredMaterials ||
    payload.surgicalMaterials
  );
  const contingencyPlan = payload.contingencyPlan || payload.alternativePlan || payload.intraoperativeContingencies || payload.planB || "";
  const surgicalTechniquesRaw = aliased.surgicalTechniques || [];
  const mainDiagnosis = pickFirstSurgicalPlanText(
    aliased.mainDiagnosis,
    aliased.diagnosis,
    aliased.primaryDiagnosis,
    aliased.diagnostico,
    payload.diagnosticSummary
  );
  const anatomicalLocation = pickFirstSurgicalPlanText(
    aliased.anatomicalLocation,
    aliased.location,
    aliased.localization,
    aliased.localizacaoAnatomica,
    aliased.localizacao,
    ""
  );
  const severity = normalizeSurgicalPlanText(payload.severity || "");
  const differentialDiagnoses = normalizeSurgicalPlanList(
    payload.differentialDiagnoses ||
    payload.differentialDiagnosis ||
    payload.differentials ||
    payload.associatedInjuries ||
    payload.associatedLesions ||
    payload.differentialAndAssociatedInjuries
  );
  const complementaryExams = normalizeSurgicalPlanList(payload.complementaryExams);
  const recommendations = normalizeSurgicalPlanList(payload.recommendations, 8);
  const scientificReferences = normalizeSurgicalPlanList(
    payload.scientificReferences ||
    payload.scientificEvidence ||
    payload.evidence ||
    payload.references ||
    payload.citations ||
    payload.bibliography,
    8
  );
  const normalizedTechniques = normalizeTechniqueList(surgicalTechniquesRaw);
  const materials = topLevelMaterials.length > 0 ? topLevelMaterials : normalizeSurgicalPlanList(aliased.materials || aliased.materialList || aliased.implants || aliased.materialsAndImplants);
  const selectedIsSurgical = selectedStrategy === "surgical";

  // Log contingency plan details
  const contingencyPlanObj = surgicalOption.contingencyPlan || {};
  const scenarios = Array.isArray(contingencyPlanObj.scenarios) ? contingencyPlanObj.scenarios : [];
  const hasContingencyPlan = scenarios.length > 0;
  const scenariosCount = scenarios.length;
  const isGeneric = scenarios.some((s: any) => 
    (s.problem?.toLowerCase().includes('definir') || 
     s.problem?.toLowerCase().includes('durante') || 
     s.action?.toLowerCase().includes('definir') ||
     s.action?.toLowerCase().includes('durante'))
  );
  const diagnosisUsed = mainDiagnosis.toLowerCase();
  const locationUsed = anatomicalLocation.toLowerCase();

  console.log("[SurgicalPlan][contingency]", {
    hasContingencyPlan,
    scenariosCount,
    isGeneric,
    diagnosisUsed,
    locationUsed
  });

  return {
    recommendation,
    selectedStrategy,
    mainDiagnosis,
    diagnosis: mainDiagnosis,
    anatomicalLocation,
    severity,
    differentialDiagnoses,
    differentialDiagnosis: differentialDiagnoses,
    associatedInjuries: differentialDiagnoses,
    associatedLesions: differentialDiagnoses,
    complementaryExams,
    preoperativePlanning: normalizeSurgicalPlanList(payload.preoperativePlanning || []),
    surgicalTechniques: normalizedTechniques || [],
    conservativePlan: {
      indication: normalizeSurgicalPlanText(conservativePlan.indication || ""),
      protocol: normalizeSurgicalPlanList(conservativePlan.protocol || []),
      followUp: normalizeSurgicalPlanList(conservativePlan.followUp || []),
      redFlags: normalizeSurgicalPlanList(conservativePlan.redFlags || []),
      conversionCriteria: normalizeSurgicalPlanList(conservativePlan.conversionCriteria || []),
    },
    surgicalOption: {
      indicationCriteria: normalizeSurgicalPlanList(surgicalOption.indicationCriteria || payload.indicationCriteria || payload.justification || []),
      preferredTechnique: normalizeSurgicalPlanText(surgicalOption.preferredTechnique || payload.surgicalPlan || payload.surgicalTechnique || payload.treatmentPlan || ""),
      patientPositioning: normalizeSurgicalPlanList(surgicalOption.patientPositioning || payload.patientPositioning || []),
      surgicalApproach: normalizeSurgicalPlanText(surgicalOption.surgicalApproach || payload.surgicalApproach || ""),
      reductionStrategy: normalizeSurgicalPlanList(surgicalOption.reductionStrategy || surgicalOption.reduction || []),
      fixationStrategy: normalizeSurgicalPlanList(surgicalOption.fixationStrategy || surgicalOption.fixation || payload.fixationStrategy || []),
      stepByStepTechnique: normalizeSurgicalPlanList(surgicalOption.stepByStepTechnique || surgicalOption.techniqueSteps || surgicalOption.steps || topLevelTechniqueProtocol),
      implantsAndMaterials: normalizeSurgicalPlanList(surgicalOption.implantsAndMaterials || surgicalOption.materials || materials || topLevelMaterials),
      imagingControl: normalizeSurgicalPlanList(surgicalOption.imagingControl || surgicalOption.fluoroscopyOrImagingControl || surgicalOption.imagingGuidance || topLevelImagingControl),
      intraoperativeChecks: normalizeSurgicalPlanList(surgicalOption.intraoperativeChecks || surgicalOption.checks || payload.intraoperativeSteps || []),
      closureAndImmobilization: normalizeSurgicalPlanList(surgicalOption.closureAndImmobilization || surgicalOption.closure || topLevelPostoperativeCare),
      postoperativeProtocol: normalizeSurgicalPlanList(surgicalOption.postoperativeProtocol || surgicalOption.postop || topLevelPostoperativeCare),
      risksAndPitfalls: normalizeSurgicalPlanList(surgicalOption.risksAndPitfalls || surgicalOption.risks || topLevelRisks),
      contingencyPlan: (() => {
        const cp = surgicalOption.contingencyPlan || payload.contingencyPlan || payload.alternativePlan || payload.planB || {};
        return {
          title: cp.title || "",
          summary: cp.summary || "",
          scenarios: Array.isArray(cp.scenarios) ? cp.scenarios.map((s: any) => ({
            problem: s.problem || "",
            action: s.action || ""
          })) : [],
          intraoperativeDecisionPoints: normalizeSurgicalPlanList(cp.intraoperativeDecisionPoints || []),
          conversionOptions: normalizeSurgicalPlanList(cp.conversionOptions || [])
        };
      })(),
    },
    scientificReferences,
    scientificEvidence: scientificReferences,
    evidence: scientificReferences,
    materials: materials.length > 0
      ? materials
      : Array.from(new Set([
          ...normalizedTechniques.flatMap((item: any) => Array.isArray(item.materials) ? item.materials : []),
          ...normalizeSurgicalPlanList(surgicalOption.implantsAndMaterials || []),
        ].map((item) => normalizeSurgicalPlanText(item)).filter(Boolean))),
    postoperativeCare: topLevelPostoperativeCare,
    contingencyPlan: (() => {
      const cp = surgicalOption.contingencyPlan || payload.contingencyPlan || payload.alternativePlan || payload.planB || {};
      const scenarios = Array.isArray(cp.scenarios) ? cp.scenarios : [];
      return scenarios.map((s: any) => `${s.problem || ""}: ${s.action || ""}`).filter(Boolean);
    })(),
    alternativePlan: normalizeSurgicalPlanList(payload.alternativePlan || payload.contingencyPlan || payload.intraoperativeContingencies || payload.planB || surgicalOption.planB || topLevelPlanB),
    planB: (() => {
      const cp = surgicalOption.contingencyPlan || payload.contingencyPlan || payload.alternativePlan || payload.planB || {};
      const scenarios = Array.isArray(cp.scenarios) ? cp.scenarios : [];
      const scenarioLines = scenarios.map((s: any) => `${s.problem || ""}: ${s.action || ""}`).filter(Boolean);
      return scenarioLines.length > 0 ? scenarioLines : topLevelPlanB;
    })(),
    clinicalInterpretation: normalizeSurgicalPlanText(payload.clinicalInterpretation || payload.justification || payload.treatmentPlan || ""),
    treatmentPlan: normalizeSurgicalPlanText(payload.treatmentPlan || payload.clinicalInterpretation || ""),
    surgicalIndicationReason: normalizeSurgicalPlanText(payload.surgicalIndicationReason || payload.justification || ""),
    recommendations,
    level: normalizeSurgicalPlanText(payload.level || ""),
    affectedRoot: normalizeSurgicalPlanText(payload.affectedRoot || ""),
    classification: normalizeSurgicalPlanText(payload.classification || ""),
  };
};

export const normalizeSurgicalPlanResponse = (rawText: string): NormalizedSurgicalPlanResponse => {
  const raw = String(rawText || "").trim();
  const parsed = (() => {
    const directParsed = parseJsonObjectFromAiText(raw);
    if (directParsed) return directParsed;
    try {
      return JSON.parse(cleanJson(raw));
    } catch {
      return null;
    }
  })();

  if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
    const normalized = normalizeSurgicalPlanResponseObject(parsed as Record<string, any>);
    console.log("[SURGICAL_PLAN][normalized]", normalized);
    return normalized;
  }

  const diagnosis = extractLegacySurgicalPlanSection(raw, ["[DIAGNÓSTICO]", "[DIAGNOSTICO]", "DIAGNÓSTICO", "DIAGNOSTICO", "DIAGNOSIS"]).split("\n")[0] || "";
  const differentialDiagnoses = normalizeSurgicalPlanList(
    extractLegacySurgicalPlanSection(raw, ["[DIAGNÓSTICOS_DIFERENCIAIS]", "[DIAGNOSTICOS_DIFERENCIAIS]", "DIAGNÓSTICOS DIFERENCIAIS", "DIAGNOSTICOS DIFERENCIAIS", "DIFFERENTIAL DIAGNOSIS"])
  );
  const materials = normalizeSurgicalPlanList(
    extractLegacySurgicalPlanSection(raw, ["[MATERIAIS]", "[MATERIALS]", "MATERIAIS", "MATERIALS"])
  );
  const techniqueText = extractLegacySurgicalPlanSection(raw, ["[TECNICA]", "[TÉCNICA]", "[TECHNIQUE]", "TECNICA", "TÉCNICA", "TECHNIQUE"]);
  const tipsText = extractLegacySurgicalPlanSection(raw, ["[DICAS]", "[TIPS]", "[PEARLS]", "DICAS", "TIPS", "PEARLS", "PITFALLS"]);
  const planBText = extractLegacySurgicalPlanSection(raw, ["[PLANO_B]", "[PLANO B]", "[PLAN_B]", "[PLAN B]", "PLANO_B", "PLANO B", "PLAN_B", "PLAN B"]);
  const conservativeIndication = extractLegacySurgicalPlanSection(raw, ["[CONDUTA]", "[MANAGEMENT]", "[CONDUCTA]", "CONDUTA", "MANAGEMENT", "CONDUCTA"]);
  const recommendation = normalizeRecommendation(raw);
  const selectedStrategy = recommendation === "uncertain" ? null : recommendation;
  const normalized = {
    recommendation,
    selectedStrategy,
    mainDiagnosis: normalizeSurgicalPlanText(diagnosis),
    diagnosis: normalizeSurgicalPlanText(diagnosis),
    anatomicalLocation: "",
    severity: "",
    differentialDiagnoses,
    complementaryExams: [],
    preoperativePlanning: [],
    conservativePlan: {
      indication: normalizeSurgicalPlanText(conservativeIndication || ""),
      protocol: normalizeSurgicalPlanList(conservativeIndication ? [conservativeIndication] : []),
      followUp: [],
      redFlags: [],
      conversionCriteria: [],
    },
    surgicalOption: {
      indicationCriteria: [],
      preferredTechnique: normalizeSurgicalPlanText(techniqueText || ""),
      patientPositioning: [],
      surgicalApproach: "",
      reductionStrategy: [],
      fixationStrategy: [],
      stepByStepTechnique: normalizeSurgicalPlanList(techniqueText) || [],
      implantsAndMaterials: materials,
      imagingControl: [],
      intraoperativeChecks: [],
      closureAndImmobilization: [],
      postoperativeProtocol: [],
      risksAndPitfalls: normalizeSurgicalPlanList(tipsText) || [],
      contingencyPlan: {
        title: "",
        summary: "",
        scenarios: [],
        intraoperativeDecisionPoints: [],
        conversionOptions: [],
      },
      planB: normalizeSurgicalPlanList(planBText || []),
    },
    scientificReferences: [],
    clinicalInterpretation: normalizeSurgicalPlanText(conservativeIndication || ""),
    treatmentPlan: normalizeSurgicalPlanText(techniqueText || ""),
    surgicalIndicationReason: normalizeSurgicalPlanText(tipsText || ""),
    recommendations: [],
  };

  console.log("[SURGICAL_PLAN][normalized]", normalized);
  return normalized;
};

export const extractSpineFindings = async (
  query: string,
  lang: Language = "pt",
  imageBase64?: string
): Promise<SpineFindings> => {
  const ai = createGeminiClient("generateSurgicalPlan");
  const cleanBase64 = imageBase64 ? stripDataUrlPrefix(String(imageBase64).trim()) : "";
  const mimeMatch = String(imageBase64 || "").match(/^data:(image\/[a-zA-Z0-9+.-]+);base64,/i);
  const mimeType = mimeMatch?.[1] || "image/jpeg";
  const hasImage = Boolean(cleanBase64);

  const prompt = lang === "en"
    ? `You are an experienced spine surgeon and musculoskeletal radiologist.

Identify the exact spinal level and disc lesion from the provided radiograph and clinical context.
Do not invent a generic hernia.
Do not omit the level.
If the previous diagnosis conflicts with the image, follow the image.
If level cannot be confirmed, return an error instead of guessing.

Clinical context:
${query}

Return ONLY valid JSON with:
{
  "region": "cervical spine | thoracic spine | lumbar spine | sacral spine | exact region",
  "level": "e.g. L4-L5",
  "discCondition": "disc herniation | protrusion | extrusion | bulge | degeneration | other",
  "herniaType": "posterolateral | central | foraminal | extraforaminal | sequestered | none",
  "nerveCompression": true,
  "severity": "leve | moderada | grave",
  "confidence": 0.0
}`
    : `Você é um cirurgião de coluna experiente e radiologista musculoesquelético.

Identifique o nível espinhal exato e a lesão discal a partir da radiografia e do contexto clínico.
Não invente uma hérnia genérica.
Não omita o nível.
Se o diagnóstico anterior conflitar com a imagem, siga a imagem.
Se o nível não puder ser confirmado, retorne erro em vez de adivinhar.

Contexto clínico:
${query}

Retorne APENAS JSON válido com:
{
  "region": "coluna cervical | coluna torácica | coluna lombar | sacro | região exata",
  "level": "ex.: L4-L5",
  "discCondition": "hérnia de disco | protrusão | extrusão | abaulamento | degeneração | outro",
  "herniaType": "posterolateral | central | foraminal | extraforaminal | sequestrada | none",
  "nerveCompression": true,
  "severity": "leve | moderada | grave",
  "confidence": 0.0
}`;

  const response = await ai.models.generateContent({
    model: "gemini-3-pro-preview",
    contents: hasImage
      ? {
          parts: [
            { inlineData: { data: cleanBase64, mimeType } },
            { text: prompt },
          ],
        }
      : prompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          region: { type: Type.STRING },
          level: { type: Type.STRING },
          discCondition: { type: Type.STRING },
          herniaType: { type: Type.STRING },
          nerveCompression: { type: Type.BOOLEAN },
          severity: { type: Type.STRING },
          confidence: { type: Type.NUMBER },
          conflictWithPreviousDiagnosis: { type: Type.BOOLEAN },
          correctedDiagnosis: { type: Type.STRING },
        },
        required: ["region", "level", "discCondition", "herniaType", "nerveCompression", "severity", "confidence"],
      },
    },
  });

  const parsed = JSON.parse(cleanJson(response.text || "{}")) as SpineFindings;
  const level = normalizeSpineLevel(parsed.level || "");
  if (!level) {
    throw new Error("Nível anatômico não identificado. Não foi possível gerar o planejamento da coluna.");
  }

  const severity = ["leve", "moderada", "grave"].includes(String(parsed.severity || "").toLowerCase())
    ? (String(parsed.severity).toLowerCase() as SpineFindings["severity"])
    : "moderada";

  return {
    region: String(parsed.region || "").trim() || "coluna lombar",
    level,
    discCondition: String(parsed.discCondition || "").trim() || "hérnia de disco",
    herniaType: String(parsed.herniaType || "").trim() || "posterolateral",
    nerveCompression: Boolean(parsed.nerveCompression),
    severity,
    confidence: Math.max(0, Math.min(1, Number(parsed.confidence ?? 0) || 0)),
    conflictWithPreviousDiagnosis: Boolean(parsed.conflictWithPreviousDiagnosis),
    correctedDiagnosis: String(parsed.correctedDiagnosis || "").trim(),
  };
};

const generateSpineSurgicalPlanFromFindings = async (
  findings: SpineFindings,
  lang: Language = "pt"
): Promise<SpineSurgicalPlanOutput> => {
  const ai = createGeminiClient("generateSurgicalPlan");
  const prompt = `Você é um cirurgião ortopédico de trauma e cirurgião de coluna experiente.

Crie um relatório prático, não genérico, de planejamento clínico/cirúrgico para um ortopedista especialista.

O objetivo é ajudar o médico a decidir conduta, preparar cirurgia quando indicada ou definir tratamento conservador quando cirurgia não for indicada.

NUNCA gere conteúdo genérico.
Sempre use a localização anatômica específica, o nível, o padrão da fratura/lesão, a gravidade e os achados de imagem.

Entrada estruturada:
- Região: ${findings.region}
- Nível: ${findings.level}
- Condição: ${findings.discCondition}
- Tipo: ${findings.herniaType}
- Compressão neural: ${String(findings.nerveCompression)}
- Gravidade: ${findings.severity}
- Confiança: ${findings.confidence}

Regras clínicas obrigatórias:
- Se o achado for de coluna, sempre inclua nível espinhal, raiz acometida, recomendação de RM e critérios cirúrgicos.
- Se o achado for traumático, sempre inclua localização da fratura, desvio, envolvimento articular, opções de fixação e preocupações neurovasculares.
- Se cirurgia não for indicada, explique por quê.
- Se tratamento conservador for preferido, forneça protocolo conservador estruturado.
- Não diga "não aplicável" sem explicação.
- Use bullets clínicos curtos.
- Evite parágrafos longos.
- Português médico, formal e claro.
- O nível espinhal ${findings.level} deve aparecer explicitamente em todo conteúdo relacionado à coluna.
- Se o caso sugerir hérnia discal, descreva sempre o nível, localização da hérnia, raiz possivelmente comprimida, sintomas esperados, RM lombar e critérios cirúrgicos.

Gere APENAS JSON válido com as seções abaixo:
{
  "diagnosis": "Diagnóstico principal com nível e padrão da lesão",
  "anatomicalLocation": "Localização anatômica exata",
  "classification": "Classificação técnica e severidade",
  "severity": "leve | moderada | grave",
  "level": "Nível anatômico exato",
  "affectedRoot": "Raiz nervosa possivelmente acometida ou 'não identificada'",
  "differentialDiagnoses": ["Diagnósticos diferenciais curtos e específicos"],
  "complementaryExams": ["Exames complementares, incluindo RM quando indicado"],
  "treatmentIndication": "surgical | relative | non-surgical",
  "preoperativePlanning": ["Planejamento pré-operatório em bullets curtos"],
  "surgicalTechniqueOrConservativeProtocol": ["Técnica cirúrgica ou protocolo conservador, em bullets curtos"],
  "materials": ["Materiais / implantes / OPME quando aplicável"],
  "intraoperativeSteps": ["Passos intraoperatórios objetivos ou passos conservadores"],
  "risksAndPitfalls": ["Riscos e armadilhas"],
  "planB": ["Plano B / estratégia alternativa"],
  "postoperativeCare": ["Cuidados pós-operatórios ou seguimento conservador"],
  "redFlags": ["Sinais de alerta"],
  "scientificReferences": ["Referências científicas curtas e confiáveis"],
  "clinicalInterpretation": "Interpretação clínica breve do impacto funcional e neural",
  "treatmentPlan": "Plano terapêutico resumido e objetivo",
  "surgicalIndicationReason": "Justificativa curta da indicação ou da opção conservadora",
  "recommendations": ["Recomendações finais curtas, incluindo correlação clínica e imagem"]
}

Regras adicionais:
- Se a cirurgia não for indicada, deixe isso claro com motivo técnico.
- Se o caso for conservador, ainda assim descreva o protocolo conservador de forma estruturada.
- Se a gravidade ou confiança forem baixas, seja cauteloso e não force uma técnica específica.
- Não omita nível, localização ou padrão da lesão.
- Não use texto corrido longo dentro dos campos; prefira frases curtas e bullets curtos.`;

  const response = await ai.models.generateContent({
    model: "gemini-3-pro-preview",
    contents: prompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          diagnosis: { type: Type.STRING },
          anatomicalLocation: { type: Type.STRING },
          classification: { type: Type.STRING },
          severity: { type: Type.STRING },
          level: { type: Type.STRING },
          affectedRoot: { type: Type.STRING },
          differentialDiagnoses: { type: Type.ARRAY, items: { type: Type.STRING } },
          complementaryExams: { type: Type.ARRAY, items: { type: Type.STRING } },
          treatmentIndication: { type: Type.STRING },
          preoperativePlanning: { type: Type.ARRAY, items: { type: Type.STRING } },
          surgicalTechniqueOrConservativeProtocol: { type: Type.ARRAY, items: { type: Type.STRING } },
          materials: { type: Type.ARRAY, items: { type: Type.STRING } },
          intraoperativeSteps: { type: Type.ARRAY, items: { type: Type.STRING } },
          risksAndPitfalls: { type: Type.ARRAY, items: { type: Type.STRING } },
          planB: { type: Type.ARRAY, items: { type: Type.STRING } },
          postoperativeCare: { type: Type.ARRAY, items: { type: Type.STRING } },
          redFlags: { type: Type.ARRAY, items: { type: Type.STRING } },
          scientificReferences: { type: Type.ARRAY, items: { type: Type.STRING } },
          clinicalInterpretation: { type: Type.STRING },
          treatmentPlan: { type: Type.STRING },
          surgicalIndication: { type: Type.STRING },
          surgicalIndicationReason: { type: Type.STRING },
          recommendations: { type: Type.ARRAY, items: { type: Type.STRING } },
        },
        required: [
          "diagnosis",
          "anatomicalLocation",
          "classification",
          "severity",
          "level",
          "affectedRoot",
          "differentialDiagnoses",
          "complementaryExams",
          "treatmentIndication",
          "preoperativePlanning",
          "surgicalTechniqueOrConservativeProtocol",
          "materials",
          "intraoperativeSteps",
          "risksAndPitfalls",
          "planB",
          "postoperativeCare",
          "redFlags",
          "scientificReferences",
          "clinicalInterpretation",
          "treatmentPlan",
          "surgicalIndication",
          "surgicalIndicationReason",
          "recommendations",
        ],
      },
    },
  });

  const parsed = JSON.parse(cleanJson(response.text || "{}")) as SpineSurgicalPlanOutput;
  if (!String(parsed.diagnosis || "").trim()) {
    throw new Error("Plano cirúrgico estruturado inválido para a coluna.");
  }

  const normalizeList = (value: unknown, fallback: string[] = []) => {
    const items = Array.isArray(value)
      ? value.map((item) => String(item || "").trim()).filter(Boolean)
      : [];
    return items.length > 0 ? items : fallback;
  };

  const treatmentIndication = (["surgical", "relative", "non-surgical"].includes(String(parsed.treatmentIndication || "").toLowerCase())
    ? String(parsed.treatmentIndication).toLowerCase()
    : String(parsed.surgicalIndication || "").toLowerCase()) as SpineSurgicalPlanOutput["treatmentIndication"];

  return {
    diagnosis: String(parsed.diagnosis || "").trim(),
    anatomicalLocation: String(parsed.anatomicalLocation || "").trim(),
    classification: String(parsed.classification || "").trim(),
    severity: String(parsed.severity || "").trim(),
    level: String(parsed.level || "").trim(),
    affectedRoot: String(parsed.affectedRoot || "").trim(),
    differentialDiagnoses: normalizeList(parsed.differentialDiagnoses),
    complementaryExams: normalizeList(parsed.complementaryExams),
    treatmentIndication: (["surgical", "relative", "non-surgical"].includes(treatmentIndication)
      ? treatmentIndication
      : "relative") as SpineSurgicalPlanOutput["treatmentIndication"],
    preoperativePlanning: normalizeList(parsed.preoperativePlanning),
    surgicalTechniqueOrConservativeProtocol: normalizeList(parsed.surgicalTechniqueOrConservativeProtocol),
    materials: normalizeList(parsed.materials),
    intraoperativeSteps: normalizeList(parsed.intraoperativeSteps),
    risksAndPitfalls: normalizeList(parsed.risksAndPitfalls),
    planB: normalizeList(parsed.planB),
    postoperativeCare: normalizeList(parsed.postoperativeCare),
    redFlags: normalizeList(parsed.redFlags),
    scientificReferences: normalizeList(parsed.scientificReferences),
    clinicalInterpretation: String(parsed.clinicalInterpretation || "").trim(),
    treatmentPlan: String(parsed.treatmentPlan || "").trim(),
    surgicalIndication: (["surgical", "relative", "non-surgical"].includes(String(parsed.surgicalIndication || "").toLowerCase())
      ? String(parsed.surgicalIndication).toLowerCase()
      : "relative") as SpineSurgicalPlanOutput["surgicalIndication"],
    surgicalIndicationReason: String(parsed.surgicalIndicationReason || "").trim(),
    recommendations: Array.isArray(parsed.recommendations)
      ? parsed.recommendations.map((item) => String(item || "").trim()).filter(Boolean)
      : [],
  };
};

export const generateSurgicalPlanText = async (
  query: string,
  lang: Language = 'pt',
  imageBase64?: string
) => {
  const normalizedQuery = String(query || "").trim();
  const cleanBase64 = imageBase64 ? stripDataUrlPrefix(String(imageBase64).trim()) : "";
  const mimeMatch = String(imageBase64 || "").match(/^data:(image\/[a-zA-Z0-9+.-]+);base64,/i);
  const mimeType = mimeMatch?.[1] || "image/jpeg";

  if (!normalizedQuery && !cleanBase64) {
    throw new Error("Nenhum contexto clínico ou imagem foi informado para gerar o planejamento cirúrgico.");
  }

  const ai = createGeminiClient("generateSurgicalPlan");
  const spineFindings = isSpinePlanningQuery(normalizedQuery)
    ? await extractSpineFindings(normalizedQuery, lang, imageBase64).catch(() => null)
    : null;

  const fieldsLanguage = lang === "en"
    ? {
        recommendation: "conservative | surgical | uncertain",
        selectedStrategy: "conservative | surgical",
        conservativePlan: "Conservative plan",
        surgicalOption: "Surgical option",
        diagnosis: "Diagnostic summary",
        anatomicalLocation: "Exact anatomical location",
        classification: "Technical classification",
        severity: "Severity",
        level: "Exact level",
        affectedRoot: "Possibly affected root",
        differentialDiagnoses: "Differential diagnoses",
        complementaryExams: "Complementary exams",
        preoperativePlanning: "Preoperative planning",
        indication: "Indication",
        protocol: "Protocol",
        followUp: "Follow-up",
        redFlags: "Red flags",
        conversionCriteria: "Conversion criteria",
        indicationCriteria: "Indication criteria",
        preferredTechnique: "Preferred technique",
        patientPositioning: "Patient positioning",
        surgicalApproach: "Surgical approach",
        reductionStrategy: "Reduction strategy",
        fixationStrategy: "Fixation strategy",
        stepByStepTechnique: "Step-by-step technique",
        implantsAndMaterials: "Implants and materials",
        fluoroscopyOrImagingControl: "Fluoroscopy / imaging control",
        intraoperativeChecks: "Intraoperative checks",
        closureAndImmobilization: "Closure and immobilization",
        postoperativeProtocol: "Postoperative protocol",
        risksAndPitfalls: "Risks and pitfalls",
        contingencyPlan: {
          title: "Plan B",
          summary: "",
          scenarios: [
            {
              problem: "",
              action: ""
            }
          ],
          intraoperativeDecisionPoints: [],
          conversionOptions: []
        },
        clinicalInterpretation: "Clinical interpretation",
        treatmentPlan: "Treatment plan",
        surgicalIndicationReason: "Surgical indication reason",
        recommendations: "Final recommendations",
        scientificReferences: "Scientific references",
      }
    : lang === 'es'
      ? {
          recommendation: "conservative | surgical | uncertain",
          selectedStrategy: "conservative | surgical",
          conservativePlan: "Plan conservador",
          surgicalOption: "Opción quirúrgica",
          diagnosis: "Resumen diagnóstico",
          anatomicalLocation: "Localización anatómica exacta",
          classification: "Clasificación técnica",
          severity: "Gravedad",
          level: "Nivel exacto",
          affectedRoot: "Raíz posiblemente afectada",
          differentialDiagnoses: "Diagnósticos diferenciales",
          complementaryExams: "Exámenes complementarios",
          preoperativePlanning: "Planificación preoperatoria",
          indication: "Indicación",
          protocol: "Protocolo",
          followUp: "Seguimiento",
          redFlags: "Alertas",
          conversionCriteria: "Criterios de conversión",
          indicationCriteria: "Criterios de indicación",
          preferredTechnique: "Técnica preferida",
          patientPositioning: "Posicionamiento del paciente",
          surgicalApproach: "Abordaje quirúrgico",
          reductionStrategy: "Estrategia de reducción",
          fixationStrategy: "Estrategia de fijación",
          stepByStepTechnique: "Técnica paso a paso",
          implantsAndMaterials: "Implantes y materiales",
          fluoroscopyOrImagingControl: "Control por imagen",
          intraoperativeChecks: "Verificaciones intraoperatorias",
          closureAndImmobilization: "Cierre e inmovilización",
          postoperativeProtocol: "Protocolo posoperatorio",
          risksAndPitfalls: "Riesgos y trampas",
          contingencyPlan: {
            title: "Plan B",
            summary: "",
            scenarios: [
              {
                problem: "",
                action: ""
              }
            ],
            intraoperativeDecisionPoints: [],
            conversionOptions: []
          },
          clinicalInterpretation: "Interpretación clínica",
          treatmentPlan: "Plan terapéutico",
          surgicalIndicationReason: "Justificación de la indicación",
          recommendations: "Recomendaciones finales",
          scientificReferences: "Referencias científicas",
        }
      : {
          recommendation: "conservative | surgical | uncertain",
          selectedStrategy: "conservative | surgical",
          conservativePlan: "Plano conservador",
          surgicalOption: "Opção cirúrgica",
          diagnosis: "Resumo diagnóstico",
          anatomicalLocation: "Localização anatômica exata",
          classification: "Classificação técnica",
          severity: "Gravidade",
          level: "Nível exato",
          affectedRoot: "Raiz possivelmente acometida",
          differentialDiagnoses: "Diagnósticos diferenciais",
          complementaryExams: "Exames complementares",
          preoperativePlanning: "Planejamento pré-operatório",
          indication: "Indicação",
          protocol: "Protocolo",
          followUp: "Seguimento",
          redFlags: "Red flags",
          conversionCriteria: "Critérios de conversão",
          indicationCriteria: "Critérios de indicação",
          preferredTechnique: "Técnica preferencial",
          patientPositioning: "Posicionamento do paciente",
          surgicalApproach: "Via de acesso",
          reductionStrategy: "Estratégia de redução",
          fixationStrategy: "Estratégia de fixação",
          stepByStepTechnique: "Técnica passo a passo",
          implantsAndMaterials: "Implantes e materiais",
          fluoroscopyOrImagingControl: "Controle por imagem",
          intraoperativeChecks: "Checagens intraoperatórias",
          closureAndImmobilization: "Fechamento e imobilização",
          postoperativeProtocol: "Protocolo pós-operatório",
          risksAndPitfalls: "Riscos e armadilhas",
          contingencyPlan: {
            title: "Plano B / Contingência",
            summary: "",
            scenarios: [
              {
                problem: "",
                action: ""
              }
            ],
            intraoperativeDecisionPoints: [],
            conversionOptions: []
          },
          clinicalInterpretation: "Interpretação clínica",
          treatmentPlan: "Plano terapêutico",
          surgicalIndicationReason: "Justificativa da indicação",
          recommendations: "Recomendações finais",
          scientificReferences: "Referências científicas",
        };

  const structuredContext = spineFindings
    ? `
ACHADOS ESTRUTURADOS DA COLUNA:
- região: ${spineFindings.region}
- nível: ${spineFindings.level}
- condição discal: ${spineFindings.discCondition}
- tipo de hérnia: ${spineFindings.herniaType}
- compressão neural: ${String(spineFindings.nerveCompression)}
- gravidade: ${spineFindings.severity}
- confiança: ${spineFindings.confidence}
${spineFindings.conflictWithPreviousDiagnosis ? `- conflito com diagnóstico anterior: sim\n- diagnóstico corrigido: ${spineFindings.correctedDiagnosis || ""}` : ""}`.trim()
    : "";

  const strictLanguageRule = lang === "en"
    ? `Return ALL medical content, headings, labels, techniques, explanations, evidence summaries, materials, recommendations and fallback text strictly in English. Never mix languages. Do not return Portuguese text unless the selected language is Portuguese.`
    : lang === "es"
      ? `Devuelve TODO el contenido médico, encabezados, etiquetas, técnicas, explicaciones, resúmenes de evidencia, materiales, recomendaciones y texto de respaldo estrictamente en español. Nunca mezcles idiomas. No devuelvas texto en portugués a menos que el idioma seleccionado sea portugués.`
      : `Retorne TODO o conteúdo médico, títulos, rótulos, técnicas, explicações, resumos de evidências, materiais, recomendações e texto de fallback estritamente em português. Nunca misture idiomas. Não retorne texto em inglês a menos que o idioma selecionado seja inglês.`;

  const prompt = lang === "en"
    ? `You are an experienced orthopedic trauma surgeon and spine surgeon.

Create a practical, non-generic planning report for an orthopedic specialist.
Always generate BOTH treatment paths:
1) conservative management
2) surgical technique option

The output must help the physician decide treatment without another AI call.
If surgery is not indicated, still generate a complete surgical option for physician review.
If the recommendation is uncertain, do not force a default selection.

Clinical context:
${normalizedQuery}

${structuredContext ? structuredContext + "\n" : ""}
Rules:
- Use the exact bone, joint, level, fracture pattern, lesion location, displacement, and imaging findings.
- If there is a conflict between text and image, follow the image.
- For spine, always include level and possibly affected root.
- For trauma, always include fracture location, displacement, joint involvement, fixation choices, and neurovascular concerns.
- Conservative and surgical plans must be practical and short, not generic.
- ${strictLanguageRule}
- As tÃ©cnicas devem ser especÃ­ficas ao diagnÃ³stico, Ã  localizaÃ§Ã£o anatÃ´mica e ao padrÃ£o AO/OTA.
- Nunca retorne texto genÃ©rico como "Definir tÃ©cnica apÃ³s confirmaÃ§Ã£o por TC/RM".
- Sempre preencha materiais/implantes, passo a passo, abordagem, posicionamento, riscos, plano B e evidÃªncias por tÃ©cnica quando houver indicaÃ§Ã£o cirÃºrgica.
- Se houver incerteza, ainda assim forneÃ§a planejamento preliminar especÃ­fico e sinalize a ressalva no JSON.
- Return only valid JSON using short clinical bullets in arrays where appropriate.

JSON shape:
{
  "recommendation": "conservative | surgical | uncertain",
  "selectedStrategy": "conservative | surgical",
  "diagnosis": "Main diagnosis with location and pattern",
  "anatomicalLocation": "Exact anatomical location",
  "classification": "Technical classification",
  "severity": "leve | moderada | grave | mild | moderate | severe",
  "level": "Exact spinal level or empty string",
  "affectedRoot": "Possibly affected root or empty string",
  "differentialDiagnoses": ["Short differential diagnoses"],
  "complementaryExams": ["Complementary exams"],
  "preoperativePlanning": ["Preoperative planning"],
  "conservativePlan": {
    "indication": "Why conservative treatment is appropriate or a valid option",
    "protocol": ["Conservative protocol steps"],
    "followUp": ["Follow-up and reassessment"],
    "redFlags": ["Red flags requiring re-evaluation"],
    "conversionCriteria": ["Criteria for conversion to surgery"]
  },
  "surgicalOption": {
    "indicationCriteria": ["Criteria that justify surgery"],
    "preferredTechnique": "Preferred technique summary",
    "patientPositioning": ["Patient positioning"],
    "surgicalApproach": "Surgical approach / access",
    "reductionStrategy": ["Reduction strategy"],
    "fixationStrategy": ["Fixation strategy / construct"],
    "stepByStepTechnique": ["Step-by-step technique"],
    "implantsAndMaterials": ["Implants and materials"],
    "fluoroscopyOrImagingControl": ["Image control"],
    "intraoperativeChecks": ["Final checks"],
    "closureAndImmobilization": ["Closure and immobilization"],
    "postoperativeProtocol": ["Postoperative protocol"],
    "risksAndPitfalls": ["Risks and pitfalls"],
    "planB": ["Plan B"]
  },
  "clinicalInterpretation": "Clinical interpretation",
  "treatmentPlan": "Practical treatment plan",
  "surgicalIndicationReason": "Why this recommendation was chosen",
  "recommendations": ["Final recommendations"]
}`
    : lang === "es"
      ? `Eres un cirujano ortopédico de trauma y columna con experiencia.

Crea un informe práctico y no genérico para un especialista en ortopedia.
Genera SIEMPRE ambos caminos terapéuticos:
1) tratamiento conservador
2) opción de técnica quirúrgica

La salida debe ayudar al médico a decidir el tratamiento sin otra llamada a la IA.
Si la cirugía no está indicada, aun así genera una opción quirúrgica completa para revisión médica.
Si la recomendación es incierta, no fuerces una selección por defecto.

Contexto clínico:
${normalizedQuery}

${structuredContext ? structuredContext + "\n" : ""}
Reglas:
- Usa el hueso, articulación, nivel, patrón de fractura, localización de la lesión, desplazamiento y hallazgos de imagen exactos.
- Si hay conflicto entre texto e imagen, sigue la imagen.
- En columna, incluye siempre el nivel y la raíz posiblemente afectada.
- En trauma, incluye siempre localización de fractura, desplazamiento, compromiso articular, opciones de fijación y preocupación neurovascular.
- Los planes conservador y quirúrgico deben ser prácticos y cortos, no genéricos.
- ${strictLanguageRule}
- Devuelve solo JSON válido usando viñetas breves en arreglos cuando corresponda.

Estructura JSON:
{
  "recommendation": "conservative | surgical | uncertain",
  "selectedStrategy": "conservative | surgical",
  "diagnosis": "Diagnóstico principal con localización y patrón",
  "anatomicalLocation": "Localización anatómica exacta",
  "classification": "Clasificación técnica",
  "severity": "leve | moderada | grave | mild | moderate | severe",
  "level": "Nivel espinal exacto o cadena vacía",
  "affectedRoot": "Raíz posiblemente afectada o cadena vacía",
  "differentialDiagnoses": ["Diagnósticos diferenciales breves"],
  "complementaryExams": ["Exámenes complementarios"],
  "preoperativePlanning": ["Planificación preoperatoria"],
  "conservativePlan": {
    "indication": "Por qué el tratamiento conservador es apropiado o válido",
    "protocol": ["Pasos del protocolo conservador"],
    "followUp": ["Seguimiento y reevaluación"],
    "redFlags": ["Alertas que exigen reevaluación"],
    "conversionCriteria": ["Criterios para conversión a cirugía"]
  },
  "surgicalOption": {
    "indicationCriteria": ["Criterios que justifican cirugía"],
    "preferredTechnique": "Resumen de la técnica preferida",
    "patientPositioning": ["Posicionamiento del paciente"],
    "surgicalApproach": "Abordaje / acceso quirúrgico",
    "reductionStrategy": ["Estrategia de reducción"],
    "fixationStrategy": ["Estrategia de fijación / constructo"],
    "stepByStepTechnique": ["Técnica paso a paso"],
    "implantsAndMaterials": ["Implantes y materiales"],
    "fluoroscopyOrImagingControl": ["Control por imagen"],
    "intraoperativeChecks": ["Verificaciones finales"],
    "closureAndImmobilization": ["Cierre e inmovilización"],
    "postoperativeProtocol": ["Protocolo posoperatorio"],
    "risksAndPitfalls": ["Riesgos y trampas"],
    "planB": ["Plan B"]
  },
  "clinicalInterpretation": "Interpretación clínica",
  "treatmentPlan": "Plan terapéutico práctico",
  "surgicalIndicationReason": "Por qué se eligió esta recomendación",
  "recommendations": ["Recomendaciones finales"]
}`
      : `Você é um cirurgião ortopédico de trauma e de coluna experiente.

Crie um relatório prático e não genérico para um especialista em ortopedia.
Gere SEMPRE os dois caminhos terapêuticos:
1) tratamento conservador
2) opção de técnica cirúrgica

A saída deve ajudar o médico a decidir a conduta sem nova chamada de IA.
Se a cirurgia não estiver indicada, ainda assim gere uma opção cirúrgica completa para revisão médica.
Se a recomendação for incerta, não force uma seleção padrão.

Contexto clínico:
${normalizedQuery}

${structuredContext ? structuredContext + "\n" : ""}
Regras:
- Use exatamente osso, articulação, nível, padrão da fratura, localização da lesão, desvio e achados de imagem.
- Se houver conflito entre texto e imagem, siga a imagem.
- Em coluna, inclua sempre o nível e a raiz possivelmente acometida.
- Em trauma, inclua sempre localização da fratura, desvio, envolvimento articular, opções de fixação e preocupação neurovascular.
- Os planos conservador e cirúrgico devem ser práticos e curtos, não genéricos.
- ${strictLanguageRule}
- O campo contingencyPlan deve conter situações de falha intraoperatória possíveis e condutas alternativas práticas.
- Situações incluem: redução inadequada, cominuição maior que previsto, osteoporose/baixa qualidade óssea, instabilidade após fixação, comprometimento articular, impossibilidade de fixação planejada, partes moles desfavoráveis, material insuficiente, perda de alinhamento, achado diferente da imagem.
- Condutas alternativas: trocar tipo de implante, adicionar placa/parafusos, mudar abordagem, usar fixador externo temporário, staged surgery, converter estratégia, solicitar TC complementar pós/intraoperatória se disponível, encaminhar para centro especializado quando necessário.
- Linguagem prática, cirúrgica e objetiva.
- ContingencyPlan deve ser específico ao diagnóstico/localização/AO/OTA.
- Nunca vazio ou genérico como "Definir durante o ato cirúrgico".
- Retorne apenas JSON válido, usando bullets curtos em arrays quando apropriado.

Estrutura JSON:
{
  "recommendation": "conservative | surgical | uncertain",
  "selectedStrategy": "conservative | surgical",
  "diagnosis": "Diagnóstico principal com localização e padrão",
  "anatomicalLocation": "Localização anatômica exata",
  "classification": "Classificação técnica",
  "severity": "leve | moderada | grave | mild | moderate | severe",
  "level": "Nível espinhal exato ou string vazia",
  "affectedRoot": "Raiz possivelmente acometida ou string vazia",
  "differentialDiagnoses": ["Diagnósticos diferenciais curtos"],
  "complementaryExams": ["Exames complementares"],
  "preoperativePlanning": ["Planejamento pré-operatório"],
  "conservativePlan": {
    "indication": "Por que o tratamento conservador é apropriado ou uma opção válida",
    "protocol": ["Passos do protocolo conservador"],
    "followUp": ["Seguimento e reavaliação"],
    "redFlags": ["Sinais de alerta que exigem reavaliação"],
    "conversionCriteria": ["Critérios para conversão à cirurgia"]
  },
  "surgicalOption": {
    "indicationCriteria": ["Critérios que justificam cirurgia"],
    "preferredTechnique": "Resumo da técnica preferencial",
    "patientPositioning": ["Posicionamento do paciente"],
    "surgicalApproach": "Abordagem / via de acesso",
    "reductionStrategy": ["Estratégia de redução"],
    "fixationStrategy": ["Estratégia de fixação / constructo"],
    "stepByStepTechnique": ["Técnica passo a passo"],
    "implantsAndMaterials": ["Implantes e materiais"],
    "fluoroscopyOrImagingControl": ["Controle por imagem"],
    "intraoperativeChecks": ["Verificações finais"],
    "closureAndImmobilization": ["Fechamento e imobilização"],
    "postoperativeProtocol": ["Protocolo pós-operatório"],
    "risksAndPitfalls": ["Riscos e armadilhas"],
    "contingencyPlan": {
      "title": "Plano B / Contingência",
      "summary": "",
      "scenarios": [
        {
          "problem": "",
          "action": ""
        }
      ],
      "intraoperativeDecisionPoints": [],
      "conversionOptions": []
    }
  },
  "clinicalInterpretation": "Interpretação clínica",
  "treatmentPlan": "Plano terapêutico prático",
  "surgicalIndicationReason": "Por que essa recomendação foi escolhida",
  "recommendations": ["Recomendações finais"]
}`;

  const strictPrompt = `${prompt}\n\nIMPORTANTE FINAL: Retorne somente JSON válido compatível com o schema acima. Não use tags legadas como [DIAGNÓSTICO], [DIAGNÓSTICOS_DIFERENCIAIS], [MATERIAIS], [TECNICA], [DICAS] ou [PLANO_B]. Não use markdown, texto corrido nem explicações fora do JSON.`;

  const response = await ai.models.generateContent({
    prompt: strictPrompt,
    imageBase64: cleanBase64 || undefined,
    mimeType: cleanBase64 ? mimeType : undefined,
    config: {
      temperature: 0.18,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          recommendation: { type: Type.STRING },
          selectedStrategy: { type: Type.STRING },
          diagnosis: { type: Type.STRING },
          anatomicalLocation: { type: Type.STRING },
          classification: { type: Type.STRING },
          severity: { type: Type.STRING },
          level: { type: Type.STRING },
          affectedRoot: { type: Type.STRING },
          differentialDiagnoses: { type: Type.ARRAY, items: { type: Type.STRING } },
          complementaryExams: { type: Type.ARRAY, items: { type: Type.STRING } },
          preoperativePlanning: { type: Type.ARRAY, items: { type: Type.STRING } },
          conservativePlan: {
            type: Type.OBJECT,
            properties: {
              indication: { type: Type.STRING },
              protocol: { type: Type.ARRAY, items: { type: Type.STRING } },
              followUp: { type: Type.ARRAY, items: { type: Type.STRING } },
              redFlags: { type: Type.ARRAY, items: { type: Type.STRING } },
              conversionCriteria: { type: Type.ARRAY, items: { type: Type.STRING } },
            },
            required: ["indication", "protocol", "followUp", "redFlags", "conversionCriteria"],
          },
          surgicalOption: {
            type: Type.OBJECT,
            properties: {
              indicationCriteria: { type: Type.ARRAY, items: { type: Type.STRING } },
              preferredTechnique: { type: Type.STRING },
              patientPositioning: { type: Type.ARRAY, items: { type: Type.STRING } },
              surgicalApproach: { type: Type.STRING },
              reductionStrategy: { type: Type.ARRAY, items: { type: Type.STRING } },
              fixationStrategy: { type: Type.ARRAY, items: { type: Type.STRING } },
              stepByStepTechnique: { type: Type.ARRAY, items: { type: Type.STRING } },
              implantsAndMaterials: { type: Type.ARRAY, items: { type: Type.STRING } },
              fluoroscopyOrImagingControl: { type: Type.ARRAY, items: { type: Type.STRING } },
              intraoperativeChecks: { type: Type.ARRAY, items: { type: Type.STRING } },
              closureAndImmobilization: { type: Type.ARRAY, items: { type: Type.STRING } },
              postoperativeProtocol: { type: Type.ARRAY, items: { type: Type.STRING } },
              risksAndPitfalls: { type: Type.ARRAY, items: { type: Type.STRING } },
              contingencyPlan: {
                type: Type.OBJECT,
                properties: {
                  title: { type: Type.STRING },
                  summary: { type: Type.STRING },
                  scenarios: {
                    type: Type.ARRAY,
                    items: {
                      type: Type.OBJECT,
                      properties: {
                        problem: { type: Type.STRING },
                        action: { type: Type.STRING }
                      }
                    }
                  },
                  intraoperativeDecisionPoints: { type: Type.ARRAY, items: { type: Type.STRING } },
                  conversionOptions: { type: Type.ARRAY, items: { type: Type.STRING } }
                }
              },
            },
            required: [
              "indicationCriteria",
              "preferredTechnique",
              "patientPositioning",
              "surgicalApproach",
              "reductionStrategy",
              "fixationStrategy",
              "stepByStepTechnique",
              "implantsAndMaterials",
              "fluoroscopyOrImagingControl",
              "intraoperativeChecks",
              "closureAndImmobilization",
              "postoperativeProtocol",
              "risksAndPitfalls",
              "contingencyPlan",
            ],
          },
          clinicalInterpretation: { type: Type.STRING },
          treatmentPlan: { type: Type.STRING },
          surgicalIndicationReason: { type: Type.STRING },
          recommendations: { type: Type.ARRAY, items: { type: Type.STRING } },
        },
        required: [
          "recommendation",
          "selectedStrategy",
          "diagnosis",
          "anatomicalLocation",
          "classification",
          "severity",
          "differentialDiagnoses",
          "complementaryExams",
          "preoperativePlanning",
          "conservativePlan",
          "surgicalOption",
          "clinicalInterpretation",
          "treatmentPlan",
          "surgicalIndicationReason",
          "recommendations",
        ],
      },
    },
  });

  const rawResponseText = typeof (response as any)?.text === "string"
    ? String((response as any).text || "")
    : JSON.stringify((response as any) || {});
  const normalized = normalizeSurgicalPlanResponse(rawResponseText);
  console.log("[SURGICAL_PLAN][normalized]", normalized);
  return JSON.stringify(normalized);
};

export type VisualDiagnosticCheck = {
  modality: string;
  views: string[];
  side: string | null;
  bone: string;
  joint: string;
  anatomicalRegion: string;
  specificLocation: string;
  suspectedDiagnosis: string;
  visualEvidence: string[];
  confidence: number;
  uncertaintyReason: string | null;
};

const normalizeVisualDiagnosticCheck = (value: unknown): VisualDiagnosticCheck => {
  const record = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  const toList = (input: unknown) => Array.isArray(input) ? input.map((item) => String(item).trim()).filter(Boolean) : [];
  return {
    modality: String(record.modality || "radiografia").trim(),
    views: toList(record.views),
    side: record.side ? String(record.side).trim() : null,
    bone: String(record.bone || "").trim(),
    joint: String(record.joint || "").trim(),
    anatomicalRegion: String(record.anatomicalRegion || "").trim(),
    specificLocation: String(record.specificLocation || "").trim(),
    suspectedDiagnosis: String(record.suspectedDiagnosis || "").trim(),
    visualEvidence: toList(record.visualEvidence),
    confidence: Number.isFinite(Number(record.confidence)) ? Number(record.confidence) : 0,
    uncertaintyReason: record.uncertaintyReason ? String(record.uncertaintyReason).trim() : null,
  };
};

export const generateVisualDiagnosticCheck = async (
  query: string,
  lang: Language = "pt",
  images: string[] = []
): Promise<VisualDiagnosticCheck> => {
  const ai = createGeminiClient("generateAtlasAnalysis");
  const imageBase64 = stripDataUrlPrefix(String(images?.[0] || "").trim());
  const prompt = `
Você é um radiologista musculoesquelético sênior.
Retorne APENAS JSON curto e objetivo em ${getClinicalResponseLanguageName(lang)}.
Analise a imagem e identifique modalidade, incidências, lateralidade, osso, articulação, região anatômica, localização específica, hipótese diagnóstica, evidências visuais, confiança e incerteza.

Contexto:
${String(query || "").trim()}
`.trim();

  const response = await ai.models.generateContent({
    prompt,
    imageBase64: imageBase64 || undefined,
    mimeType: imageBase64 ? "image/jpeg" : undefined,
    config: {
      temperature: 0.08,
      maxOutputTokens: 900,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          modality: { type: Type.STRING },
          views: { type: Type.ARRAY, items: { type: Type.STRING } },
          side: { type: Type.STRING },
          bone: { type: Type.STRING },
          joint: { type: Type.STRING },
          anatomicalRegion: { type: Type.STRING },
          specificLocation: { type: Type.STRING },
          suspectedDiagnosis: { type: Type.STRING },
          visualEvidence: { type: Type.ARRAY, items: { type: Type.STRING } },
          confidence: { type: Type.NUMBER },
          uncertaintyReason: { type: Type.STRING },
        },
      },
    },
  });

  const raw = typeof (response as any)?.text === "string"
    ? String((response as any).text || "{}")
    : JSON.stringify((response as any) || {});
  let parsed: unknown = {};
  try {
    parsed = JSON.parse(cleanJson(raw));
  } catch {
    parsed = {};
  }
  return normalizeVisualDiagnosticCheck(parsed);
};

export const generateSurgicalPlanningAnalysisJson = async (
  query: string,
  lang: Language = "pt",
  images: string[] = []
): Promise<string> => {
  const image = String(images?.[0] || "").trim();
  const visualDiagnosticCheck = await generateVisualDiagnosticCheck(query, lang, images).catch(() => null);
  return generateSurgicalPlanFast({
    clinicalContext: query,
    language: lang,
    fastMode: true,
    visualDiagnosticCheck: visualDiagnosticCheck || undefined,
    image: image ? { base64: image, mimeType: "image/jpeg" } : undefined,
  });
};

type SurgicalPlanFastPayload = {
  clinicalContext?: string;
  language?: Language;
  fastMode?: boolean;
  visualDiagnosticCheck?: Record<string, unknown>;
  image?: {
    base64?: string;
    mimeType?: string;
  };
};

const surgicalPlanV2Schema = {
  type: Type.OBJECT,
  properties: {
    diagnosis: { type: Type.STRING },
    anatomicalLocation: { type: Type.STRING },
    aoOtaClassification: { type: Type.STRING },
    managementDecision: {
      type: Type.OBJECT,
      properties: {
        type: { type: Type.STRING },
        rationale: { type: Type.STRING },
        surgicalCriteria: { type: Type.ARRAY, items: { type: Type.STRING } },
        conservativeCriteria: { type: Type.ARRAY, items: { type: Type.STRING } },
      },
      required: ["type", "rationale", "surgicalCriteria", "conservativeCriteria"],
    },
    surgicalTechniques: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          title: { type: Type.STRING },
          indication: { type: Type.ARRAY, items: { type: Type.STRING } },
          whenToChoose: { type: Type.ARRAY, items: { type: Type.STRING } },
          positioning: { type: Type.ARRAY, items: { type: Type.STRING } },
          surgicalApproach: { type: Type.ARRAY, items: { type: Type.STRING } },
          stepByStepTechnique: { type: Type.ARRAY, items: { type: Type.STRING } },
          implantsAndMaterials: { type: Type.ARRAY, items: { type: Type.STRING } },
          advantages: { type: Type.ARRAY, items: { type: Type.STRING } },
          limitationsAndRisks: { type: Type.ARRAY, items: { type: Type.STRING } },
          possibleComplications: { type: Type.ARRAY, items: { type: Type.STRING } },
          consolidationTime: { type: Type.ARRAY, items: { type: Type.STRING } },
          planB: { type: Type.ARRAY, items: { type: Type.STRING } },
          evidence: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                title: { type: Type.STRING },
                authors: { type: Type.STRING },
                journal: { type: Type.STRING },
                year: { type: Type.STRING },
                doi: { type: Type.STRING },
                url: { type: Type.STRING },
                relevance: { type: Type.STRING },
              },
              required: ["title", "relevance"],
            },
          },
        },
        required: [
          "title",
          "indication",
          "whenToChoose",
          "positioning",
          "surgicalApproach",
          "stepByStepTechnique",
          "implantsAndMaterials",
          "advantages",
          "limitationsAndRisks",
          "possibleComplications",
          "consolidationTime",
          "planB",
          "evidence",
        ],
      },
    },
    conservativeTreatment: {
      type: Type.OBJECT,
      properties: {
        indication: { type: Type.ARRAY, items: { type: Type.STRING } },
        immobilizationOrProtection: { type: Type.ARRAY, items: { type: Type.STRING } },
        medication: { type: Type.ARRAY, items: { type: Type.STRING } },
        rehabilitation: { type: Type.ARRAY, items: { type: Type.STRING } },
        followUp: { type: Type.ARRAY, items: { type: Type.STRING } },
        expectedRecovery: { type: Type.ARRAY, items: { type: Type.STRING } },
        failureCriteria: { type: Type.ARRAY, items: { type: Type.STRING } },
        conversionToSurgery: { type: Type.ARRAY, items: { type: Type.STRING } },
      },
      required: ["indication", "immobilizationOrProtection", "medication", "rehabilitation", "followUp", "expectedRecovery", "failureCriteria", "conversionToSurgery"],
    },
    postoperativeCare: {
      type: Type.OBJECT,
      properties: {
        immobilization: { type: Type.ARRAY, items: { type: Type.STRING } },
        rangeOfMotion: { type: Type.ARRAY, items: { type: Type.STRING } },
        physiotherapy: { type: Type.ARRAY, items: { type: Type.STRING } },
        weightBearing: { type: Type.ARRAY, items: { type: Type.STRING } },
        followUpImaging: { type: Type.ARRAY, items: { type: Type.STRING } },
        returnToFunction: { type: Type.ARRAY, items: { type: Type.STRING } },
        redFlags: { type: Type.ARRAY, items: { type: Type.STRING } },
      },
      required: ["immobilization", "rangeOfMotion", "physiotherapy", "weightBearing", "followUpImaging", "returnToFunction", "redFlags"],
    },
    intraoperativeContingencyPlan: { type: Type.ARRAY, items: { type: Type.STRING } },
    differentialDiagnosisAndAssociatedInjuries: { type: Type.ARRAY, items: { type: Type.STRING } },
    globalEvidence: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          title: { type: Type.STRING },
          authors: { type: Type.STRING },
          journal: { type: Type.STRING },
          year: { type: Type.STRING },
          doi: { type: Type.STRING },
          url: { type: Type.STRING },
          relevance: { type: Type.STRING },
        },
        required: ["title", "relevance"],
      },
    },
  },
  required: [
    "diagnosis",
    "anatomicalLocation",
    "aoOtaClassification",
    "managementDecision",
    "surgicalTechniques",
    "conservativeTreatment",
    "postoperativeCare",
    "intraoperativeContingencyPlan",
    "differentialDiagnosisAndAssociatedInjuries",
    "globalEvidence",
  ],
};

export const generateSurgicalPlanV2 = async (
  payload: SurgicalPlanFastPayload
): Promise<SurgicalPlanV2> => {
  const ai = createGeminiClient("generateSurgicalPlan");
  const language = payload.language || "pt";
  const responseLanguage = getClinicalResponseLanguageName(language);
  const cleanBase64 = stripDataUrlPrefix(String(payload.image?.base64 || "").trim());
  const mimeType = String(payload.image?.mimeType || "image/jpeg").trim() || "image/jpeg";
  const clinicalContext = String(payload.clinicalContext || "").trim();
  const visualDiagnosticCheck = payload.visualDiagnosticCheck || {};

  const basePrompt = `
You are a senior orthopedic surgeon specialized in trauma, musculoskeletal reconstruction, and preoperative planning.
Return ONLY valid JSON matching SurgicalPlanV2. Start with { and end with }. Do not use markdown.
Write all clinical content in ${responseLanguage}.

Absolute rules:
- Never use placeholders or generic institutional text.
- Never write "Nao informado pela IA", "Achado musculoesqueletico em avaliacao", "A IA organiza possibilidades", "decisao final e do medico", "conforme padrao da lesao", "sistema compativel", or "conteudo nao gerado".
- Do not force surgery: classify managementDecision as surgical, conservative, or indeterminate.
- If surgery is permitted, provide exactly 2 different real surgical techniques with different biomechanics, implants, and strategy.
- Each surgical step-by-step must be operational and include, when applicable: anesthesia, positioning, prep/draping, incision, exposure, neurovascular protection, reduction, provisional fixation, drills, screws, plates, wires, fluoroscopy AP/lateral, stability test, irrigation, closure, and initial immobilization.
- Materials must be specific: plate type, screw diameter, approximate quantity/length, K-wires, drills, washers, cerclage, graft, image intensifier, and instruments when applicable.
- Conservative treatment must match the pathology group. Do not use fracture templates for lumbar disc herniation, degenerative disease, tendon/ligament injury, meniscus, or chondral pathology.
- Evidence must be specific to the anatomy and technique. Use real article/guideline titles and URLs/DOIs only when you are confident.

Clinical context:
${clinicalContext}

Visual diagnostic check:
${JSON.stringify(visualDiagnosticCheck)}
`.trim();

  const callModel = async (prompt: string) =>
    ai.models.generateContent({
      prompt,
      imageBase64: cleanBase64 || undefined,
      mimeType: cleanBase64 ? mimeType : undefined,
      fastMode: false,
      clinicalContext,
      language,
      visualDiagnosticCheck,
      config: {
        temperature: 0.12,
        maxOutputTokens: 16000,
        responseMimeType: "application/json",
        responseSchema: surgicalPlanV2Schema,
      },
    });

  const extractModelText = (response: unknown) => {
    if (typeof response === "string") return response;
    const record = response as any;
    return String(
      record?.text ||
      record?.candidates?.[0]?.content?.parts?.[0]?.text ||
      record?.response?.text ||
      ""
    );
  };

  const parsePlan = (response: unknown) => {
    const rawText = extractModelText(response);
    console.log("[SurgicalPlanV2][parser_input]", {
      responseType: typeof response,
      hasText: Boolean((response as any)?.text),
      textPreview: rawText.slice(0, 120),
    });
    const parsed = extractJsonObject(rawText);
    console.log("[SurgicalPlanV2][parsed_json]", parsed);
    return {
      rawText,
      parsed,
      plan: normalizeSurgicalPlanV2(parsed),
    };
  };

  const raw = await callModel(basePrompt);
  console.log("[SurgicalPlanV2][raw_ai_response]", raw);
  let parsedResult = parsePlan(raw);
  let plan = parsedResult.plan;
  let validation = validateSurgicalPlanV2(plan);
  console.log("[SurgicalPlanV2][validation]", { ...validation, summary: summarizeSurgicalPlanV2(plan) });

  if (!validation.valid) {
    const repairPrompt = `
Your previous SurgicalPlanV2 JSON was incomplete or generic.
Repair it once. Return ONLY complete valid JSON, no markdown.
Fix these validation problems: ${validation.reasons.join(", ")}.
Preserve the same diagnosis/anatomy if present and fill missing clinical fields with specific orthopedic content, not placeholders.

Previous JSON:
${parsedResult.rawText}
`.trim();
    console.warn("[SurgicalPlanV2][repair_start]", validation.reasons);
    const repairedRaw = await callModel(repairPrompt);
    console.log("[SurgicalPlanV2][repair_raw_ai_response]", repairedRaw);
    parsedResult = parsePlan(repairedRaw);
    plan = parsedResult.plan;
    validation = validateSurgicalPlanV2(plan);
    console.log("[SurgicalPlanV2][repair_validation]", { ...validation, summary: summarizeSurgicalPlanV2(plan) });
  }

  if (!plan || !validation.valid) {
    throw new Error("Nao foi possivel gerar um plano cirurgico especifico. Tente novamente ou informe o diagnostico/localizacao.");
  }

  return plan;
};

export const generateSurgicalPlanFast = async (
  payload: SurgicalPlanFastPayload
): Promise<string> => {
  const ai = createGeminiClient("generateSurgicalPlan");
  const language = payload.language || "pt";
  const responseLanguage = getClinicalResponseLanguageName(language);
  const cleanBase64 = stripDataUrlPrefix(String(payload.image?.base64 || "").trim());
  const mimeType = String(payload.image?.mimeType || "image/jpeg").trim() || "image/jpeg";
  const fastMode = payload.fastMode !== false;
  const strictLanguageRule = language === "en"
    ? `Return ALL medical content, headings, labels, techniques, explanations, evidence summaries, materials, recommendations and fallback text strictly in English. Never mix languages. Do not return Portuguese text unless the selected language is Portuguese.`
    : language === "es"
      ? `Devuelve TODO el contenido médico, encabezados, etiquetas, técnicas, explicaciones, resúmenes de evidencia, materiales, recomendaciones y texto de respaldo estrictamente en español. Nunca mezcles idiomas. No devuelvas texto en portugués a menos que el idioma seleccionado sea portugués.`
      : `Retorne TODO o conteúdo médico, títulos, rótulos, técnicas, explicações, resumos de evidências, materiais, recomendações e texto de fallback estritamente em português. Nunca misture idiomas. Não retorne texto em inglês a menos que o idioma selecionado seja inglês.`;

  const prompt = `
Você é um cirurgião ortopédico de trauma e radiologista musculoesquelético sênior.
Responda APENAS com JSON válido, curto e objetivo, em ${responseLanguage}.
Use a checagem visual como fonte principal.
Não gere evidências, DOI, PubMed, relatório ou texto longo.
Se houver indicação cirúrgica, retorne exatamente 2 técnicas compatíveis.
Se o caso for conservador, descreva conduta e critérios de conversão.
- ${strictLanguageRule}
- Não use linguagem genérica nem placeholders como "conforme anatomia", "conforme padrão da lesão", "sistema compatível", "confirmar diagnóstico", "definir abordagem", "a decisão final é do médico", "não informado pela IA", "achado musculoesquelético em avaliação" ou "localização anatômica a confirmar".
- Cada técnica cirúrgica deve ser operatória e específica ao caso, com biomecânica, implante e estratégia diferentes entre si.
- Cada técnica deve conter: indicação específica, quando escolher, posicionamento do paciente, via de acesso, anestesia/preparo/campos, incisão, exposição, redução, fios guia/K-wires quando aplicável, brocas, parafusos/placa/arruelas/cerclagem/enxerto quando aplicável, controle fluoroscópico AP/perfil, teste de estabilidade, irrigação, fechamento e imobilização inicial.
- Materiais devem citar tipo de implante, diâmetro/comprimento aproximado, quantidade e instrumental principal. Não aceite "sistema compatível" como resposta.
- Se a técnica for para patela, privilegie nomes reais como "cerclagem em oito / tension band wiring", "parafusos canulados com arruelas" ou "placa anatômica bloqueada anterior" quando apropriado.
- Se a primeira resposta vier genérica ou incompleta, o sistema fará 1 retry de reparo; portanto já entregue conteúdo específico.
- Se houver cirurgia, produza 2 técnicas realmente distintas em biomecânica, implante e filosofia cirúrgica; se a cirurgia não for indicada, deixe o caminho conservador claramente estruturado sem inventar técnica cirúrgica rasa.

CONTEXTO CLÍNICO:
${String(payload.clinicalContext || "").trim()}

CHECAGEM VISUAL:
${JSON.stringify(payload.visualDiagnosticCheck || {})}
`.trim();

  const generateSurgicalPlanResponse = async (promptText: string) =>
    ai.models.generateContent({
      prompt: promptText,
      imageBase64: cleanBase64 || undefined,
      mimeType: cleanBase64 ? mimeType : undefined,
      fastMode,
      clinicalContext: String(payload.clinicalContext || "").trim(),
      language,
      visualDiagnosticCheck: payload.visualDiagnosticCheck,
      config: {
        temperature: 0.08,
        maxOutputTokens: 1600,
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            diagnosis: { type: Type.STRING },
            anatomicalLocation: { type: Type.STRING },
            lesionClassification: { type: Type.STRING },
            severity: { type: Type.STRING },
            classification: {
              type: Type.OBJECT,
              properties: {
                system: { type: Type.STRING },
                code: { type: Type.STRING },
                isApproximate: { type: Type.BOOLEAN },
                severity: { type: Type.STRING },
                description: { type: Type.STRING },
                rationale: { type: Type.STRING },
              },
              required: ["system", "code", "isApproximate", "severity", "description", "rationale"],
            },
            confidence: { type: Type.NUMBER },
            treatmentStrategy: { type: Type.STRING },
            conservativePlan: { type: Type.STRING },
            surgicalPlan: { type: Type.STRING },
            surgicalTechniques: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  title: { type: Type.STRING },
                  isMostCurrent: { type: Type.BOOLEAN },
                  isAiRecommended: { type: Type.BOOLEAN },
                  confidence: { type: Type.NUMBER },
                  recommendationReason: { type: Type.STRING },
                  indication: { type: Type.STRING },
                  whenToChoose: { type: Type.STRING },
                  patientPositioning: { type: Type.ARRAY, items: { type: Type.STRING } },
                  surgicalApproach: { type: Type.STRING },
                  advantages: { type: Type.ARRAY, items: { type: Type.STRING } },
                  limitations: { type: Type.ARRAY, items: { type: Type.STRING } },
                  materials: { type: Type.ARRAY, items: { type: Type.STRING } },
                  stepByStep: { type: Type.ARRAY, items: { type: Type.STRING } },
                  practicalNotes: { type: Type.ARRAY, items: { type: Type.STRING } },
                  fluoroscopyOrImagingControl: { type: Type.ARRAY, items: { type: Type.STRING } },
                  intraoperativeChecks: { type: Type.ARRAY, items: { type: Type.STRING } },
                  closureAndImmobilization: { type: Type.ARRAY, items: { type: Type.STRING } },
                  postoperativeProtocol: { type: Type.ARRAY, items: { type: Type.STRING } },
                  planB: { type: Type.ARRAY, items: { type: Type.STRING } },
                  consolidationTime: { type: Type.STRING },
                },
                required: [
                  "title",
                  "isMostCurrent",
                  "isAiRecommended",
                  "confidence",
                  "recommendationReason",
                  "indication",
                  "whenToChoose",
                  "patientPositioning",
                  "surgicalApproach",
                  "advantages",
                  "limitations",
                  "materials",
                  "stepByStep",
                  "practicalNotes",
                  "fluoroscopyOrImagingControl",
                  "intraoperativeChecks",
                  "closureAndImmobilization",
                  "postoperativeProtocol",
                  "planB",
                  "consolidationTime",
                ],
              },
            },
            techniqueComparison: { type: Type.STRING },
            selectionCriteria: { type: Type.STRING },
            materials: { type: Type.ARRAY, items: { type: Type.STRING } },
            materialList: { type: Type.ARRAY, items: { type: Type.STRING } },
            contingencyPlan: { type: Type.ARRAY, items: { type: Type.STRING } },
            stepByStepTechnique: { type: Type.ARRAY, items: { type: Type.STRING } },
            risks: { type: Type.ARRAY, items: { type: Type.STRING } },
            planB: { type: Type.STRING },
            postoperativeCare: { type: Type.STRING },
            differentialDiagnosis: { type: Type.ARRAY, items: { type: Type.STRING } },
            recommendations: { type: Type.ARRAY, items: { type: Type.STRING } },
            limitations: { type: Type.ARRAY, items: { type: Type.STRING } },
          },
          required: [
            "diagnosis",
            "anatomicalLocation",
            "lesionClassification",
            "severity",
            "classification",
            "confidence",
            "treatmentStrategy",
            "conservativePlan",
            "surgicalPlan",
            "surgicalTechniques",
            "techniqueComparison",
            "selectionCriteria",
            "materials",
            "stepByStepTechnique",
            "risks",
            "planB",
            "postoperativeCare",
            "differentialDiagnosis",
            "recommendations",
            "limitations",
          ],
        },
      },
    });

  const parseRawSurgicalPlan = (rawText: string) => {
    const parsed = parseJsonObjectFromAiText(rawText) || (() => {
      try {
        return JSON.parse(cleanJson(rawText));
      } catch {
        return null;
      }
    })();
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Record<string, any>) : null;
  };

  const getTextBlocks = (parsed: Record<string, any> | null) => {
    const techniques = Array.isArray(parsed?.surgicalTechniques) ? parsed?.surgicalTechniques : [];
    return [
      parsed?.diagnosis,
      parsed?.anatomicalLocation,
      parsed?.lesionClassification,
      parsed?.surgicalPlan,
      parsed?.justification,
      parsed?.classificationReason,
      parsed?.treatmentPlan,
      parsed?.surgicalIndicationReason,
      ...(Array.isArray(parsed?.materials) ? parsed.materials : []),
      ...(Array.isArray(parsed?.materialList) ? parsed.materialList : []),
      ...(Array.isArray(parsed?.stepByStepTechnique) ? parsed.stepByStepTechnique : []),
      ...(Array.isArray(parsed?.risks) ? parsed.risks : []),
      parsed?.planB,
      parsed?.postoperativeCare,
      ...(Array.isArray(parsed?.differentialDiagnosis) ? parsed.differentialDiagnosis : []),
      ...(Array.isArray(parsed?.recommendations) ? parsed.recommendations : []),
      ...techniques.flatMap((item: any) => [
        item?.title,
        item?.indication,
        item?.whenToChoose,
        ...(Array.isArray(item?.patientPositioning) ? item.patientPositioning : []),
        item?.surgicalApproach,
        ...(Array.isArray(item?.advantages) ? item.advantages : []),
        ...(Array.isArray(item?.limitations) ? item.limitations : []),
        ...(Array.isArray(item?.materials) ? item.materials : []),
        ...(Array.isArray(item?.stepByStep) ? item.stepByStep : []),
        ...(Array.isArray(item?.practicalNotes) ? item.practicalNotes : []),
        ...(Array.isArray(item?.fluoroscopyOrImagingControl) ? item.fluoroscopyOrImagingControl : []),
        ...(Array.isArray(item?.intraoperativeChecks) ? item.intraoperativeChecks : []),
        ...(Array.isArray(item?.closureAndImmobilization) ? item.closureAndImmobilization : []),
        ...(Array.isArray(item?.postoperativeProtocol) ? item.postoperativeProtocol : []),
        ...(Array.isArray(item?.planB) ? item.planB : []),
        item?.consolidationTime,
      ]),
    ]
      .map((item) => normalizeClinicalText(String(item || "")))
      .filter(Boolean);
  };

  const genericMarkers = [
    "confirmar diagnostico",
    "definir abordagem",
    "conforme anatomia",
    "conforme padrao da lesao",
    "sistema compativel",
    "decisao final e do medico",
    "nao informado pela ia",
    "achado musculoesqueletico em avaliacao",
    "localizacao anatomica a confirmar",
    "conforme segmento anatomico",
    "confirmar tecnica",
  ];

  const needsRepair = (parsed: Record<string, any> | null, rawText: string) => {
    const blocks = getTextBlocks(parsed);
    const text = normalizeClinicalText([rawText, ...blocks].join(" "));
    if (!text) return ["empty_response"];
    const reasons = genericMarkers.filter((marker) => text.includes(marker));
    if (reasons.length > 0) return reasons;
    const techCount = Array.isArray(parsed?.surgicalTechniques) ? parsed.surgicalTechniques.length : 0;
    if (techCount === 0 && /cirurg/i.test(text) && /patela|úmero|humer|escafoide|tíbia|tibia|coluna|spine|clav/i.test(text)) {
      return ["techniques_missing"];
    }
    return [];
  };

  const renderResponse = async (promptText: string, repair = false) => {
    const response = await generateSurgicalPlanResponse(
      repair
        ? `${promptText}\n\nA resposta anterior foi genérica ou incompleta. Refaça o JSON preenchendo especificamente os campos genéricos com conteúdo técnico real. Não use placeholders como "conforme anatomia", "sistema compatível", "confirmar diagnóstico", "definir abordagem", "a decisão final é do médico" ou "não informado pela IA". Garanta que as 2 técnicas, quando houver indicação cirúrgica, sejam realmente diferentes em biomecânica, implante e estratégia.`
        : promptText
    );

    const rawText = typeof (response as any)?.text === "string"
      ? String((response as any).text || "{}")
      : JSON.stringify((response as any) || {});
    const parsed = parseRawSurgicalPlan(rawText);
    const genericIssues = needsRepair(parsed, rawText);

    if (!repair && genericIssues.length > 0) {
      console.warn("[SurgicalPlan][generic_content_detected]", {
        reasons: genericIssues,
        preview: rawText.slice(0, 240),
      });
      return renderResponse(promptText, true);
    }

    if (repair && genericIssues.length > 0) {
      console.warn("[SurgicalPlan][generic_content_persisted_after_retry]", {
        reasons: genericIssues,
        preview: rawText.slice(0, 240),
      });
    }

    return rawText;
  };

  return cleanJson(await renderResponse(prompt));
};

export type ScientificEvidenceItem = {
  title: string;
  source: string;
  url: string;
  year: string;
  evidenceLevel: string;
  summary: string;
};

export type StructuredSurgicalPlanEvidence = {
  diagnosis: string;
  differentialDiagnosis: string[];
  materials: string[];
  justification: string;
  surgicalIndication: 'surgical' | 'relative' | 'non-surgical';
  isSurgical?: boolean;
  classificationReason?: string;
  surgicalTechnique: string;
  scientificEvidence: ScientificEvidenceItem[];
  evidenceSearchTerms?: string[];
  medicalDisclaimer: string;
  tips?: string;
  planB?: string;
  primaryStructureType?: "ossea" | "ligamentar" | "tendinea" | "meniscal" | "mista" | "indeterminada";
  primaryStructureName?: string;
  associatedStructures?: string[];
  lesionConfidence?: "baixa" | "moderada" | "alta";
};

export const generateScientificSurgicalPlanText = async (
  query: string,
  lang: Language = 'pt',
  caseContext?: StructuredFractureCase,
  options?: {
    concise?: boolean;
  }
) => {
  const ai = createGeminiClient("generateSurgicalPlan");
  const frozenCaseBlock = "";
  const useConcisePrompt = Boolean(options?.concise);
  const concisePrompt = (lang === 'en'
    ? `Act as a senior orthopedic surgeon. Create a concise structured planning summary for: "${query}".${frozenCaseBlock}

Return STRICT JSON with:
{
  "diagnosis": "Main diagnosis",
  "differentialDiagnosis": ["2 to 3 close differentials"],
  "materials": ["Key implants/materials if needed"],
  "justification": "Short justification",
  "surgicalIndication": "surgical | relative | non-surgical",
  "classificationReason": "1 short sentence",
  "surgicalTechnique": "Brief technique or conservative outline",
  "scientificEvidence": [{"title":"Short reference","source":"AAOS/AO Foundation/PubMed","url":"https://pubmed.ncbi.nlm.nih.gov/","year":"2024","evidenceLevel":"Guideline or review","summary":"Short support"}],
  "medicalDisclaimer": "Medical disclaimer",
  "tips": "Short tips",
  "planB": "Short fallback plan"
}

Rules:
- Keep it short and case-specific.
- Do not invent details.
- If uncertain, prefer relative or non-surgical.
- Always include the anatomical location and likely conduct.
- Return 1 to 3 scientific references only.`
    : lang === 'es'
      ? `Actúa como cirujano ortopédico senior. Crea un resumen estructurado y breve para: "${query}".${frozenCaseBlock}

Devuelve JSON ESTRICTO con:
{
  "diagnosis": "Diagnóstico principal",
  "differentialDiagnosis": ["2 a 3 diferenciales cercanos"],
  "materials": ["Implantes/materiales clave si son necesarios"],
  "justification": "Justificación breve",
  "surgicalIndication": "surgical | relative | non-surgical",
  "classificationReason": "1 frase corta",
  "surgicalTechnique": "Técnica breve o esquema conservador",
  "scientificEvidence": [{"title":"Referencia breve","source":"AAOS/AO Foundation/PubMed","url":"https://pubmed.ncbi.nlm.nih.gov/","year":"2024","evidenceLevel":"Guía o revisión","summary":"Apoyo breve"}],
  "medicalDisclaimer": "Aviso médico",
  "tips": "Consejos breves",
  "planB": "Plan alternativo breve"
}

Reglas:
- Manténlo breve y específico del caso.
- No inventes detalles.
- Si hay duda, prefiere relative o non-surgical.
- Incluye siempre la localización anatómica y la conducta probable.
- Devuelve solo 1 a 3 referencias científicas.`
      : `Atue como cirurgião ortopédico sênior. Crie um resumo estruturado e breve para: "${query}".${frozenCaseBlock}

Retorne JSON ESTRITO com:
{
  "diagnosis": "Diagnóstico principal",
  "differentialDiagnosis": ["2 a 3 diferenciais próximos"],
  "materials": ["Materiais/implantes-chave se necessários"],
  "justification": "Justificativa breve",
  "surgicalIndication": "surgical | relative | non-surgical",
  "classificationReason": "1 frase curta",
  "surgicalTechnique": "Técnica breve ou conduta conservadora",
  "scientificEvidence": [{"title":"Referência breve","source":"AAOS/AO Foundation/PubMed","url":"https://pubmed.ncbi.nlm.nih.gov/","year":"2024","evidenceLevel":"Diretriz ou revisão","summary":"Suporte breve"}],
  "medicalDisclaimer": "Aviso médico",
  "tips": "Dicas breves",
  "planB": "Plano alternativo breve"
}

Regras:
- Mantenha curto e específico do caso.
- Não invente detalhes.
- Se houver dúvida, prefira relative ou non-surgical.
- Inclua sempre a localização anatômica e a conduta provável.
- Retorne apenas 1 a 3 referências científicas.`);
  const prompt = (lang === 'en'
    ? `Act as a senior orthopedic surgeon. Create a structured surgical planning report for: "${query}".${frozenCaseBlock}

       Return STRICT JSON with these fields:
         {
           "diagnosis": "Technical diagnosis and classification",
           "differentialDiagnosis": ["3 to 5 clinically plausible differentials from the same anatomical region"],
           "materials": ["Specific implants/instruments, one item per entry"],
           "justification": "Short clinical justification for the chosen strategy",
           "surgicalIndication": "surgical | relative | non-surgical",
           "classificationReason": "1 to 2 short sentences explaining the classification",
           "surgicalTechnique": "Step-by-step surgical technique",
         "scientificEvidence": [
           {
             "title": "Real article/guideline title",
             "source": "AAOS, AO Foundation, PubMed, society name, guideline body, systematic review, or meta-analysis",
             "url": "Real clickable source URL",
             "year": "Publication year",
             "evidenceLevel": "Evidence level if known",
             "summary": "Short evidence summary linked to this decision"
           }
         ],
         "medicalDisclaimer": "Medical disclaimer",
         "tips": "Surgical pearls and pitfalls",
         "planB": "Contingency plan if primary fixation fails"
       }

       Rules:
       - Do not invent references, DOI, URLs, or publication years.
       - If you are not sure a citation is real, keep scientificEvidence filled with short, general but reliable orthopedic references instead of returning an empty array.
       - Prioritize recognized sources such as AAOS, AO Foundation, PubMed, official guidelines, recognized orthopedic societies, systematic reviews, meta-analyses, and consensus statements.
       - Set surgicalIndication to "surgical" only when there is a clear surgical indication supported by orthopedic evidence.
       - Set surgicalIndication to "relative" when surgery may be needed, but an initial conservative approach is reasonable and clinically acceptable.
       - Set surgicalIndication to "non-surgical" when the best-supported management is conservative or when surgery is not clearly indicated.
       - If the frozen case confidence is low or moderate, prefer "relative" or "non-surgical"; do not force "surgical".
       - Consider instability, displacement, articular incongruity, fragmentation, neurologic compression, progressive neurologic deficit, major functional loss, and urgent fixation/decompression/reconstruction patterns when deciding "surgical".
       - Use "relative" when surgery may be needed, but pain, function, evolution, response to conservative treatment, and clinical correlation still justify an initial conservative trial.
       - Use "non-surgical" when conservative treatment is the standard initial approach and there are no important structural, neurologic, or vascular red flags.
       - Do not classify everything as conservative by default.
       - Provide classificationReason as 1 or 2 short sentences citing stability, displacement, compression, function, urgency, or progression risk.
       - If the frozen case confidence is low or moderate, keep surgicalTechnique generic or deferred and avoid a specific access/fixation plan.
       - Do not default to surgery without a clear indication.
       - Keep differential diagnoses limited to the same bone/joint segment and avoid unrelated conditions.`
    : (lang === 'es'
      ? `ActÃºa como un cirujano ortopÃ©dico senior. Crea un informe estructurado de planificaciÃ³n quirÃºrgica para: "${query}".${frozenCaseBlock}

         Devuelve JSON ESTRICTO con estos campos:
         {
           "diagnosis": "DiagnÃ³stico tÃ©cnico y clasificaciÃ³n",
           "differentialDiagnosis": ["3 a 5 diferenciales clÃ­nicamente plausibles de la misma regiÃ³n anatÃ³mica"],
           "materials": ["Lista de implantes/instrumental, un Ã­tem por entrada"],
           "justification": "JustificaciÃ³n clÃ­nica breve de la estrategia",
           "classificationReason": "1 o 2 frases cortas explicando la clasificaciÃ³n",
           "surgicalIndication": "surgical | relative | non-surgical",
           "surgicalTechnique": "TÃ©cnica quirÃºrgica paso a paso",
           "scientificEvidence": [
             {
               "title": "TÃ­tulo real de artÃ­culo/guÃ­a",
               "source": "AAOS, AO Foundation, PubMed, sociedad ortopÃ©dica, guÃ­a oficial, revisiÃ³n sistemÃ¡tica o metaanÃ¡lisis",
               "url": "URL real y clicable",
               "year": "AÃ±o de publicaciÃ³n",
               "evidenceLevel": "Nivel de evidencia si se conoce",
               "summary": "Resumen breve de la evidencia"
             }
           ],
           "medicalDisclaimer": "Aviso mÃ©dico",
           "tips": "Trucos y consejos quirÃºrgicos",
           "planB": "Plan de contingencia"
         }

         Reglas:
         - No inventes referencias, DOI, URLs ni aÃ±os de publicaciÃ³n.
         - Si no tienes seguridad sobre una cita, mantÃ©n scientificEvidence lleno con referencias ortopÃ©dicas generales confiables en lugar de devolver un array vacÃ­o.
         - Prioriza fuentes reconocidas como AAOS, AO Foundation, PubMed, guÃ­as oficiales, sociedades ortopÃ©dicas reconocidas, revisiones sistemÃ¡ticas, metaanÃ¡lisis y consensos.
         - Considera inestabilidad, desplazamiento, incongruencia articular, fragmentaciÃ³n, compresiÃ³n neurolÃ³gica, dÃ©ficit neurolÃ³gico progresivo, pÃ©rdida funcional importante y patrones tÃ­picos de fijaciÃ³n/descompresiÃ³n/reconstrucciÃ³n al decidir "surgical".
         - Usa "relative" cuando la cirugÃ­a pueda ser necesaria, pero el dolor, la funciÃ³n, la evoluciÃ³n, la respuesta al tratamiento conservador y la correlaciÃ³n clÃ­nica aÃºn justifiquen una prueba conservadora inicial.
         - Usa "non-surgical" cuando el tratamiento conservador sea el abordaje inicial estÃ¡ndar y no existan red flags estructurales, neurolÃ³gicas o vasculares importantes.
         - No clasifiques todo como conservador por defecto.
         - Proporciona classificationReason en 1 o 2 frases breves citando estabilidad, desplazamiento, compresiÃ³n, funciÃ³n, urgencia o riesgo de progresiÃ³n.
         - MantÃ©n los diferenciales en la misma regiÃ³n anatÃ³mica y evita condiciones no relacionadas.`
      : `Atue como um cirurgiÃ£o ortopÃ©dico sÃªnior. Crie um relatÃ³rio estruturado de planejamento cirÃºrgico para: "${query}".${frozenCaseBlock}

         Retorne JSON ESTRITO com estes campos:
         {
           "diagnosis": "DiagnÃ³stico tÃ©cnico e classificaÃ§Ã£o",
           "differentialDiagnosis": ["3 a 5 diagnÃ³sticos diferenciais plausÃ­veis da mesma regiÃ£o anatÃ´mica"],
           "materials": ["Lista de implantes/instrumentais, um item por entrada"],
           "justification": "Justificativa clÃ­nica breve da estratÃ©gia",
           "classificationReason": "1 ou 2 frases curtas explicando a classificaÃ§Ã£o",
           "surgicalIndication": "surgical | relative | non-surgical",
           "surgicalTechnique": "TÃ©cnica cirÃºrgica passo a passo",
           "scientificEvidence": [
             {
               "title": "TÃ­tulo real de artigo/diretriz",
               "source": "AAOS, AO Foundation, PubMed, sociedade ortopÃ©dica, diretriz oficial, revisÃ£o sistemÃ¡tica ou meta-anÃ¡lise",
               "url": "URL real e clicÃ¡vel",
               "year": "Ano de publicaÃ§Ã£o",
               "evidenceLevel": "NÃ­vel de evidÃªncia se conhecido",
               "summary": "Resumo curto da evidÃªncia"
             }
           ],
           "medicalDisclaimer": "Aviso mÃ©dico",
           "tips": "Dicas, macetes e armadilhas (pearls and pitfalls)",
           "planB": "Plano de contingÃªncia / EstratÃ©gia de resgate caso a estabilidade nÃ£o seja alcanÃ§ada"
         }

         Regras:
         - NÃ£o invente referÃªncias, DOI, URLs nem anos de publicaÃ§Ã£o.
         - Se nÃ£o tiver certeza de uma citaÃ§Ã£o, mantenha scientificEvidence preenchido com referÃªncias ortopÃ©dicas gerais confiÃ¡veis em vez de devolver um array vazio.
         - Priorize fontes reconhecidas como AAOS, AO Foundation, PubMed, diretrizes oficiais, sociedades ortopÃ©dicas reconhecidas, revisÃµes sistemÃ¡ticas, meta-anÃ¡lises e consensos.
         - Considere instabilidade, desvio, incongruÃªncia articular, fragmentaÃ§Ã£o, compressÃ£o neurolÃ³gica, dÃ©ficit neurolÃ³gico progressivo, perda funcional importante e padrÃµes tÃ­picos de fixaÃ§Ã£o/descompressÃ£o/reconstruÃ§Ã£o ao decidir "surgical".
         - Use "relative" quando a cirurgia possa ser necessÃ¡ria, mas dor, funÃ§Ã£o, evoluÃ§Ã£o, resposta ao tratamento conservador e correlaÃ§Ã£o clÃ­nica ainda justifiquem uma tentativa conservadora inicial.
         - Use "non-surgical" quando o tratamento conservador for a abordagem inicial padrÃ£o e nÃ£o houver red flags estruturais, neurolÃ³gicas ou vasculares importantes.
         - NÃ£o classifique tudo como conservador por padrÃ£o.
         - Mantenha os diagnÃ³sticos diferenciais na mesma regiÃ£o anatÃ´mica e evite condiÃ§Ãµes nÃ£o relacionadas.`))
    + `\n\n${getSelectedLanguageInstruction(lang)}`;

  const surgicalIndicatorInstruction = (lang === 'en'
    ? `Classify surgicalIndication explicitly as one of: "surgical", "relative", or "non-surgical". Use "surgical" only for clear evidence-supported surgical indication. Use "relative" when surgery may be needed, but an initial conservative approach is reasonable and clinically acceptable. Use "non-surgical" when conservative management is best supported or surgery is not clearly indicated. Do not default to surgery without clear indication.`
    : lang === 'es'
      ? `Clasifica surgicalIndication de forma explÃ­cita como uno de estos valores: "surgical", "relative" o "non-surgical". Usa "surgical" solo cuando exista una indicaciÃ³n quirÃºrgica clara respaldada por evidencia. Usa "relative" cuando la cirugÃ­a pueda ser necesaria, pero un abordaje conservador inicial sea razonable y clÃ­nicamente aceptable. Usa "non-surgical" cuando la mejor conducta sea conservadora o la cirugÃ­a no estÃ© claramente indicada. No asumas cirugÃ­a sin indicaciÃ³n clara.`
      : `Classifique surgicalIndication explicitamente como um destes valores: "surgical", "relative" ou "non-surgical". Use "surgical" apenas quando houver indicaÃ§Ã£o cirÃºrgica clara e respaldada por evidÃªncia. Use "relative" quando a cirurgia possa ser necessÃ¡ria, mas uma conduta conservadora inicial seja razoÃ¡vel e clinicamente aceitÃ¡vel. Use "non-surgical" quando a melhor conduta for conservadora ou quando a cirurgia nÃ£o estiver claramente indicada. NÃ£o assuma cirurgia sem indicaÃ§Ã£o clara.`);

  const evidenceSearchInstruction = (lang === 'en'
    ? `If you cannot confirm a real citation with confidence, still return 1 to 4 general but reliable orthopedic references and provide evidenceSearchTerms with case-specific search phrases for manual validation.`
    : lang === 'es'
      ? `Si no puedes confirmar una cita real con seguridad, devuelve 1 a 4 referencias ortopÃ©dicas generales y proporciona evidenceSearchTerms con frases de bÃºsqueda especÃ­ficas del caso para validaciÃ³n manual.`
      : `Se nÃ£o conseguir confirmar uma citaÃ§Ã£o real com seguranÃ§a, retorne 1 a 4 referÃªncias ortopÃ©dicas gerais e forneÃ§a evidenceSearchTerms com frases de busca especÃ­ficas do caso para validaÃ§Ã£o manual.`);

  const scientificEvidenceStyleInstruction = (lang === 'en'
    ? `scientificEvidence STYLE:
- Keep it short: 1 to 4 items max.
- Each item must be a concise source/topic line, not a paragraph.
- Prefer formats like "AO Surgery Reference - scaphoid fractures", "AAOS", "SBOT", "PubMed", or "Herbert classification - scaphoid waist".
- Do not repeat the surgical technique inside scientificEvidence.
- If no reliable citation is confirmed, still return 1 to 4 general but reliable orthopedic references.`
    : lang === 'es'
      ? `ESTILO de scientificEvidence:
- MantÃ©nlo corto: 1 a 4 Ã­tems como mÃ¡ximo.
- Cada Ã­tem debe ser una lÃ­nea breve de fuente/tema, no un pÃ¡rrafo.
- Prefiere formatos como "AO Surgery Reference - fracturas de escafoides", "AAOS", "SBOT", "PubMed" o "Herbert classification - cintura del escafoides".
- No repitas la tÃ©cnica quirÃºrgica dentro de scientificEvidence.
- Si no se confirma una cita fiable, devuelve 1 a 4 referencias ortopÃ©dicas generales.`
      : `ESTILO de scientificEvidence:
- Mantenha curto: no mÃ¡ximo 1 a 4 itens.
- Cada item deve ser uma linha breve de fonte/tema, nÃ£o um parÃ¡grafo.
- Prefira formatos como "AO Surgery Reference - fraturas de escafoide", "AAOS", "SBOT", "PubMed" ou "Herbert classification - cintura do escafoide".
- NÃ£o repita a tÃ©cnica cirÃºrgica dentro de scientificEvidence.
- Se nenhuma citaÃ§Ã£o confiÃ¡vel for confirmada, devolva 1 a 4 referÃªncias ortopÃ©dicas gerais.`);

  const scientificEvidenceCompletenessInstruction = (lang === 'en'
    ? `scientificEvidence COMPLETENESS:
- Always return 1 to 4 scientificEvidence items. Never return an empty array.
- If a case-specific citation cannot be confirmed, use general but reliable orthopedic references related to the diagnosis or technique.
- Keep each item short and practical. Do not add long explanations.
- Do not repeat the surgical technique inside scientificEvidence.
- Favor AO Surgery Reference, AAOS, PubMed, official guidelines, recognized orthopedic societies, systematic reviews, meta-analyses, and consensus statements.`
    : lang === 'es'
      ? `COMPLETITUD de scientificEvidence:
- Devuelve siempre 1 a 4 Ã­tems de scientificEvidence. Nunca devuelvas un array vacÃ­o.
- Si no puedes confirmar una cita especÃ­fica del caso, usa referencias ortopÃ©dicas generales confiables relacionadas con el diagnÃ³stico o la tÃ©cnica.
- MantÃ©n cada Ã­tem corto y prÃ¡ctico. No aÃ±adas explicaciones largas.
- No repitas la tÃ©cnica quirÃºrgica dentro de scientificEvidence.
- Favorece AO Surgery Reference, AAOS, PubMed, guÃ­as oficiales, sociedades ortopÃ©dicas reconocidas, revisiones sistemÃ¡ticas, metaanÃ¡lisis y consensos.`
      : `COMPLETUDE de scientificEvidence:
- Retorne sempre 1 a 4 itens em scientificEvidence. Nunca retorne um array vazio.
- Se nÃ£o conseguir confirmar uma citaÃ§Ã£o especÃ­fica do caso, use referÃªncias ortopÃ©dicas gerais confiÃ¡veis relacionadas ao diagnÃ³stico ou Ã  tÃ©cnica.
- Mantenha cada item curto e prÃ¡tico. NÃ£o adicione explicaÃ§Ãµes longas.
- NÃ£o repita a tÃ©cnica cirÃºrgica dentro de scientificEvidence.
- FavoreÃ§a AO Surgery Reference, AAOS, PubMed, diretrizes oficiais, sociedades ortopÃ©dicas reconhecidas, revisÃµes sistemÃ¡ticas, meta-anÃ¡lises e consensos.`);

  const musculoskeletalScopeInstruction = "";
  const finalPrompt = useConcisePrompt ? concisePrompt : prompt;

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: `${finalPrompt}\n${getClinicalResponseInstruction(lang)}\n${surgicalIndicatorInstruction}\n${useConcisePrompt ? "" : `${evidenceSearchInstruction}\n${scientificEvidenceStyleInstruction}\n${scientificEvidenceCompletenessInstruction}`}`,
      config: {
        temperature: 0.2,
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            diagnosis: { type: Type.STRING },
            differentialDiagnosis: { type: Type.ARRAY, items: { type: Type.STRING } },
            materials: { type: Type.ARRAY, items: { type: Type.STRING } },
            justification: { type: Type.STRING },
            surgicalIndication: { type: Type.STRING },
            classificationReason: { type: Type.STRING },
            surgicalTechnique: { type: Type.STRING },
            scientificEvidence: {
              type: Type.ARRAY,
              items: {
                type: Type.OBJECT,
                properties: {
                  title: { type: Type.STRING },
                  source: { type: Type.STRING },
                  url: { type: Type.STRING },
                  year: { type: Type.STRING },
                  evidenceLevel: { type: Type.STRING },
                  summary: { type: Type.STRING },
                },
                required: ["title", "source", "url", "year", "evidenceLevel", "summary"],
              },
            },
            evidenceSearchTerms: {
              type: Type.ARRAY,
              items: { type: Type.STRING },
            },
            medicalDisclaimer: { type: Type.STRING },
            tips: { type: Type.STRING },
            planB: { type: Type.STRING },
          },
          required: [
            "diagnosis",
            "differentialDiagnosis",
            "materials",
            "justification",
            "surgicalIndication",
            "classificationReason",
            "surgicalTechnique",
            "scientificEvidence",
            "medicalDisclaimer",
          ],
        },
      },
    });
    console.log("RAW MODEL RESPONSE:", response.text);
    return cleanJson(response.text || "{}");
  } catch (error) {
    console.error("Scientific Plan Generation Error:", error);
    throw error;
  }
};

export const calculateSurgicalBiometrics = async (diagnosis: string, materials: string) => {
  const ai = createGeminiClient("generateContent");
  
  // Truncate inputs to avoid huge prompt payloads if accidental base64/large text passed
  const safeDiag = diagnosis ? diagnosis.substring(0, 500) : "Trauma";
  const safeMat = materials ? materials.substring(0, 500) : "Implants";

  const prompt = `
    Act as an orthopedic biomechanics expert.
    Based on the DIAGNOSIS: "${safeDiag}" 
    and MATERIALS: "${safeMat}"
    
    Calculate/Estimate realistic intraoperative telemetry values:
    1. screwLength: Average length (mm) of the MAIN screw used in this specific anatomy (e.g., Distal Radius ~18-24mm, Femur Nail ~90-110mm lag screw, Humerus ~30mm).
    2. canalOcc: Canal Occupation % (for nails) OR Plate Surface Coverage % (for plates). Range 0-100.
    3. angulation: Anatomical valgus/varus alignment target or correction angle in degrees.
    
    Return ONLY JSON: { "screwLength": number, "canalOcc": number, "angulation": number }
  `;

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: prompt,
      config: {
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            screwLength: { type: Type.NUMBER },
            canalOcc: { type: Type.NUMBER },
            angulation: { type: Type.NUMBER }
          }
        }
      }
    });
    return JSON.parse(cleanJson(response.text || '{"screwLength":0,"canalOcc":0,"angulation":0}'));
  } catch (e) {
    console.error("Biometrics calculation failed", e);
    return { screwLength: 0, canalOcc: 0, angulation: 0 };
  }
};

// NEW FUNCTION: Defines strict geometry to ensure consistency
export const defineSurgicalGeometry = async (diagnosis: string, materials: string) => {
  const ai = createGeminiClient("generateContent");
  const prompt = `
    Act as a Surgical Simulation Engine Physics Core.
    Diagnosis: ${diagnosis.substring(0, 500)}
    Implants: ${materials.substring(0, 500)}

    Define the EXACT 3D GEOMETRY for the implant placement to ensure consistency across multiple rendered views.
    
    Return a precise text description covering:
    1. Entry Point (Anatomical Landmark)
    2. Trajectory Vector (e.g., "From Distal-Lateral to Proximal-Medial at 45 degrees")
    3. End Point / Cortex engagement
    4. Plate Positioning (if applicable, e.g., "Strictly Volar surface")
    5. Screw Count & Configuration (e.g., "3 distal locking screws, 1 proximal dynamic screw")

    Keep it concise but geometrically absolute. This text will be the "Source of Truth" for an image generator.
  `;

  try {
    const response = await fetchWithRetry(async () => {
      return ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: prompt,
        config: { temperature: 0.1 }
      });
    }, 1, 1500, 30000);
    return response.text || `Standard placement for ${diagnosis}`;
  } catch (e) {
    console.error("Geometry definition failed", e);
    return `Standard anatomical placement for ${diagnosis}`;
  }
};

const trustedScientificDomains = [
  "pubmed.ncbi.nlm.nih.gov",
  "www.pubmed.ncbi.nlm.nih.gov",
  "aaos.org",
  "www.aaos.org",
  "aofoundation.org",
  "www.aofoundation.org",
  "doi.org",
  "www.doi.org",
];

const isTrustedScientificUrl = (value?: string) => {
  if (!value) return false;
  try {
    const url = new URL(value.trim());
    const hostname = url.hostname.toLowerCase();
    if (!trustedScientificDomains.includes(hostname)) return false;
    if (hostname.includes("doi.org")) {
      return /^https?:\/\/(www\.)?doi\.org\/10\./i.test(value.trim());
    }
    return true;
  } catch {
    return false;
  }
};

type PostOpRenderMode = 'standard' | 'postop';

export type PostOpGenerationContext = {
  anatomy: string;
  bone?: string;
  side?: string;
  view?: string;
  phase?: string;
  fractureType?: string;
  diagnosis?: string;
  fixation?: string;
  technique?: string;
  anatomicalAnchors?: string;
  referenceCases?: AtlasReferenceCase[];
};

const getPostOpForbiddenAnatomy = (anatomy: string) => {
  const normalized = (anatomy || "").toLowerCase();

  if (/(joelho|knee|tibia|tÃ­bia|femur|fÃªmur|patela|patella)/.test(normalized)) {
    return "Do not generate hand, wrist, forearm, elbow, shoulder, clavicle, hip, pelvis, or unrelated upper-limb anatomy.";
  }

  if (/(cotovelo|elbow|umer|Ãºmero|humer|radio|rÃ¡dio|ulna|forearm)/.test(normalized)) {
    return "Do not generate hand, wrist, knee, tibia, femur, hip, pelvis, ankle, foot, or unrelated lower-limb anatomy.";
  }

  if (/(punho|wrist|mao|mÃ£o|hand|carp|radius|rÃ¡dio|ulna)/.test(normalized)) {
    return "Do not generate knee, tibia, femur, hip, pelvis, ankle, foot, or unrelated lower-limb anatomy.";
  }

  return "Do not generate hand or wrist when the exam anatomy is not hand/wrist. Do not generate unrelated bones or joints.";
};

const getPostOpProjectionHardwareRules = (
  context?: PostOpGenerationContext,
  diagnosis?: string,
  materials?: string
) => {
  const combined = `${context?.anatomy || ""} ${context?.bone || ""} ${context?.diagnosis || ""} ${diagnosis || ""} ${materials || ""}`.toLowerCase();

  if (/(patela|patella)/.test(combined)) {
    return `
PATELLA FIXATION HARDWARE RULES:
- If the construct is tension-band fixation, show two thin parallel Kirschner wires traversing the patella longitudinally.
- The Kirschner wires must remain straight, metallic, continuous, and anatomically centered in the patellar fixation path.
- Show a figure-of-eight tension band / cerclage loop anterior to the patella when supported by the materials and technique.
- The cerclage wire must look thin, metallic, taut, and mechanically plausible.
- Do not create thick rods, bulky implants, or random hardware shapes.
- Do not place hardware in the distal femur or proximal tibia unless explicitly required by the case.
- The fixation must visually correspond to a standard postoperative radiograph of patellar fracture fixation.
- Preserve the patellar contour and fragment relationship while showing stable fixation.
- Metal density must be strongly radiopaque with crisp edges, like real orthopedic hardware on X-ray.
`;
  }

  if (/(joelho|knee)/.test(combined)) {
    return `
KNEE HARDWARE RULES:
- Keep all hardware aligned with the operated bone segment only.
- Do not place implants in unrelated compartments.
- Hardware must be sharply radiopaque and follow plausible orthopedic fixation trajectories.
- Avoid bulky, decorative, or stylized implants.
`;
  }

  return `
GENERAL HARDWARE RULES:
- Hardware must be metallic, radiopaque, sharply defined, and anatomically plausible.
- No floating implants, no duplicated components, and no impossible trajectories.
`;
};

export const buildPostOpPrompt = (
  context: PostOpGenerationContext,
  geometryContext?: string,
  renderMode: PostOpRenderMode = 'postop'
) => {
  const anatomy = context.anatomy || "same anatomy as input X-ray";
  const bone = context.bone || anatomy;
  const side = context.side || "preserve original side";
  const view = context.view || "same view as input exam";
  const phase = context.phase || "postop";
  const fractureType = context.fractureType || "preserve fracture morphology from the exam";
  const diagnosis = context.diagnosis || "preserve exam-supported diagnosis only";
  const fixation = context.fixation || "only clinically coherent fixation";
  const technique = context.technique || "preserve planned fixation logic only";
  const anchors = context.anatomicalAnchors || "preserve the same lesion zone and bone landmarks from the source exam";
  const referenceCasesText = formatAtlasReferenceCasesForPrompt(context.referenceCases || []);

  return `Generate a postoperative orthopedic reconstruction strictly matching the same anatomy shown in the input X-ray.
This case is: anatomy=${anatomy}; bone=${bone}; side=${side}; view=${view}; phase=${phase}; fractureType=${fractureType}; diagnosis=${diagnosis}; fixation=${fixation}.
Preserve the exact anatomical region, laterality, and joint or bone orientation from the source exam.
Show only a clinically coherent postoperative fixation appropriate for ${anatomy} and ${bone}.

STRICT POST-OP CONSTRAINTS:
- Same anatomy only: ${anatomy}
- Same bone only: ${bone}
- Same side only: ${side}
- Same view/incidence only: ${view}
- Same phase only: ${phase}
- Same fracture family only: ${fractureType}
- Same diagnosis family only: ${diagnosis}
- Anatomical anchors: ${anchors}
- Implant plan: ${fixation}
- Technique intent: ${technique}
- Geometry source of truth: ${geometryContext || "preserve standard placement only within the detected anatomy"}
- Keep the output anchored to the same segment and fixation logic shown by the closest curated reference cases.

NEGATIVE CONSTRAINTS:
- Do not change body part.
- Do not change anatomical region.
- Do not change the primary bone or joint segment.
- Do not generate hand or wrist anatomy unless the detected anatomy is actually hand/wrist.
- Do not generate unrelated bones.
- Do not invent implants incompatible with the detected anatomy.
- ${getPostOpForbiddenAnatomy(anatomy)}

CURATED ATLAS REFERENCE CASES:
${referenceCasesText || "- No curated reference case matched strongly enough; rely strictly on anatomy, bone, view, and fixation constraints above."}

RENDER TARGET:
- ${renderMode === 'postop' ? 'Technical postoperative orthopedic render with coherent hardware placement.' : 'High-fidelity postoperative orthopedic render with strict anatomy lock.'}`;
};

const getPostOpRenderProfile = (mode: PostOpRenderMode = 'postop') => {
  if (mode === 'standard') {
    return {
      imageSize: '4K' as const,
      renderPrompt: `GENERATE: MEDICAL 3D VOLUME RENDERING (CT RECONSTRUCTION STYLE).
                     SUBJECT: Post-operative anatomy with orthopedic hardware.

                     CRITICAL BIOMECHANICAL & GEOMETRY RULES (PHYSICS ENGINE):
                     1. INTRAMEDULLARY NAILS: Must be encompassed BY the bone. Show the nail INSIDE the medullary canal via semi-transparent bone shading (Ghost Mode).
                     2. PLATES: Must follow the contour of the cortical bone surface exactly (Periosteal contact).
                     3. SCREWS: Must penetrate from near cortex to far cortex. Do not let screws float in soft tissue.
                     4. REDUCTION: Show the fracture line reduced (bones aligned).

                     VISUAL STYLE:
                     - "Phantom View" or "Ghost View".
                     - Bone: 40% Opacity, White/Beige to reveal internal hardware.
                     - Metal: 100% Opacity, High Contrast Titanium/Steel.
                     - Background: Clean Medical Dark/Blue.
                     - NO SKIN, NO MUSCLE. Focus strictly on BONE + HARDWARE.`,
    };
  }

  return {
    imageSize: '1K' as const,
    renderPrompt: `GENERATE: TECHNICAL POST-OPERATIVE ORTHOPEDIC RENDER.
                   SUBJECT: Bone plus coherent hardware only.
                   PRIORITIES:
                   1. Preserve the same anatomy, side, and operated zone.
                   2. Show bone alignment and implant position clearly.
                   3. Keep plates, screws, or nails anatomically plausible.
                   4. Prefer speed and clarity over rich anatomy.
                   VISUAL STYLE:
                   - Simple orthopedic ghost view.
                   - Minimal surrounding anatomy.
                   - No skin, no muscle, no tendons, no ligaments.
                   - Clean neutral medical background.`,
  };
};

export const simulatePostOpResult = async (
  originalImageBase64: string,
  diagnosis: string,
  technique: string,
  materials: string,
  geometryContext?: string,
  renderMode: PostOpRenderMode = 'postop',
  postOpContext?: PostOpGenerationContext
) => {
  // Temporariamente desativado por baixa confiabilidade clÃ­nica do pÃ³s-operatÃ³rio/RX series.
  return null;

  const ai = createGeminiClient("generateContent");
  const preparedInput = await downscaleImageDataUrl(originalImageBase64, 1280, 0.8);
  const mimeMatch = preparedInput.match(/^data:(image\/\w+);base64,/);
  const mimeType = mimeMatch?.[1] ?? 'image/jpeg';
  const cleanBase64 = preparedInput.replace(/^data:image\/\w+;base64,/, "");
  const renderProfile = getPostOpRenderProfile(renderMode);
  const strictPostOpPrompt = buildPostOpPrompt({
    anatomy: postOpContext?.anatomy || diagnosis,
    side: postOpContext?.side,
    view: postOpContext?.view,
    diagnosis: postOpContext?.diagnosis || diagnosis,
    fixation: postOpContext?.fixation || materials,
    technique: postOpContext?.technique || technique,
    anatomicalAnchors: postOpContext?.anatomicalAnchors,
  }, geometryContext, renderMode);

  try {
    return await fetchWithRetry(async () => {
      const response = await ai.models.generateContent({
        model: 'gemini-3-pro-image-preview',
        contents: {
          parts: [
            { inlineData: { data: cleanBase64, mimeType: mimeType } },
            { text: `${strictPostOpPrompt}

                     ${renderProfile.renderPrompt}

                     SURGICAL DATA:
                     Diagnosis: ${diagnosis.substring(0,500)}
                     Technique: ${technique.substring(0,500)}
                     Implants: ${materials.substring(0,500)}
                     Positioning: ${geometryContext || "Anatomical standard placement"}` }
          ],
        },
        config: { imageConfig: { aspectRatio: "1:1", imageSize: renderProfile.imageSize } }
      });
      
      for (const part of response.candidates?.[0]?.content?.parts || []) {
        if (part.inlineData) return part.inlineData.data;
      }
      
      const textPart = response.candidates?.[0]?.content?.parts?.find(
        (part: { text?: string }) => Boolean(part.text)
      )?.text;
      if (textPart) {
         console.warn("Model returned text instead of image:", textPart);
         throw new Error("Simulation Refused: Safety guidelines prevented image generation.");
      }
      
      return null;
    }, 1, 1500, renderMode === 'postop' ? 45000 : 70000); 
  } catch (error: any) {
    console.error("PostOp Sim Error:", error);
    throw error;
  }
};

// MODIFIED: Accepts an optional referenceImageBase64 to enforce consistency
export const simulatePostOpProjection = async (
  originalImageBase64: string,
  diagnosis: string,
  materials: string,
  view: 'AP' | 'Lateral' | 'Oblique',
  geometryContext?: string,
  referenceImageBase64?: string | null,
  postOpContext?: PostOpGenerationContext
) => {
  // Temporariamente desativado por baixa confiabilidade clÃ­nica do pÃ³s-operatÃ³rio/RX series.
  return null;

  const ai = createGeminiClient("generateContent");
  const preparedInput = await downscaleImageDataUrl(
    originalImageBase64,
    1280,
    0.8
  );
  const mimeMatch = preparedInput.match(/^data:(image\/\w+);base64,/);
  const mimeType = mimeMatch?.[1] ?? 'image/jpeg';
  const cleanBase64 = preparedInput.replace(/^data:image\/\w+;base64,/, "");
  const hardwareRules = getPostOpProjectionHardwareRules(postOpContext, diagnosis, materials);
  const strictPostOpPrompt = `
GENERATE A REALISTIC POST-OPERATIVE ORTHOPEDIC RADIOGRAPH, NOT A 3D RENDER AND NOT AN ILLUSTRATION.

GEOMETRIC AUTHORITY:
- The first image is the absolute geometric source of truth.
- Preserve the original anatomy exactly: bone length, bone proportions, joint spacing, axis, incidence, rotation, and laterality.
- Do not change the global size, position, or rotation of the patella, knee, or any other bone segment.
- Only add clinically coherent post-operative fixation.

RADIOGRAPHIC REALISM RULES:
- Match a true radiograph look with DICOM-style contrast.
- Cortical bone must appear bright and well-defined.
- Trabecular detail should remain subtle and realistic.
- Soft tissues must stay smooth, low-contrast, and non-exaggerated.
- The background must be clean and radiographic, without studio shading.
- No CGI appearance, no volumetric lighting, no 3D sculpted anatomy, no illustrative style.
- Do not invent new fractures, deformities, or secondary findings.

HARDWARE RULES:
- Kirschner wires must be thin, straight, continuous, metallic, and anatomically plausible.
- Tension band / cerclage constructs must follow a coherent radiographic trajectory.
- Screws and pins must be centered within the intended bone and aligned to the fracture or fixation target.
- No implant may float in soft tissue or cross impossible trajectories.
- Metal must be strongly radiopaque and visually integrated as real surgical hardware.
${hardwareRules}

VIEW-SPECIFIC RULES:
- AP must preserve a true frontal radiographic projection of the original exam.
- Lateral must preserve a true profile projection of the original exam.
- Oblique must be a plausible rotation derived from the same case, not a new anatomy.
- If a reference image is provided, use it as the source of truth for the hardware arrangement and rotate only the projection to the requested view.

CLINICAL INPUTS:
- Diagnosis: ${diagnosis.substring(0, 500)}
- Materials: ${materials.substring(0, 500)}
- Geometry context: ${geometryContext || "preserve standard placement only within the detected anatomy"}
- View: ${view}
- Surgical context: ${postOpContext?.technique || postOpContext?.fixation || "clinically coherent fixation only"}

FINAL REQUIREMENT:
- Return a believable postoperative radiograph with faithful anatomy, real hardware integration, and the requested ${view} incidence.
- Preserve the original exam geometry and make only the necessary fixation changes.
`.trim();

  console.log("[POSTOP-PROJECTION] diagnosis =", diagnosis);
  console.log("[POSTOP-PROJECTION] materials =", materials);
  console.log("[POSTOP-PROJECTION] geometryContext =", geometryContext);
  console.log("[POSTOP-PROJECTION] strictPostOpPrompt =", strictPostOpPrompt);
  console.log("[POSTOP-PROJECTION] usingReference =", !!referenceImageBase64);
  
  const parts: any[] = [
    { inlineData: { data: cleanBase64, mimeType: mimeType } }
  ];

  let consistencyPrompt = "";

  // If a reference image (AP View) is provided, use it as the anchor for consistency
  const referenceImage = referenceImageBase64?.trim() ?? "";
  if (referenceImage.length > 0) {
    const preparedReference = await downscaleImageDataUrl(referenceImage, 1280, 0.8);
    const refMimeMatch = preparedReference.match(/^data:(image\/\w+);base64,/);
    const refMime = refMimeMatch?.[1] ?? 'image/png';
    const cleanRef = preparedReference.replace(/^data:image\/\w+;base64,/, "");
    parts.push({ inlineData: { data: cleanRef, mimeType: refMime } });
    
    consistencyPrompt = `
      CRITICAL REFERENCE INSTRUCTION:
      - The SECOND image is the reference radiograph for the same surgical case.
      - Use it as the source of truth for implant configuration, number of screws, plate length, wire path, pin position, and fixation trajectory.
      - Task: rotate only the projection to generate the requested ${view} view while preserving the same postoperative hardware layout.
      - Do not add, remove, shorten, lengthen, or relocate implants.
    `;
  } else {
    consistencyPrompt = `
      - Ensure the implant position matches the described geometry exactly.
      - Preserve a true postoperative radiographic projection for the requested ${view} view.
    `;
  }

  const viewPrompts = {
    'AP': "Anteroposterior (AP) view.",
    'Lateral': "Lateral (Profile) view. ROTATE THE BONE 90 DEGREES.",
    'Oblique': "Oblique view. ROTATE THE BONE 45 DEGREES."
  };

  try {
    return await fetchWithRetry(async () => {
      const response = await ai.models.generateContent({
        model: 'gemini-3-pro-image-preview',
        contents: {
          parts: [
            ...parts,
            { text: `${strictPostOpPrompt}

                     REQUESTED VIEW: ${viewPrompts[view]}

                     CONSISTENCY RULES:
                     ${consistencyPrompt}

                     RADIOGRAPHIC QUALITY TARGET:
                     - Preserve the original exam geometry with high fidelity.
                     - Keep the image convincingly post-operative, not synthetic.
                     - Favor a true radiographic appearance over visual dramatization.` }
          ],
        },
        config: { imageConfig: { aspectRatio: "1:1", imageSize: "4K" } }
      });
      
      for (const part of response.candidates?.[0]?.content?.parts || []) {
        if (part.inlineData) return part.inlineData.data;
      }
      return null;
    }, 1, 2000, 90000);
  } catch (error: any) {
    console.error("PostOp Projection Error:", error);
    throw error;
  }
};

export const generateOrthoDiagram = async (
  description: string,
  style: '2D' | '3D' = '3D',
  lesion: string = "",
  quality: '1K' | '2K' | '4K' = '4K',
  mode: AtlasGenerationMode = 'detailed',
  analysis?: AtlasImageAnalysis
) => {
  const ai = createGeminiClient("generateContent");
  const effectiveMode: AtlasGenerationMode = mode;
  const effectiveStyle: '2D' | '3D' = style;
  const visualMode = getAtlasVisualModePrompt(effectiveStyle, effectiveMode);
  const frozenCase = analysis ? (analysis.structuredFractureCase || freezeStructuredFractureCase(analysis)) : undefined;
  const strictPrompt = frozenCase ? buildStrictAtlasVisualPrompt({ ...analysis!, ...frozenCase } as AtlasImageAnalysis, 'pt', effectiveMode) : "";
  const finalRenderPrompt = analysis
    ? getAtlasFinalRenderPrompt(
        `${frozenCase?.examRegion || analysis.examRegion || analysis.anatomicalRegion || analysis.atlasFindings?.anatomyRegion || description}`.trim(),
        effectiveStyle,
        effectiveMode,
        {
          anchors: analysis.anatomicalAnchors,
          visualPrompt: strictPrompt,
        }
      )
    : getAtlasFinalRenderPrompt("Original exam anatomy and any confirmed abnormality", effectiveStyle, effectiveMode);

  const finalQuality = quality;

  try {
    return await fetchWithRetry(async () => {
      const response = await ai.models.generateContent({
        model: 'gemini-3-pro-image-preview',
        contents: { parts: [{ text: `
          ${finalRenderPrompt}
          ${visualMode.visualInstructions}
          ${strictPrompt ? `\nSTRICT STRUCTURED FINDINGS:\n${strictPrompt}\n` : ""}

          ANATOMICAL PRECISION RULES:
          1. EXACT LOCATION: The pathology must be strictly in the anatomical zone described.
          2. REALISM: ${effectiveMode === 'fast' ? (effectiveStyle === '2D' ? 'Use a clean 2D atlas illustration with the fracture/pathology clearly visible.' : 'Use a simplified 3D model with the fracture/pathology clearly visible.') : (effectiveStyle === '2D' ? 'Use a detailed 2D orthopedic atlas illustration with clear cortical and fracture definition.' : 'Use realistic bone and soft-tissue forms with high anatomical detail.')}
          3. BACKGROUND: ${effectiveStyle === '2D' ? 'Clean neutral medical illustration background.' : (effectiveMode === 'fast' ? 'Clean neutral background.' : 'Clean dark medical studio.')}
          4. FOCUS: Center the pathology clearly.
          ` }] },
        config: { imageConfig: { aspectRatio: "1:1", imageSize: finalQuality } },
      });
      for (const part of response.candidates?.[0]?.content?.parts || []) {
        if (part.inlineData) return part.inlineData.data;
      }
      return null;
    }, 1, 2000, 90000);
  } catch (error: any) {
    throw error;
  }
};

// New export: generateSurgicalNote
export const generateSurgicalNote = async (procedureName: string, details: string, lang: Language = 'pt') => {
  const ai = createGeminiClient("generateContent");
  const prompt = (lang === "pt"
    ? `Atue como um cirurgiÃƒÂ£o sÃƒÂªnior. Escreva uma NOTA DE PRONTUÃƒÂRIO OPERATÃƒâ€œRIO formal e tÃƒÂ©cnica para o procedimento: "${procedureName}".
       Detalhes especÃƒÂ­ficos do caso: "${details}".
       
       Estrutura obrigatÃƒÂ³ria:
       1. IdentificaÃƒÂ§ÃƒÂ£o e DiagnÃƒÂ³stico PrÃƒÂ©-OperatÃƒÂ³rio
       2. DescriÃƒÂ§ÃƒÂ£o da TÃƒÂ©cnica CirÃƒÂºrgica (Passo a passo detalhado, usando terminologia mÃƒÂ©dica correta)
       3. Achados Intra-operatÃƒÂ³rios
       4. Materiais Implantados (se houver)
       5. Plano PÃƒÂ³s-OperatÃƒÂ³rio Imediato
       
       Use formataÃƒÂ§ÃƒÂ£o Markdown para tÃƒÂ­tulos e listas. Seja conciso e profissional.`
    : `Act as a senior surgeon. Write a formal OPERATIVE NOTE for: "${procedureName}".
       Specific details: "${details}".
       
       Required Structure:
       1. ID & Pre-op Diagnosis
       2. Surgical Technique Description (Detailed steps, medical terminology)
       3. Intra-operative Findings
       4. Implants (if any)
       5. Immediate Post-op Plan
       
       Use Markdown. Be concise and professional.`)
    + `\n\n${getSelectedLanguageInstruction(lang)}`;

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: prompt,
      config: { temperature: 0.3 }
    });
    return response.text || "";
  } catch (error) {
    console.error("Surgical Note Error:", error);
    throw error;
  }
};

// New export: generatePatientOrientations
export const generatePatientOrientations = async (procedureName: string, lang: Language = 'pt') => {
  const ai = createGeminiClient("generateContent");
  const prompt = (lang === "pt"
    ? `Escreva um GUIA DE ORIENTAÃƒâ€¡Ãƒâ€¢ES AO PACIENTE para pÃƒÂ³s-operatÃƒÂ³rio de: "${procedureName}".
       Linguagem: Simples, acolhedora e direta (nÃƒÂ­vel de leitura 5Ã‚Âª sÃƒÂ©rie).
       
       TÃƒÂ³picos:
       - Cuidados com a ferida operatÃƒÂ³ria/curativo
       - Controle da dor (medicaÃƒÂ§ÃƒÂ£o e gelo)
       - Mobilidade e Repouso (o que pode e nÃƒÂ£o pode fazer)
       - Sinais de Alerta (quando ir ao pronto-socorro)
       - Retorno ao consultÃƒÂ³rio
       
       Use Markdown.`
    : `Write a PATIENT GUIDE for post-op recovery of: "${procedureName}".
       Language: Simple, welcoming, direct (5th grade reading level).
       
       Topics:
       - Wound care
       - Pain management
       - Mobility & Rest
       - Warning Signs
       - Follow-up
       
       Use Markdown.`)
    + `\n\n${getSelectedLanguageInstruction(lang)}`;

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: prompt,
      config: { temperature: 0.4 }
    });
    return response.text || "";
  } catch (error) {
    console.error("Patient Orientation Error:", error);
    throw error;
  }
};

type StructuredPatientOrientations = {
  woundCare: string;
  painManagement: string;
  mobility: string;
  warningSigns: string[];
  rehab: string;
};

const buildStructuredPatientOrientationFallback = (
  diagnosisOrProcedure: string,
  lang: Language = "pt"
): StructuredPatientOrientations => {
  const context = String(diagnosisOrProcedure || "").trim().replace(/\s+/g, " ").slice(0, 140);
  if (lang === "en") {
    return {
      woundCare: context ? `Local wound care and dressing instructions for ${context}.` : "Local wound care and dressing instructions.",
      painManagement: "Analgesia as prescribed, adjusted to pain and clinical response.",
      mobility: "Gradual mobilization according to stability, pain, and orthopedic guidance.",
      warningSigns: ["Worsening pain or swelling", "Fever or wound drainage", "New neurovascular changes"],
      rehab: "Physical therapy according to healing stage and medical clearance.",
    };
  }
  if (lang === "es") {
    return {
      woundCare: context ? `Curaciones y cuidado local según ${context}.` : "Curaciones y cuidado local según el caso.",
      painManagement: "Analgesia según prescripción y respuesta clínica.",
      mobility: "Movilización gradual según estabilidad, dolor y orientación ortopédica.",
      warningSigns: ["Empeoramiento del dolor o edema", "Fiebre o secreción", "Cambios neurovasculares"],
      rehab: "Fisioterapia según la fase de consolidación y la liberación médica.",
    };
  }
  return {
    woundCare: context ? `Curativo e cuidados locais conforme ${context}.` : "Curativo e cuidados locais conforme o procedimento.",
    painManagement: "Analgesia conforme prescrição e resposta clínica.",
    mobility: "Mobilização gradual conforme estabilidade, dor e orientação ortopédica.",
    warningSigns: ["Piora da dor ou do edema", "Febre ou secreção", "Alterações neurovasculares"],
    rehab: "Fisioterapia conforme a fase de consolidação e liberação médica.",
  };
};

export const fallbackStructuredOrientation = ({
  diagnosisOrProcedure,
  anatomicalLocation,
  aoOtaCode,
  clinicalDescription,
  language = "pt",
}: {
  diagnosisOrProcedure?: string;
  anatomicalLocation?: string;
  aoOtaCode?: string;
  clinicalDescription?: string;
  language?: Language;
}): StructuredPatientOrientations => {
  const diagnosis = String(diagnosisOrProcedure || clinicalDescription || "").trim();
  const location = String(anatomicalLocation || "").trim();
  const code = String(aoOtaCode || "").trim();
  const base = buildStructuredPatientOrientationFallback(diagnosis, language);
  const locationSuffix = location ? ` ${location}.` : "";
  const codeSuffix = code ? ` ${code}.` : "";

  return {
    ...base,
    woundCare: `${base.woundCare}${locationSuffix}${codeSuffix}`.trim(),
    mobility: `${base.mobility}${locationSuffix}`.trim(),
  };
};

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number, timeoutLabel: string): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => {
          const error: any = new Error(timeoutLabel);
          error.code = "deadline-exceeded";
          error.status = 504;
          reject(error);
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
};

// New export: generateStructuredPatientOrientations (returning JSON)
export const generateStructuredPatientOrientations = async (diagnosisOrProcedure: string, lang: Language = 'pt') => {
  const ai = createGeminiClient("generateContent");
  const prompt = (lang === "pt"
    ? `Gere orientaÃƒÂ§ÃƒÂµes pÃƒÂ³s-operatÃƒÂ³rias estruturadas para: "${diagnosisOrProcedure}".
       Retorne JSON estrito:
       {
         "woundCare": "Texto curto sobre curativo",
         "painManagement": "Texto curto sobre dor",
         "mobility": "Texto curto sobre carga/movimento",
         "warningSigns": ["Sinal 1", "Sinal 2"],
         "rehab": "Texto curto sobre fisioterapia"
       }`
    : `Generate structured post-op instructions for: "${diagnosisOrProcedure}".
       Return strict JSON:
       {
         "woundCare": "Short text",
         "painManagement": "Short text",
         "mobility": "Short text",
         "warningSigns": ["Sign 1", "Sign 2"],
         "rehab": "Short text"
       }`)
    + `\n\n${getSelectedLanguageInstruction(lang)}`;
  const strictJsonInstruction = lang === "en"
    ? `Return ONLY valid JSON. No Markdown. No code fences. No explanation. Do not write "Here is". The response must start with { and end with }.`
    : lang === "es"
      ? `Devuelve SOLO JSON válido. Sin Markdown. Sin bloques de código. Sin explicaciones. No escribas "Here is". La respuesta debe empezar con { y terminar con }.`
      : `Responda APENAS com JSON válido. Sem Markdown. Sem blocos de código. Sem explicações. Não escreva "Here is". A resposta deve começar com { e terminar com }.`;
  const localizedPrompt = `${prompt}\n${getClinicalResponseInstruction(lang)}\n${strictJsonInstruction}`;
  const fallback = buildStructuredPatientOrientationFallback(diagnosisOrProcedure, lang);

  try {
    const response = await withTimeout(
      ai.models.generateContent({
        __atlasDisableLanguageRetry: true,
        contents: localizedPrompt,
        config: { 
          temperature: 0.1,
          maxOutputTokens: 220,
          responseMimeType: "application/json",
          responseSchema: {
              type: Type.OBJECT,
              properties: {
                  woundCare: { type: Type.STRING },
                  painManagement: { type: Type.STRING },
                  mobility: { type: Type.STRING },
                  warningSigns: { type: Type.ARRAY, items: { type: Type.STRING } },
                  rehab: { type: Type.STRING }
              }
          }
        }
      }),
      12000,
      "Structured orientation timed out"
    );
    const rawText = String(response.text || "");
    console.info("[StructuredOrientation][raw_preview]", {
      language: lang,
      preview: rawText.slice(0, 200),
    });
    const parsed = extractJsonObject(rawText) || parseJsonObjectFromAiText(cleanJson(rawText)) || null;
    if (!parsed) {
      console.warn("[StructuredOrientation][parse_failed_cleaned]", {
        language: lang,
        preview: rawText.slice(0, 200),
      });
      return {
        woundCare: "",
        painManagement: "",
        mobility: "",
        warningSigns: [],
        rehab: "",
      };
    }
    return {
      woundCare: String(parsed.woundCare || ''),
      painManagement: String(parsed.painManagement || ''),
      mobility: String(parsed.mobility || ''),
      warningSigns: Array.isArray(parsed.warningSigns) ? parsed.warningSigns.map((item: unknown) => String(item || '')) : [],
      rehab: String(parsed.rehab || ''),
    };
  } catch (error) {
    console.warn("[StructuredOrientation][fallback]", {
      language: lang,
      diagnosisOrProcedure: String(diagnosisOrProcedure || "").trim().slice(0, 160),
      error: String((error as any)?.message || error || ""),
    });
    return fallback;
  }
};

export type OrthoBannerStyle =
  | 'dark-premium'
  | 'light-clinical'
  | 'abstract-medical'
  | 'tech-orthopedic'
  | 'minimal-luxury';

export type OrthoBannerConcept = {
  style: OrthoBannerStyle;
  templateId: OrthoBannerStyle;
  styleTemplate: OrthoBannerStyle;
  backgroundSpec: string;
  layoutSpec: string;
  paletteSpec: string;
  typographySpec: string;
  decorativeSpec: string;
  titlePrimary: string;
  titleSecondary: string;
  headline: string;
  subheadline: string;
  tagline: string;
  supportText: string;
  tone: string;
  accentTone: string;
  textColor: string;
  primaryColor: string;
  accentColor: string;
  colorScheme: string;
  backgroundStyle: string;
  layoutVariant: string;
  backgroundPrompt: string;
  factualProfileData?: {
    doctorName: string;
    specialty: string;
    crm: string;
    crmState: string;
    hospital: string;
    phone: string;
    email: string;
    site: string;
    displayName: string;
  };
  aiCopy?: {
    headline: string;
    subheadline: string;
    tagline: string;
    supportText: string;
    tone: string;
  };
};

export type OrthoBannerOption = OrthoBannerConcept & {
  imageUrl: string;
};

type OrthoBannerGenerationInput = {
  doctorName: string;
  specialty: string;
  crm: string;
  crmState: string;
  hospital: string;
  phone: string;
  email: string;
  site: string;
  displayName: string;
  stylePreference?: OrthoBannerStyle | 'auto';
  templatePreference?: OrthoBannerStyle | 'auto';
  backgroundSpecPreference?: string;
  layoutSpecPreference?: string;
  paletteSpecPreference?: string;
  typographySpecPreference?: string;
  decorativeSpecPreference?: string;
  bannerTagline?: string;
  bannerSupportText?: string;
  language?: Language;
};

const normalizeBannerText = (value: unknown) =>
  String(value ?? '')
    .replace(/\r/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const clampBannerNumber = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const normalizeBannerHex = (value: unknown, fallback: string) => {
  const raw = normalizeBannerText(value).replace(/\s+/g, '');
  return /^#([0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(raw) ? raw : fallback;
};

const hashBannerSeed = (value: string) => {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

const createBannerRandom = (seed: string) => {
  let state = hashBannerSeed(seed) || 1;
  return () => {
    state = (Math.imul(1664525, state) + 1013904223) >>> 0;
    return state / 0x100000000;
  };
};

const svgDataUrl = (svg: string) => `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg.trim())}`;
const BANNER_BACKGROUND_SPEC_KEY: string = 'backgroundSpec';
const BANNER_LAYOUT_SPEC_KEY: string = 'layoutSpec';
const BANNER_PALETTE_SPEC_KEY: string = 'paletteSpec';
const BANNER_TYPOGRAPHY_SPEC_KEY: string = 'typographySpec';
const BANNER_DECORATIVE_SPEC_KEY: string = 'decorativeSpec';

const extractInlineImageData = (response: any) => {
  const parts = response?.candidates?.[0]?.content?.parts || [];
  for (const part of parts) {
    if (part?.inlineData?.data) return String(part.inlineData.data);
  }
  return null;
};

const buildOrthoBannerImagePrompt = (concept: OrthoBannerConcept, input: OrthoBannerGenerationInput) => {
  const styleDirective = (() => {
    const style = concept.styleTemplate || concept.templateId || concept.style;
    if (style === 'dark-premium') {
      return 'dark premium, cinematic, deep navy, elegant, high-contrast, luxury medical branding';
    }
    if (style === 'light-clinical') {
      return 'hospital institutional, bright clinical white, airy, calm, premium private-hospital branding, clean and high-trust';
    }
    if (style === 'abstract-medical') {
      return 'abstract medical, layered soft shapes, subtle geometry, refined texture, sophisticated and original';
    }
    if (style === 'tech-orthopedic') {
      return 'tech orthopedic, precision grid, technical lines, data-inspired details, modern medical engineering feel';
    }
    return 'minimal luxury, restrained, elegant, spacious, premium medical identity';
  })();
  const specLines = [
    `Style: ${concept.styleTemplate}`,
    `Style directive: ${styleDirective}`,
    `Background spec: ${concept.backgroundSpec || concept.backgroundStyle || 'premium-medical'}`,
    `Layout spec: ${concept.layoutSpec || concept.layoutVariant || 'wide horizontal split'}`,
    `Palette spec: ${concept.paletteSpec || concept.colorScheme || 'blue-white premium medical'}`,
    `Typography spec: ${concept.typographySpec || 'professional medical typography'}`,
    `Decorative spec: ${concept.decorativeSpec || 'subtle medical geometry'}`,
    `Tone: ${concept.tone || 'premium and clinical'}`,
    `Headline: ${concept.headline || concept.titlePrimary || ''}`,
    `Subtitle: ${concept.subheadline || concept.titleSecondary || ''}`,
    `Tagline: ${concept.tagline || ''}`,
    `Support text: ${concept.supportText || ''}`,
  ].filter(Boolean);
  const factualBlock = buildOrthoBannerFactualProfileBlock(input);
  const creativeBlock = buildOrthoBannerCreativeBlock(concept);

  return `
Create a complete premium horizontal medical banner image for an orthopedic and traumatology specialist.

Generate a genuinely distinct banner concept from the other options. Do not reuse the same composition, same background, or same feel.

The banner must be a finished banner image, not a template, not a mockup, and not an image with additional text overlays.

Use the factual profile data below exactly as provided. These values are mandatory and must not be invented, substituted, translated, renamed, or changed in meaning or spelling. If a factual field is missing, omit it rather than hallucinating a replacement.

${factualBlock}

The only creative content the model may invent is the institutional copy below. Use it for the banner's marketing tone, hierarchy, and atmosphere, but do not replace the factual profile data with it.

${creativeBlock}

Creative direction:
${specLines.map((line) => `- ${line}`).join('\n')}

Image requirements:
- Horizontal banner layout, wide 5:1 feel
- Premium medical / orthopedic / trauma identity
- Professional typography with a clear hierarchy and large, readable text
- Strong contrast and clean spacing; nothing should feel cramped or glued to the edges
- Left side should feel like the hero area
- Right side should feel like a controlled institutional information block
- Text must be embedded into the image itself
- No overlapping text
- Balanced spacing
- Clean institutional look
- Healthcare colors with blues, whites, cyan, teal, or restrained accents
- Use subtle medical / orthopedic design elements only
- Avoid clutter, stock-like medical icons, and random decorative noise
- Avoid generic template-stock layouts
- The banner should look like premium private hospital branding or a high-end orthopedic clinic identity
- If text is long, rebalance the composition and adjust typography rather than crowding it
- Produce a fully composed banner image with the text already positioned correctly
- The factual profile block must remain factual, and the creative block must remain creative; do not mix the two
- Do not hallucinate doctor identity, hospital identity, contact details, or website values
- Do not replace factual profile values with invented marketing copy
- If a factual value is absent, omit it rather than fabricating it

Return only the image result. Do not return markdown or explanation.
`.trim();
};

const generateInlineBannerImage = async (
  ai: ReturnType<typeof createPrivateGenAI>,
  model: string,
  concept: OrthoBannerConcept,
  input: OrthoBannerGenerationInput,
  index: number
) => {
  const prompt = buildOrthoBannerImagePrompt(concept, input);
  console.info('[Ortho Banner IA] rendering image concept', {
    model,
    index,
    style: concept.styleTemplate,
    layoutSpec: concept.layoutSpec,
    backgroundSpec: concept.backgroundSpec,
    paletteSpec: concept.paletteSpec,
    typographySpec: concept.typographySpec,
    decorativeSpec: concept.decorativeSpec,
  });

  const response = await fetchWithRetry(async () => {
    const result = await ai.models.generateContent({
      model,
      contents: prompt,
      config: {
        imageConfig: {
          aspectRatio: '16:5',
          imageSize: '4K',
        },
      },
    });
    return result;
  }, 1, 1200, 90000);

  const inlineImage = extractInlineImageData(response);
  console.info('[Ortho Banner IA] image response', {
    model,
    index,
    hasInlineImage: Boolean(inlineImage),
    responseType: response?.candidates?.[0]?.content?.parts?.map((part: any) => Object.keys(part || {})),
  });
  return inlineImage;
};

const buildOrthoBannerArtworkDataUrl = (
  concept: OrthoBannerConcept,
  input: OrthoBannerGenerationInput,
  index: number
) => {
  const style = concept.styleTemplate || concept.templateId || concept.style;
  const backgroundSpec = normalizeBannerText(concept.backgroundSpec || concept.backgroundStyle || style).toLowerCase();
  const layoutVariant = normalizeBannerText(concept.layoutSpec || concept.layoutVariant || 'balanced').toLowerCase();
  const paletteSpec = normalizeBannerText(concept.paletteSpec || concept.colorScheme || '').toLowerCase();
  const typographySpec = normalizeBannerText(concept.typographySpec || '').toLowerCase();
  const decorativeSpec = normalizeBannerText(concept.decorativeSpec || '').toLowerCase();
  const seed = [
    input.doctorName,
    input.specialty,
    input.hospital,
    input.displayName,
    style,
    backgroundSpec,
    layoutVariant,
    paletteSpec,
    typographySpec,
    decorativeSpec,
    concept.backgroundPrompt,
    String(index),
    String(Date.now()),
  ].join('|');
  const rand = createBannerRandom(seed);
  const width = 1600;
  const height = 320;

  const paletteKey =
    paletteSpec ||
    backgroundSpec ||
    style;
  const isLightFamily =
    paletteKey.includes('light') ||
    paletteKey.includes('clean') ||
    paletteKey.includes('minimal') ||
    paletteKey.includes('luxury') ||
    paletteKey.includes('pearl') ||
    paletteKey.includes('ice');
  const isTechFamily = paletteKey.includes('tech') || paletteKey.includes('cyan');
  const primary = normalizeBannerHex(
    concept.primaryColor,
    isLightFamily ? '#F8FAFC' : backgroundSpec.includes('abstract') ? '#10253F' : '#0B1320'
  );
  const accent = normalizeBannerHex(
    concept.accentColor,
    isTechFamily ? '#67E8F9' : isLightFamily ? '#60A5FA' : '#22D3EE'
  );
  const textColor = normalizeBannerHex(
    concept.textColor,
    isLightFamily ? '#0F172A' : '#FFFFFF'
  );
  const backgroundKey = (() => {
    const key = backgroundSpec || style;
    if (key.includes('gradient')) return isLightFamily ? 'light-clinical' : isTechFamily ? 'tech-orthopedic' : 'dark-premium';
    if (key.includes('tech') || key.includes('orthopedic') || key.includes('data')) return 'tech-orthopedic';
    if (key.includes('light') || key.includes('hospital') || key.includes('clean')) return 'light-clinical';
    if (key.includes('abstract') || key.includes('texture') || key.includes('geometric') || key.includes('pattern')) return 'abstract-medical';
    if (key.includes('minimal') || key.includes('institutional') || key.includes('pearl') || key.includes('luxury')) return 'minimal-luxury';
    return 'dark-premium';
  })();
  const decorativeOverlay = (() => {
    const key = `${decorativeSpec} ${layoutVariant} ${backgroundSpec}`;
    if (key.includes('grid')) {
      return `<path d="M96 44H1504M96 276H1504M112 44V276M1488 44V276" stroke="${accent}" stroke-opacity="0.09" stroke-width="1.4" stroke-linecap="round"/>`;
    }
    if (key.includes('hex')) {
      return `<path d="M1240 82L1280 104L1280 148L1240 170L1200 148L1200 104L1240 82Z" stroke="${accent}" stroke-opacity="0.16" stroke-width="2" fill="none"/><path d="M1322 148L1350 164L1350 194L1322 210L1294 194L1294 164L1322 148Z" stroke="${textColor}" stroke-opacity="0.10" stroke-width="2" fill="none"/>`;
    }
    if (key.includes('wave')) {
      return `<path d="M0 224C166 180 350 160 522 178C718 200 894 236 1098 220C1280 206 1428 170 1600 140" stroke="${accent}" stroke-opacity="0.14" stroke-width="2.5" stroke-linecap="round"/>`;
    }
    if (key.includes('bar')) {
      return `<path d="M136 86H476M136 118H392M1172 80H1448M1172 112H1398" stroke="${textColor}" stroke-opacity="0.08" stroke-width="4" stroke-linecap="round"/>`;
    }
    if (key.includes('line') || key.includes('surgical')) {
      return `<path d="M188 62L284 62M236 14L236 110M1220 44L1388 44M1304 4L1304 112" stroke="${accent}" stroke-opacity="0.16" stroke-width="3" stroke-linecap="round"/>`;
    }
    if (key.includes('glow')) {
      return `<circle cx="1288" cy="112" r="42" fill="${accent}" fill-opacity="0.08"/><circle cx="1328" cy="160" r="16" fill="${textColor}" fill-opacity="0.08"/>`;
    }
    return '';
  })();

  const shift = (offset: number, max: number) => clampBannerNumber(Math.round(offset + (rand() - 0.5) * max), 0, width);
  const shiftY = (offset: number, max: number) => clampBannerNumber(Math.round(offset + (rand() - 0.5) * max), 0, height);
  const opacity = (base: number, spread = 0.06) => clampBannerNumber(base + (rand() - 0.5) * spread, 0.03, 0.9).toFixed(2);
  const strokeWidth = (base: number) => clampBannerNumber(base + rand() * 1.8, 1, 8).toFixed(1);

  const coreBackground =
    style === 'light-clinical' || style === 'minimal-luxury'
      ? `
        <defs>
          <linearGradient id="bg" x1="0" y1="0" x2="1600" y2="320" gradientUnits="userSpaceOnUse">
            <stop offset="0" stop-color="${primary}" />
            <stop offset="0.55" stop-color="${style === 'minimal-luxury' ? '#F8FAFC' : '#EEF6FF'}" />
            <stop offset="1" stop-color="${style === 'minimal-luxury' ? '#F3F4F6' : '#DBEAFE'}" />
          </linearGradient>
        </defs>
      `
      : `
        <defs>
          <linearGradient id="bg" x1="0" y1="0" x2="1600" y2="320" gradientUnits="userSpaceOnUse">
            <stop offset="0" stop-color="${style === 'tech-orthopedic' ? '#07111E' : '#08111B'}" />
            <stop offset="0.5" stop-color="${style === 'abstract-medical' ? '#132238' : '#10253F'}" />
            <stop offset="1" stop-color="#07111E" />
          </linearGradient>
        </defs>
      `;

  const commonOverlays = `
    <rect width="1600" height="320" fill="url(#bg)"/>
  `;

  const styleArt = (() => {
    if (backgroundKey === 'dark-premium') {
      const x1 = shift(220, 120);
      const x2 = shift(1280, 120);
      return `
        <defs>
          <radialGradient id="glowA" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(${x1} ${shiftY(96, 40)}) rotate(90) scale(${clampBannerNumber(170 + rand() * 60, 140, 240)} ${clampBannerNumber(300 + rand() * 80, 240, 360)})">
            <stop offset="0" stop-color="${accent}" stop-opacity="${opacity(0.34, 0.08)}"/>
            <stop offset="1" stop-color="${accent}" stop-opacity="0"/>
          </radialGradient>
          <linearGradient id="streak" x1="${clampBannerNumber(x1 - 120, 0, width)}" y1="120" x2="${clampBannerNumber(x2 + 120, 0, width)}" y2="220" gradientUnits="userSpaceOnUse">
            <stop offset="0" stop-color="${accent}" stop-opacity="0"/>
            <stop offset="0.42" stop-color="${accent}" stop-opacity="${opacity(0.52, 0.12)}"/>
            <stop offset="1" stop-color="${textColor}" stop-opacity="0"/>
          </linearGradient>
          <linearGradient id="lineGlow" x1="160" y1="70" x2="1480" y2="210" gradientUnits="userSpaceOnUse">
            <stop stop-color="${accent}" stop-opacity="${opacity(0.9, 0.05)}" />
            <stop offset="1" stop-color="${textColor}" stop-opacity="${opacity(0.55, 0.05)}" />
          </linearGradient>
          <pattern id="grid" width="44" height="44" patternUnits="userSpaceOnUse">
            <path d="M44 0H0V44" stroke="${textColor}" stroke-opacity="0.07" stroke-width="1"/>
          </pattern>
        </defs>
        <rect width="1600" height="320" fill="url(#glowA)"/>
        <rect width="1600" height="320" fill="url(#grid)"/>
        <circle cx="${shift(1180, 180)}" cy="${shiftY(90, 40)}" r="${clampBannerNumber(72 + rand() * 40, 58, 126)}" fill="${accent}" fill-opacity="${opacity(0.12, 0.04)}"/>
        <circle cx="${shift(320, 160)}" cy="${shiftY(230, 30)}" r="${clampBannerNumber(110 + rand() * 38, 90, 150)}" fill="${accent}" fill-opacity="${opacity(0.08, 0.03)}"/>
        <path d="M0 250C180 192 320 178 506 214C716 256 858 104 1048 146C1208 183 1376 166 1600 118V320H0V250Z" fill="#0B1D32" fill-opacity="${opacity(0.84, 0.05)}"/>
        <path d="M-40 188C168 128 338 104 534 150C737 198 903 208 1080 146C1232 93 1394 84 1640 126" stroke="url(#streak)" stroke-width="${strokeWidth(4)}" stroke-linecap="round"/>
        <path d="M-10 206C206 138 384 118 570 158C770 201 938 208 1116 158C1286 111 1424 96 1620 112" stroke="url(#lineGlow)" stroke-opacity="${opacity(0.24, 0.04)}" stroke-width="${strokeWidth(2.2)}" stroke-linecap="round"/>
        <path d="M1060 66L1128 66M1094 32L1094 100" stroke="${accent}" stroke-opacity="${opacity(0.16, 0.03)}" stroke-width="4" stroke-linecap="round"/>
        <path d="M1094 60L1128 60M1111 43L1111 77" stroke="${accent}" stroke-opacity="${opacity(0.22, 0.03)}" stroke-width="4" stroke-linecap="round"/>
      `;
    }

    if (backgroundKey === 'light-clinical') {
      const waveLift = layoutVariant.includes('airy') || layoutVariant.includes('wide') ? 0.04 : 0.08;
      return `
        <defs>
          <radialGradient id="glow" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(${shift(320, 90)} ${shiftY(108, 40)}) rotate(90) scale(${clampBannerNumber(150 + rand() * 50, 120, 220)} ${clampBannerNumber(260 + rand() * 40, 220, 330)})">
            <stop offset="0" stop-color="${accent}" stop-opacity="${opacity(0.30, 0.06)}"/>
            <stop offset="1" stop-color="${accent}" stop-opacity="0"/>
          </radialGradient>
          <pattern id="grid" width="48" height="48" patternUnits="userSpaceOnUse">
            <path d="M48 0H0V48" stroke="#94A3B8" stroke-opacity="0.10" stroke-width="1"/>
          </pattern>
          <linearGradient id="wash" x1="0" y1="0" x2="1600" y2="320" gradientUnits="userSpaceOnUse">
            <stop offset="0" stop-color="${accent}" stop-opacity="0.16"/>
            <stop offset="1" stop-color="${primary}" stop-opacity="0"/>
          </linearGradient>
        </defs>
        <rect width="1600" height="320" fill="url(#glow)"/>
        <rect width="1600" height="320" fill="url(#grid)"/>
        <path d="M0 262C220 214 410 206 572 236C764 273 910 169 1096 168C1270 168 1420 194 1600 152V320H0V262Z" fill="${accent}" fill-opacity="${opacity(0.14, 0.04)}"/>
        <path d="M-24 180C204 130 382 108 556 138C744 170 910 160 1086 130C1268 98 1418 96 1638 138" stroke="${accent}" stroke-opacity="${opacity(0.24, 0.05)}" stroke-width="${strokeWidth(4)}" stroke-linecap="round"/>
        <rect x="0" y="0" width="1600" height="320" fill="url(#wash)" opacity="${opacity(0.8, 0.05)}"/>
        <circle cx="${shift(1240, 120)}" cy="${shiftY(82, 28)}" r="${clampBannerNumber(28 + rand() * 14, 22, 44)}" stroke="${accent}" stroke-opacity="${opacity(0.16, 0.04)}" stroke-width="2"/>
        <circle cx="${shift(1290, 140)}" cy="${shiftY(138, 26)}" r="${clampBannerNumber(14 + rand() * 10, 10, 26)}" stroke="${accent}" stroke-opacity="${opacity(0.14, 0.04)}" stroke-width="2"/>
        <path d="M140 74H${clampBannerNumber(280 + Math.round(rand() * 180), 220, 560)}" stroke="${textColor}" stroke-opacity="0.08" stroke-width="2"/>
        <path d="M1206 40H${clampBannerNumber(1396 + Math.round(rand() * 120), 1240, 1560)}" stroke="${accent}" stroke-opacity="0.10" stroke-width="2"/>
        <path d="M0 ${Math.round(240 - waveLift * 320)}C${Math.round(220 + rand() * 60)} ${Math.round(198 - waveLift * 110)} ${Math.round(410 + rand() * 60)} ${Math.round(190 - waveLift * 70)} ${Math.round(572 + rand() * 60)} ${Math.round(220 - waveLift * 40)}C${Math.round(764 + rand() * 40)} ${Math.round(246 - waveLift * 40)} ${Math.round(910 + rand() * 20)} ${Math.round(164 + waveLift * 20)} ${Math.round(1096 + rand() * 20)} ${Math.round(166 + waveLift * 16)}C${Math.round(1270 + rand() * 20)} ${Math.round(166 + waveLift * 8)} ${Math.round(1420 + rand() * 10)} ${Math.round(188 + waveLift * 10)} 1600 ${Math.round(146 + waveLift * 10)}V320H0V${Math.round(240 - waveLift * 320)}Z" fill="${accent}" fill-opacity="${opacity(0.08, 0.03)}"/>
      `;
    }

    if (backgroundKey === 'abstract-medical') {
      return `
        <defs>
          <radialGradient id="glow1" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(${shift(270, 120)} ${shiftY(94, 36)}) rotate(90) scale(${clampBannerNumber(120 + rand() * 30, 90, 170)} ${clampBannerNumber(260 + rand() * 50, 220, 320)})">
            <stop offset="0" stop-color="${accent}" stop-opacity="${opacity(0.28, 0.08)}"/>
            <stop offset="1" stop-color="${accent}" stop-opacity="0"/>
          </radialGradient>
          <radialGradient id="glow2" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(${shift(1320, 120)} ${shiftY(150, 36)}) rotate(90) scale(${clampBannerNumber(110 + rand() * 30, 80, 160)} ${clampBannerNumber(220 + rand() * 40, 180, 300)})">
            <stop offset="0" stop-color="${textColor}" stop-opacity="${opacity(0.20, 0.06)}"/>
            <stop offset="1" stop-color="${textColor}" stop-opacity="0"/>
          </radialGradient>
          <pattern id="hex" width="84" height="72" patternUnits="userSpaceOnUse">
            <path d="M42 3L63 15V39L42 51L21 39V15L42 3Z" stroke="${textColor}" stroke-opacity="0.08" stroke-width="1"/>
          </pattern>
          <linearGradient id="wave" x1="0" y1="0" x2="1600" y2="320" gradientUnits="userSpaceOnUse">
            <stop offset="0" stop-color="${accent}" stop-opacity="0"/>
            <stop offset="0.5" stop-color="${accent}" stop-opacity="${opacity(0.28, 0.05)}"/>
            <stop offset="1" stop-color="${textColor}" stop-opacity="0"/>
          </linearGradient>
        </defs>
        <rect width="1600" height="320" fill="url(#glow1)"/>
        <rect width="1600" height="320" fill="url(#glow2)"/>
        <rect width="1600" height="320" fill="url(#hex)"/>
        <path d="M0 274C178 194 378 176 546 206C738 241 900 92 1080 136C1254 179 1384 182 1600 106V320H0V274Z" fill="${primary}" fill-opacity="${opacity(0.78, 0.06)}"/>
        <path d="M-20 212C160 176 302 160 474 176C690 197 832 94 1014 104C1168 112 1300 154 1614 124" stroke="${accent}" stroke-opacity="${opacity(0.20, 0.05)}" stroke-width="${strokeWidth(4)}" stroke-linecap="round"/>
        <path d="M-22 236C184 176 350 168 530 190C720 214 902 156 1068 160C1234 164 1414 196 1638 146" stroke="${textColor}" stroke-opacity="0.12" stroke-width="${strokeWidth(2.1)}" stroke-linecap="round"/>
        <circle cx="${shift(1188, 120)}" cy="${shiftY(84, 26)}" r="${clampBannerNumber(92 + rand() * 18, 82, 126)}" fill="${accent}" fill-opacity="${opacity(0.06, 0.03)}"/>
        <circle cx="${shift(1248, 130)}" cy="${shiftY(94, 26)}" r="${clampBannerNumber(156 + rand() * 16, 132, 186)}" stroke="${accent}" stroke-opacity="0.08" stroke-width="2"/>
        <path d="M1100 76H1276M1188 0V160" stroke="${textColor}" stroke-opacity="0.08" stroke-width="2" stroke-linecap="round"/>
      `;
    }

    if (backgroundKey === 'tech-orthopedic') {
      return `
        <defs>
          <radialGradient id="glow" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(${shift(1260, 100)} ${shiftY(112, 28)}) rotate(90) scale(${clampBannerNumber(118 + rand() * 24, 90, 160)} ${clampBannerNumber(240 + rand() * 50, 180, 300)})">
            <stop offset="0" stop-color="${accent}" stop-opacity="${opacity(0.24, 0.05)}"/>
            <stop offset="1" stop-color="${accent}" stop-opacity="0"/>
          </radialGradient>
          <pattern id="grid" width="36" height="36" patternUnits="userSpaceOnUse">
            <path d="M36 0H0V36" stroke="${accent}" stroke-opacity="0.10" stroke-width="1"/>
          </pattern>
          <linearGradient id="trace" x1="0" y1="0" x2="1600" y2="320" gradientUnits="userSpaceOnUse">
            <stop offset="0" stop-color="${accent}" stop-opacity="0"/>
            <stop offset="0.5" stop-color="${accent}" stop-opacity="${opacity(0.40, 0.08)}"/>
            <stop offset="1" stop-color="${textColor}" stop-opacity="0"/>
          </linearGradient>
        </defs>
        <rect width="1600" height="320" fill="url(#glow)"/>
        <rect width="1600" height="320" fill="url(#grid)"/>
        <path d="M0 236C162 186 304 168 480 194C682 224 848 114 1028 128C1220 144 1368 184 1600 126V320H0V236Z" fill="${primary}" fill-opacity="${opacity(0.72, 0.05)}"/>
        <path d="M-20 98H380M160 0V320M264 0V320" stroke="${textColor}" stroke-opacity="0.10" stroke-width="2"/>
        <path d="M1104 58H1500" stroke="${accent}" stroke-opacity="0.14" stroke-width="2"/>
        <path d="M1116 244H1502" stroke="${accent}" stroke-opacity="0.10" stroke-width="2"/>
        <path d="M1066 84H1384" stroke="url(#trace)" stroke-opacity="0.18" stroke-width="4" stroke-linecap="round"/>
        <path d="M1220 38V282" stroke="${textColor}" stroke-opacity="0.08" stroke-width="3"/>
        <circle cx="1220" cy="160" r="64" stroke="${accent}" stroke-opacity="0.14" stroke-width="2"/>
        <circle cx="1220" cy="160" r="16" fill="${accent}" fill-opacity="0.12"/>
        <path d="M1184 160H1256M1220 124V196" stroke="${accent}" stroke-opacity="0.18" stroke-width="3" stroke-linecap="round"/>
        <path d="M0 74C120 74 176 74 280 74" stroke="${accent}" stroke-opacity="0.14" stroke-width="2" />
      `;
    }

    // minimal-luxury
    return `
      <defs>
        <radialGradient id="glow" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(${shift(1340, 100)} ${shiftY(84, 20)}) rotate(90) scale(${clampBannerNumber(88 + rand() * 18, 70, 120)} ${clampBannerNumber(180 + rand() * 36, 140, 220)})">
          <stop offset="0" stop-color="${accent}" stop-opacity="${opacity(0.18, 0.04)}"/>
          <stop offset="1" stop-color="${accent}" stop-opacity="0"/>
        </radialGradient>
        <linearGradient id="accentLine" x1="120" y1="52" x2="1478" y2="248" gradientUnits="userSpaceOnUse">
          <stop offset="0" stop-color="${accent}" stop-opacity="0"/>
          <stop offset="0.5" stop-color="${accent}" stop-opacity="${opacity(0.22, 0.04)}"/>
          <stop offset="1" stop-color="${textColor}" stop-opacity="0"/>
        </linearGradient>
      </defs>
      <rect width="1600" height="320" fill="url(#glow)"/>
      <path d="M0 276C182 240 334 214 516 220C712 228 886 170 1088 178C1262 186 1414 208 1600 160V320H0V276Z" fill="${accent}" fill-opacity="${opacity(0.08, 0.03)}"/>
      <path d="M88 66H1512" stroke="url(#accentLine)" stroke-width="2" stroke-linecap="round"/>
      <path d="M148 92H${clampBannerNumber(692 + Math.round(rand() * 180), 260, 1200)}" stroke="${textColor}" stroke-opacity="0.08" stroke-width="2"/>
      <path d="M916 92H1452" stroke="${textColor}" stroke-opacity="0.08" stroke-width="2"/>
      <path d="M120 236H1480" stroke="${accent}" stroke-opacity="0.08" stroke-width="2"/>
      <circle cx="${shift(1344, 80)}" cy="${shiftY(96, 22)}" r="${clampBannerNumber(18 + rand() * 8, 12, 28)}" stroke="${accent}" stroke-opacity="0.14" stroke-width="2"/>
      <circle cx="${shift(1408, 80)}" cy="${shiftY(148, 22)}" r="${clampBannerNumber(12 + rand() * 6, 10, 22)}" stroke="${accent}" stroke-opacity="0.14" stroke-width="2"/>
      <path d="M1250 54L1300 54M1275 29L1275 79" stroke="${accent}" stroke-opacity="0.14" stroke-width="3" stroke-linecap="round"/>
      <path d="M0 118H320" stroke="${textColor}" stroke-opacity="0.05" stroke-width="2"/>
    `;
  })();

  const svg = `
    <svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" fill="none" xmlns="http://www.w3.org/2000/svg">
      ${coreBackground}
      ${commonOverlays}
      ${decorativeOverlay}
      ${styleArt}
    </svg>
  `;

  return svgDataUrl(svg);
};

const buildOrthoBannerContext = (input: OrthoBannerGenerationInput) => [
  `Doctor name: ${normalizeBannerText(input.doctorName)}`,
  `Specialty: ${normalizeBannerText(input.specialty)}`,
  `CRM/UF: ${normalizeBannerText(input.crm)} / ${normalizeBannerText(input.crmState)}`.trim(),
  `Hospital/clinic: ${normalizeBannerText(input.hospital)}`,
  `Phone: ${normalizeBannerText(input.phone)}`,
  `Email: ${normalizeBannerText(input.email)}`,
  `Site: ${normalizeBannerText(input.site)}`,
  `Header display name: ${normalizeBannerText(input.displayName)}`,
].join('\n');

const buildOrthoBannerFactualProfileBlock = (input: OrthoBannerGenerationInput) => [
  'FACTUAL PROFILE DATA',
  `- doctorName: ${normalizeBannerText(input.doctorName) || '[missing]'}`,
  `- specialty: ${normalizeBannerText(input.specialty) || '[missing]'}`,
  `- crm: ${normalizeBannerText(input.crm) || '[missing]'}`,
  `- crmState: ${normalizeBannerText(input.crmState) || '[missing]'}`,
  `- hospital: ${normalizeBannerText(input.hospital) || '[missing]'}`,
  `- phone: ${normalizeBannerText(input.phone) || '[missing]'}`,
  `- email: ${normalizeBannerText(input.email) || '[missing]'}`,
  `- site: ${normalizeBannerText(input.site) || '[missing]'}`,
  `- displayName: ${normalizeBannerText(input.displayName) || '[missing]'}`,
].join('\n');

const buildOrthoBannerCreativeBlock = (concept: OrthoBannerConcept) => [
  'CREATIVE COPY',
  `- headline: ${normalizeBannerText(concept.headline || concept.titlePrimary || '')}`,
  `- subheadline: ${normalizeBannerText(concept.subheadline || concept.titleSecondary || '')}`,
  `- tagline: ${normalizeBannerText(concept.tagline || '')}`,
  `- supportText: ${normalizeBannerText(concept.supportText || '')}`,
  `- tone: ${normalizeBannerText(concept.tone || '')}`,
].join('\n');

const buildOrthoBannerCopyPrompt = (input: OrthoBannerGenerationInput) => {
  const styleHint = input.stylePreference && input.stylePreference !== 'auto'
    ? `Preferred style direction: ${input.stylePreference}.`
    : 'Use a balanced mix of dark-premium, light-clinical, abstract-medical, tech-orthopedic, and minimal-luxury medical directions.';
  const existingTagline = normalizeBannerText(input.bannerTagline || '');
  const existingSupportText = normalizeBannerText(input.bannerSupportText || '');

  return `
You are designing premium orthopedic banner concepts for a professional medical credential system.

GOAL:
Create 5 banner concepts for a wide horizontal banner. Each concept must feel like a full designer-created banner direction, not a rigid template.

HARD RULES:
- Return STRICT JSON only.
- No markdown, no explanation, no code fences.
- Do not generate random images.
- Do not invent free-form layouts outside the banner safe areas.
- The system will compose the final banner, but your concept must define the creative direction.
- Keep the copy elegant, concise, and professional.
- If an existing tagline is provided, improve it while preserving meaning.
- If an existing support line is provided, improve it while preserving meaning.
- The output must include richer visual specifications, not only copy.
- Vary the backgroundSpec, layoutSpec, paletteSpec, typographySpec, and decorativeSpec across the five concepts.

INPUT CONTEXT:
${buildOrthoBannerContext(input)}
${existingTagline ? `\nEXISTING TAGLINE:\n${existingTagline}\n` : ''}
${existingSupportText ? `\nEXISTING SUPPORT LINE:\n${existingSupportText}\n` : ''}
${normalizeBannerText(input.backgroundSpecPreference || '') ? `\nPREFERRED BACKGROUND SPEC:\n${normalizeBannerText(input.backgroundSpecPreference || '')}\n` : ''}
${normalizeBannerText(input.layoutSpecPreference || '') ? `\nPREFERRED LAYOUT SPEC:\n${normalizeBannerText(input.layoutSpecPreference || '')}\n` : ''}
${normalizeBannerText(input.paletteSpecPreference || '') ? `\nPREFERRED PALETTE SPEC:\n${normalizeBannerText(input.paletteSpecPreference || '')}\n` : ''}
${normalizeBannerText(input.typographySpecPreference || '') ? `\nPREFERRED TYPOGRAPHY SPEC:\n${normalizeBannerText(input.typographySpecPreference || '')}\n` : ''}
${normalizeBannerText(input.decorativeSpecPreference || '') ? `\nPREFERRED DECORATIVE SPEC:\n${normalizeBannerText(input.decorativeSpecPreference || '')}\n` : ''}

DESIGN RULES:
- Structure is fixed by the app: wide left-safe area for headline and support copy, and right-safe area for institutional information.
- The background must feel premium, clinical, and controlled.
- Keep the copy suitable for a 5:1 banner crop.
- The text must be sophisticated, readable, and suited to orthopedic / trauma communication.
- The concept should create a strong visual hierarchy and a premium institutional feel.
- Every concept must use a different backgroundSpec, layoutSpec, paletteSpec, typographySpec, and decorativeSpec combination.
- Treat the doctor/profile fields as factual and immutable. Creative freedom applies only to headline, subheadline, tagline, supportText, tone, and visual specs.
- Never invent or alter the doctor identity, clinic/hospital name, CRM, phone, email, or website.

${styleHint}

Return JSON in this shape:
{
  "concepts": [
    {
      "styleTemplate": "dark-premium | light-clinical | abstract-medical | tech-orthopedic | minimal-luxury",
      "style": "dark-premium | light-clinical | abstract-medical | tech-orthopedic | minimal-luxury",
      "templateId": "dark-premium | light-clinical | abstract-medical | tech-orthopedic | minimal-luxury",
      "backgroundSpec": "gradient-medical | texture-medical | pattern-geometric | clean-hospital | premium-dark | technological | institutional | abstract-medical",
      "layoutSpec": "left-hero-right-info | centered-title-right-info | split-balance | right-compact | wide-minimal | institutional-clean",
      "paletteSpec": "short palette label",
      "typographySpec": "condensed | elegant | technical | premium | clean",
      "decorativeSpec": "waves | grids | hexagons | medical-traces | bars | glow | surgical-lines | subtle",
      "headline": "UPPERCASE SHORT HEADLINE",
      "subheadline": "UPPERCASE SHORT SUBTITLE",
      "titlePrimary": "UPPERCASE SHORT HEADLINE",
      "titleSecondary": "UPPERCASE SHORT SUBTITLE",
      "tagline": "Short premium institutional tagline",
      "supportText": "Short supporting line or value proposition",
      "tone": "Short premium tone description",
      "accentTone": "light | cyan | blue | teal | ice",
      "textColor": "#FFFFFF or a readable color",
      "primaryColor": "#HEX",
      "accentColor": "#HEX",
      "colorScheme": "short palette label",
      "backgroundStyle": "dark-premium | light-clinical | abstract-medical | tech-orthopedic | minimal-luxury",
      "layoutVariant": "left_hero | balanced | right_compact | airy | split",
      "backgroundPrompt": "Premium medical background direction and atmosphere only"
    }
  ]
}

Make exactly 5 concepts, one for each styleTemplate: dark-premium, light-clinical, abstract-medical, tech-orthopedic, minimal-luxury.
`.trim();
};

const generateOrthoBannerConcepts = async (input: OrthoBannerGenerationInput): Promise<OrthoBannerConcept[]> => {
  const ai = createGeminiClient("generateBannerAI");
  const conceptModels = ['gemini-2.0-flash', 'gemini-3-flash-preview'];

  console.info('[Ortho Banner IA] concept generation start', {
    doctorName: input.doctorName,
    specialty: input.specialty,
    crm: input.crm,
    crmState: input.crmState,
    hospital: input.hospital,
    stylePreference: input.stylePreference,
    language: input.language,
  });

  let lastError: unknown = null;

  for (const model of conceptModels) {
    try {
      console.info('[Ortho Banner IA] requesting concepts', { model });
      const response = await fetchWithRetry(async () => {
        const result = await ai.models.generateContent({
          model,
          contents: buildOrthoBannerCopyPrompt(input),
          config: {
            responseMimeType: 'application/json',
            responseSchema: {
              type: Type.OBJECT,
              properties: {
                concepts: {
                  type: Type.ARRAY,
                  items: {
                    type: Type.OBJECT,
                    properties: {
                      styleTemplate: { type: Type.STRING },
                      style: { type: Type.STRING },
                      templateId: { type: Type.STRING },
                      titlePrimary: { type: Type.STRING },
                      titleSecondary: { type: Type.STRING },
                      headline: { type: Type.STRING },
                      subheadline: { type: Type.STRING },
                      tagline: { type: Type.STRING },
                      supportText: { type: Type.STRING },
                      tone: { type: Type.STRING },
                      accentTone: { type: Type.STRING },
                      textColor: { type: Type.STRING },
                      primaryColor: { type: Type.STRING },
                      accentColor: { type: Type.STRING },
                      colorScheme: { type: Type.STRING },
                      backgroundStyle: { type: Type.STRING },
                      backgroundSpec: { type: Type.STRING },
                      layoutVariant: { type: Type.STRING },
                      layoutSpec: { type: Type.STRING },
                      paletteSpec: { type: Type.STRING },
                      typographySpec: { type: Type.STRING },
                      decorativeSpec: { type: Type.STRING },
                      backgroundPrompt: { type: Type.STRING },
                    },
                    required: ['styleTemplate', 'style', 'templateId', 'headline', 'subheadline', 'titlePrimary', 'titleSecondary', 'tagline', 'supportText', 'tone', 'accentTone', 'textColor', 'primaryColor', 'accentColor', 'colorScheme', 'backgroundStyle', 'backgroundSpec', 'layoutVariant', 'layoutSpec', 'paletteSpec', 'typographySpec', 'decorativeSpec', 'backgroundPrompt'],
                  },
                },
              },
              required: ['concepts'],
            },
          },
        });

        const parsed = JSON.parse(cleanJson(result.text || '{}'));
        return parsed;
      }, 1, 1200, 45000);

      const concepts = Array.isArray(response?.concepts) ? response.concepts : [];
      console.info('[Ortho Banner IA] concepts received', {
        model,
        count: concepts.length,
        sample: concepts[0],
      });

      const normalized = concepts
        .map((concept: any): OrthoBannerConcept | null => {
          const styleTemplate = String(concept?.styleTemplate || concept?.style || concept?.templateId || '').toLowerCase() as OrthoBannerStyle;
          if (!['dark-premium', 'light-clinical', 'abstract-medical', 'tech-orthopedic', 'minimal-luxury'].includes(styleTemplate)) return null;

          return {
            style: styleTemplate,
            templateId: styleTemplate,
            styleTemplate,
            titlePrimary: normalizeBannerText(concept?.titlePrimary || concept?.headline || '').toUpperCase(),
            titleSecondary: normalizeBannerText(concept?.titleSecondary || concept?.subheadline || '').toUpperCase(),
            headline: normalizeBannerText(concept?.headline || concept?.titlePrimary || '').toUpperCase(),
            subheadline: normalizeBannerText(concept?.subheadline || concept?.titleSecondary || '').toUpperCase(),
            tagline: normalizeBannerText(concept?.tagline || ''),
            supportText: normalizeBannerText(concept?.supportText || ''),
            tone: normalizeBannerText(concept?.tone || ''),
            accentTone: normalizeBannerText(concept?.accentTone || ''),
            textColor: normalizeBannerText(concept?.textColor || ''),
            primaryColor: normalizeBannerText(concept?.primaryColor || ''),
            accentColor: normalizeBannerText(concept?.accentColor || ''),
            colorScheme: normalizeBannerText(concept?.colorScheme || ''),
            backgroundStyle: normalizeBannerText(concept?.backgroundStyle || ''),
            backgroundSpec: normalizeBannerText(concept?.backgroundSpec || concept?.backgroundStyle || ''),
            layoutVariant: normalizeBannerText(concept?.layoutVariant || ''),
            layoutSpec: normalizeBannerText(concept?.layoutSpec || concept?.layoutVariant || ''),
            paletteSpec: normalizeBannerText(concept?.paletteSpec || concept?.colorScheme || ''),
            typographySpec: normalizeBannerText(concept?.typographySpec || ''),
            decorativeSpec: normalizeBannerText(concept?.decorativeSpec || ''),
            backgroundPrompt: normalizeBannerText(concept?.backgroundPrompt || ''),
          };
        })
        .filter(Boolean) as OrthoBannerConcept[];

      const fallbackStyles: OrthoBannerStyle[] = ['dark-premium', 'light-clinical', 'abstract-medical', 'tech-orthopedic', 'minimal-luxury'];
      const fallbackLayoutVariants = ['left_hero', 'balanced', 'right_compact', 'airy', 'split'];
      const fallbackColorPairs = [
        { primaryColor: '#0B1320', accentColor: '#22D3EE', textColor: '#FFFFFF', colorScheme: 'navy-cyan' },
        { primaryColor: '#F8FAFC', accentColor: '#60A5FA', textColor: '#0F172A', colorScheme: 'ice-blue' },
        { primaryColor: '#10253F', accentColor: '#2DD4BF', textColor: '#FFFFFF', colorScheme: 'teal-ink' },
        { primaryColor: '#07111E', accentColor: '#67E8F9', textColor: '#FFFFFF', colorScheme: 'tech-cyan' },
        { primaryColor: '#F8FAFC', accentColor: '#A78BFA', textColor: '#0F172A', colorScheme: 'luxury-silver' },
      ];
      while (normalized.length < 5) {
        const index = normalized.length;
        const style = fallbackStyles[index] || 'dark-premium';
        const colorPair = fallbackColorPairs[index] || fallbackColorPairs[1];
        normalized.push({
          style,
          templateId: style,
          styleTemplate: style,
          [BANNER_BACKGROUND_SPEC_KEY]:
            style === 'tech-orthopedic'
              ? 'technological'
              : style === 'light-clinical'
                ? 'clean-hospital'
                : style === 'abstract-medical'
                  ? 'abstract-medical'
                  : style === 'dark-premium'
                    ? 'premium-dark'
                    : 'minimal-luxury',
          [BANNER_LAYOUT_SPEC_KEY]:
            fallbackLayoutVariants[index] === 'left_hero'
              ? 'left-hero-right-info'
              : fallbackLayoutVariants[index] === 'right_compact'
                ? 'right-compact'
                : fallbackLayoutVariants[index] === 'airy'
                  ? 'wide-minimal'
                  : fallbackLayoutVariants[index] === 'split'
                    ? 'split-balance'
                    : 'institutional-clean',
          paletteSpec:
            style === 'tech-orthopedic'
              ? 'tech-cyan'
              : style === 'light-clinical'
                ? 'ice-blue'
                : style === 'abstract-medical'
                  ? 'teal-ink'
                  : style === 'dark-premium'
                    ? 'navy-cyan'
                    : 'luxury-silver',
          typographySpec:
            style === 'tech-orthopedic'
              ? 'technical'
              : style === 'light-clinical'
                ? 'clean'
                : style === 'abstract-medical'
                  ? 'elegant'
                  : style === 'dark-premium'
                    ? 'premium'
                    : 'minimal',
          decorativeSpec:
            style === 'tech-orthopedic'
              ? 'grids'
              : style === 'light-clinical'
                ? 'bars'
                : style === 'abstract-medical'
                  ? 'hexagons'
                  : style === 'dark-premium'
                    ? 'waves'
                    : 'glow',
          titlePrimary: style === 'minimal-luxury' ? 'ORTHO ID' : 'ORTHO PRECISION',
          titleSecondary: style === 'minimal-luxury' ? 'MEDICAL IDENTITY' : 'MEDICAL CREDENTIAL',
          headline: style === 'minimal-luxury' ? 'ORTHO ID' : 'ORTHO PRECISION',
          subheadline: style === 'minimal-luxury' ? 'MEDICAL IDENTITY' : 'MEDICAL CREDENTIAL',
          tagline:
            style === 'tech-orthopedic'
              ? 'Clinical precision with modern technical clarity'
              : style === 'light-clinical'
                ? 'Clear, calm, and institutionally refined'
                : style === 'abstract-medical'
                  ? 'Structured care with expressive visual depth'
                  : style === 'dark-premium'
                    ? 'Elevated orthopedic identity with a premium signature'
                    : 'Minimal, elegant, and highly legible',
          supportText:
            style === 'tech-orthopedic'
              ? 'Designed for clarity, expertise, and trust.'
              : style === 'light-clinical'
                ? 'Clean communication with soft contrast.'
                : style === 'abstract-medical'
                  ? 'Modern medical language with visual depth.'
                  : style === 'dark-premium'
                    ? 'Premium specialist presence with restrained luxury.'
                    : 'Minimal branding with maximum readability.',
          tone:
            style === 'tech-orthopedic'
              ? 'Technical and precise'
              : style === 'light-clinical'
                ? 'Clean and clinical'
                : style === 'abstract-medical'
                  ? 'Artful and structured'
                  : style === 'dark-premium'
                    ? 'Premium and commanding'
                    : 'Minimal and luxurious',
          accentTone: style === 'dark-premium' ? 'cyan' : style === 'light-clinical' ? 'blue' : style === 'abstract-medical' ? 'teal' : style === 'tech-orthopedic' ? 'ice' : 'cyan',
          textColor: colorPair.textColor,
          primaryColor: colorPair.primaryColor,
          accentColor: colorPair.accentColor,
          colorScheme: colorPair.colorScheme,
          backgroundStyle: style,
          backgroundSpec:
            style === 'tech-orthopedic'
              ? 'technological'
              : style === 'light-clinical'
                ? 'clean-hospital'
                : style === 'abstract-medical'
                  ? 'abstract-medical'
                  : style === 'dark-premium'
                    ? 'premium-dark'
                    : 'minimal-luxury',
          layoutVariant: fallbackLayoutVariants[index] || 'balanced',
          layoutSpec:
            fallbackLayoutVariants[index] === 'left_hero'
              ? 'left-hero-right-info'
              : fallbackLayoutVariants[index] === 'right_compact'
                ? 'right-compact'
                : fallbackLayoutVariants[index] === 'airy'
                  ? 'wide-minimal'
                  : fallbackLayoutVariants[index] === 'split'
                    ? 'split-balance'
                    : 'institutional-clean',
          [BANNER_PALETTE_SPEC_KEY]: colorPair.colorScheme,
          [BANNER_TYPOGRAPHY_SPEC_KEY]:
            style === 'tech-orthopedic'
              ? 'technical'
              : style === 'light-clinical'
                ? 'clean'
                : style === 'abstract-medical'
                  ? 'elegant'
                  : style === 'dark-premium'
                    ? 'premium'
                    : 'minimal',
          [BANNER_DECORATIVE_SPEC_KEY]:
            style === 'tech-orthopedic'
              ? 'grids'
              : style === 'light-clinical'
                ? 'bars'
                : style === 'abstract-medical'
                  ? 'hexagons'
                  : style === 'dark-premium'
                    ? 'waves'
                    : 'glow',
          backgroundPrompt:
            style === 'tech-orthopedic'
              ? 'tech-focused orthopedic banner with clean grid geometry, subtle data-line motifs, premium medical depth, no text'
              : style === 'light-clinical'
                ? 'light clinical banner with bright airy gradients, soft hospital cleanliness, lots of negative space, no text'
                : style === 'abstract-medical'
                  ? 'abstract medical banner with layered translucent shapes, premium institutional texture, no text'
                  : style === 'dark-premium'
                    ? 'dark premium medical banner with deep navy gradients, elegant cyan streaks, and refined luxury atmosphere, no text'
                    : 'minimal luxury medical banner with soft bright gradients, restrained accents, and premium whitespace, no text',
        });
      }

      return normalized.slice(0, 5);
    } catch (error) {
      lastError = error;
      console.warn('[Ortho Banner IA] concept model failed', { model, error });
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error('Unable to generate banner concepts with IA.');
};

export const generateOrthoBannerOptions = async (
  input: OrthoBannerGenerationInput
): Promise<OrthoBannerOption[]> => {
  const concepts = await generateOrthoBannerConcepts(input);
  const ai = createGeminiClient("generateBannerAI");
  const imageModels = ['gemini-3-pro-image-preview', 'imagen-4.0-generate-001'];

  const options: OrthoBannerOption[] = [];
  for (let index = 0; index < Math.min(concepts.length, 4); index += 1) {
    const concept = concepts[index];
    let imageUrl: string | null = null;
    let lastImageError: unknown = null;

    for (const model of imageModels) {
      try {
        imageUrl = await generateInlineBannerImage(ai, model, concept, input, index);
        if (imageUrl) break;
      } catch (error) {
        lastImageError = error;
        console.warn('[Ortho Banner IA] image model failed', {
          model,
          index,
          style: concept.styleTemplate,
          error,
        });
      }
    }

    if (!imageUrl) {
      console.warn('[Ortho Banner IA] falling back to generated SVG banner', {
        index,
        style: concept.styleTemplate,
        lastImageError,
      });
      imageUrl = buildOrthoBannerArtworkDataUrl(concept, input, index);
    }

    options.push({
      ...concept,
      factualProfileData: {
        doctorName: input.doctorName,
        specialty: input.specialty,
        crm: input.crm,
        crmState: input.crmState,
        hospital: input.hospital,
        phone: input.phone,
        email: input.email,
        site: input.site,
        displayName: input.displayName,
      },
      aiCopy: {
        headline: concept.headline,
        subheadline: concept.subheadline,
        tagline: concept.tagline,
        supportText: concept.supportText,
        tone: concept.tone,
      },
      imageUrl: imageUrl || resolveOrthoBannerTemplateAsset(concept.styleTemplate || concept.templateId || concept.style),
    });
  }

  console.info('[Ortho Banner IA] creative options generated', {
    total: options.length,
    styles: options.map((option) => option.style),
  });

  return options;
};

// New export: generate360OrthoVideo
export const generate360OrthoVideo = async (promptText: string, context: string, imageBase64?: string) => {
  const ai = createGeminiClient("generateContent");
  
  try {
    let operation;
    const finalPrompt = `Cinematic 360 degree rotation view of ${promptText}. Medical anatomical visualization. High detail 8k render. Studio lighting. ${context}`;

    if (imageBase64) {
      const mimeMatch = imageBase64.match(/^data:(image\/\w+);base64,/);
      const mimeType = mimeMatch ? mimeMatch[1] : 'image/png';
      const cleanBase64 = imageBase64.replace(/^data:image\/\w+;base64,/, "");
      
      operation = await ai.models.generateVideos({
        model: 'veo-3.1-fast-generate-preview', // Or veo-3.1-generate-preview for higher quality but slower
        prompt: finalPrompt,
        image: {
            imageBytes: cleanBase64,
            mimeType: mimeType
        },
        config: {
            numberOfVideos: 1,
            resolution: '720p',
            aspectRatio: '16:9'
        }
      });
    } else {
      operation = await ai.models.generateVideos({
        model: 'veo-3.1-fast-generate-preview',
        prompt: finalPrompt,
        config: {
            numberOfVideos: 1,
            resolution: '720p',
            aspectRatio: '16:9'
        }
      });
    }

    // Polling logic
    while (!operation.done) {
        await new Promise(resolve => setTimeout(resolve, 5000)); // Check every 5s
        operation = await ai.operations.getVideosOperation({operation: operation});
    }

    const videoUri = operation.response?.generatedVideos?.[0]?.video?.uri;
    if (!videoUri) throw new Error("Video generation failed or returned no URI");

    // The URI needs the API key appended to be fetched if it's a download link, 
    // BUT the Google GenAI SDK usually provides a URI that is accessible or we might need to handle it.
    // Documentation says: "The response.body contains the MP4 bytes. You must append an API key when fetching from the download link."
    
    // We will return the URI with key appended for the frontend to use in <video src="...">
    return { videoUrl: videoUri };

  } catch (error) {
    console.error("Video Generation Error:", error);
    throw error;
  }
};

// NEW FUNCTION: Search Orthopedic Guidelines
export const searchOrthopedicGuidelines = async (query: string, lang: Language = 'pt') => {
  const ai = createGeminiClient("searchOrthopedicGuidelines");
  const prompt = (lang === "pt"
    ? `Atue como um pesquisador ortopédico sênior. O usuário está buscando DIRETRIZES E ATUALIZAÇÕES sobre: "${query}".

       Estruture a resposta obrigatoriamente nestas seções:
       1. Diagnóstico e classificação
       2. Indicações de tratamento
          - conservador
          - cirúrgico
       3. Técnicas cirúrgicas detalhadas
       4. Materiais
       5. Pós-operatório
       6. Complicações

       Regras para técnicas cirúrgicas:
       - Para cada técnica, inclua nome, indicação, posicionamento do paciente, via de acesso, passo a passo, pontos críticos e complicações.
       - Evite respostas genéricas sem detalhamento prático.
       - Use linguagem médica, objetiva e útil para ortopedistas.
       - Cite fontes/sociedades médicas relevantes quando possível, sem inventar URLs.

       Formatação: Markdown claro, com títulos numerados, listas e negrito para facilitar a leitura rápida.`
    : `Act as a senior orthopedic researcher. The user is searching for GUIDELINES AND UPDATES on: "${query}".

       Structure the answer with the following mandatory sections:
       1. Diagnosis and classification
       2. Treatment indications
          - conservative
          - surgical
       3. Detailed surgical techniques
       4. Materials
       5. Post-operative care
       6. Complications

       Technique rules:
       - For each technique, include name, indication, patient positioning, surgical approach, step-by-step technique, critical points, and complications.
       - Avoid generic answers without practical detail.
       - Use medical, objective language useful for orthopedic surgeons.
       - Cite relevant societies/sources where possible, without inventing URLs.

       Format: Clear Markdown with numbered titles, lists, and bold text for quick reading.`)
    + `\n\n${getSelectedLanguageInstruction(lang)}`;

  try {
    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: prompt,
      config: { temperature: 0.2 }
    });
    return response.text || "";
  } catch (error) {
    console.error("Guideline Search Error:", error);
    throw error;
  }
};


